"use client";
import { Background, BaseEdge, Controls, EdgeLabelRenderer, Handle, Panel, Position, ReactFlow, addEdge, applyEdgeChanges, applyNodeChanges, getBezierPath, reconnectEdge, useNodeId, useNodesData, useReactFlow, useStore, useStoreApi } from "@xyflow/react";
import { AlertCircle, Atom, AudioLines, Box, Brain, Brush, Camera, CheckCircle, CheckIcon, ChevronDownIcon, ChevronRight, ChevronRightIcon, ChevronUpIcon, Circle, CircleIcon, Clapperboard, Clock, Combine, Download, Droplets, Ear, Edit3, Eye, File as File$1, FileArchive, FileCode, FileSpreadsheet, FileText, Film, HelpCircle, Image as Image$1, ImageIcon, Layers, Library, Lightbulb, Link, Loader2, Lock, Maximize2, MenuIcon, MessageSquare, Mic, MicOff, Move3d, Music, PackagePlus, Palette, Pause, Pencil, PersonStanding, Play, Plus, Presentation, Puzzle, RectangleHorizontal, RefreshCw, RotateCcw, Scissors, Sparkles, Split, Square, StopCircle, Tag, Trash, Trash2, Type, Unlock, Upload, Video, VolumeX, Wand2, X, XIcon } from "lucide-react";
import * as React$1 from "react";
import React, { Component, forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslations } from "use-intl";
import { z } from "zod";
import "zustand/vanilla";
import { createTranslator } from "use-intl/core";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { v4 } from "uuid";
import { create } from "zustand";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useShallow } from "zustand/react/shallow";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as LabelPrimitive from "@radix-ui/react-label";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { createPortal } from "react-dom";
import { Masonry } from "masonic";
import * as THREE from "three";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
//#region \0rolldown/runtime.js
var __defProp = Object.defineProperty;
var __exportAll = (all, no_symbols) => {
	let target = {};
	for (var name in all) __defProp(target, name, {
		get: all[name],
		enumerable: true
	});
	if (!no_symbols) __defProp(target, Symbol.toStringTag, { value: "Module" });
	return target;
};
//#endregion
//#region src/core/constants/modality-nodes.ts
const MODALITY_NODE_TYPES = [
	"imageNode",
	"textNode",
	"videoNode",
	"audioNode",
	"fileNode",
	"modelNode",
	"linkNode"
];
const isModalityNode = (type) => {
	return !!type && MODALITY_NODE_TYPES.includes(type);
};
//#endregion
//#region src/core/generated/abi/index.ts
const ABI_DEFINITIONS = {
	Asset: {
		type: "object",
		required: ["bytesBase64"],
		properties: {
			bytesBase64: {
				type: "string",
				minLength: 1
			},
			filename: { type: "string" },
			mime: { type: "string" }
		},
		additionalProperties: false
	},
	FileRef: {
		type: "object",
		required: ["file_key"],
		properties: {
			file_key: {
				type: "string",
				minLength: 1
			},
			mime: { type: "string" },
			filename: { type: "string" }
		},
		additionalProperties: false
	},
	VideoRef: {
		type: "object",
		required: ["file_key"],
		properties: {
			file_key: {
				type: "string",
				minLength: 1
			},
			mime: { type: "string" },
			filename: { type: "string" }
		},
		additionalProperties: false
	},
	AudioRef: {
		type: "object",
		required: ["file_key"],
		properties: {
			file_key: {
				type: "string",
				minLength: 1
			},
			mime: { type: "string" },
			filename: { type: "string" }
		},
		additionalProperties: false
	},
	ImageRef: {
		type: "object",
		required: ["file_key"],
		properties: {
			file_key: {
				type: "string",
				minLength: 1
			},
			mime: { type: "string" },
			filename: { type: "string" }
		},
		additionalProperties: false
	},
	ModelRef: {
		type: "object",
		required: ["file_key"],
		properties: {
			file_key: {
				type: "string",
				minLength: 1
			},
			mime: { type: "string" },
			filename: { type: "string" }
		},
		additionalProperties: false
	}
};
const ABI_NODES = {
	"gen-text": {
		inputs: {
			type: "object",
			required: ["text"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				userPrompt: { type: "string" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" }
			},
			additionalProperties: false
		}
	},
	"split-text": {
		inputs: {
			type: "object",
			required: ["text"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				userPrompt: { type: "string" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				texts: {
					type: "array",
					items: { type: "string" },
					"x-expand-each": true
				}
			},
			additionalProperties: false
		}
	},
	"combine-text": {
		inputs: {
			type: "object",
			required: ["texts"],
			properties: {
				texts: {
					type: "array",
					items: {
						type: "string",
						minLength: 1
					}
				},
				userPrompt: { type: "string" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" }
			},
			additionalProperties: false
		}
	},
	"image-fusion": {
		inputs: {
			type: "object",
			required: ["text"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				images: {
					type: "array",
					items: { $ref: "#/$defs/Asset" }
				},
				seed: { type: "integer" },
				width: { type: "integer" },
				height: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"images-gen-video": {
		inputs: {
			type: "object",
			required: ["text"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				images: {
					type: "array",
					items: { $ref: "#/$defs/Asset" }
				},
				duration: { type: "number" },
				seed: { type: "integer" },
				width: { type: "integer" },
				height: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"refs-gen-video": {
		inputs: {
			type: "object",
			required: ["text"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				images: {
					type: "array",
					items: { $ref: "#/$defs/Asset" }
				},
				videos: {
					type: "array",
					items: { $ref: "#/$defs/Asset" }
				},
				audios: {
					type: "array",
					items: { $ref: "#/$defs/Asset" }
				},
				duration: { type: "number" },
				seed: { type: "integer" },
				width: { type: "integer" },
				height: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"image-gen-text": {
		inputs: {
			type: "object",
			required: ["text"],
			properties: {
				text: { type: "string" },
				system: { type: "string" },
				max_new_tokens: { type: "integer" },
				enable_thinking: { type: "boolean" },
				temperature: { type: "number" },
				top_p: { type: "number" },
				top_k: { type: "integer" },
				image: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" }
			},
			additionalProperties: false
		}
	},
	"video-gen-text": {
		inputs: {
			type: "object",
			required: ["text", "video"],
			properties: {
				text: { type: "string" },
				system: { type: "string" },
				max_new_tokens: { type: "integer" },
				enable_thinking: { type: "boolean" },
				temperature: { type: "number" },
				top_p: { type: "number" },
				top_k: { type: "integer" },
				video: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" }
			},
			additionalProperties: false
		}
	},
	transcribe: {
		inputs: {
			type: "object",
			required: ["audio"],
			properties: {
				context: { type: "string" },
				language: { type: "string" },
				max_new_tokens: { type: "number" },
				audio: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" }
			},
			additionalProperties: false
		}
	},
	"concat-videos": {
		inputs: {
			type: "object",
			required: ["videos"],
			properties: { videos: {
				type: "array",
				items: { $ref: "#/$defs/Asset" }
			} },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"extract-audio": {
		inputs: {
			type: "object",
			required: ["video"],
			properties: { video: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"remove-video-audio": {
		inputs: {
			type: "object",
			required: ["video"],
			properties: { video: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"merge-video-audio": {
		inputs: {
			type: "object",
			required: ["video", "audio"],
			properties: {
				video: { $ref: "#/$defs/Asset" },
				audio: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"audio-video-lip-sync": {
		inputs: {
			type: "object",
			required: ["video", "audio"],
			properties: {
				audio: { $ref: "#/$defs/Asset" },
				text: { type: "string" },
				seed: { type: "number" },
				video: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"get-first-frame": {
		inputs: {
			type: "object",
			required: ["video"],
			properties: { video: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"get-last-frame": {
		inputs: {
			type: "object",
			required: ["video"],
			properties: { video: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"parse-document": {
		inputs: {
			type: "object",
			required: ["document"],
			properties: { document: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" }
			},
			additionalProperties: false
		}
	},
	"split-video": {
		inputs: {
			type: "object",
			required: ["video"],
			properties: {
				video: { $ref: "#/$defs/Asset" },
				threshold: { type: "number" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video_parts: {
					type: "array",
					items: { $ref: "#/$defs/VideoRef" }
				}
			},
			additionalProperties: false
		}
	},
	link: {
		inputs: {
			type: "object",
			required: ["url"],
			properties: { url: {
				type: "string",
				minLength: 1
			} },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				mainText: { type: "string" },
				thinking: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" },
				video: { $ref: "#/$defs/VideoRef" },
				audio: { $ref: "#/$defs/AudioRef" },
				extractedTexts: {
					type: "array",
					items: { type: "string" }
				}
			},
			additionalProperties: false
		}
	},
	"image-gen": {
		inputs: {
			type: "object",
			required: [],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				width: { type: "integer" },
				height: { type: "integer" },
				seed: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"gen-music": {
		inputs: {
			type: "object",
			required: [],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				lyrics: { type: "string" },
				tags: { type: "string" },
				language: { type: "string" },
				keyscale: { type: "string" },
				bpm: { type: "number" },
				songTitle: { type: "string" },
				duration: { type: "number" },
				seed: { type: "integer" },
				ref_audio: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"text-gen-speech-preset": {
		inputs: {
			type: "object",
			required: ["text"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				language: { type: "string" },
				speaker: { type: "string" },
				instruct: { type: "string" },
				max_new_tokens: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"image-gen-video": {
		inputs: {
			type: "object",
			required: [
				"text",
				"image",
				"duration"
			],
			properties: {
				duration: { type: "number" },
				text: { type: "string" },
				seed: { type: "number" },
				height: { type: "integer" },
				width: { type: "integer" },
				enhance_prompt: { type: "boolean" },
				image: { $ref: "#/$defs/Asset" },
				image_frame_idx: { type: "integer" },
				image_strength: { type: "number" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"image-edit": {
		inputs: {
			type: "object",
			required: ["text", "image"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				image: { $ref: "#/$defs/Asset" },
				seed: { type: "integer" },
				match_input_size: { type: "boolean" },
				width: { type: "integer" },
				height: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"image-upscale": {
		inputs: {
			type: "object",
			required: ["image"],
			properties: {
				image: { $ref: "#/$defs/Asset" },
				resolution: { type: "string" },
				seed: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"video-upscale": {
		inputs: {
			type: "object",
			required: ["video"],
			properties: {
				video: { $ref: "#/$defs/Asset" },
				resolution: { type: "string" },
				seed: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"video-edit": {
		inputs: {
			type: "object",
			required: ["text", "video"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				video: { $ref: "#/$defs/Asset" },
				seed: { type: "integer" },
				width: { type: "integer" },
				height: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"image-describe": {
		inputs: {
			type: "object",
			required: ["image"],
			properties: {
				text: { type: "string" },
				userPrompt: { type: "string" },
				image: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" }
			},
			additionalProperties: false
		}
	},
	"video-describe": {
		inputs: {
			type: "object",
			required: ["video"],
			properties: {
				text: { type: "string" },
				userPrompt: { type: "string" },
				video: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" }
			},
			additionalProperties: false
		}
	},
	"audio-describe": {
		inputs: {
			type: "object",
			required: ["audio"],
			properties: {
				text: { type: "string" },
				userPrompt: { type: "string" },
				audio: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" }
			},
			additionalProperties: false
		}
	},
	"music-repaint": {
		inputs: {
			type: "object",
			required: [
				"audio",
				"start_time",
				"end_time"
			],
			properties: {
				audio: { $ref: "#/$defs/Asset" },
				text: { type: "string" },
				lyrics: { type: "string" },
				start_time: { type: "number" },
				end_time: { type: "number" },
				strength: { type: "number" },
				seed: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"music-cover": {
		inputs: {
			type: "object",
			required: ["audio"],
			properties: {
				audio: { $ref: "#/$defs/Asset" },
				ref_audio: { $ref: "#/$defs/Asset" },
				text: { type: "string" },
				lyrics: { type: "string" },
				strength: { type: "number" },
				seed: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"music-extract": {
		inputs: {
			type: "object",
			required: ["audio", "track"],
			properties: {
				audio: { $ref: "#/$defs/Asset" },
				track: { type: "string" },
				seed: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"separate-sound": {
		inputs: {
			type: "object",
			required: ["audio", "text"],
			properties: {
				audio: { $ref: "#/$defs/Asset" },
				text: { type: "string" },
				spans: {
					type: "array",
					items: {
						type: "object",
						required: ["start", "end"],
						properties: {
							start: { type: "number" },
							end: { type: "number" }
						},
						additionalProperties: false
					}
				}
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				target: { $ref: "#/$defs/AudioRef" },
				residual: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"music-lego": {
		inputs: {
			type: "object",
			required: ["audio", "track"],
			properties: {
				audio: { $ref: "#/$defs/Asset" },
				track: { type: "string" },
				text: { type: "string" },
				lyrics: { type: "string" },
				seed: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"music-complete": {
		inputs: {
			type: "object",
			required: ["audio"],
			properties: {
				audio: { $ref: "#/$defs/Asset" },
				tracks: {
					type: "array",
					items: { type: "string" }
				},
				text: { type: "string" },
				seed: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"music-brief": {
		inputs: {
			type: "object",
			required: ["text"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				instrumental: { type: "boolean" },
				language: { type: "string" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				lyrics: { type: "string" },
				tags: { type: "string" },
				bpm: { type: "number" },
				keyscale: { type: "string" },
				duration: { type: "number" },
				language: { type: "string" }
			},
			additionalProperties: false
		}
	},
	"audio-image-gen-video": {
		inputs: {
			type: "object",
			required: ["image", "audio"],
			properties: {
				text: { type: "string" },
				height: { type: "integer" },
				width: { type: "integer" },
				image: { $ref: "#/$defs/Asset" },
				audio: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"speech-text-gen-video": {
		inputs: {
			type: "object",
			required: ["text", "audio"],
			properties: {
				text: { type: "string" },
				seed: { type: "number" },
				height: { type: "integer" },
				width: { type: "integer" },
				enhance_prompt: { type: "boolean" },
				image: { $ref: "#/$defs/Asset" },
				image_frame_idx: { type: "integer" },
				image_strength: { type: "number" },
				audio: { $ref: "#/$defs/Asset" },
				audio_start_time: { type: "number" },
				duration: { type: "number" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"video-image-gen-video-mix": {
		inputs: {
			type: "object",
			required: ["image", "video"],
			properties: {
				text: { type: "string" },
				image: { $ref: "#/$defs/Asset" },
				video: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"video-image-gen-video-move": {
		inputs: {
			type: "object",
			required: ["image", "video"],
			properties: {
				text: { type: "string" },
				duration: { type: "number" },
				seed: { type: "number" },
				height: { type: "integer" },
				width: { type: "integer" },
				enhance_prompt: { type: "boolean" },
				image: { $ref: "#/$defs/Asset" },
				video: { $ref: "#/$defs/Asset" },
				attention_strength: { type: "number" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"image-image-gen-video": {
		inputs: {
			type: "object",
			required: [
				"text",
				"image",
				"end_image"
			],
			properties: {
				text: { type: "string" },
				duration: { type: "number" },
				seed: { type: "number" },
				height: { type: "integer" },
				width: { type: "integer" },
				enhance_prompt: { type: "boolean" },
				image: { $ref: "#/$defs/Asset" },
				image_strength: { type: "number" },
				end_image: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"text-gen-video": {
		inputs: {
			type: "object",
			required: ["text", "duration"],
			properties: {
				text: { type: "string" },
				duration: { type: "number" },
				seed: { type: "number" },
				height: { type: "integer" },
				width: { type: "integer" },
				enhance_prompt: { type: "boolean" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"image-gen-model": {
		inputs: {
			type: "object",
			required: ["image"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				width: { type: "integer" },
				height: { type: "integer" },
				seed: { type: "integer" },
				image: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				model: { $ref: "#/$defs/ModelRef" }
			},
			additionalProperties: false
		}
	},
	"speech-video-gen-video": {
		inputs: {
			type: "object",
			required: ["video", "text"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				video: { $ref: "#/$defs/Asset" },
				enhance_prompt: { type: "boolean" },
				reference_strength: { type: "number" },
				seed: { type: "number" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"text-gen-speech-clone": {
		inputs: {
			type: "object",
			required: ["text", "ref_audio"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				ref_audio: { $ref: "#/$defs/Asset" },
				language: { type: "string" },
				ref_text: { type: "string" },
				x_vector_only: { type: "boolean" },
				max_new_tokens: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"transcribe-timestamp": {
		inputs: {
			type: "object",
			required: ["audio"],
			properties: {
				context: { type: "string" },
				language: { type: "string" },
				max_new_tokens: { type: "number" },
				audio: { $ref: "#/$defs/Asset" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				text: { type: "string" },
				time_stamps: {
					type: "array",
					items: {
						type: "object",
						required: [
							"start",
							"end",
							"text"
						],
						properties: {
							start: { type: "number" },
							end: { type: "number" },
							text: { type: "string" }
						},
						additionalProperties: false
					}
				}
			},
			additionalProperties: false
		}
	},
	"text-gen-speech-instruct": {
		inputs: {
			type: "object",
			required: ["text"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				language: { type: "string" },
				instruct: { type: "string" },
				max_new_tokens: { type: "integer" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"drop-video": {
		inputs: {
			type: "object",
			required: ["videos"],
			properties: {
				query: { type: "string" },
				videos: {
					type: "array",
					items: { $ref: "#/$defs/Asset" }
				}
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				clips: {
					type: "array",
					items: { $ref: "#/$defs/VideoRef" }
				}
			},
			additionalProperties: false
		}
	},
	"arrange-group": {
		inputs: {
			type: "object",
			required: [],
			properties: {
				items: {
					type: "array",
					items: {
						type: "object",
						required: [],
						properties: {
							text: { type: "string" },
							id: { type: "string" }
						},
						additionalProperties: false
					}
				},
				infos: {
					type: "array",
					items: {
						type: "object",
						properties: {},
						additionalProperties: true
					}
				},
				query: { type: "string" },
				groupCount: { type: "integer" },
				duplicatable: { type: "boolean" },
				images: {
					type: "array",
					items: { $ref: "#/$defs/Asset" }
				},
				fileKeys: {
					type: "array",
					items: { $ref: "#/$defs/Asset" }
				}
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				groups: {
					type: "array",
					items: {
						type: "array",
						items: { $ref: "#/$defs/VideoRef" }
					},
					"x-expand-each": true
				}
			},
			additionalProperties: false
		}
	},
	separate_speaker: {
		inputs: {
			type: "object",
			required: ["audio"],
			properties: { audio: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				outputKeys: {
					type: "array",
					items: { $ref: "#/$defs/AudioRef" },
					"x-expand-each": true
				}
			},
			additionalProperties: false
		}
	},
	separate_audio_track: {
		inputs: {
			type: "object",
			required: ["audio"],
			properties: { audio: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				file_key: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	denoise_audio: {
		inputs: {
			type: "object",
			required: ["fileKey"],
			properties: { fileKey: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	convert_voice: {
		inputs: {
			type: "object",
			required: ["sourceKey", "targetKey"],
			properties: {
				sourceKey: { $ref: "#/$defs/Asset" },
				targetKey: { type: "string" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	remove_watermark: {
		inputs: {
			type: "object",
			required: ["fileKey"],
			properties: { fileKey: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	subtitle_remove: {
		inputs: {
			type: "object",
			required: ["fileKey"],
			properties: { fileKey: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				video: { $ref: "#/$defs/VideoRef" }
			},
			additionalProperties: false
		}
	},
	"text-audio-gen-speech": {
		inputs: {
			type: "object",
			required: ["text", "audio"],
			properties: {
				text: {
					type: "string",
					minLength: 1
				},
				audio: { $ref: "#/$defs/Asset" },
				emotion: { type: "string" },
				style: { type: "string" }
			},
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				audio: { $ref: "#/$defs/AudioRef" }
			},
			additionalProperties: false
		}
	},
	"image-pose": {
		inputs: {
			type: "object",
			required: ["image"],
			properties: { image: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"image-body-seg": {
		inputs: {
			type: "object",
			required: ["image"],
			properties: { image: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"image-normal": {
		inputs: {
			type: "object",
			required: ["image"],
			properties: { image: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"image-matting": {
		inputs: {
			type: "object",
			required: ["image"],
			properties: { image: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				image: { $ref: "#/$defs/ImageRef" }
			},
			additionalProperties: false
		}
	},
	"video-gen-model": {
		inputs: {
			type: "object",
			required: ["video"],
			properties: { video: { $ref: "#/$defs/Asset" } },
			additionalProperties: false
		},
		outputs: {
			type: "object",
			required: ["success"],
			properties: {
				success: { type: "boolean" },
				error: { type: "string" },
				model: { $ref: "#/$defs/ModelRef" }
			},
			additionalProperties: false
		}
	}
};
//#endregion
//#region src/core/abi/handle-introspect.ts
const REF_TO_NODE_TYPE$1 = {
	Asset: "imageNode",
	FileRef: "fileNode",
	ImageRef: "imageNode",
	AudioRef: "audioNode",
	VideoRef: "videoNode",
	ModelRef: "modelNode"
};
/** Reserved output fields that are protocol-level, not data routes. */
const OUTPUT_META_FIELDS = /* @__PURE__ */ new Set([
	"success",
	"error",
	"thinking"
]);
function resolveRef$1(refStr) {
	if (!refStr.startsWith("#/$defs/")) return void 0;
	const name = refStr.slice(8);
	return ABI_DEFINITIONS[name];
}
function refName(refStr) {
	if (!refStr || !refStr.startsWith("#/$defs/")) return void 0;
	const name = refStr.slice(8);
	return name in REF_TO_NODE_TYPE$1 ? name : void 0;
}
function flattenSchema(schema) {
	if (typeof schema?.$ref === "string") {
		const resolved = resolveRef$1(schema.$ref);
		if (resolved) {
			const { $ref: _r, ...rest } = schema;
			return {
				...resolved,
				...rest
			};
		}
	}
	return schema;
}
/**
* For `Asset` ($ref) inputs the schema itself doesn't carry the media kind,
* so fall back to the field name. Order matters (`audio` first to win over
* a `xxx_audio` field, etc.).
*/
function inferNodeTypeFromFieldName(field) {
	const lower = field.toLowerCase();
	if (/(^|_|-)audio($|_|-|key)/.test(lower) || lower.includes("audio") || lower.includes("speech") || lower.includes("voice")) return "audioNode";
	if (/(^|_|-)video($|_|-|key)/.test(lower) || lower.includes("video")) return "videoNode";
	if (/(^|_|-)image($|_|-|key)/.test(lower) || lower.includes("image")) return "imageNode";
	return "imageNode";
}
function classifyInputField(field, schema, required) {
	const refKind = refName(typeof schema?.$ref === "string" ? schema.$ref : void 0);
	if (refKind) return {
		kind: "handle",
		nodeType: refKind === "Asset" ? inferNodeTypeFromFieldName(field) : REF_TO_NODE_TYPE$1[refKind],
		path: "fileKeys[0]",
		array: false,
		required
	};
	if (schema?.type === "array") {
		const items = schema.items;
		if (items) {
			const itemRefKind = refName(typeof items.$ref === "string" ? items.$ref : void 0);
			if (itemRefKind) return {
				kind: "handle",
				nodeType: itemRefKind === "Asset" ? inferNodeTypeFromFieldName(field) : REF_TO_NODE_TYPE$1[itemRefKind],
				path: "fileKeys",
				array: true,
				required
			};
			if (items.type === "string" && (field === "texts" || field === "text")) return {
				kind: "handle",
				nodeType: "textNode",
				path: "texts",
				array: true,
				required
			};
		}
		return {
			kind: "config",
			required,
			schema
		};
	}
	return {
		kind: "config",
		required,
		schema
	};
}
function classifyOutputs(outputs) {
	const props = outputs?.properties;
	if (!props) return [];
	const out = [];
	for (const [field, schema] of Object.entries(props)) {
		if (OUTPUT_META_FIELDS.has(field)) continue;
		const flat = flattenSchema(schema);
		const directRef = refName(schema?.$ref);
		if (directRef) {
			out.push({
				field,
				nodeType: REF_TO_NODE_TYPE$1[directRef],
				expandEach: false
			});
			continue;
		}
		if (flat?.type === "array") {
			const items = flat.items;
			if (!items) continue;
			const itemRef = refName(items.$ref);
			const expandEach = schema["x-expand-each"] === true;
			if (itemRef) {
				out.push({
					field,
					nodeType: REF_TO_NODE_TYPE$1[itemRef],
					expandEach
				});
				continue;
			}
			if (items.type === "array") {
				const innerItems = items.items;
				const innerRef = refName(innerItems?.$ref);
				if (innerRef) out.push({
					field,
					nodeType: REF_TO_NODE_TYPE$1[innerRef],
					expandEach: true
				});
				continue;
			}
			if (items.type === "string") out.push({
				field,
				nodeType: "textNode",
				expandEach
			});
			continue;
		}
		if (flat?.type === "string") out.push({
			field,
			nodeType: "textNode",
			expandEach: false
		});
	}
	return out;
}
const TOPOLOGY_CACHE = /* @__PURE__ */ new Map();
function getAbiTopology(feature) {
	const cached = TOPOLOGY_CACHE.get(feature);
	if (cached) return cached;
	const node = ABI_NODES[feature];
	if (!node) throw new Error(`[abi/handle-introspect] Unknown feature: ${feature}`);
	const inputs = node.inputs;
	const required = new Set(Array.isArray(inputs?.required) ? inputs.required : []);
	const props = inputs?.properties ?? {};
	const inputOrder = [];
	const inputMap = {};
	for (const [field, schema] of Object.entries(props)) {
		inputOrder.push(field);
		inputMap[field] = classifyInputField(field, schema, required.has(field));
	}
	const outputs = classifyOutputs(node.outputs);
	const topology = {
		feature,
		inputs: inputMap,
		inputOrder,
		requiredInputs: [...required].filter((f) => f in inputMap),
		outputs
	};
	TOPOLOGY_CACHE.set(feature, topology);
	return topology;
}
/** Build the canonical target Handle id for an ABI input field. */
function targetHandleId(field) {
	return `in:${field}`;
}
/** Build the canonical source Handle id for an ABI output field. */
function sourceHandleId(field) {
	return `out:${field}`;
}
/** Parse a Handle id like "in:foo" → "foo". Returns undefined for non-conforming ids. */
function parseTargetHandleId(handle) {
	if (!handle || !handle.startsWith("in:")) return void 0;
	return handle.slice(3);
}
function parseSourceHandleId(handle) {
	if (!handle || !handle.startsWith("out:")) return void 0;
	return handle.slice(4);
}
//#endregion
//#region src/core/utils/path-utils.ts
/**
* Reads a value from a flat object by path.
* Supports array index notation: "fileKeys[0]" or plain fields: "texts".
*/
function getValueByPath(obj, path) {
	const arrayMatch = path.match(/^(\w+)\[(\d+)\]$/);
	if (arrayMatch) {
		const [, key, indexStr] = arrayMatch;
		const arr = obj[key];
		if (Array.isArray(arr)) return arr[parseInt(indexStr, 10)];
		return;
	}
	return obj[path];
}
//#endregion
//#region src/core/abi/resolve.ts
const PATH_DEFAULTS = {
	fileScalar: "fileKeys[0]",
	fileArray: "fileKeys",
	textScalar: "texts[0]",
	textArray: "texts"
};
function defaultPath(cls) {
	if (cls.nodeType === "textNode") return cls.array ? PATH_DEFAULTS.textArray : PATH_DEFAULTS.textScalar;
	return cls.array ? PATH_DEFAULTS.fileArray : PATH_DEFAULTS.fileScalar;
}
function resolveSpec(feature, sourceSpec) {
	const topology = getAbiTopology(feature);
	const fields = {};
	let batchField;
	for (const fieldName of topology.inputOrder) {
		const defaultCls = topology.inputs[fieldName];
		const override = sourceSpec?.[fieldName];
		if (!override) {
			if (defaultCls.kind === "handle") fields[fieldName] = {
				kind: "handle",
				nodeType: defaultCls.nodeType,
				path: defaultCls.path,
				array: defaultCls.array,
				required: defaultCls.required
			};
			else fields[fieldName] = {
				kind: "config",
				required: defaultCls.required
			};
			continue;
		}
		switch (override.kind) {
			case "handle": {
				const pluginIsArray = defaultCls.kind === "handle" ? defaultCls.array : false;
				const baseCls = defaultCls.kind === "handle" ? defaultCls : {
					kind: "handle",
					nodeType: override.nodeType ?? "textNode",
					path: override.path ?? PATH_DEFAULTS.textScalar,
					array: !!override.batch || !!override.collect,
					required: defaultCls.required
				};
				const nodeType = override.nodeType ?? baseCls.nodeType;
				const pathArray = !!override.batch || !!override.collect || baseCls.array;
				fields[fieldName] = {
					kind: "handle",
					nodeType,
					path: override.path ?? defaultPath({
						...baseCls,
						nodeType,
						array: pathArray
					}),
					batch: override.batch,
					collect: override.collect,
					array: pluginIsArray,
					manual: override.manual,
					required: defaultCls.required
				};
				if (override.batch) batchField = fieldName;
				break;
			}
			case "config":
				fields[fieldName] = {
					kind: "config",
					required: defaultCls.required
				};
				break;
			case "static":
				fields[fieldName] = {
					kind: "static",
					value: override.value,
					required: defaultCls.required
				};
				break;
			case "input": fields[fieldName] = {
				kind: "input",
				inputName: override.inputName,
				required: defaultCls.required
			};
		}
	}
	return {
		topology,
		fields,
		batchField
	};
}
/**
* For a given target node, walk its incoming edges and, for each ABI handle
* field, pull the upstream value(s) from the source node's data using the
* configured path. Returns map keyed by field name.
*/
function collectHandleValues(targetNodeId, spec, nodes, edges) {
	const result = {};
	const lookup = nodes instanceof Map ? nodes : new Map(nodes.map((n) => [n.id, n]));
	for (const [field, resolved] of Object.entries(spec.fields)) {
		if (resolved.kind !== "handle") continue;
		if (resolved.batch || resolved.collect) {
			const values = [];
			for (const edge of edges) {
				if (edge.target !== targetNodeId) continue;
				if (parseTargetHandleId(edge.targetHandle) !== field) continue;
				const src = lookup.get(edge.source);
				if (!src) continue;
				const v = getValueByPath(src.data, resolved.path);
				if (v === void 0 || v === null) continue;
				if (Array.isArray(v)) values.push(...v);
				else values.push(v);
			}
			result[field] = values;
			continue;
		}
		let value;
		for (const edge of edges) {
			if (edge.target !== targetNodeId) continue;
			if (parseTargetHandleId(edge.targetHandle) !== field) continue;
			const src = lookup.get(edge.source);
			if (!src) continue;
			value = getValueByPath(src.data, resolved.path);
			if (value !== void 0 && value !== null) break;
		}
		result[field] = value;
	}
	return result;
}
/**
* Build one prompt per element in the batch field (if any), otherwise a
* single prompt. Static / input overrides are inlined.
*
* Empty / null values are dropped for optional fields. Required fields with
* no value are still written onto the prompt object so callers can detect
* gaps before creating a task (see `promptMissingRequired`).
*/
function buildPrompts({ spec, configValues, handleValues }) {
	const fields = spec.fields;
	const fieldOrder = spec.topology.inputOrder;
	const baseAssemble = (overrides) => {
		const prompt = {};
		for (const field of fieldOrder) {
			const f = fields[field];
			if (!f) continue;
			let v;
			if (field in overrides) v = overrides[field];
			else if (f.kind === "handle") {
				v = handleValues[field];
				if ((v === void 0 || v === null || v === "") && f.manual) v = configValues[field];
			} else if (f.kind === "config") v = configValues[field];
			else if (f.kind === "static") v = f.value;
			else if (f.kind === "input") v = configValues[field];
			if (v === void 0 || v === null || v === "") {
				if (!f.required) continue;
			}
			prompt[field] = v;
		}
		return prompt;
	};
	if (!spec.batchField) return [baseAssemble({})];
	const batchValue = handleValues[spec.batchField];
	const items = Array.isArray(batchValue) ? batchValue : [];
	if (items.length === 0) return [];
	return items.map((item) => baseAssemble({ [spec.batchField]: item }));
}
/** First required ABI input missing from a built prompt, if any. */
function promptMissingRequired(spec, prompt) {
	for (const field of spec.topology.requiredInputs) {
		const v = prompt[field];
		if (v === void 0 || v === null || v === "") return field;
		if (Array.isArray(v) && v.length === 0) return field;
	}
}
//#endregion
//#region src/core/abi/sources.ts
/** Mark a field as fed from an upstream handle. Pass options to refine type/path. */
function handle(opts) {
	return {
		kind: "handle",
		...opts
	};
}
/** Mark a handle field as accepting batched upstream values (one prompt per value). */
function batchOn(opts) {
	return {
		kind: "handle",
		batch: true,
		...opts
	};
}
/**
* Mark a handle field as collecting values from every matching incoming edge
* into a single array (concat-style; no batch expansion).
*/
function collectAll(opts) {
	return {
		kind: "handle",
		collect: true,
		...opts
	};
}
/** Force a field to be a form-config value (defaults already do this for scalars). */
function configField() {
	return { kind: "config" };
}
//#endregion
//#region src/core/abi/node-feature-registry.ts
/**
* Static ABI registry keyed by React Flow node type — the single source of
* truth for "which ABI slot does this node type implement, and how are its
* inputs sourced".
*
* `NODE_TYPE_TO_ABI_FEATURE` maps a node type to its ABI feature slot;
* `NODE_TYPE_SOURCE_SPEC` carries the per-type `sourceSpec` overrides. Both
* are consumed headlessly (exporter, connection validation, agent tools) and
* by the canvas components (`useAbiForm` / `useAbiExecution` / `AbiHandles`),
* so a workflow exports identically whether or not any component is mounted.
*
* Every ABI node component (`nodes/{transfer,compose,batch,decompose}/`) must
* have an entry here; modality / add / data nodes are intentionally omitted —
* they're not ABI-driven.
*/
const NODE_TYPE_TO_ABI_FEATURE = {
	genTextNode: "gen-text",
	imageGenVideoNode: "image-gen-video",
	textGenVideoNode: "text-gen-video",
	imageGenModelNode: "image-gen-model",
	imagePoseNode: "image-pose",
	imageBodySegNode: "image-body-seg",
	imageNormalNode: "image-normal",
	imageMattingNode: "image-matting",
	videoGenModelNode: "video-gen-model",
	speechGenVideoNode: "speech-text-gen-video",
	imageGenImageNode: "image-edit",
	imageGenImageUpscaleNode: "image-upscale",
	textGenImageNode: "image-gen",
	textGenMusicNode: "gen-music",
	textGenSpeechCloneNode: "text-gen-speech-clone",
	textGenSpeechCloneComposeNode: "text-gen-speech-clone",
	imageGenVideoComposeNode: "image-gen-video",
	textGenSpeechPresetNode: "text-gen-speech-preset",
	textGenSpeechInstructNode: "text-gen-speech-instruct",
	removeVideoSubtitleNode: "subtitle_remove",
	videoUpscaleNode: "video-upscale",
	videoEditNode: "video-edit",
	removeWatermarkNode: "remove_watermark",
	extractAudioNode: "extract-audio",
	removeVideoAudioNode: "remove-video-audio",
	denoiseAudioSubtitleNode: "denoise_audio",
	separateAudioTrackNode: "separate_audio_track",
	separateSpeakerNode: "separate_speaker",
	convertVoiceNode: "convert_voice",
	audioDescribeNode: "audio-describe",
	imageGenTextNode: "image-gen-text",
	videoGenTextNode: "video-gen-text",
	videoGenTextSpeechRecognizeNode: "transcribe",
	audioGenTextSpeechRecognizeNode: "transcribe",
	fileGenTextNode: "parse-document",
	linkGenTextNode: "link",
	getFirstFrameNode: "get-first-frame",
	getLastFrameNode: "get-last-frame",
	musicRepaintNode: "music-repaint",
	musicExtractNode: "music-extract",
	musicLegoNode: "music-lego",
	musicCompleteNode: "music-complete",
	dropVideoNode: "drop-video",
	arrangeNode: "arrange-group",
	concatVideoNode: "concat-videos",
	mergeVideoAudioNode: "merge-video-audio",
	audioVideoLipSyncNode: "audio-video-lip-sync",
	imageFusionNode: "image-fusion",
	imagesGenVideoNode: "images-gen-video",
	refsGenVideoNode: "refs-gen-video",
	speechImageGenVideoNode: "audio-image-gen-video",
	speechTextGenVideoNode: "speech-text-gen-video",
	speechVideoGenVideoNode: "speech-video-gen-video",
	videoImageGenVideoMixNode: "video-image-gen-video-mix",
	videoImageGenVideoMoveNode: "video-image-gen-video-move",
	imageImageGenVideoNode: "image-image-gen-video",
	textAudioGenSpeechNode: "text-audio-gen-speech",
	textsGenTextNode: "combine-text",
	concatVideoComposeNode: "concat-videos",
	musicCoverNode: "music-cover",
	splitVideoNode: "split-video",
	splitTextNode: "split-text",
	musicBriefNode: "music-brief",
	separateSoundNode: "separate-sound"
};
function featureForNodeType(nodeType) {
	if (!nodeType) return void 0;
	return NODE_TYPE_TO_ABI_FEATURE[nodeType];
}
/**
* Per-node-type `sourceSpec` overrides. Types absent here use the bare ABI
* topology (`resolveSpec(feature, {})`).
*/
const NODE_TYPE_SOURCE_SPEC = {
	textGenVideoNode: { text: batchOn({
		nodeType: "textNode",
		path: "texts"
	}) },
	imageGenVideoNode: {
		image: batchOn(),
		text: configField()
	},
	imageGenVideoComposeNode: {
		image: batchOn(),
		text: handle({
			nodeType: "textNode",
			path: "texts[0]"
		})
	},
	imageGenImageNode: {
		image: handle({ nodeType: "imageNode" }),
		text: handle({
			nodeType: "textNode",
			path: "texts[0]",
			manual: true
		})
	},
	videoEditNode: {
		video: handle({ nodeType: "videoNode" }),
		text: handle({
			nodeType: "textNode",
			path: "texts[0]",
			manual: true
		})
	},
	speechTextGenVideoNode: {
		text: handle({
			nodeType: "textNode",
			path: "texts[0]"
		}),
		audio: handle({ nodeType: "audioNode" })
	},
	speechImageGenVideoNode: {
		image: handle({ nodeType: "imageNode" }),
		audio: handle({ nodeType: "audioNode" })
	},
	speechVideoGenVideoNode: {
		video: handle({ nodeType: "videoNode" }),
		text: handle({
			nodeType: "textNode",
			path: "texts[0]"
		})
	},
	audioVideoLipSyncNode: {
		video: handle({ nodeType: "videoNode" }),
		audio: handle({ nodeType: "audioNode" }),
		text: configField()
	},
	videoImageGenVideoMoveNode: {
		image: handle({ nodeType: "imageNode" }),
		video: handle({ nodeType: "videoNode" }),
		text: handle({
			nodeType: "textNode",
			path: "texts[0]",
			manual: true
		})
	},
	textGenSpeechCloneComposeNode: { text: batchOn({
		nodeType: "textNode",
		path: "texts"
	}) },
	imageFusionNode: {
		images: collectAll(),
		text: handle({
			nodeType: "textNode",
			path: "texts[0]",
			manual: true
		})
	},
	imagesGenVideoNode: {
		images: collectAll(),
		text: handle({
			nodeType: "textNode",
			path: "texts[0]",
			manual: true
		}),
		duration: configField()
	},
	refsGenVideoNode: {
		images: collectAll(),
		videos: collectAll(),
		audios: collectAll(),
		text: handle({
			nodeType: "textNode",
			path: "texts[0]",
			manual: true
		}),
		duration: configField()
	},
	arrangeNode: { fileKeys: collectAll({ nodeType: "videoNode" }) },
	audioDescribeNode: { audio: batchOn() },
	audioGenTextSpeechRecognizeNode: { audio: batchOn() },
	concatVideoComposeNode: { videos: collectAll() },
	concatVideoNode: { videos: collectAll() },
	convertVoiceNode: { sourceKey: batchOn({ nodeType: "audioNode" }) },
	denoiseAudioSubtitleNode: { fileKey: batchOn({ nodeType: "audioNode" }) },
	dropVideoNode: { videos: collectAll() },
	extractAudioNode: { video: batchOn() },
	fileGenTextNode: { document: batchOn({ nodeType: "fileNode" }) },
	genTextNode: { text: batchOn({
		nodeType: "textNode",
		path: "texts"
	}) },
	getFirstFrameNode: { video: batchOn() },
	getLastFrameNode: { video: batchOn() },
	imageBodySegNode: { image: batchOn() },
	imageGenImageUpscaleNode: { image: batchOn() },
	imageGenModelNode: { image: batchOn() },
	imageGenTextNode: { image: batchOn() },
	imageMattingNode: { image: batchOn() },
	imageNormalNode: { image: batchOn() },
	imagePoseNode: { image: batchOn() },
	mergeVideoAudioNode: { video: batchOn() },
	removeVideoAudioNode: { video: batchOn() },
	removeVideoSubtitleNode: { fileKey: batchOn({ nodeType: "videoNode" }) },
	removeWatermarkNode: { fileKey: batchOn({ nodeType: "videoNode" }) },
	separateAudioTrackNode: { audio: batchOn() },
	separateSpeakerNode: { audio: batchOn() },
	speechGenVideoNode: { audio: handle({ nodeType: "audioNode" }) },
	splitTextNode: { text: handle({
		nodeType: "textNode",
		path: "texts[0]"
	}) },
	splitVideoNode: { video: batchOn() },
	textAudioGenSpeechNode: { text: batchOn({
		nodeType: "textNode",
		path: "texts"
	}) },
	textGenImageNode: { text: batchOn({
		nodeType: "textNode",
		path: "texts"
	}) },
	textGenMusicNode: {
		tags: handle({
			nodeType: "textNode",
			path: "texts[0]"
		}),
		lyrics: handle({
			nodeType: "textNode",
			path: "texts[0]"
		})
	},
	textGenSpeechCloneNode: {
		text: batchOn({
			nodeType: "textNode",
			path: "texts"
		}),
		ref_audio: configField()
	},
	textGenSpeechInstructNode: { text: batchOn({
		nodeType: "textNode",
		path: "texts"
	}) },
	textGenSpeechPresetNode: { text: batchOn({
		nodeType: "textNode",
		path: "texts"
	}) },
	textsGenTextNode: { texts: collectAll() },
	videoGenModelNode: { video: batchOn() },
	videoGenTextNode: { video: batchOn() },
	videoGenTextSpeechRecognizeNode: { audio: batchOn({ nodeType: "videoNode" }) },
	videoUpscaleNode: { video: batchOn() },
	linkGenTextNode: { url: batchOn({
		nodeType: "linkNode",
		path: "texts"
	}) },
	musicBriefNode: { text: handle({
		nodeType: "textNode",
		path: "texts[0]"
	}) },
	separateSoundNode: {
		audio: handle({ nodeType: "audioNode" }),
		text: handle({
			nodeType: "textNode",
			path: "texts[0]",
			manual: true
		})
	}
};
/**
* Feature + sourceSpec overrides for `nodeType`, or `undefined` when the type
* is not ABI-driven (data / add / modality nodes).
*/
function abiSpecForNodeType(nodeType) {
	const feature = featureForNodeType(nodeType);
	if (!feature || !nodeType) return void 0;
	return {
		feature,
		sourceSpec: NODE_TYPE_SOURCE_SPEC[nodeType] ?? {}
	};
}
const resolvedSpecCache = /* @__PURE__ */ new Map();
/**
* Fully resolved (default-merged) spec for `nodeType`, or `undefined` when the
* type is not ABI-driven. Memoized per type — the tables are static.
*/
function resolvedSpecForNodeType(nodeType) {
	if (!nodeType) return void 0;
	const cached = resolvedSpecCache.get(nodeType);
	if (cached) return cached;
	const abi = abiSpecForNodeType(nodeType);
	if (!abi) return void 0;
	const spec = resolveSpec(abi.feature, abi.sourceSpec);
	resolvedSpecCache.set(nodeType, spec);
	return spec;
}
/**
* Resolve the canonical Handle ids for a new edge that connects
* `sourceType` → `targetType`. Mirrors what `AbiHandles` and the modality
* components render, so `collectHandleValues` and the connection validator
* can wire the edge correctly.
*
* Returns `undefined` fields when a side can't be classified; the edge is still
* created, just with the missing handle id absent (matching prior behavior).
*
* **Important:** the bare ABI topology classifies plain `string` inputs as
* `config`. Many node components upgrade those to handles via `sourceSpec`
* (e.g. `gen-text` has `text: batchOn(...)`). If a caller has access to the
* already-resolved spec (e.g. from `useAbiExecution`'s `specRef`), it should
* pass `targetSpec` so the override is respected. Otherwise we fall back to
* raw topology — sufficient for fields the ABI itself classifies as a handle
* (arrays of strings/refs, $ref scalars), but will miss sourceSpec-only
* promotions. The post-mount heal in `useAbiExecution` covers that case.
*
* For ABI targets with multiple handles of the same upstream nodeType (e.g.
* `image-fusion` taking a batch of images), the caller can pass `usedTargetHandles`
* so we pick the next unused field, falling back to the first match.
*/
function resolveEdgeHandles(args) {
	const { sourceType, targetType, usedTargetHandles, targetSpec } = args;
	let sourceHandle;
	if (sourceType && isModalityNode(sourceType)) sourceHandle = sourceHandleId(sourceType);
	else if (sourceType && targetType && isModalityNode(targetType)) {
		const feature = featureForNodeType(sourceType);
		if (feature) {
			const output = getAbiTopology(feature).outputs.find((o) => o.nodeType === targetType);
			if (output) sourceHandle = sourceHandleId(output.field);
		}
	}
	let targetHandle;
	if (targetType && isModalityNode(targetType)) targetHandle = `in:${targetType}`;
	else if (sourceType && isModalityNode(sourceType)) {
		const upstreamNodeType = sourceType;
		const fieldOrder = targetSpec?.topology.inputOrder ?? (targetType ? (() => {
			const feature = featureForNodeType(targetType);
			return feature ? getAbiTopology(feature).inputOrder : [];
		})() : []);
		const isHandleForUpstream = (field) => {
			if (targetSpec) {
				const f = targetSpec.fields[field];
				return f?.kind === "handle" && f.nodeType === upstreamNodeType;
			}
			if (!targetType) return false;
			const feature = featureForNodeType(targetType);
			if (!feature) return false;
			const f = getAbiTopology(feature).inputs[field];
			return f?.kind === "handle" && f.nodeType === upstreamNodeType;
		};
		let firstMatch;
		for (const field of fieldOrder) {
			if (!isHandleForUpstream(field)) continue;
			const handleId = targetHandleId(field);
			if (firstMatch === void 0) firstMatch = handleId;
			if (!usedTargetHandles || !usedTargetHandles.has(handleId)) {
				targetHandle = handleId;
				break;
			}
		}
		if (!targetHandle) targetHandle = firstMatch;
	}
	return {
		sourceHandle,
		targetHandle
	};
}
//#endregion
//#region src/core/workflow/executable-workflow.ts
/**
* Data node type mapping
*/
const DATA_NODE_TYPES = {
	textNode: {
		dataType: "text",
		outputField: "texts"
	},
	imageNode: {
		dataType: "image",
		outputField: "fileKeys"
	},
	audioNode: {
		dataType: "audio",
		outputField: "fileKeys"
	},
	videoNode: {
		dataType: "video",
		outputField: "fileKeys"
	},
	modelNode: {
		dataType: "model",
		outputField: "fileKeys"
	},
	fileNode: {
		dataType: "file",
		outputField: "fileKeys"
	},
	linkNode: {
		dataType: "text",
		outputField: "texts"
	}
};
//#endregion
//#region src/core/workflow/flow-connection-shared.ts
/**
* Shared helpers for RF connection validation (`connection-rules.ts`,
* `connection-validator.ts`).
*/
/** Add node type → logical output type (consistent with workflow-exporter). */
const ADD_NODE_OUTPUT_TYPE = {
	addImageNode: "imageNode",
	addVideoNode: "videoNode",
	addAudioNode: "audioNode",
	addTextNode: "textNode",
	addModelNode: "modelNode",
	addFileNode: "fileNode",
	addLinkNode: "linkNode"
};
/**
* Logical upstream output RF type (“textNode”, “imageNode”, …).
*
* - data node → itself
* - ABI node → if `sourceHandle = out:<field>` resolves to a specific output
*   route, returns that route's modality. Otherwise falls back to the first
*   non-expanding ABI output's nodeType.
* - add node → fixed mapping
*/
function getEffectiveOutputType(nodeType, sourceHandle) {
	if (!nodeType) return void 0;
	if (nodeType in DATA_NODE_TYPES) return nodeType;
	const spec = resolvedSpecForNodeType(nodeType);
	if (spec) {
		if (sourceHandle?.startsWith("out:")) {
			const field = sourceHandle.slice(4);
			const match = spec.topology.outputs.find((o) => o.field === field);
			if (match) return match.nodeType;
		}
		const primary = spec.topology.outputs.find((o) => !o.expandEach) ?? spec.topology.outputs[0];
		if (primary) return primary.nodeType;
	}
	return ADD_NODE_OUTPUT_TYPE[nodeType];
}
//#endregion
//#region src/core/abi/edge-target-options.ts
/**
* Target input fields on `edge`'s downstream node that are compatible with the
* upstream output's modality. Returns `[]` for non-ABI targets or when no
* matching handle exists. A result of length ≥ 2 means the connection is
* ambiguous and the inline select should be shown.
*/
function getEdgeTargetOptions(edge, nodes) {
	const sourceNode = nodes.find((n) => n.id === edge.source);
	if (!sourceNode) return [];
	const upstreamType = getEffectiveOutputType(sourceNode.type, edge.sourceHandle);
	if (!upstreamType) return [];
	const spec = resolvedSpecForNodeType(nodes.find((n) => n.id === edge.target)?.type);
	if (!spec) return [];
	const options = [];
	for (const field of spec.topology.inputOrder) {
		const f = spec.fields[field];
		if (f?.kind === "handle" && f.nodeType === upstreamType) options.push({
			handleId: targetHandleId(field),
			field,
			single: !f.array && !f.collect,
			feature: spec.topology.feature
		});
	}
	return options;
}
//#endregion
//#region src/core/workflow/connection-validator.ts
function resolveRefs(schema, depth = 0) {
	if (depth > 48 || typeof schema !== "object" || schema === null) return schema ?? {};
	const s = { ...schema };
	if (!s.$ref || typeof s.$ref !== "string") return s;
	const ref = s.$ref;
	if (!ref.startsWith("#/$defs/")) return s;
	const name = ref.slice(8);
	const def = ABI_DEFINITIONS[name] ?? ABI_DEFINITIONS[name];
	if (!def) return s;
	const { $ref: _r, ...rest } = s;
	return resolveRefs({
		...def,
		...rest
	}, depth + 1);
}
function normalizeJsonTypes(schema) {
	if (!schema) return null;
	const t = schema.type;
	if (t === void 0) return [];
	return Array.isArray(t) ? t : [t];
}
function enumsCertainlyDisjoint(producer, consumer) {
	const ae = Array.isArray(producer.enum) ? producer.enum : void 0;
	const be = Array.isArray(consumer.enum) ? consumer.enum : void 0;
	if (!ae?.length || !be?.length) return false;
	const set = new Set(be.map(String));
	for (const v of ae) if (set.has(String(v))) return false;
	return true;
}
function flattenTypeTags(schema) {
	const tags = normalizeJsonTypes(schema);
	const out = /* @__PURE__ */ new Set();
	if (!tags) return out;
	for (const t of tags) out.add(String(t));
	return out;
}
/** True when EVERY producer primitive tag clashes with EVERY consumer primitive tag-set. */
function rootTypeBundlesDisjoint(producer, consumer) {
	const pTags = flattenTypeTags(producer);
	const qTags = flattenTypeTags(consumer);
	if (!pTags.size || !qTags.size) return false;
	for (const a of pTags) {
		let hasCompat = false;
		for (const b of qTags) if (!primitiveTypesExplicitlyContradict(a, b)) {
			hasCompat = true;
			break;
		}
		if (!hasCompat) return true;
	}
	return false;
}
/** JSON Schema `number`/`integer` are treated as overlapping families. */
function primitiveTypesExplicitlyContradict(prodTag, consumerTag) {
	if (prodTag === consumerTag) return false;
	const nums = /* @__PURE__ */ new Set(["number", "integer"]);
	if (nums.has(prodTag) && nums.has(consumerTag)) return false;
	return [
		["string", "object"],
		["string", "integer"],
		["string", "number"],
		["string", "boolean"],
		["object", "string"],
		["object", "integer"],
		["object", "number"],
		["object", "boolean"],
		["boolean", "integer"],
		["boolean", "number"],
		["integer", "boolean"],
		["number", "boolean"],
		["integer", "string"],
		["number", "string"],
		["boolean", "string"]
	].some(([a, b]) => prodTag === a && consumerTag === b || prodTag === b && consumerTag === a);
}
function collectBranches(s) {
	const list = s.anyOf ?? s.oneOf;
	if (!list || list.length === 0) return [];
	const out = [];
	for (const b of list) {
		if (typeof b === "boolean") continue;
		out.push(resolveRefs(b));
	}
	return out;
}
function schemasAtomicCompare(producer, consumer, depth) {
	if (depth > 48) return "unknown";
	const prod = resolveRefs(producer);
	const cons = resolveRefs(consumer);
	const prodBranches = collectBranches(prod);
	if (prodBranches.length > 0) {
		let allCompat = true;
		let allDisj = true;
		for (const branch of prodBranches) {
			const r = schemasAtomicCompare(branch, cons, depth + 1);
			if (r !== "compatible") allCompat = false;
			if (r !== "disjoint") allDisj = false;
		}
		if (allCompat) return "compatible";
		if (allDisj) return "disjoint";
		return "unknown";
	}
	const consBranches = collectBranches(cons);
	if (consBranches.length > 0) {
		let anyCompat = false;
		let allDisj = true;
		for (const branch of consBranches) {
			const r = schemasAtomicCompare(prod, branch, depth + 1);
			if (r === "compatible") anyCompat = true;
			if (r !== "disjoint") allDisj = false;
		}
		if (anyCompat) return "compatible";
		if (allDisj) return "disjoint";
		return "unknown";
	}
	if (prod.$ref && (!prod.properties || Object.keys(prod.properties ?? {}).length === 0)) return "unknown";
	if (cons.$ref && (!cons.properties || Object.keys(cons.properties ?? {}).length === 0)) return "unknown";
	if (enumsCertainlyDisjoint(prod, cons)) return "disjoint";
	if (rootTypeBundlesDisjoint(prod, cons)) return "disjoint";
	if (prod.type === "object" && prod.properties && Object.keys(prod.properties).length > 0) {
		let inspected = false;
		for (const [key, pv] of Object.entries(prod.properties)) {
			const cv = cons.properties?.[key];
			if (!cv) continue;
			inspected = true;
			if (schemasAtomicCompare(pv, cv, depth + 1) === "disjoint") return "disjoint";
		}
		/** Matched overlapping keys survived → conservative compatibility */
		if (inspected) return "compatible";
	}
	if (prod.type === "array" || cons.type === "array") return "unknown";
	const pTags = flattenTypeTags(prod);
	const qTags = flattenTypeTags(cons);
	if (!pTags.size || !qTags.size) return "unknown";
	return "compatible";
}
function getAbiOutputProperty(slot, field) {
	const raw = resolveRefs(ABI_NODES[slot].outputs).properties?.[field];
	return raw ? resolveRefs(raw) : void 0;
}
function getAbiInputProperties(slot) {
	return resolveRefs(ABI_NODES[slot].inputs).properties ?? {};
}
function consumerFieldSchema(targetSlot, paramKey) {
	const props = getAbiInputProperties(targetSlot)[paramKey];
	return props ? resolveRefs(props) : void 0;
}
/** Extract the consumer ABI input field name from an `in:<field>` target handle. */
function consumerFieldFromTargetHandle(targetHandle) {
	if (typeof targetHandle !== "string") return void 0;
	if (!targetHandle.startsWith("in:")) return void 0;
	return targetHandle.slice(3).trim() || void 0;
}
/**
* When both ends are ABI nodes (by stored `feature` string matching `ABI_NODES`),
* refine connection validity structurally between narrowed producer output payload
* and mapped consumer input slots.
*
* Anything outside strict ABI↔ABI or inconclusive pairwise checks returns `undefined`,
* leaving the decision to the coarser modality check in `connection-rules`.
*/
function tryAbiCompatibility(connection, nodes) {
	const sourceNode = nodes.find((n) => n.id === connection.source);
	const targetNode = nodes.find((n) => n.id === connection.target);
	if (!sourceNode || !targetNode) return void 0;
	const srcRf = sourceNode.type ?? "";
	const tgtRf = targetNode.type ?? "";
	if (srcRf in DATA_NODE_TYPES || tgtRf in DATA_NODE_TYPES) return void 0;
	const sourceSlot = featureForNodeType(srcRf);
	const targetSlot = featureForNodeType(tgtRf);
	if (!sourceSlot || !targetSlot) return void 0;
	if (!getEffectiveOutputType(srcRf, connection.sourceHandle)) return void 0;
	const consumerField = consumerFieldFromTargetHandle(connection.targetHandle);
	if (!consumerField) return void 0;
	if (!(consumerField in getAbiInputProperties(targetSlot))) return void 0;
	const producerField = parseSourceHandleId(connection.sourceHandle);
	if (!producerField) return void 0;
	const producerNarrow = getAbiOutputProperty(sourceSlot, producerField);
	if (!producerNarrow) return void 0;
	const consumerSch = consumerFieldSchema(targetSlot, consumerField);
	if (!consumerSch) return void 0;
	const result = schemasAtomicCompare(producerNarrow, consumerSch, 0);
	if (result === "compatible") return true;
	if (result === "disjoint") return false;
}
//#endregion
//#region src/core/workflow/connection-rules.ts
function resolveSpecForNode(targetNodeType) {
	return resolvedSpecForNodeType(targetNodeType);
}
/**
* Target handles that accept only a single edge: every ABI handle field whose
* plugin shape is a scalar (`!array`) and that isn't a batch/collect fan-in
* (those legitimately receive multiple upstream values).
*/
function singleEdgeHandlesForTarget(targetNodeType) {
	const spec = resolveSpecForNode(targetNodeType);
	if (!spec) return /* @__PURE__ */ new Set();
	const out = /* @__PURE__ */ new Set();
	for (const [field, f] of Object.entries(spec.fields)) {
		if (f.kind !== "handle") continue;
		if (f.array || f.batch || f.collect) continue;
		out.add(targetHandleId(field));
	}
	return out;
}
/** Modality (RF nodeType) that a specific ABI target handle expects, if resolvable. */
function expectedTargetHandleType(targetNodeType, targetHandle) {
	const field = parseTargetHandleId(targetHandle);
	if (!field) return void 0;
	const spec = resolveSpecForNode(targetNodeType);
	if (!spec) return void 0;
	const f = spec.fields[field];
	return f?.kind === "handle" ? f.nodeType : void 0;
}
/** Set of upstream node types this target accepts on any handle. */
function collectUpstreamTypesForTarget(targetNodeType) {
	const spec = resolveSpecForNode(targetNodeType);
	if (!spec) return /* @__PURE__ */ new Set();
	const out = /* @__PURE__ */ new Set();
	for (const f of Object.values(spec.fields)) if (f.kind === "handle") out.add(f.nodeType);
	return out;
}
/**
* Check whether an incoming edge already occupies the same targetHandle for
* handles that only allow a single connection. `ignoreEdgeId` excludes the edge
* currently being reconnected, so dragging an edge's source endpoint (target
* handle unchanged) isn't rejected as a self-collision.
*/
function hasDuplicateTargetHandle(edges, connection, targetNodeType, ignoreEdgeId) {
	const targetId = connection.target;
	if (!targetId) return false;
	const th = connection.targetHandle;
	if (!th) return false;
	if (!singleEdgeHandlesForTarget(targetNodeType).has(th)) return false;
	return edges.some((e) => e.id !== ignoreEdgeId && e.target === targetId && e.targetHandle === th);
}
/**
* Add nodes own a single out handle and represent one created asset, so their
* output fans out to at most one downstream edge. `ignoreEdgeId` excludes the
* edge being reconnected (so dragging its target endpoint isn't a self-collision).
*/
function hasDuplicateAddSourceEdge(edges, connection, sourceType, ignoreEdgeId) {
	if (!sourceType || !(sourceType in ADD_NODE_OUTPUT_TYPE)) return false;
	const sourceId = connection.source;
	if (!sourceId) return false;
	return edges.some((e) => e.id !== ignoreEdgeId && e.source === sourceId);
}
/**
* Validate whether a connection is allowed. Unknown configurations are allowed
* by default so unregistered or non-ABI nodes remain connectable.
*
* `ignoreEdgeId` is the edge being reconnected (excluded from duplicate checks).
*/
function isValidFlowConnection(connection, nodes, edges, ignoreEdgeId) {
	const source = connection.source;
	const target = connection.target;
	if (!source || !target || source === target) return false;
	const sourceNode = nodes.find((n) => n.id === source);
	const targetNode = nodes.find((n) => n.id === target);
	if (!sourceNode || !targetNode) return false;
	if (hasDuplicateTargetHandle(edges, connection, targetNode.type, ignoreEdgeId)) return false;
	if (hasDuplicateAddSourceEdge(edges, connection, sourceNode.type, ignoreEdgeId)) return false;
	const outType = getEffectiveOutputType(sourceNode.type, connection.sourceHandle);
	/** Must stay before ABI refinement (stricter behavioural guardrails). */
	if (!outType) return true;
	const sourceType = sourceNode.type ?? "";
	const targetType = targetNode.type ?? "";
	if (targetType in DATA_NODE_TYPES) {
		if (sourceType in DATA_NODE_TYPES) return false;
		return outType === targetType;
	}
	const expected = expectedTargetHandleType(targetNode.type, connection.targetHandle);
	if (expected && expected !== outType) return false;
	const abiDecision = tryAbiCompatibility(connection, nodes);
	if (abiDecision !== void 0) return abiDecision;
	const allowed = collectUpstreamTypesForTarget(targetNode.type);
	if (allowed.size === 0) return true;
	return allowed.has(outType);
}
//#endregion
//#region src/core/logger.ts
/**
* Small dev/prod-aware logger. Use `debug` for trace noise; it no-ops in production.
* Use `info` for messages that must appear in all environments (e.g. CLI success lines).
* Prefer over raw `console.*` in app code.
*/
const isVerbose = typeof process !== "undefined" && true;
const logger = {
	debug: (...args) => {
		if (isVerbose) console.log(...args);
	},
	info: (...args) => {
		console.log(...args);
	},
	warn: (...args) => {
		console.warn(...args);
	},
	error: (...args) => {
		console.error(...args);
	}
};
//#endregion
//#region src/core/workflow/parser.ts
/**
* Workflow parser class
*/
var WorkflowParser = class {
	nodes;
	edges;
	nodeMap;
	inDegreeMap;
	outDegreeMap;
	adjacencyList;
	reverseAdjacencyList;
	constructor(workflow) {
		if ("flow" in workflow) {
			this.nodes = workflow.flow.nodes;
			this.edges = workflow.flow.edges;
		} else {
			this.nodes = workflow.nodes;
			this.edges = workflow.edges;
		}
		this.nodeMap = /* @__PURE__ */ new Map();
		this.inDegreeMap = /* @__PURE__ */ new Map();
		this.outDegreeMap = /* @__PURE__ */ new Map();
		this.adjacencyList = /* @__PURE__ */ new Map();
		this.reverseAdjacencyList = /* @__PURE__ */ new Map();
		this.buildGraph();
	}
	/**
	* Build the graph structure
	*/
	buildGraph() {
		for (const node of this.nodes) {
			this.nodeMap.set(node.id, node);
			this.inDegreeMap.set(node.id, 0);
			this.outDegreeMap.set(node.id, 0);
			this.adjacencyList.set(node.id, []);
			this.reverseAdjacencyList.set(node.id, []);
		}
		for (const edge of this.edges) {
			const { source, target } = edge;
			if (!this.nodeMap.has(source) || !this.nodeMap.has(target)) continue;
			this.adjacencyList.get(source).push(target);
			this.reverseAdjacencyList.get(target).push(source);
			this.outDegreeMap.set(source, (this.outDegreeMap.get(source) ?? 0) + 1);
			this.inDegreeMap.set(target, (this.inDegreeMap.get(target) ?? 0) + 1);
		}
	}
	/**
	* Get start nodes (nodes with in-degree 0)
	*/
	getStartNodes() {
		const startNodes = [];
		for (const [nodeId, inDegree] of this.inDegreeMap) if (inDegree === 0) startNodes.push(nodeId);
		return startNodes;
	}
	/**
	* Get end nodes (nodes with out-degree 0)
	*/
	getEndNodes() {
		const endNodes = [];
		for (const [nodeId, outDegree] of this.outDegreeMap) if (outDegree === 0) endNodes.push(nodeId);
		return endNodes;
	}
	/**
	* Get a node's dependencies (upstream nodes)
	*/
	getDependencies(nodeId) {
		return this.reverseAdjacencyList.get(nodeId) ?? [];
	}
	/**
	* Get a node's downstream nodes
	*/
	getDependents(nodeId) {
		return this.adjacencyList.get(nodeId) ?? [];
	}
	/**
	* Generate an execution plan using topological sort
	* Based on Kahn's algorithm, grouped by level (same level can execute in parallel)
	*/
	generateExecutionPlan() {
		const nodeInfoMap = /* @__PURE__ */ new Map();
		const levels = [];
		const visited = /* @__PURE__ */ new Set();
		const inDegreeWorkingCopy = new Map(this.inDegreeMap);
		for (const node of this.nodes) nodeInfoMap.set(node.id, {
			id: node.id,
			type: node.type ?? "unknown",
			status: "pending",
			dependencies: this.getDependencies(node.id),
			dependents: this.getDependents(node.id),
			level: -1,
			data: node.data ?? {}
		});
		let currentLevel = [];
		for (const [nodeId, inDegree] of inDegreeWorkingCopy) if (inDegree === 0) {
			currentLevel.push(nodeId);
			visited.add(nodeId);
			const info = nodeInfoMap.get(nodeId);
			if (info) info.level = 0;
		}
		let levelIndex = 0;
		while (currentLevel.length > 0) {
			levels.push([...currentLevel]);
			const nextLevel = [];
			for (const nodeId of currentLevel) {
				const dependents = this.getDependents(nodeId);
				for (const dependent of dependents) {
					const newInDegree = (inDegreeWorkingCopy.get(dependent) ?? 1) - 1;
					inDegreeWorkingCopy.set(dependent, newInDegree);
					if (newInDegree === 0 && !visited.has(dependent)) {
						nextLevel.push(dependent);
						visited.add(dependent);
						const info = nodeInfoMap.get(dependent);
						if (info) info.level = levelIndex + 1;
					}
				}
			}
			currentLevel = nextLevel;
			levelIndex++;
		}
		if (visited.size !== this.nodes.length) {
			logger.warn("[WorkflowParser] A cycle was detected in the workflow; some nodes cannot execute");
			for (const node of this.nodes) if (!visited.has(node.id)) {
				const info = nodeInfoMap.get(node.id);
				if (info) info.status = "skipped";
			}
		}
		return {
			levels,
			nodeInfoMap,
			startNodes: this.getStartNodes(),
			endNodes: this.getEndNodes(),
			totalNodes: this.nodes.length,
			totalEdges: this.edges.length
		};
	}
	/**
	* Get node info
	*/
	getNode(nodeId) {
		return this.nodeMap.get(nodeId);
	}
	/**
	* Get all nodes
	*/
	getAllNodes() {
		return this.nodes;
	}
	/**
	* Get all edges
	*/
	getAllEdges() {
		return this.edges;
	}
	/**
	* Check whether the workflow is valid (no cycles)
	*/
	isValid() {
		return this.generateExecutionPlan().levels.reduce((sum, level) => sum + level.length, 0) === this.nodes.length;
	}
	/**
	* Get all paths from a given node to the end
	*/
	getPathsToEnd(startNodeId) {
		const paths = [];
		const endNodes = new Set(this.getEndNodes());
		const dfs = (currentId, currentPath) => {
			currentPath.push(currentId);
			if (endNodes.has(currentId)) paths.push([...currentPath]);
			else {
				const dependents = this.getDependents(currentId);
				for (const dependent of dependents) dfs(dependent, currentPath);
			}
			currentPath.pop();
		};
		dfs(startNodeId, []);
		return paths;
	}
	/**
	* Get a visualized string of the execution order (for debugging)
	*/
	getExecutionOrderString() {
		const plan = this.generateExecutionPlan();
		const lines = [];
		lines.push(`=== Workflow Execution Plan ===`);
		lines.push(`Total nodes: ${plan.totalNodes}`);
		lines.push(`Total edges: ${plan.totalEdges}`);
		lines.push(`Start nodes: ${plan.startNodes.join(", ")}`);
		lines.push(`End nodes: ${plan.endNodes.join(", ")}`);
		lines.push("");
		for (let i = 0; i < plan.levels.length; i++) {
			const level = plan.levels[i];
			lines.push(`Level ${i + 1} (${level.length} nodes can execute in parallel):`);
			for (const nodeId of level) if (plan.nodeInfoMap.get(nodeId)) {
				const typeName = this.getNode(nodeId)?.type ?? "unknown";
				lines.push(`  - [${typeName}] ${nodeId.substring(0, 8)}...`);
			}
			lines.push("");
		}
		return lines.join("\n");
	}
};
//#endregion
//#region src/core/constants/media-node-max-width.ts
/**
* Image/video nodes: the canvas node width is proportional to the media long-edge pixel count (for example, 1024 and 2048 are about 1:2),
* making resolution differences easier to compare visually. Min/max values prevent nodes from becoming too small or covering the canvas.
*/
/** Reference long edge (px): this length maps to a node width of REF_DISPLAY_WIDTH_PX */
const MEDIA_NODE_REF_LONG_EDGE_PX = 1024;
/**
* Calculate the canvas node width (px) linearly from the long edge, rounded and clamped.
*/
function proportionalMediaNodeWidthPx(width, height) {
	const long = Math.max(width, height);
	if (!Number.isFinite(long) || long <= 0) return 256;
	const raw = long / MEDIA_NODE_REF_LONG_EDGE_PX * 256;
	return Math.round(Math.min(720, Math.max(120, raw)));
}
//#endregion
//#region src/core/constants/media-options.ts
const IMAGE_ASPECT_RATIOS = [
	{
		value: "9:16",
		label: "portrait",
		width: 720,
		height: 1280
	},
	{
		value: "16:9",
		label: "landscape",
		width: 1280,
		height: 720
	},
	{
		value: "1:1",
		label: "square",
		width: 1024,
		height: 1024
	},
	{
		value: "4:3",
		label: "standard",
		width: 1024,
		height: 768
	},
	{
		value: "3:4",
		label: "verticalStandard",
		width: 768,
		height: 1024
	}
];
const IMAGE_RESOLUTION_TIERS = [
	{
		value: "1k",
		label: "1K",
		scale: 1
	},
	{
		value: "2k",
		label: "2K",
		scale: 2
	},
	{
		value: "4k",
		label: "4K",
		scale: 4
	}
];
const VIDEO_ASPECT_RATIOS = [
	{
		value: "9:16",
		label: "portrait",
		width: 576,
		height: 1024
	},
	{
		value: "16:9",
		label: "landscape",
		width: 1024,
		height: 576
	},
	{
		value: "1:1",
		label: "square",
		width: 1024,
		height: 1024
	},
	{
		value: "4:3",
		label: "standard",
		width: 1024,
		height: 768
	},
	{
		value: "3:4",
		label: "verticalStandard",
		width: 768,
		height: 1024
	}
];
const VIDEO_DURATIONS = [
	{
		value: "5",
		label: "5s"
	},
	{
		value: "10",
		label: "10s"
	},
	{
		value: "15",
		label: "15s"
	},
	{
		value: "30",
		label: "30s"
	},
	{
		value: "60",
		label: "1min"
	}
];
function clampVideoDuration(raw) {
	return Math.max(1, Math.min(30, Math.round(raw)));
}
/** Icon sizing lookup for aspect ratio thumbnails */
function getAspectRatioIconSize(ratio) {
	switch (ratio) {
		case "16:9": return {
			width: "16px",
			height: "9px"
		};
		case "9:16": return {
			width: "8px",
			height: "14px"
		};
		case "1:1": return {
			width: "12px",
			height: "12px"
		};
		case "4:3": return {
			width: "14px",
			height: "10px"
		};
		case "3:4": return {
			width: "10px",
			height: "13px"
		};
		default: return {
			width: "12px",
			height: "12px"
		};
	}
}
//#endregion
//#region src/core/constants/task-status.ts
/**
* Unified task status definitions
*
* Status hierarchy:
* - TASK_*:     Single-task level status
* - WORKFLOW_*: Workflow level status
* - NODE_*:     Node level status (workflow only)
*
* Status lifecycle:
* PENDING -> RUNNING -> COMPLETED/FAILED/CANCELLED
*/
/**
* General task status (single-task and workflow level)
*/
const TaskStatus = {
	PENDING: "PENDING",
	RUNNING: "RUNNING",
	COMPLETED: "COMPLETED",
	FAILED: "FAILED",
	CANCELLED: "CANCELLED"
};
/**
* Workflow level status
*/
const WorkflowStatus = {
	WORKFLOW_STARTED: "WORKFLOW_STARTED",
	WORKFLOW_COMPLETED: "WORKFLOW_COMPLETED",
	WORKFLOW_FAILED: "WORKFLOW_FAILED",
	WORKFLOW_CANCELLED: "WORKFLOW_CANCELLED"
};
/**
* Node level status
*/
const NodeStatus = {
	NODE_STARTED: "NODE_STARTED",
	NODE_RUNNING: "NODE_RUNNING",
	NODE_COMPLETED: "NODE_COMPLETED",
	NODE_FAILED: "NODE_FAILED"
};
/**
* Set of terminal statuses
*/
const TERMINAL_STATUSES = /* @__PURE__ */ new Set([
	TaskStatus.COMPLETED,
	TaskStatus.FAILED,
	TaskStatus.CANCELLED,
	WorkflowStatus.WORKFLOW_COMPLETED,
	WorkflowStatus.WORKFLOW_FAILED,
	WorkflowStatus.WORKFLOW_CANCELLED
]);
TaskStatus.RUNNING, NodeStatus.NODE_STARTED, NodeStatus.NODE_RUNNING;
/**
* Check whether the status is a terminal status
*/
function isTerminalStatus(status) {
	return TERMINAL_STATUSES.has(status);
}
/**
* Map SSE status to internal task status
* Used in use-task.ts and other front-end state management
*/
function mapSSEStatusToTaskStatus(sseStatus) {
	switch (sseStatus) {
		case TaskStatus.PENDING: return "PENDING";
		case TaskStatus.RUNNING:
		case NodeStatus.NODE_STARTED:
		case NodeStatus.NODE_RUNNING: return "PROCESSING";
		case TaskStatus.COMPLETED:
		case WorkflowStatus.WORKFLOW_COMPLETED:
		case NodeStatus.NODE_COMPLETED: return "COMPLETED";
		case TaskStatus.CANCELLED:
		case WorkflowStatus.WORKFLOW_CANCELLED: return "CANCELLED";
		case TaskStatus.FAILED:
		case WorkflowStatus.WORKFLOW_FAILED:
		case NodeStatus.NODE_FAILED: return "FAILED";
		default: return "PENDING";
	}
}
//#endregion
//#region abi/tongflow.abi.json
var tongflow_abi_default = {
	version: 1,
	$defs: {
		"Asset": {
			"type": "object",
			"required": ["bytesBase64"],
			"properties": {
				"bytesBase64": {
					"type": "string",
					"minLength": 1
				},
				"filename": { "type": "string" },
				"mime": { "type": "string" }
			},
			"additionalProperties": false
		},
		"FileRef": {
			"type": "object",
			"required": ["file_key"],
			"properties": {
				"file_key": {
					"type": "string",
					"minLength": 1
				},
				"mime": { "type": "string" },
				"filename": { "type": "string" }
			},
			"additionalProperties": false
		},
		"VideoRef": {
			"type": "object",
			"required": ["file_key"],
			"properties": {
				"file_key": {
					"type": "string",
					"minLength": 1
				},
				"mime": { "type": "string" },
				"filename": { "type": "string" }
			},
			"additionalProperties": false
		},
		"AudioRef": {
			"type": "object",
			"required": ["file_key"],
			"properties": {
				"file_key": {
					"type": "string",
					"minLength": 1
				},
				"mime": { "type": "string" },
				"filename": { "type": "string" }
			},
			"additionalProperties": false
		},
		"ImageRef": {
			"type": "object",
			"required": ["file_key"],
			"properties": {
				"file_key": {
					"type": "string",
					"minLength": 1
				},
				"mime": { "type": "string" },
				"filename": { "type": "string" }
			},
			"additionalProperties": false
		},
		"ModelRef": {
			"type": "object",
			"required": ["file_key"],
			"properties": {
				"file_key": {
					"type": "string",
					"minLength": 1
				},
				"mime": { "type": "string" },
				"filename": { "type": "string" }
			},
			"additionalProperties": false
		}
	},
	nodes: [
		{
			"nodeSlot": "gen-text",
			"inputs": {
				"type": "object",
				"required": ["text"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"userPrompt": { "type": "string" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "split-text",
			"inputs": {
				"type": "object",
				"required": ["text"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"userPrompt": { "type": "string" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"texts": {
						"type": "array",
						"items": { "type": "string" },
						"x-expand-each": true
					}
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "combine-text",
			"inputs": {
				"type": "object",
				"required": ["texts"],
				"properties": {
					"texts": {
						"type": "array",
						"items": {
							"type": "string",
							"minLength": 1
						}
					},
					"userPrompt": { "type": "string" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-fusion",
			"inputs": {
				"type": "object",
				"required": ["text"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"images": {
						"type": "array",
						"items": { "$ref": "#/$defs/Asset" }
					},
					"seed": { "type": "integer" },
					"width": { "type": "integer" },
					"height": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "images-gen-video",
			"inputs": {
				"type": "object",
				"required": ["text"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"images": {
						"type": "array",
						"items": { "$ref": "#/$defs/Asset" }
					},
					"duration": { "type": "number" },
					"seed": { "type": "integer" },
					"width": { "type": "integer" },
					"height": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "refs-gen-video",
			"inputs": {
				"type": "object",
				"required": ["text"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"images": {
						"type": "array",
						"items": { "$ref": "#/$defs/Asset" }
					},
					"videos": {
						"type": "array",
						"items": { "$ref": "#/$defs/Asset" }
					},
					"audios": {
						"type": "array",
						"items": { "$ref": "#/$defs/Asset" }
					},
					"duration": { "type": "number" },
					"seed": { "type": "integer" },
					"width": { "type": "integer" },
					"height": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-gen-text",
			"inputs": {
				"type": "object",
				"required": ["text"],
				"properties": {
					"text": { "type": "string" },
					"system": { "type": "string" },
					"max_new_tokens": { "type": "integer" },
					"enable_thinking": { "type": "boolean" },
					"temperature": { "type": "number" },
					"top_p": { "type": "number" },
					"top_k": { "type": "integer" },
					"image": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "video-gen-text",
			"inputs": {
				"type": "object",
				"required": ["text", "video"],
				"properties": {
					"text": { "type": "string" },
					"system": { "type": "string" },
					"max_new_tokens": { "type": "integer" },
					"enable_thinking": { "type": "boolean" },
					"temperature": { "type": "number" },
					"top_p": { "type": "number" },
					"top_k": { "type": "integer" },
					"video": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "transcribe",
			"inputs": {
				"type": "object",
				"required": ["audio"],
				"properties": {
					"context": { "type": "string" },
					"language": { "type": "string" },
					"max_new_tokens": { "type": "number" },
					"audio": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "concat-videos",
			"inputs": {
				"type": "object",
				"required": ["videos"],
				"properties": { "videos": {
					"type": "array",
					"items": { "$ref": "#/$defs/Asset" }
				} },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "extract-audio",
			"inputs": {
				"type": "object",
				"required": ["video"],
				"properties": { "video": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "remove-video-audio",
			"inputs": {
				"type": "object",
				"required": ["video"],
				"properties": { "video": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "merge-video-audio",
			"inputs": {
				"type": "object",
				"required": ["video", "audio"],
				"properties": {
					"video": { "$ref": "#/$defs/Asset" },
					"audio": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "audio-video-lip-sync",
			"inputs": {
				"type": "object",
				"required": ["video", "audio"],
				"properties": {
					"audio": { "$ref": "#/$defs/Asset" },
					"text": { "type": "string" },
					"seed": { "type": "number" },
					"video": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "get-first-frame",
			"inputs": {
				"type": "object",
				"required": ["video"],
				"properties": { "video": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "get-last-frame",
			"inputs": {
				"type": "object",
				"required": ["video"],
				"properties": { "video": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "parse-document",
			"inputs": {
				"type": "object",
				"required": ["document"],
				"properties": { "document": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "split-video",
			"inputs": {
				"type": "object",
				"required": ["video"],
				"properties": {
					"video": { "$ref": "#/$defs/Asset" },
					"threshold": { "type": "number" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video_parts": {
						"type": "array",
						"items": { "$ref": "#/$defs/VideoRef" }
					}
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "link",
			"inputs": {
				"type": "object",
				"required": ["url"],
				"properties": { "url": {
					"type": "string",
					"minLength": 1
				} },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"mainText": { "type": "string" },
					"thinking": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" },
					"video": { "$ref": "#/$defs/VideoRef" },
					"audio": { "$ref": "#/$defs/AudioRef" },
					"extractedTexts": {
						"type": "array",
						"items": { "type": "string" }
					}
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-gen",
			"inputs": {
				"type": "object",
				"required": [],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"width": { "type": "integer" },
					"height": { "type": "integer" },
					"seed": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "gen-music",
			"inputs": {
				"type": "object",
				"required": [],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"lyrics": { "type": "string" },
					"tags": { "type": "string" },
					"language": { "type": "string" },
					"keyscale": { "type": "string" },
					"bpm": { "type": "number" },
					"songTitle": { "type": "string" },
					"duration": { "type": "number" },
					"seed": { "type": "integer" },
					"ref_audio": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "text-gen-speech-preset",
			"inputs": {
				"type": "object",
				"required": ["text"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"language": { "type": "string" },
					"speaker": { "type": "string" },
					"instruct": { "type": "string" },
					"max_new_tokens": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-gen-video",
			"inputs": {
				"type": "object",
				"required": [
					"text",
					"image",
					"duration"
				],
				"properties": {
					"duration": { "type": "number" },
					"text": { "type": "string" },
					"seed": { "type": "number" },
					"height": { "type": "integer" },
					"width": { "type": "integer" },
					"enhance_prompt": { "type": "boolean" },
					"image": { "$ref": "#/$defs/Asset" },
					"image_frame_idx": { "type": "integer" },
					"image_strength": { "type": "number" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-edit",
			"inputs": {
				"type": "object",
				"required": ["text", "image"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"image": { "$ref": "#/$defs/Asset" },
					"seed": { "type": "integer" },
					"match_input_size": { "type": "boolean" },
					"width": { "type": "integer" },
					"height": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-upscale",
			"inputs": {
				"type": "object",
				"required": ["image"],
				"properties": {
					"image": { "$ref": "#/$defs/Asset" },
					"resolution": { "type": "string" },
					"seed": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "video-upscale",
			"inputs": {
				"type": "object",
				"required": ["video"],
				"properties": {
					"video": { "$ref": "#/$defs/Asset" },
					"resolution": { "type": "string" },
					"seed": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "video-edit",
			"inputs": {
				"type": "object",
				"required": ["text", "video"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"video": { "$ref": "#/$defs/Asset" },
					"seed": { "type": "integer" },
					"width": { "type": "integer" },
					"height": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-describe",
			"inputs": {
				"type": "object",
				"required": ["image"],
				"properties": {
					"text": { "type": "string" },
					"userPrompt": { "type": "string" },
					"image": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "video-describe",
			"inputs": {
				"type": "object",
				"required": ["video"],
				"properties": {
					"text": { "type": "string" },
					"userPrompt": { "type": "string" },
					"video": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "audio-describe",
			"inputs": {
				"type": "object",
				"required": ["audio"],
				"properties": {
					"text": { "type": "string" },
					"userPrompt": { "type": "string" },
					"audio": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "music-repaint",
			"inputs": {
				"type": "object",
				"required": [
					"audio",
					"start_time",
					"end_time"
				],
				"properties": {
					"audio": { "$ref": "#/$defs/Asset" },
					"text": { "type": "string" },
					"lyrics": { "type": "string" },
					"start_time": { "type": "number" },
					"end_time": { "type": "number" },
					"strength": { "type": "number" },
					"seed": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "music-cover",
			"inputs": {
				"type": "object",
				"required": ["audio"],
				"properties": {
					"audio": { "$ref": "#/$defs/Asset" },
					"ref_audio": { "$ref": "#/$defs/Asset" },
					"text": { "type": "string" },
					"lyrics": { "type": "string" },
					"strength": { "type": "number" },
					"seed": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "music-extract",
			"inputs": {
				"type": "object",
				"required": ["audio", "track"],
				"properties": {
					"audio": { "$ref": "#/$defs/Asset" },
					"track": { "type": "string" },
					"seed": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "separate-sound",
			"inputs": {
				"type": "object",
				"required": ["audio", "text"],
				"properties": {
					"audio": { "$ref": "#/$defs/Asset" },
					"text": { "type": "string" },
					"spans": {
						"type": "array",
						"items": {
							"type": "object",
							"required": ["start", "end"],
							"properties": {
								"start": { "type": "number" },
								"end": { "type": "number" }
							},
							"additionalProperties": false
						}
					}
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"target": { "$ref": "#/$defs/AudioRef" },
					"residual": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "music-lego",
			"inputs": {
				"type": "object",
				"required": ["audio", "track"],
				"properties": {
					"audio": { "$ref": "#/$defs/Asset" },
					"track": { "type": "string" },
					"text": { "type": "string" },
					"lyrics": { "type": "string" },
					"seed": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "music-complete",
			"inputs": {
				"type": "object",
				"required": ["audio"],
				"properties": {
					"audio": { "$ref": "#/$defs/Asset" },
					"tracks": {
						"type": "array",
						"items": { "type": "string" }
					},
					"text": { "type": "string" },
					"seed": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "music-brief",
			"inputs": {
				"type": "object",
				"required": ["text"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"instrumental": { "type": "boolean" },
					"language": { "type": "string" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"lyrics": { "type": "string" },
					"tags": { "type": "string" },
					"bpm": { "type": "number" },
					"keyscale": { "type": "string" },
					"duration": { "type": "number" },
					"language": { "type": "string" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "audio-image-gen-video",
			"inputs": {
				"type": "object",
				"required": ["image", "audio"],
				"properties": {
					"text": { "type": "string" },
					"height": { "type": "integer" },
					"width": { "type": "integer" },
					"image": { "$ref": "#/$defs/Asset" },
					"audio": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "speech-text-gen-video",
			"inputs": {
				"type": "object",
				"required": ["text", "audio"],
				"properties": {
					"text": { "type": "string" },
					"seed": { "type": "number" },
					"height": { "type": "integer" },
					"width": { "type": "integer" },
					"enhance_prompt": { "type": "boolean" },
					"image": { "$ref": "#/$defs/Asset" },
					"image_frame_idx": { "type": "integer" },
					"image_strength": { "type": "number" },
					"audio": { "$ref": "#/$defs/Asset" },
					"audio_start_time": { "type": "number" },
					"duration": { "type": "number" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "video-image-gen-video-mix",
			"inputs": {
				"type": "object",
				"required": ["image", "video"],
				"properties": {
					"text": { "type": "string" },
					"image": { "$ref": "#/$defs/Asset" },
					"video": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "video-image-gen-video-move",
			"inputs": {
				"type": "object",
				"required": ["image", "video"],
				"properties": {
					"text": { "type": "string" },
					"duration": { "type": "number" },
					"seed": { "type": "number" },
					"height": { "type": "integer" },
					"width": { "type": "integer" },
					"enhance_prompt": { "type": "boolean" },
					"image": { "$ref": "#/$defs/Asset" },
					"video": { "$ref": "#/$defs/Asset" },
					"attention_strength": { "type": "number" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-image-gen-video",
			"inputs": {
				"type": "object",
				"required": [
					"text",
					"image",
					"end_image"
				],
				"properties": {
					"text": { "type": "string" },
					"duration": { "type": "number" },
					"seed": { "type": "number" },
					"height": { "type": "integer" },
					"width": { "type": "integer" },
					"enhance_prompt": { "type": "boolean" },
					"image": { "$ref": "#/$defs/Asset" },
					"image_strength": { "type": "number" },
					"end_image": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "text-gen-video",
			"inputs": {
				"type": "object",
				"required": ["text", "duration"],
				"properties": {
					"text": { "type": "string" },
					"duration": { "type": "number" },
					"seed": { "type": "number" },
					"height": { "type": "integer" },
					"width": { "type": "integer" },
					"enhance_prompt": { "type": "boolean" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-gen-model",
			"inputs": {
				"type": "object",
				"required": ["image"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"width": { "type": "integer" },
					"height": { "type": "integer" },
					"seed": { "type": "integer" },
					"image": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"model": { "$ref": "#/$defs/ModelRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "speech-video-gen-video",
			"inputs": {
				"type": "object",
				"required": ["video", "text"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"video": { "$ref": "#/$defs/Asset" },
					"enhance_prompt": { "type": "boolean" },
					"reference_strength": { "type": "number" },
					"seed": { "type": "number" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "text-gen-speech-clone",
			"inputs": {
				"type": "object",
				"required": ["text", "ref_audio"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"ref_audio": { "$ref": "#/$defs/Asset" },
					"language": { "type": "string" },
					"ref_text": { "type": "string" },
					"x_vector_only": { "type": "boolean" },
					"max_new_tokens": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "transcribe-timestamp",
			"inputs": {
				"type": "object",
				"required": ["audio"],
				"properties": {
					"context": { "type": "string" },
					"language": { "type": "string" },
					"max_new_tokens": { "type": "number" },
					"audio": { "$ref": "#/$defs/Asset" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"text": { "type": "string" },
					"time_stamps": {
						"type": "array",
						"items": {
							"type": "object",
							"required": [
								"start",
								"end",
								"text"
							],
							"properties": {
								"start": { "type": "number" },
								"end": { "type": "number" },
								"text": { "type": "string" }
							},
							"additionalProperties": false
						}
					}
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "text-gen-speech-instruct",
			"inputs": {
				"type": "object",
				"required": ["text"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"language": { "type": "string" },
					"instruct": { "type": "string" },
					"max_new_tokens": { "type": "integer" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "drop-video",
			"inputs": {
				"type": "object",
				"required": ["videos"],
				"properties": {
					"query": { "type": "string" },
					"videos": {
						"type": "array",
						"items": { "$ref": "#/$defs/Asset" }
					}
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"clips": {
						"type": "array",
						"items": { "$ref": "#/$defs/VideoRef" }
					}
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "arrange-group",
			"inputs": {
				"type": "object",
				"required": [],
				"properties": {
					"items": {
						"type": "array",
						"items": {
							"type": "object",
							"required": [],
							"properties": {
								"text": { "type": "string" },
								"id": { "type": "string" }
							},
							"additionalProperties": false
						}
					},
					"infos": {
						"type": "array",
						"items": {
							"type": "object",
							"properties": {},
							"additionalProperties": true
						}
					},
					"query": { "type": "string" },
					"groupCount": { "type": "integer" },
					"duplicatable": { "type": "boolean" },
					"images": {
						"type": "array",
						"items": { "$ref": "#/$defs/Asset" }
					},
					"fileKeys": {
						"type": "array",
						"items": { "$ref": "#/$defs/Asset" }
					}
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"groups": {
						"type": "array",
						"items": {
							"type": "array",
							"items": { "$ref": "#/$defs/VideoRef" }
						},
						"x-expand-each": true
					}
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "separate_speaker",
			"inputs": {
				"type": "object",
				"required": ["audio"],
				"properties": { "audio": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"outputKeys": {
						"type": "array",
						"items": { "$ref": "#/$defs/AudioRef" },
						"x-expand-each": true
					}
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "separate_audio_track",
			"inputs": {
				"type": "object",
				"required": ["audio"],
				"properties": { "audio": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"file_key": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "denoise_audio",
			"inputs": {
				"type": "object",
				"required": ["fileKey"],
				"properties": { "fileKey": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "convert_voice",
			"inputs": {
				"type": "object",
				"required": ["sourceKey", "targetKey"],
				"properties": {
					"sourceKey": { "$ref": "#/$defs/Asset" },
					"targetKey": { "type": "string" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "remove_watermark",
			"inputs": {
				"type": "object",
				"required": ["fileKey"],
				"properties": { "fileKey": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "subtitle_remove",
			"inputs": {
				"type": "object",
				"required": ["fileKey"],
				"properties": { "fileKey": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"video": { "$ref": "#/$defs/VideoRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "text-audio-gen-speech",
			"inputs": {
				"type": "object",
				"required": ["text", "audio"],
				"properties": {
					"text": {
						"type": "string",
						"minLength": 1
					},
					"audio": { "$ref": "#/$defs/Asset" },
					"emotion": { "type": "string" },
					"style": { "type": "string" }
				},
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"audio": { "$ref": "#/$defs/AudioRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-pose",
			"inputs": {
				"type": "object",
				"required": ["image"],
				"properties": { "image": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-body-seg",
			"inputs": {
				"type": "object",
				"required": ["image"],
				"properties": { "image": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-normal",
			"inputs": {
				"type": "object",
				"required": ["image"],
				"properties": { "image": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "image-matting",
			"inputs": {
				"type": "object",
				"required": ["image"],
				"properties": { "image": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"image": { "$ref": "#/$defs/ImageRef" }
				},
				"additionalProperties": false
			}
		},
		{
			"nodeSlot": "video-gen-model",
			"inputs": {
				"type": "object",
				"required": ["video"],
				"properties": { "video": { "$ref": "#/$defs/Asset" } },
				"additionalProperties": false
			},
			"outputs": {
				"type": "object",
				"required": ["success"],
				"properties": {
					"success": { "type": "boolean" },
					"error": { "type": "string" },
					"model": { "$ref": "#/$defs/ModelRef" }
				},
				"additionalProperties": false
			}
		}
	]
};
//#endregion
//#region src/core/schema/tongflow-abi.ts
const AbiNodeSchema = z.object({
	nodeSlot: z.string().min(1),
	inputs: z.unknown(),
	outputs: z.unknown()
});
const TongflowAbiFileSchema = z.object({
	version: z.number().int().min(1),
	source: z.string().optional(),
	$defs: z.record(z.string(), z.unknown()).optional(),
	nodes: z.array(AbiNodeSchema)
});
const REF_TO_NODE_TYPE = {
	VideoRef: "videoNode",
	AudioRef: "audioNode",
	ImageRef: "imageNode",
	ModelRef: "modelNode",
	FileRef: "fileNode"
};
function resolveRef(refStr) {
	return refStr.split("/").pop() ?? "";
}
function resolveAbiOutputMappings(node) {
	const outputs = node.outputs;
	if (!outputs || typeof outputs !== "object") return [];
	const properties = outputs.properties;
	if (!properties) return [];
	const routes = [];
	for (const [field, schema] of Object.entries(properties)) {
		if (field === "success" || field === "error" || field === "thinking") continue;
		const ref = schema["$ref"];
		if (ref) {
			const refName = resolveRef(ref);
			const nodeType = REF_TO_NODE_TYPE[refName];
			if (nodeType) routes.push({
				sourceField: field,
				nodeType,
				dataField: "fileKeys",
				expandEach: false,
				itemValuePath: "file_key"
			});
			continue;
		}
		if (schema.type === "string") {
			routes.push({
				sourceField: field,
				nodeType: "textNode",
				dataField: "texts",
				expandEach: false
			});
			continue;
		}
		if (schema.type === "array") {
			const items = schema.items;
			if (!items) continue;
			const expandEach = schema["x-expand-each"] === true;
			const itemRef = items["$ref"];
			if (itemRef) {
				const refName = resolveRef(itemRef);
				const nodeType = REF_TO_NODE_TYPE[refName];
				if (nodeType) routes.push({
					sourceField: field,
					nodeType,
					dataField: "fileKeys",
					expandEach,
					itemValuePath: "file_key"
				});
				continue;
			}
			if (items.type === "array") {
				const innerItems = items.items;
				if (!innerItems) continue;
				const innerRef = innerItems["$ref"];
				if (innerRef) {
					const refName = resolveRef(innerRef);
					const nodeType = REF_TO_NODE_TYPE[refName];
					if (nodeType) routes.push({
						sourceField: field,
						nodeType,
						dataField: "fileKeys",
						expandEach: true,
						itemValuePath: "file_key",
						isArrayOfArrays: true
					});
				}
				continue;
			}
			if (items.type === "string") routes.push({
				sourceField: field,
				nodeType: "textNode",
				dataField: "texts",
				expandEach
			});
		}
	}
	return routes;
}
const parsed = TongflowAbiFileSchema.parse(tongflow_abi_default);
const bySlot = /* @__PURE__ */ new Map();
for (const n of parsed.nodes) bySlot.set(n.nodeSlot, n);
parsed.version;
parsed.nodes;
function getAbiNodeBySlot(nodeSlot) {
	return bySlot.get(nodeSlot);
}
//#endregion
//#region src/core/registry/model-catalog.ts
/** Read a dot-path (`a.b.c`) off an unknown JSON value; `undefined` when absent. */
function getPath(value, path) {
	let cur = value;
	for (const key of path.split(".")) {
		if (cur === null || typeof cur !== "object") return void 0;
		cur = cur[key];
	}
	return cur;
}
/** Substring match against the field's JSON serialization (strings as-is). */
function fieldContains(value, token) {
	if (value === void 0 || value === null) return false;
	return (typeof value === "string" ? value : JSON.stringify(value)).includes(token);
}
/**
* Filter a fetched catalog payload into `slot -> model ids`, applying the
* plugin's declared `exclude` and per-slot `slots` rules (substring tokens,
* `!`-prefixed = must be absent). Ids keep catalog order; malformed records
* are skipped. Pure — the fetch lives in the canvas.
*/
function filterModelCatalog(catalog, payload) {
	const out = {};
	for (const slot of Object.keys(catalog.slots)) out[slot] = [];
	const items = getPath(payload, catalog.items);
	if (!Array.isArray(items)) return out;
	const exclude = Object.entries(catalog.exclude ?? {});
	for (const record of items) {
		const id = getPath(record, catalog.id);
		if (typeof id !== "string" || !id.trim()) continue;
		if (exclude.some(([field, literal]) => getPath(record, field) === literal)) continue;
		for (const [slot, rules] of Object.entries(catalog.slots)) if (Object.entries(rules).every(([field, tokens]) => {
			const value = getPath(record, field);
			return (Array.isArray(tokens) ? tokens : [tokens]).every((token) => token.startsWith("!") ? !fieldContains(value, token.slice(1)) : fieldContains(value, token));
		}) && !out[slot].includes(id)) out[slot].push(id);
	}
	return out;
}
//#endregion
//#region src/core/registry/plugin-params.ts
/**
* The params a node offers for the active plugin + model: every declared entry
* whose `models` gate is absent or names the selected model.
*/
function visiblePluginParams(params, model) {
	if (!params) return [];
	const out = [];
	for (const [name, spec] of Object.entries(params)) {
		if (spec.models && !(model && spec.models.includes(model))) continue;
		out.push({
			name,
			spec
		});
	}
	return out;
}
/** True when `value` is a legal setting for `spec` (type + range/options). */
function isValidPluginParamValue(spec, value) {
	switch (spec.type) {
		case "select": return (spec.options ?? []).some((o) => o === value);
		case "boolean": return typeof value === "boolean";
		case "text": return typeof value === "string";
		case "number":
		case "integer":
			if (typeof value !== "number" || !Number.isFinite(value)) return false;
			if (spec.type === "integer" && !Number.isInteger(value)) return false;
			if (spec.min !== void 0 && value < spec.min) return false;
			if (spec.max !== void 0 && value > spec.max) return false;
			return true;
		default: return false;
	}
}
/**
* Keep only the entries of `values` that the visible params accept and that
* differ from the declared default. Unset means "plugin default", so a value
* equal to it is dropped; the result is what travels to the plugin.
*/
function normalizePluginParams(entries, values) {
	const out = {};
	if (!values) return out;
	for (const { name, spec } of entries) {
		if (!(name in values)) continue;
		const v = values[name];
		if (!isValidPluginParamValue(spec, v)) continue;
		if (spec.default !== void 0 && v === spec.default) continue;
		out[name] = v;
	}
	return out;
}
/** Shallow key/value equality for two param records. */
function samePluginParams(a, b) {
	const ka = Object.keys(a ?? {});
	const kb = Object.keys(b ?? {});
	if (ka.length !== kb.length) return false;
	return ka.every((k) => Object.is(a?.[k], b?.[k]));
}
/**
* Deep-copy the current canvas state for the history stack, stripping
* transient interaction flags so undo never restores a stale selection
* or a mid-drag state.
*/
function snapshotFlow(nodes, edges) {
	const snapshot = structuredClone({
		nodes,
		edges
	});
	for (const node of snapshot.nodes) {
		delete node.selected;
		delete node.dragging;
	}
	return snapshot;
}
/** Append a snapshot to the past stack, dropping the oldest beyond the cap. */
function pushSnapshot(past, snapshot, limit = 100) {
	const next = past.concat(snapshot);
	return next.length > limit ? next.slice(next.length - limit) : next;
}
let focusGeneration = 0;
function currentFocusGeneration() {
	return focusGeneration;
}
//#endregion
//#region src/core/workflow/layout/node-dims.ts
const MEDIA_NODE_SIZE = {
	w: 256,
	h: 304
};
const NODE_SIZE_TABLE = {
	textNode: {
		w: 256,
		h: 96
	},
	linkNode: {
		w: 300,
		h: 160
	},
	fileNode: {
		w: 160,
		h: 150
	},
	modelNode: {
		w: 256,
		h: 260
	},
	imageNode: MEDIA_NODE_SIZE,
	videoNode: MEDIA_NODE_SIZE,
	audioNode: {
		w: 360,
		h: 140
	},
	addTextNode: {
		w: 480,
		h: 320
	},
	addImageNode: {
		w: 480,
		h: 320
	},
	addVideoNode: {
		w: 480,
		h: 320
	},
	addAudioNode: {
		w: 480,
		h: 320
	},
	addModelNode: {
		w: 480,
		h: 320
	},
	addLinkNode: {
		w: 480,
		h: 320
	},
	addFileNode: {
		w: 360,
		h: 320
	},
	imageFusionNode: {
		w: 480,
		h: 780
	},
	refsGenVideoNode: {
		w: 480,
		h: 780
	},
	textGenMusicNode: {
		w: 520,
		h: 480
	},
	musicExtractNode: {
		w: 420,
		h: 400
	}
};
const DEFAULT_EXECUTABLE_SIZE = {
	w: 480,
	h: 400
};
const DEFAULT_SIZE = {
	w: 256,
	h: 80
};
function estimateNodeSize(node) {
	const { measured, type } = node;
	if (measured && typeof measured.width === "number" && measured.width > 0 && typeof measured.height === "number" && measured.height > 0) return {
		w: measured.width,
		h: measured.height
	};
	if (type && NODE_SIZE_TABLE[type]) return NODE_SIZE_TABLE[type];
	if (type && type in NODE_TYPE_TO_ABI_FEATURE) return DEFAULT_EXECUTABLE_SIZE;
	return DEFAULT_SIZE;
}
//#endregion
//#region src/core/workflow/layout/auto-layout.ts
/**
* Union of the weakly-connected components containing any of `seedIds`.
* Used to scope a layout to just the region a change touched.
*/
function componentsContaining(seedIds, nodes, edges) {
	const nodeIds = new Set(nodes.map((n) => n.id));
	const adjacency = /* @__PURE__ */ new Map();
	const link = (from, to) => {
		const list = adjacency.get(from);
		if (list) list.push(to);
		else adjacency.set(from, [to]);
	};
	for (const e of edges) {
		if (!nodeIds.has(e.source) || !nodeIds.has(e.target)) continue;
		link(e.source, e.target);
		link(e.target, e.source);
	}
	const seen = /* @__PURE__ */ new Set();
	const queue = [];
	for (const id of seedIds) if (nodeIds.has(id) && !seen.has(id)) {
		seen.add(id);
		queue.push(id);
	}
	while (queue.length > 0) {
		const id = queue.shift();
		for (const next of adjacency.get(id) ?? []) if (!seen.has(next)) {
			seen.add(next);
			queue.push(next);
		}
	}
	return seen;
}
function splitComponents(nodes, edges) {
	const remaining = new Set(nodes.map((n) => n.id));
	const byId = new Map(nodes.map((n) => [n.id, n]));
	const components = [];
	for (const node of nodes) {
		if (!remaining.has(node.id)) continue;
		const member = componentsContaining([node.id], nodes, edges);
		for (const id of member) remaining.delete(id);
		components.push([...member].map((id) => byId.get(id)));
	}
	return components;
}
function bboxOf(nodes, positions) {
	let minX = Number.POSITIVE_INFINITY;
	let maxX = Number.NEGATIVE_INFINITY;
	let minY = Number.POSITIVE_INFINITY;
	let maxY = Number.NEGATIVE_INFINITY;
	for (const node of nodes) {
		const pos = positions.get(node.id) ?? node.position;
		const { w, h } = estimateNodeSize(node);
		minX = Math.min(minX, pos.x - w / 2);
		maxX = Math.max(maxX, pos.x + w / 2);
		minY = Math.min(minY, pos.y - h / 2);
		maxY = Math.max(maxY, pos.y + h / 2);
	}
	return {
		minX,
		maxX,
		minY,
		maxY
	};
}
/** Layout one weakly-connected component in local coordinates. */
function layoutComponent(componentNodes, edges) {
	const ids = new Set(componentNodes.map((n) => n.id));
	const componentEdges = edges.filter((e) => ids.has(e.source) && ids.has(e.target));
	const plan = new WorkflowParser({
		nodes: componentNodes,
		edges: componentEdges
	}).generateExecutionPlan();
	const columns = /* @__PURE__ */ new Map();
	let skippedCycle = false;
	for (const node of componentNodes) {
		const level = plan.nodeInfoMap.get(node.id)?.level ?? -1;
		if (level < 0) {
			skippedCycle = true;
			continue;
		}
		const col = columns.get(level);
		if (col) col.push(node);
		else columns.set(level, [node]);
	}
	if (skippedCycle) logger.warn("[auto-layout] cycle detected; cyclic nodes were left in place");
	if (columns.size === 0) return null;
	const levels = [...columns.keys()].sort((a, b) => a - b);
	const colWidth = /* @__PURE__ */ new Map();
	for (const level of levels) colWidth.set(level, Math.max(...columns.get(level).map((n) => estimateNodeSize(n).w)));
	const colX = /* @__PURE__ */ new Map();
	let x = 0;
	for (let i = 0; i < levels.length; i++) {
		const level = levels[i];
		const w = colWidth.get(level);
		x = i === 0 ? w / 2 : x + colWidth.get(levels[i - 1]) / 2 + 100 + w / 2;
		colX.set(level, x);
	}
	const upstream = /* @__PURE__ */ new Map();
	for (const e of componentEdges) {
		const list = upstream.get(e.target);
		if (list) list.push(e.source);
		else upstream.set(e.target, [e.source]);
	}
	const result = /* @__PURE__ */ new Map();
	for (const level of levels) {
		const colNodes = columns.get(level);
		const sortKey = (node) => {
			if (level === levels[0]) return node.position.y;
			const ups = (upstream.get(node.id) ?? []).map((id) => result.get(id)?.y).filter((y) => y !== void 0);
			if (ups.length === 0) return node.position.y;
			return ups.reduce((s, y) => s + y, 0) / ups.length;
		};
		const ordered = [...colNodes].sort((a, b) => sortKey(a) - sortKey(b));
		let cursor = -(ordered.reduce((s, n) => s + estimateNodeSize(n).h, 0) + 60 * Math.max(0, ordered.length - 1)) / 2;
		for (const node of ordered) {
			const { h } = estimateNodeSize(node);
			result.set(node.id, {
				x: colX.get(level),
				y: cursor + h / 2
			});
			cursor += h + 60;
		}
	}
	return result;
}
function computeAutoLayout(nodes, edges, opts = {}) {
	const scoped = opts.scope ? nodes.filter((n) => opts.scope?.has(n.id)) : nodes;
	if (scoped.length === 0) return /* @__PURE__ */ new Map();
	const scopedIds = new Set(scoped.map((n) => n.id));
	const scopedEdges = edges.filter((e) => scopedIds.has(e.source) && scopedIds.has(e.target));
	const components = splitComponents(scoped, scopedEdges);
	const moved = /* @__PURE__ */ new Map();
	const placedBoxes = [];
	const orderedComponents = [...components].sort((a, b) => bboxOf(a, /* @__PURE__ */ new Map()).minY - bboxOf(b, /* @__PURE__ */ new Map()).minY);
	for (const component of orderedComponents) {
		const local = layoutComponent(component, scopedEdges);
		if (!local) continue;
		const laidOutNodes = component.filter((n) => local.has(n.id));
		const oldBox = bboxOf(laidOutNodes, /* @__PURE__ */ new Map());
		const newBox = bboxOf(laidOutNodes, local);
		const dx = (oldBox.minX + oldBox.maxX) / 2 - (newBox.minX + newBox.maxX) / 2;
		let dy = (oldBox.minY + oldBox.maxY) / 2 - (newBox.minY + newBox.maxY) / 2;
		let anchored = {
			minX: newBox.minX + dx,
			maxX: newBox.maxX + dx,
			minY: newBox.minY + dy,
			maxY: newBox.maxY + dy
		};
		for (const prev of placedBoxes) if (anchored.minX < prev.maxX && anchored.maxX > prev.minX && anchored.minY < prev.maxY && anchored.maxY > prev.minY) {
			const shift = prev.maxY - anchored.minY + 160;
			dy += shift;
			anchored = {
				...anchored,
				minY: anchored.minY + shift,
				maxY: anchored.maxY + shift
			};
		}
		placedBoxes.push(anchored);
		for (const [id, pos] of local) {
			const nx = pos.x + dx;
			const ny = pos.y + dy;
			const node = laidOutNodes.find((n) => n.id === id);
			if (Math.abs(nx - node.position.x) > .5 || Math.abs(ny - node.position.y) > .5) moved.set(id, {
				x: nx,
				y: ny
			});
		}
	}
	return moved;
}
//#endregion
//#region src/core/store/flow-store.ts
function isDataNode(nodeType) {
	return nodeType in DATA_NODE_TYPES;
}
function defaultCreateId() {
	return globalThis.crypto.randomUUID();
}
/**
* Append `edge` unless an identical connection (source/target/handles) already
* exists — the same dedupe React Flow's `addEdge` performs.
*/
function addEdgeIfAbsent(edge, edges) {
	return edges.some((e) => e.source === edge.source && e.target === edge.target && (e.sourceHandle ?? null) === (edge.sourceHandle ?? null) && (e.targetHandle ?? null) === (edge.targetHandle ?? null)) ? edges : edges.concat(edge);
}
function createDebounce$1(callback, delay) {
	let timeoutId = null;
	return (...args) => {
		if (timeoutId) clearTimeout(timeoutId);
		timeoutId = setTimeout(() => {
			callback(...args);
			timeoutId = null;
		}, delay);
	};
}
const LAYOUT_SETTLE_WINDOW_MS = 1500;
/**
* The core state + actions as a zustand slice, so a host can compose it with
* its own bindings (`create<FlowCoreState & Extra>()((set, get) => ({
* ...createFlowSlice(set, get), ...extra }))`).
*/
function createFlowSlice(set, get, options = {}) {
	const createId = options.createId ?? defaultCreateId;
	const now = options.now ?? Date.now;
	const lastCommit = {
		source: "",
		focusGen: -1
	};
	const resetCommitTracker = () => {
		lastCommit.source = "";
		lastCommit.focusGen = -1;
	};
	const layoutSettleWatch = {
		scope: null,
		until: 0
	};
	const cancelLayoutSettleWatch = () => {
		layoutSettleWatch.scope = null;
		layoutSettleWatch.until = 0;
	};
	const debouncedSettleRelayout = createDebounce$1((run) => run(), 300);
	return {
		nodes: options.initial?.nodes ?? [],
		edges: options.initial?.edges ?? [],
		workflowName: options.initial?.workflowName ?? "",
		workflowId: options.initial?.workflowId ?? null,
		workflowDescription: options.initial?.workflowDescription ?? "",
		comboMode: false,
		comboSelectedIds: /* @__PURE__ */ new Set(),
		reconnectingEdgeId: null,
		autoLayout: (seedIds, opts) => {
			const { nodes, edges } = get();
			const scope = seedIds && seedIds.length > 0 ? componentsContaining(seedIds, nodes, edges) : void 0;
			const moved = computeAutoLayout(nodes, edges, { scope });
			if (moved.size === 0) return false;
			if (opts?.history !== false) {
				get().commitHistory("layout");
				queueMicrotask(resetCommitTracker);
			}
			const newNodes = get().nodes.map((n) => {
				const pos = moved.get(n.id);
				return pos ? {
					...n,
					position: pos
				} : n;
			});
			set({ nodes: newNodes });
			layoutSettleWatch.scope = scope ?? new Set(newNodes.map((n) => n.id));
			layoutSettleWatch.until = now() + LAYOUT_SETTLE_WINDOW_MS;
			return true;
		},
		cancelLayoutSettleWatch,
		notifyNodesMeasured: (nodeIds) => {
			const watched = layoutSettleWatch.scope;
			if (!watched) return;
			if (now() >= layoutSettleWatch.until) {
				cancelLayoutSettleWatch();
				return;
			}
			if (!nodeIds.some((id) => watched.has(id))) return;
			debouncedSettleRelayout(() => {
				if (layoutSettleWatch.scope === watched && now() < layoutSettleWatch.until + LAYOUT_SETTLE_WINDOW_MS) get().autoLayout([...watched], { history: false });
			});
		},
		historyPast: [],
		historyFuture: [],
		commitHistory: (source) => {
			const focusGen = currentFocusGeneration();
			if (source && source === lastCommit.source && focusGen === lastCommit.focusGen) return;
			lastCommit.source = source ?? "";
			lastCommit.focusGen = focusGen;
			const { nodes, edges, historyPast } = get();
			set({
				historyPast: pushSnapshot(historyPast, snapshotFlow(nodes, edges)),
				historyFuture: []
			});
		},
		resetHistoryCoalescing: resetCommitTracker,
		undo: () => {
			const { historyPast, historyFuture, nodes, edges } = get();
			const previous = historyPast[historyPast.length - 1];
			if (!previous) return;
			resetCommitTracker();
			set({
				nodes: previous.nodes,
				edges: previous.edges,
				historyPast: historyPast.slice(0, -1),
				historyFuture: historyFuture.concat(snapshotFlow(nodes, edges)),
				selectedNodes: [],
				comboMode: false,
				comboSelectedIds: /* @__PURE__ */ new Set()
			});
		},
		redo: () => {
			const { historyPast, historyFuture, nodes, edges } = get();
			const next = historyFuture[historyFuture.length - 1];
			if (!next) return;
			resetCommitTracker();
			set({
				nodes: next.nodes,
				edges: next.edges,
				historyPast: pushSnapshot(historyPast, snapshotFlow(nodes, edges)),
				historyFuture: historyFuture.slice(0, -1),
				selectedNodes: [],
				comboMode: false,
				comboSelectedIds: /* @__PURE__ */ new Set()
			});
		},
		clearHistory: () => {
			resetCommitTracker();
			set({
				historyPast: [],
				historyFuture: []
			});
		},
		nodeCreatedCallbacks: /* @__PURE__ */ new Set(),
		onNodeCreated: (callback) => {
			const callbacks = get().nodeCreatedCallbacks;
			callbacks.add(callback);
			return () => {
				callbacks.delete(callback);
			};
		},
		computeMap: /* @__PURE__ */ new Map(),
		registerCompute: (id, fn) => {
			const map = new Map(get().computeMap);
			map.set(id, fn);
			set({ computeMap: map });
		},
		getCompute: (id) => get().computeMap.get(id),
		selectedNodes: [],
		setSelectedNodes: (nodes) => set({ selectedNodes: nodes }),
		setNodes: (nodes) => set({ nodes }),
		setEdges: (edges) => set({ edges }),
		setReconnectingEdgeId: (id) => set({ reconnectingEdgeId: id }),
		pruneEdgesOf: (nodeIds) => {
			if (nodeIds.length === 0) return;
			const idSet = new Set(nodeIds);
			set({ edges: get().edges.filter((e) => !idSet.has(e.source) && !idSet.has(e.target)) });
		},
		updates: (nodeId, data, opts) => {
			if (opts?.history !== false) get().commitHistory(`update:${nodeId}`);
			set({ nodes: get().nodes.map((node) => node.id === nodeId ? {
				...node,
				data
			} : node) });
		},
		addNode: (node, position) => {
			get().commitHistory();
			const { nodes } = get();
			let defaultX = 100;
			let defaultY = 100;
			if (position) {
				defaultX = position.x;
				defaultY = position.y;
			} else if (nodes.length > 0) {
				const rightEdge = (n) => n.position.x + estimateNodeSize(n).w / 2;
				const rightmostNode = nodes.reduce((rightmost, current) => rightEdge(current) > rightEdge(rightmost) ? current : rightmost);
				defaultX = rightEdge(rightmostNode) + 100 + estimateNodeSize({ type: node.type }).w / 2;
				defaultY = rightmostNode.position.y;
			}
			const nodeId = createId();
			const newNode = {
				id: nodeId,
				type: node.type,
				position: {
					x: defaultX,
					y: defaultY
				},
				origin: [.5, .5],
				data: node?.data ?? {}
			};
			set({ nodes: nodes.concat(newNode) });
			get().nodeCreatedCallbacks.forEach((cb) => cb([nodeId]));
			return nodeId;
		},
		removeNode: (nodeId) => {
			get().commitHistory();
			const { nodes, edges } = get();
			set({
				nodes: nodes.filter((node) => node.id !== nodeId),
				edges: edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
			});
		},
		expands: (nodeId, possibleNodes) => {
			const { nodes } = get();
			let { edges } = get();
			const currNode = nodes.find((node) => node.id === nodeId);
			if (!currNode) return [];
			get().commitHistory();
			const sourceIsDataNode = isDataNode(currNode.type ?? "");
			const existingChildNodes = edges.filter((edge) => edge.source === currNode.id).map((edge) => nodes.find((n) => n.id === edge.target)).filter(Boolean);
			const existingChildrenByType = /* @__PURE__ */ new Map();
			if (!sourceIsDataNode) for (const child of existingChildNodes) {
				if (!child.type) continue;
				const bucket = existingChildrenByType.get(child.type);
				if (bucket) bucket.push(child);
				else existingChildrenByType.set(child.type, [child]);
			}
			const reuseCursorByType = /* @__PURE__ */ new Map();
			const { position } = currNode;
			const ids = [];
			const newNodes = [];
			const newlyCreatedIds = [];
			let updatedNodes = [...nodes];
			const nodesToCreate = possibleNodes.filter(({ type }) => {
				const available = existingChildrenByType.get(type)?.length ?? 0;
				const cursor = reuseCursorByType.get(type) ?? 0;
				if (cursor < available) {
					reuseCursorByType.set(type, cursor + 1);
					return false;
				}
				return true;
			});
			reuseCursorByType.clear();
			const parentHalfW = estimateNodeSize(currNode).w / 2;
			const freshSizes = nodesToCreate.map((n) => estimateNodeSize(n));
			const totalH = freshSizes.reduce((sum, s) => sum + s.h, 0) + 60 * Math.max(0, freshSizes.length - 1);
			let yCursor;
			if (existingChildNodes.length > 0) yCursor = Math.max(...existingChildNodes.map((child) => child.position.y + estimateNodeSize(child).h / 2)) + 60;
			else yCursor = position.y - totalH / 2;
			let newNodeIndex = 0;
			for (const { type, data = {} } of possibleNodes) {
				const bucket = existingChildrenByType.get(type);
				const cursor = reuseCursorByType.get(type) ?? 0;
				const existingChild = bucket && cursor < bucket.length ? bucket[cursor] : void 0;
				if (existingChild) reuseCursorByType.set(type, cursor + 1);
				if (existingChild) {
					ids.push(existingChild.id);
					updatedNodes = updatedNodes.map((node) => {
						if (node.id === existingChild.id) return {
							...node,
							data: {
								...node.data,
								...data
							}
						};
						return node;
					});
				} else {
					const newNodeId = createId();
					ids.push(newNodeId);
					newlyCreatedIds.push(newNodeId);
					const edgeId = createId();
					const size = freshSizes[newNodeIndex];
					newNodes.push({
						id: newNodeId,
						type,
						position: {
							x: position.x + parentHalfW + 100 + size.w / 2,
							y: yCursor + size.h / 2
						},
						origin: [.5, .5],
						data
					});
					yCursor += size.h + 60;
					const { sourceHandle, targetHandle } = resolveEdgeHandles({
						sourceType: currNode.type,
						targetType: type,
						targetSpec: resolvedSpecForNodeType(type)
					});
					edges = addEdgeIfAbsent({
						id: edgeId,
						source: `${currNode.id}`,
						target: newNodeId,
						type: "custom-edge",
						...sourceHandle ? { sourceHandle } : {},
						...targetHandle ? { targetHandle } : {}
					}, edges);
					newNodeIndex++;
				}
			}
			set({
				nodes: updatedNodes.concat(newNodes),
				edges: [...edges]
			});
			if (newlyCreatedIds.length > 0) get().nodeCreatedCallbacks.forEach((cb) => cb(newlyCreatedIds));
			return ids;
		},
		compose: ({ type, data }) => {
			get().commitHistory();
			const { comboSelectedIds, nodes, edges } = get();
			const nodeId = createId();
			const positions = Array.from(comboSelectedIds).map((id) => {
				const node = nodes.find((n) => n.id === id);
				if (!node) return null;
				const size = estimateNodeSize(node);
				return {
					x: node.position.x,
					y: node.position.y,
					width: size.w,
					height: size.h
				};
			}).filter((pos) => pos !== null);
			const rightmostX = Math.max(...positions.map((pos) => pos.x + pos.width / 2));
			const centerY = (Math.min(...positions.map((pos) => pos.y - pos.height / 2)) + Math.max(...positions.map((pos) => pos.y + pos.height / 2))) / 2;
			const newNode = {
				id: nodeId,
				type,
				position: {
					x: rightmostX + 100 + estimateNodeSize({ type }).w / 2,
					y: centerY
				},
				origin: [.5, .5],
				data: data ?? {}
			};
			const usedTargetHandles = /* @__PURE__ */ new Set();
			const newEdges = Array.from(comboSelectedIds).map((id) => {
				const node = nodes.find((n) => n.id === id);
				if (!node) return null;
				const { sourceHandle, targetHandle } = resolveEdgeHandles({
					sourceType: node.type,
					targetType: type,
					usedTargetHandles,
					targetSpec: resolvedSpecForNodeType(type)
				});
				if (targetHandle) usedTargetHandles.add(targetHandle);
				return {
					id: createId(),
					source: `${node.id}`,
					target: nodeId,
					type: "custom-edge",
					...sourceHandle ? { sourceHandle } : {},
					...targetHandle ? { targetHandle } : {}
				};
			}).filter(Boolean);
			set({
				nodes: nodes.concat([newNode]),
				edges: edges.concat(newEdges)
			});
			get().clearCombo();
			get().nodeCreatedCallbacks.forEach((cb) => cb([nodeId]));
			return nodeId;
		},
		setComboMode: (enabled) => {
			if (!enabled) set({
				comboMode: false,
				comboSelectedIds: /* @__PURE__ */ new Set()
			});
			else set({ comboMode: true });
		},
		isInCombo: (id) => get().comboSelectedIds.has(id),
		toggleCombo: (id) => {
			const next = new Set(get().comboSelectedIds);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			set({
				comboSelectedIds: next,
				comboMode: next.size > 0
			});
		},
		clearCombo: () => set({
			comboMode: false,
			comboSelectedIds: /* @__PURE__ */ new Set()
		}),
		setWorkflowName: (name) => set({ workflowName: name }),
		setWorkflowId: (id) => set({ workflowId: id }),
		setWorkflowDescription: (description) => set({ workflowDescription: description })
	};
}
//#endregion
//#region src/core/workflow/flow-node-data.ts
/**
* Narrow unknown RF `node.data` (store / composite reads) to `BaseNodeData`.
* Prefer typed `NodeProps<Node<BaseNodeData, …>>` on components; use this where
* the graph edge does not carry a concrete literal (e.g. `useNodesData`).
*/
function coerceBaseNodeData(data) {
	if (data !== null && typeof data === "object") return data;
	return {};
}
//#endregion
//#region src/canvas/lib/file/loader-queue.ts
var FileLoaderQueue = class {
	enqueue(task) {
		task.onSuccess(task.url);
	}
	enqueueBatch(tasks) {
		for (const task of tasks) task.onSuccess(task.url);
	}
	cancel(_taskId) {}
	getStats() {
		return {
			pending: 0,
			active: 0,
			completed: 0,
			failed: 0
		};
	}
};
let globalQueue = null;
function getGlobalFileQueue() {
	if (!globalQueue) globalQueue = new FileLoaderQueue();
	return globalQueue;
}
//#endregion
//#region src/canvas/i18n/messages/en.json
var en_default = {
	Workspace: {
		"edges": {
			"deleteConfirmTitle": "Delete connection?",
			"deleteConfirmDescription": "This connection isn't attached to any node. Delete it?",
			"delete": "Delete",
			"cancel": "Cancel"
		},
		"handles": {
			"text": "Text",
			"image": "Image",
			"image2": "Image 2",
			"images": "Images",
			"end_image": "End image",
			"video": "Video",
			"videos": "Videos",
			"audio": "Audio",
			"speech": "Speech",
			"ref_audio": "Reference audio",
			"ref_text": "Reference text",
			"document": "Document",
			"byFeature": { "image-image-gen-video": {
				"image": "First frame",
				"end_image": "Last frame"
			} }
		},
		"modeSwitch": {
			"createMode": "Create Mode",
			"executeMode": "Execute Mode",
			"appMode": "App Mode",
			"upgradeRequired": "Upgrade to unlock Execute Mode"
		},
		"appView": {
			"inputsTitle": "Inputs",
			"outputsTitle": "Results",
			"run": "Run",
			"cancel": "Stop",
			"stepProgress": "Step {current} of {total}",
			"completed": "Done",
			"failed": "Something went wrong",
			"emptyWorkflow": "The canvas is empty. Build a workflow first.",
			"invalidWorkflow": "This workflow has a cycle and can't run.",
			"nothingToRun": "This workflow has no runnable steps yet.",
			"noOutputsYet": "Results will show up here after a run.",
			"dropFilesHere": "Drop files here or click to upload",
			"copyText": "Copy",
			"download": "Download",
			"goToCreateMode": "Go to Create Mode",
			"textPlaceholder": "Type here…",
			"fieldType": {
				"text": "Text",
				"image": "Image",
				"audio": "Audio",
				"video": "Video",
				"model": "3D model",
				"file": "File"
			}
		},
		"smartIsland": {
			"category": {
				"digitalHuman": "Digital Human",
				"artDesign": "Art & Design",
				"videoAd": "Video & Ads",
				"docProcessing": "Doc Processing",
				"knowledgeLearning": "Knowledge Learning",
				"musicMV": "Music/MV"
			},
			"enterWorkflowName": "Enter workflow name",
			"saveSuccess": "Saved successfully!",
			"saveFailed": "Save failed, please try again",
			"generateVideo": "Generate Video",
			"generateAudio": "Generate Audio",
			"generateText": "Generate Text",
			"generateImage": "Generate Image",
			"generateMusic": "Generate Music",
			"generate3D": "Generate 3D",
			"detectPose": "Pose",
			"segmentBody": "Segment Body",
			"estimateNormal": "Normals",
			"extractForeground": "Matting",
			"captureMotion": "Motion Capture",
			"firstLastFrameVideo": "First/Last Frame Video",
			"confirmExecute": "Confirm Execute",
			"deployModalWorkers": "Deploy Modal Workers",
			"deployModalRunning": "Deploying Modal workers...",
			"deployModalSuccess": "Modal workers deployed",
			"deployModalFailed": "Modal deploy failed",
			"deployModalUnsupported": "This action is only available in the desktop app",
			"saveAndExecute": "Save and Execute",
			"cancel": "Cancel",
			"executeConfirmTitle": "Confirm Execute Workflow",
			"executeConfirmDescSaved": "The workflow will be saved with the latest state before execution. Confirm execution?",
			"executeConfirmDescNew": "Please save the workflow before execution. Generated materials will be associated with this workflow for future reference and management.",
			"executeWorkflow": "Execute Workflow",
			"saveWorkflow": "Save Workflow",
			"executing": "Executing...",
			"saving": "Saving...",
			"estimatedCost": "Estimated Cost:",
			"estimatedTime": "Estimated Time:",
			"workflowName": "Workflow Name",
			"workflowNamePlaceholder": "Enter workflow name",
			"workflowDescription": "Workflow Description",
			"workflowDescPlaceholder": "Enter workflow description (optional)",
			"tooltip3D": "3D",
			"tooltipDocument": "Document",
			"tooltipImage": "Image",
			"tooltipText": "Text",
			"tooltipVideo": "Video",
			"tooltipAudio": "Audio",
			"tooltipLink": "Link",
			"split": "Split",
			"splitText": "Smart Split",
			"speechRecognize": "Speech Recognition",
			"separateAudio": "Separate Vocals",
			"separateSpeaker": "Speaker Separation",
			"separateSound": "Extract Sound",
			"denoise": "Denoise",
			"convertVoice": "Voice Replacement",
			"filter": "Filter",
			"arrange": "Arrange",
			"concat": "Concat",
			"describeReverse": "Describe",
			"upscale": "HD Upscale",
			"removeSubtitle": "Remove Subtitle",
			"removeWatermark": "Remove Watermark",
			"extractAudioTrack": "Extract audio track",
			"removeVideoAudio": "Remove audio",
			"slice": "Slice",
			"firstFrame": "First Frame",
			"lastFrame": "Last Frame",
			"editImage": "Edit Image",
			"editVideo": "Edit Video",
			"parseDocument": "Parse Document",
			"extractContent": "Extract Content",
			"mergeGroup": "Merge Group",
			"imageFusion": "Image Fusion",
			"imagesGenVideo": "Reference to Video",
			"refsGenVideo": "Omni Reference Video",
			"rewriteText": "Rewrite Text",
			"textToSpeech": "Text to Speech",
			"textToSpeechClone": "Generate Speech (Clone)",
			"textToSpeechPreset": "Generate Speech (Preset)",
			"textToSpeechInstruct": "Generate Speech (Describe)",
			"motionControl": "Motion Control",
			"videoTransfer": "Video Transfer",
			"characterReplace": "Character Replace",
			"merge": "Merge",
			"lipSync": "Lip Sync",
			"lipDub": "Lip Dub",
			"cloneVoice": "Clone Voice",
			"speechGenVideo": "Audio Generate Video",
			"repaintMusic": "Repaint",
			"coverMusic": "Cover",
			"extractStem": "Extract Stem",
			"addTrack": "Add Track",
			"completeMusic": "Complete",
			"musicBrief": "Music Brief"
		},
		"toast": {
			"executing": "Executing...",
			"completed": "Completed",
			"cancelled": "Cancelled",
			"cancelling": "Cancelling...",
			"failed": "Failed",
			"nodes": "Nodes",
			"cancelTask": "Cancel Task",
			"totalDuration": "Total Duration",
			"taskCompleted": "Task completed",
			"taskFailed": "Task failed",
			"taskFailedWithMsg": "Task failed: {error}",
			"level": "Level {level}"
		},
		"dialog": {
			"myWorkflows": "My Workflows",
			"noWorkflows": "No workflows",
			"savedWorkflowsHint": "Saved workflows will appear here",
			"noMore": "No more workflows",
			"loadSuccess": "Loaded successfully!",
			"loadFailed": "Load failed, please try again",
			"open": "Open"
		},
		"menu": {
			"save": "Save",
			"saveAs": "Save As",
			"clear": "Clear",
			"confirmClear": "Are you sure you want to clear the current workflow? This action cannot be undone.",
			"cleared": "Workflow cleared",
			"saveAsNew": "Save as new workflow",
			"saveWorkflow": "Save Workflow",
			"saveNew": "Save as new",
			"name": "Name",
			"enterName": "Enter workflow name",
			"descOptional": "Description (Optional)",
			"enterDesc": "Enter workflow description",
			"saving": "Saving...",
			"update": "Update",
			"cancel": "Cancel",
			"saveSuccess": "Saved successfully!",
			"saveFailed": "Save failed, please try again",
			"exportJson": "Export JSON",
			"exportWorkflow": "Export Workflow",
			"exportExecutable": "Export Executable",
			"importJson": "Import JSON",
			"importJsonInvalid": "Could not parse: invalid JSON or structure.",
			"importJsonNoCanvas": "This file has no canvas data (needs originalFlow, flow, or nodes/edges at the root).",
			"importJsonSuccess": "Loaded workflow ({nodes} nodes, {edges} edges)",
			"importJsonReadFailed": "Failed to read file",
			"exportJsonSuccess": "Download started",
			"exportJsonFailed": "Export failed, please try again"
		},
		"nodes": {
			"base": {
				"moreActions": "More options",
				"execute": "Execute",
				"cancelExecution": "Cancel execution",
				"removeComment": "Remove comment",
				"commentPlaceholder": "Add a comment for users...",
				"clickToAddComment": "Click to add a comment...",
				"addComment": "Add comment",
				"delete": "Delete",
				"deleteConfirmTitle": "Confirm Deletion",
				"deleteConfirmDescription": "This node has {count} downstream node(s). Please choose how to delete.",
				"deleteNodeOnly": "Delete this node only",
				"deleteNodeAndChildren": "Delete this node and all downstream nodes ({count})",
				"deleteChildrenOnly": "Delete downstream nodes only ({count})",
				"run": "Run",
				"preview": "Preview",
				"lock": "Lock Node",
				"unlock": "Unlock Node",
				"startCombo": "Start Combo",
				"addToCombo": "Add to Combo",
				"removeFromCombo": "Remove from Combo",
				"edit": "Edit",
				"cancel": "Cancel",
				"confirm": "Confirm",
				"fullscreenEdit": "Fullscreen Edit",
				"startVoice": "Start Voice Input",
				"stopVoice": "Stop Voice Input",
				"editText": "Edit Text",
				"select": "Select",
				"selected": "Selected",
				"modalNotDeployedTitle": "Modal app not deployed",
				"modalNotDeployedDescription": "Feature \"{feature}\" requires Modal app \"{app}\". It is not deployed yet. Deploy now?",
				"modalDeployHint": "1) Set MODAL_TOKEN_ID / MODAL_TOKEN_SECRET. 2) Ensure plugins/ is present; the server scans plugin annotations in memory. 3) Ensure `python -m modal` works (e.g. pip install modal), then use one-click deploy to run modal deploy on each plugin deploy.py from that registry.",
				"modalDeployDismiss": "Cancel",
				"modalDeployAction": "Download & deploy",
				"modalDeploying": "Deploying Modal workers…",
				"modalDeployDone": "Deployment completed",
				"modalDeployError": "Deployment failed",
				"modalDeployingAction": "Deploying…",
				"modalDeployProgressHint": "Deployed {done}/{total}",
				"pluginImplementationTitle": "Implementation",
				"pluginModelTitle": "Model",
				"pluginParamsTitle": "Advanced",
				"pluginParamsReset": "Reset",
				"pluginParamDefault": "default {value}",
				"pluginImplementationLoading": "Loading plugin implementations…",
				"pluginImplementationEmpty": "No plugin implementations were scanned for this capability. Add Modal or API plugins under the repo's plugins/ directory (see README), then reload this page.",
				"pluginRegistryLoadError": "Could not load the plugin registry: {message}",
				"pluginSelectPlaceholder": "Select a repo…",
				"missingImplTitle": "Missing Implementation",
				"missingImplDescription": "No plugin is installed for this node (or none is selected). Install one from the plugin manager, or pick an installed plugin, before running."
			},
			"common": {
				"enterInstructions": "Enter instructions...",
				"manualInput": "Input",
				"library": "Library",
				"addText": "Add Text",
				"enterText": "Enter text content...",
				"descOptional": "Description (Optional)",
				"fromUpstreamText": "From upstream text node",
				"videoDesc": "Video description",
				"aspectRatio": "Aspect Ratio",
				"resolution": "Resolution",
				"duration": "Duration",
				"currentSize": "Current Size:",
				"currentDuration": "Current Duration:",
				"upscaleTierLabel": "Output tier (long edge)",
				"upscaleTierHint": "Aspect ratio follows input; only the long edge is scaled.",
				"upscaleTier1k": "1K · ~1920",
				"upscaleTier2k": "2K · ~2560",
				"upscaleTier4k": "4K · ~3840",
				"seconds": "s",
				"minute": "min",
				"thinkingProcess": "Thinking Process",
				"performanceMode": "Performance Mode",
				"ecoMode": "💰 Eco Mode",
				"proMode": "⚡ Pro Mode",
				"modelSelect": "Model",
				"models": {
					"imageEdit": "FLUX2 Klein image edit",
					"imageGen": "Z-Image text-to-image",
					"imageFusion": "FLUX2 Klein image fusion",
					"imageGenVideo": "LTX2 image-to-video",
					"features": {
						"gen-text": "Default (OpenRouter)",
						"gen-text-gemini": "Gemini",
						"gen-text-openai": "OpenAI",
						"split-text": "Smart split",
						"combine-text": "Merge texts",
						"combine-text-openai": "Merge texts (OpenAI)",
						"combine-text-gemini": "Merge texts (Gemini)",
						"image-fusion": "FLUX2 Klein fusion",
						"image-gen-text": "Image captioning",
						"video-gen-text": "Video captioning",
						"transcribe": "Speech to text",
						"transcribe-timestamp": "Speech to text (with timestamps)",
						"concat-videos": "Concatenate videos",
						"extract-audio": "Extract audio track",
						"remove-video-audio": "Remove audio (silent video)",
						"merge-video-audio": "Mux audio + video",
						"get-first-frame": "First frame",
						"get-last-frame": "Last frame",
						"parse-document": "Document to text",
						"split-video": "Split by shots",
						"link": "Fetch from URL",
						"gen-video": "Text to video",
						"image-gen": "Z-Image text-to-image",
						"gen-music": "ACE-Step music",
						"text-gen-speech-preset": "TTS (preset style)",
						"image-gen-video": "Image to video",
						"image-edit": "FLUX2 Klein edit",
						"image-upscale": "Image upscale",
						"video-upscale": "Video upscale",
						"image-describe": "Image understanding",
						"video-describe": "Video understanding",
						"audio-describe": "Audio understanding",
						"music-repaint": "Music repaint",
						"music-cover": "Music cover",
						"music-extract": "Stem extraction",
						"music-lego": "Add track",
						"music-complete": "Arrangement completion",
						"music-brief": "Music brief",
						"audio-image-gen-video": "Speech+image to video",
						"speech-text-gen-video": "Speech+text to video",
						"video-image-gen-video-mix": "Video+image mix",
						"video-image-gen-video-move": "Video+image move",
						"image-image-gen-video": "Image+image to video",
						"text-gen-video": "Text to video",
						"image-gen-model": "Image to 3D",
						"speech-video-gen-video": "Video lip dub (text+video)",
						"audio-video-lip-sync": "Audio+video lip sync",
						"text-gen-speech-clone": "Voice clone TTS",
						"text-gen-speech-instruct": "Instruct TTS",
						"video-image-move-animal": "Video+image move (animal)",
						"video-image-move": "Video+image move (human)",
						"video-image-move-muti": "Video+image move (multi)",
						"video-image-move-animal-muti": "Video+image move (animal, multi)",
						"wan-animate-mix": "WAN animate mix",
						"subtitle-remove": "Subtitle removal",
						"remove-watermark": "Watermark removal",
						"denoise-audio": "Audio denoise",
						"separate-audio-track": "Vocal / accompaniment",
						"separate-speaker": "Speaker diarization",
						"convert-voice": "Voice replacement",
						"drop-video": "Filter clips",
						"arrange-group": "Arrange batch"
					}
				},
				"voice": "Voice",
				"selectVoice": "Select Voice",
				"previewVoice": "Preview Voice",
				"uploadVoice": "Upload Voice",
				"recordVoice": "Record Voice",
				"voiceClone": "Voice Clone",
				"voicePreset": "Preset Style",
				"voiceDescribe": "Custom Description",
				"gender": "Gender",
				"selectGender": "Select Gender (Optional)",
				"emotion": "Emotion",
				"style": "Style",
				"selectEmotion": "Select Emotion (Optional)",
				"selectStyle": "Select Style (Optional)",
				"voiceDescription": "Voice Description",
				"voiceDescriptionPlaceholder": "Enter voice style description, e.g.: A gentle and sweet female voice, speaking slowly...",
				"referenceAudio": "Reference Audio",
				"noReferenceAudio": "Upload or record a reference clip (≥3 s)",
				"referenceText": "Reference Text",
				"referenceTextPlaceholder": "Optional: transcript of the reference audio — improves clone quality",
				"referenceTextCloneHint": "Leave empty for fast x-vector clone; paste the reference transcript for higher-quality ICL clone",
				"language": "Language",
				"inputText": "Input Text",
				"voiceOptions": {
					"female": "Female Voice",
					"male": "Male Voice"
				}
			},
			"titles": {
				"imageGenVideo": "Image to Video",
				"textGenVideo": "Text to Video",
				"addText": "Add Text",
				"textGenText": "Text Generation",
				"combineText": "Combine Texts",
				"textGenImage": "Text to Image",
				"textGenSpeechClone": "Generate Speech (Clone)",
				"textGenSpeechCloneCompose": "Voice Clone (Text + Audio)",
				"imageGenVideoCompose": "Image to Video (Text + Image)",
				"textGenSpeechPreset": "Generate Speech (Preset)",
				"textGenSpeechInstruct": "Generate Speech (Describe)",
				"imageFusion": "Image Fusion",
				"videoUpscale": "Video Upscale",
				"videoGenText": "Describe Video",
				"speechRecognize": "Speech Recognition",
				"textGenMusic": "Text to Music",
				"speechGenVideo": "Audio to Video",
				"convertVoice": "Voice Replacement",
				"denoiseAudio": "Audio Denoise",
				"fileGenText": "Document Parser",
				"linkGenText": "Link Extractor",
				"removeSubtitle": "Remove Subtitle",
				"removeWatermark": "Remove Watermark",
				"separateAudio": "Split Audio & Video",
				"imageGenText": "Image Description",
				"audioDescribe": "Audio Description",
				"imageGenImage": "Image Edit",
				"videoEdit": "Video Edit",
				"imageGenModel": "Image to 3D Model",
				"imagePose": "Pose Detection",
				"imageBodySeg": "Body-Part Segmentation",
				"imageNormal": "Surface Normal",
				"imageMatting": "Human Matting",
				"videoGenModel": "Video Motion Capture",
				"imageUpscale": "HD Upscale",
				"separateTrack": "Vocal Extract",
				"separateSpeaker": "Speaker Separation",
				"separateSound": "Separate Sound",
				"speechImageGenVideo": "Speech+Image to Video",
				"concatVideo": "Concat Video",
				"mergeVideoAudio": "Merge Video Audio",
				"audioVideoLipSync": "Video Lip Sync",
				"speechTextGenVideo": "Speech+Text to Video",
				"speechVideoGenVideo": "Video Lip Dub",
				"imageImageGenVideo": "First/Last Frame Video",
				"imagesGenVideo": "Images to Video",
				"refsGenVideo": "Omni Reference to Video",
				"textAudioGenSpeech": "Text+Audio to Speech",
				"videoImageMix": "Video+Image Mix",
				"videoImageMove": "Video+Image Move",
				"splitVideo": "Split Video",
				"splitText": "Smart Split",
				"getFirstFrame": "First Frame",
				"getLastFrame": "Last Frame",
				"extractAudioTrack": "Extract audio track",
				"removeVideoAudio": "Remove audio (silent video)",
				"musicRepaint": "Repaint Music",
				"musicCover": "Music Cover",
				"musicExtract": "Extract Stem",
				"musicLego": "Add Track",
				"musicComplete": "Complete Arrangement",
				"musicBrief": "Music Brief"
			},
			"options": {
				"portrait": "Portrait",
				"landscape": "Landscape",
				"square": "Square",
				"standard": "Standard",
				"verticalStandard": "Vertical Standard",
				"human": "Human",
				"animal": "Animal",
				"single": "Single",
				"multi": "Multi"
			},
			"actions": {
				"generateVideo": "Generate Video",
				"generateImage": "Generate Image",
				"generateSpeech": "Generate Speech",
				"startFusion": "Start Fusion",
				"convertText": "Convert to Text",
				"startUpscale": "Start Upscale",
				"describeVideo": "Describe Video",
				"recognizeSpeech": "Recognize Speech",
				"generateMusic": "Generate Music",
				"generateAudio": "Generate Audio",
				"startReplace": "Start Replace",
				"startDenoise": "Start Denoise",
				"parseDocument": "Parse Document",
				"extractContent": "Extract Content",
				"removeSubtitle": "Remove Subtitle",
				"removeWatermark": "Remove Watermark",
				"compressVideo": "Compress Video",
				"extractAudio": "Start Split",
				"describeImage": "Describe Image",
				"describeAudio": "Describe Audio",
				"editImage": "Edit Image",
				"editVideo": "Edit Video",
				"generate3DModel": "Generate 3D Model",
				"detectPose": "Detect Pose",
				"segmentBody": "Segment Body Parts",
				"estimateNormal": "Estimate Normals",
				"extractForeground": "Extract Foreground",
				"captureMotion": "Capture Motion",
				"imageUpscale": "HD Upscale",
				"extractVocals": "Extract Vocals",
				"startSeparation": "Start Separation",
				"concatVideo": "Concat Video",
				"startMerge": "Start Merge",
				"lipDub": "Lip Dub",
				"motionTrackGenerate": "Generate (motion track)",
				"splitVideo": "Split Video",
				"splitText": "Smart Split",
				"getFirstFrame": "Get First Frame",
				"getLastFrame": "Get Last Frame",
				"extractAudioTrack": "Extract audio",
				"removeVideoAudio": "Remove audio",
				"repaintMusic": "Repaint",
				"coverMusic": "Generate Cover",
				"extractStem": "Extract",
				"separateSound": "Separate",
				"addTrack": "Generate Track",
				"completeMusic": "Complete",
				"musicBrief": "Generate Brief"
			},
			"params": {
				"prompt": "Prompt",
				"model": "Model",
				"modelDefault": "Default (OpenRouter free)",
				"geminiModel": "Gemini model",
				"openaiModel": "OpenAI model",
				"style": "Style",
				"sampler": "Sampler",
				"seed": "Seed",
				"steps": "Steps",
				"cfg": "CFG Scale"
			},
			"emotions": {
				"none": "None",
				"happy": "Happy",
				"angry": "Angry",
				"sad": "Sad",
				"fear": "Fear",
				"surprised": "Surprised",
				"confusion": "Confusion",
				"empathy": "Empathy",
				"embarrass": "Embarrassed",
				"excited": "Excited",
				"depressed": "Depressed",
				"admiration": "Admiration",
				"coldness": "Coldness"
			},
			"genders": {
				"none": "None",
				"male": "Male",
				"female": "Female"
			},
			"styles": {
				"none": "None",
				"serious": "Serious",
				"arrogant": "Arrogant",
				"child": "Child",
				"older": "Older",
				"girl": "Girl",
				"pure": "Pure",
				"sister": "Mature Female",
				"sweet": "Sweet",
				"exaggerated": "Exaggerated",
				"ethereal": "Ethereal",
				"whisper": "Whisper",
				"generous": "Generous",
				"recite": "Recite",
				"act_coy": "Coquettish",
				"warm": "Warm",
				"shy": "Shy",
				"comfort": "Comforting",
				"authority": "Authoritative",
				"chat": "Chat",
				"radio": "Radio",
				"soulful": "Soulful",
				"gentle": "Gentle",
				"story": "Storytelling",
				"vivid": "Vivid",
				"program": "Host",
				"news": "News",
				"advertising": "Advertising",
				"roar": "Roar",
				"murmur": "Murmur",
				"shout": "Shout",
				"deeply": "Deep",
				"loudly": "Loud"
			},
			"recorder": {
				"title": "Record Voice",
				"desc": "Record an audio clip as a reference for voice synthesis",
				"start": "Start Recording",
				"stop": "Stop Recording",
				"preview": "Preview",
				"retry": "Retry",
				"uploading": "Uploading...",
				"confirm": "Confirm",
				"cancel": "Cancel",
				"recording": "Recording...",
				"recorded": "Recording Complete",
				"micError": "Cannot access microphone, please check permissions",
				"uploadError": "Upload failed, please try again",
				"uploadFailed": "Upload Failed"
			},
			"imageFusion": {
				"loading": "Loading...",
				"imageReference": "Image Reference",
				"imageReferenceHint": "Click image to insert reference in prompt",
				"fusionPrompt": "Fusion Prompt",
				"fusionPromptPlaceholder": "Enter fusion instructions, e.g., keep style of Image 1, fuse features of all images, highlight colors of Image 2, combine composition of Image 3...",
				"imageLabel": "Image"
			},
			"refsGenVideo": {
				"videoReference": "Video Reference",
				"videoLabel": "Video",
				"audioReference": "Audio Reference",
				"audioLabel": "Audio",
				"referenceHint": "Up to 9 images, 3 videos and 3 audio clips (12 files total); audio must come with an image or video. Click a thumbnail to insert its reference in the prompt.",
				"promptPlaceholder": "Describe the video, e.g., the character from Image 1 speaks with the voice of Audio 1, continuing the scene of Video 1..."
			},
			"video": { "fileCount": "Files to process:" },
			"speechRecognize": { "withTimestamp": "With Timestamp" },
			"music": {
				"handleStyle": "Style",
				"handleLyric": "Lyrics",
				"referenceAudio": "Reference audio",
				"inputLyrics": "Input Lyrics",
				"audioDuration": "Audio Duration",
				"currentDuration": "Current Duration:",
				"seconds": "s",
				"15s": "15s",
				"30s": "30s",
				"1min": "1 min",
				"2min": "2 min",
				"styleSettings": "Music Style",
				"voiceGender": "Voice",
				"genders": {
					"male": "Male",
					"female": "Female"
				},
				"timbre": "Timbre",
				"timbres": {
					"bright": "Bright",
					"dark": "Dark",
					"warm": "Warm",
					"soft": "Soft",
					"rock": "Rock",
					"vocal": "Vocal"
				},
				"genre": "Genre",
				"genres": {
					"pop": "Pop",
					"rock": "Rock",
					"electronic": "Electronic",
					"hiphop": "Hip Hop",
					"jazz": "Jazz",
					"blues": "Blues",
					"classical": "Classical",
					"folk": "Folk",
					"country": "Country",
					"rnb": "R&B",
					"kpop": "K-Pop",
					"reggae": "Reggae",
					"soul": "Soul",
					"dance": "Dance",
					"rap": "Rap",
					"classicRock": "Classic Rock",
					"hardRock": "Hard Rock",
					"rockabilly": "Rockabilly",
					"dancepop": "Dance Pop",
					"house": "House",
					"deephouse": "Deep House",
					"experimental": "Experimental",
					"experimentalPop": "Experimental Pop",
					"popPunk": "Pop Punk",
					"rockAndRoll": "Rock and Roll",
					"popRock": "Pop Rock"
				},
				"mood": "Mood",
				"moods": {
					"happy": "Happy",
					"sad": "Sad",
					"romantic": "Romantic",
					"melancholic": "Melancholic",
					"uplifting": "Uplifting",
					"intense": "Intense",
					"emotional": "Emotional",
					"angry": "Angry"
				},
				"instrumentAndRhythm": "Instrument & Rhythm",
				"instrument": "Instrument",
				"selectedCount": "{count} selected",
				"instruments": {
					"piano": "Piano",
					"guitar": "Guitar",
					"acousticGuitar": "Acoustic Guitar",
					"electricGuitar": "Electric Guitar",
					"drums": "Drums",
					"synthesizer": "Synthesizer",
					"violin": "Violin",
					"saxophone": "Saxophone",
					"bass": "Bass",
					"beats": "Beats",
					"strings": "Strings",
					"cello": "Cello",
					"trumpet": "Trumpet",
					"harmonica": "Harmonica",
					"banjo": "Banjo",
					"fiddle": "Fiddle",
					"brass": "Brass",
					"doubleBass": "Double Bass"
				},
				"bpm": "BPM",
				"slow": "Slow",
				"moderate": "Moderate",
				"fast": "Fast",
				"songTitle": "Song Title",
				"songTitlePlaceholder": "Enter song title (optional)",
				"noTagsSelected": "No tags selected",
				"tagsPlaceholder": "e.g. female, pop, bright, piano, happy, 120bpm",
				"tagsHint": "Enter music style tags separated by commas, e.g. gender, genre, timbre, instruments, mood, BPM",
				"fromUpstreamReadonly": "From upstream (read-only)",
				"lyricsPlaceholder": "Enter lyrics when the lyric input is not connected",
				"language": "Language",
				"keyscale": "Key Scale",
				"auto": "Auto"
			},
			"musicTasks": {
				"promptLabel": "Caption",
				"promptPlaceholder": "Describe the music style, mood, instruments...",
				"lyricsLabel": "Lyrics",
				"lyricsPlaceholder": "Lyrics (optional)...",
				"startTime": "Start (s)",
				"endTime": "End (s)",
				"strength": "Strength",
				"track": "Track",
				"tracks": "Tracks to add",
				"instrumental": "Instrumental",
				"ideaPlaceholder": "One sentence describing the song you want...",
				"sourceAudio": "Source Audio",
				"refAudio": "Reference Audio",
				"trackOptions": {
					"vocals": "Vocals",
					"backing_vocals": "Backing Vocals",
					"drums": "Drums",
					"percussion": "Percussion",
					"bass": "Bass",
					"guitar": "Guitar",
					"keyboard": "Keyboard",
					"synth": "Synth",
					"strings": "Strings",
					"brass": "Brass",
					"woodwinds": "Woodwinds",
					"fx": "FX"
				}
			},
			"speechGenVideo": {
				"loading": "Loading...",
				"inputContent": "Input Content",
				"audio": "Audio",
				"connectAudioHint": "Connect an audio node or upload an audio file",
				"audioPromptLabel": "Audio Prompt",
				"audioPromptPlaceholder": "Enter audio dubbing prompt"
			},
			"convertVoice": {
				"voiceLabel": "Voice:",
				"selectVoice": "Select Voice",
				"previewVoice": "Preview Voice",
				"uploadVoice": "Upload Voice",
				"recordVoice": "Record Voice",
				"recordAudio": "Record Audio",
				"recordHint": "You can record audio here.",
				"recordNeedImpl": "Audio recording feature needs implementation",
				"done": "Done"
			},
			"removeWatermark": { "hint": "Automatically detect and remove Sora video watermarks" },
			"videoGenText": {
				"promptLabel": "Custom Prompt",
				"promptPlaceholder": "Leave empty for default prompt, or enter custom instructions, e.g.: Only describe the dialogues in the video..."
			},
			"imageGenText": {
				"promptLabel": "Custom Prompt",
				"promptPlaceholder": "Leave empty for default prompt, or enter custom instructions, e.g.: Describe the main subject, style, or on-image text..."
			},
			"audioDescribe": {
				"promptLabel": "Custom Prompt",
				"promptPlaceholder": "Leave empty for default prompt, or enter custom instructions, e.g.: Describe the genre, mood, instruments, or vocals..."
			},
			"textGenText": { "instructions": "Instructions" },
			"lipDub": {
				"targetDialogue": "Target dialogue (prompt)",
				"targetDialoguePlaceholder": "Full prompt with character description and target-language lines (model does not translate for you)",
				"promptHint": "Output resolution follows the reference video. Use a full prompt (character + target-language lines; no auto-translation). Match original clip length; single speaker only."
			},
			"lipSync": {
				"scenePrompt": "Scene description (optional)",
				"scenePromptPlaceholder": "Describe the shot and speaker. Lip sync follows the connected audio — do not paste full dialogue here.",
				"audioDrivenHint": "Mouth motion and audio come from the audio input. Keep audio duration close to the reference video. Default output 720p."
			},
			"motionTrack": {
				"connectHint": "Connect a first-frame image, a reference video (its motion is auto-tracked and transferred), and a text prompt (appearance/style only).",
				"controlVideo": "Reference video",
				"promptPlaceholder": "Describe appearance and style — motion is extracted from the reference video.",
				"promptHint": "Describe appearance/style only; motion is auto-extracted from the reference video. Duration slider caps the reference clip length.",
				"missing": "Still needed: {items}"
			},
			"imageEdit": {
				"performanceMode": "Performance Mode",
				"ecoMode": "💰 Eco (FLUX.2 Klein 9B)",
				"proMode": "⚡ Pro Mode",
				"editInstruction": "Edit Instruction",
				"fromUpstream": "(from upstream node)",
				"editPlaceholder": "Enter image edit instructions, e.g.: increase contrast, adjust color temperature to warm, enhance details"
			},
			"videoEdit": {
				"editInstruction": "Edit Instruction",
				"fromUpstream": "(from upstream node)",
				"editPlaceholder": "Enter video edit instructions, e.g.: remove subtitles, apply a watercolor style, change the sky to sunset",
				"connectTextVideo": "Connect a text node and a video node"
			},
			"compose": {
				"loading": "Loading...",
				"mediaFiles": "Media Files",
				"image": "Image",
				"audio": "Audio",
				"video": "Video",
				"text": "Text",
				"noText": "No Text",
				"inputContent": "Input Content",
				"videoFiles": "Video Files",
				"connectImageAudio": "Please connect an image and an audio file",
				"connectVideo": "Please connect at least one video file",
				"connectImageVideoAudio": "Please connect an image, a video and an audio file",
				"connectTextAudio": "Please connect text content and an audio file",
				"connectTextReferenceAudio": "Connect a text node and an audio node (reference voice)",
				"connectTextImage": "Connect a text node and an image node",
				"connectTextVideoNode": "Connect a video node and a text node (target dialogue goes in the prompt)",
				"referenceAudio": "Reference audio",
				"referenceTextOptional": "Reference transcript (optional, improves clone quality)",
				"connectVideoAudio": "Please connect a video and an audio file",
				"generatePromptLabel": "Generate Prompt",
				"generatePromptPlaceholder": "Enter video generation prompt describing content, style, action, etc. (optional)",
				"inputText": "Input Text",
				"inputTextPlaceholder": "Enter text content",
				"describeVideo": "Describe the video you want to generate...",
				"firstFrame": "First Frame",
				"lastFrame": "Last Frame",
				"connectTwoImages": "Please connect two images (first and last frame)",
				"inputData": "Input Data",
				"connectTextAudioNode": "Please connect text and audio nodes",
				"emotion": "Emotion:",
				"style": "Style:",
				"selectEmotion": "Select emotion style (optional)",
				"selectStyle": "Select voice style (optional)",
				"lipSync": "Lip Sync",
				"connectImageVideo": "Please connect an image and a video file",
				"startSlice": "Start Slice"
			},
			"modal": {
				"audioPreview": "Audio Preview",
				"audio": "Audio",
				"audios": "Audios ({count})",
				"loading": "Loading...",
				"unavailable": "Content unavailable",
				"more": "More",
				"moreOptions": "More options",
				"actions": "Actions",
				"textPreview": "Text Preview",
				"text": "Text",
				"texts": "Texts ({count})",
				"imagePreview": "Image Preview",
				"image": "Image",
				"images": "Images ({count})",
				"videoPreview": "Video Preview",
				"video": "Video",
				"videos": "Videos ({count})",
				"file": "File",
				"files": "Files ({count})",
				"link": "Link",
				"links": "Links ({count})",
				"fullScreenPreview": "Full screen preview",
				"fullScreenWaterfall": "Full screen waterfall preview",
				"model3DPreview": "3D Model Preview",
				"model3D": "3D Model",
				"models3D": "3D Models ({count})",
				"resetView": "Reset view",
				"playAnimation": "Play animation",
				"pauseAnimation": "Pause animation",
				"downloadModel": "Download model",
				"loading3DModel": "Loading 3D model...",
				"errorLoadingModel": "Error loading model",
				"dragToRotate": "Left Click + Drag to Rotate | Scroll to Zoom | Click Reset to Center",
				"noModelLoaded": "No 3D model loaded",
				"loadingPreview": "Loading preview...",
				"unsupportedFormat": "Unsupported Format",
				"supportedFormats": "Supported",
				"download": "Download",
				"textEditor": "Text Editor",
				"enterText": "Enter text content..."
			},
			"add": {
				"addAudio": "Add Audio",
				"addImage": "Add Image",
				"addVideo": "Add Video",
				"addText": "Add Text",
				"addFile": "Add File",
				"addModel": "Add 3D Model",
				"addLink": "Add Link",
				"record": "Record",
				"recordVideo": "Record Video",
				"upload": "Upload",
				"library": "Library",
				"recorded": "Recorded",
				"uploading": "Uploading",
				"confirmAndUpload": "Confirm & Upload",
				"dropToUpload": "Drop files to upload",
				"dragOrClick": "Drag files here or click to upload",
				"supportedAudioFormats": "Supports MP3, WAV, OGG, M4A",
				"supportedImageFormats": "Supports common image formats",
				"supportedVideoFormats": "Supports MP4, MOV, WEBM",
				"browseFiles": "Browse Files",
				"canvas": "Canvas",
				"camera": "Camera",
				"openCanvas": "Open Canvas",
				"canvasDrawing": "Canvas Drawing",
				"canvasDrawingHint": "Draw on the canvas below, then click finish to save",
				"clearCanvas": "Clear Canvas",
				"finishAndSave": "Finish & Save",
				"clickToStartCamera": "Click to Start Camera",
				"takePhoto": "Take Photo",
				"closeCamera": "Close Camera",
				"cameraError": "Cannot access camera",
				"cameraErrorHint": "Please ensure: 1. Using HTTPS or localhost 2. Camera permission allowed 3. Camera not in use by other apps",
				"inputText": "Input Text",
				"textPlaceholder": "Please enter text content...",
				"generate": "Confirm",
				"linkPlaceholder": "Please enter a link",
				"extractContent": "Extract Content",
				"cancel": "Cancel",
				"startRecordingVideo": "Start Recording Video",
				"addDocument": "Add Document",
				"supportedDocFormats": "Supports multiple document formats",
				"dragModel": "Drag 3D model files here or click to upload",
				"supportedModelFormats": "Supported formats",
				"modelHint": "Tip: Supports the following 3D file formats:"
			},
			"batch": {
				"arrangeGroup": "Arrange Groups",
				"startArrange": "Start Arrange",
				"describeRequirements": "Describe your requirements...",
				"groupCount": "Group Count",
				"allowDuplicate": "Allow Duplicates",
				"videoFilter": "Video Filter",
				"startFilter": "Start Filter"
			},
			"voice": {
				"timbre": "Voice:",
				"selectTimbre": "Select voice",
				"previewTimbre": "Preview current voice",
				"uploadTimbre": "Upload voice",
				"recordTimbre": "Record voice",
				"emotion": "Emotion:",
				"replaceTimbre": "Replace voice"
			},
			"separateSound": {
				"textLabel": "Sound to isolate",
				"textPlaceholder": "e.g. dog barking, piano, sirens in the background"
			}
		}
	},
	Upload: {
		"failed": "Upload failed, please retry later",
		"fileFailed": "Failed to upload \"{name}\": {reason}",
		"partialFailed": "Some or all files failed to upload",
		"maxFilesExceeded": "Maximum {max} files allowed",
		"fileTooLarge": "File \"{name}\" is too large (max {max}MB)",
		"fileTypeNotAccepted": "File \"{name}\" type not accepted",
		"readImageFailed": "Failed to read image information",
		"readVideoFailed": "Failed to read video information"
	},
	LibInput: {
		"deleteSuccess": "Material deleted",
		"deleteFailed": "Failed to delete",
		"emptyText": "Empty text",
		"loadFailed": "Failed to load materials",
		"added": "Material added",
		"retry": "Retry",
		"emptyMaterial": "No {type} materials yet",
		"typeImage": "image",
		"typeVideo": "video",
		"typeAudio": "audio",
		"typeText": "text",
		"typeFile": "file",
		"typeModel": "model",
		"favoriteHint": "Click the ❤️ icon on a node to favorite the material",
		"portfolio": "Portfolio"
	},
	Recorder: {
		"audioStart": "Start recording",
		"audioCancel": "Cancel",
		"audioCancelTooltip": "Cancel recording",
		"audioFinish": "Finish",
		"audioFinishTooltip": "Finish recording",
		"audioPermissionError": "Cannot access microphone. Please grant permission.",
		"videoPermissionError": "Cannot access camera or microphone. Please grant permission.",
		"videoStart": "Start recording",
		"videoStop": "Stop recording"
	},
	Languages: {
		"auto": "Auto",
		"zh": "Chinese",
		"ja": "Japanese",
		"yue": "Cantonese",
		"en": "English",
		"ko": "Korean",
		"fr": "French",
		"es": "Spanish",
		"de": "German",
		"ru": "Russian",
		"pt": "Portuguese",
		"it": "Italian"
	},
	Errors: {
		"somethingWentWrong": "Something went wrong",
		"tryAgain": "Try again",
		"details": "Details",
		"copy": "Copy",
		"copied": "Copied",
		"close": "Close",
		"openModalBilling": "Open Modal billing",
		"needHelp": "Stuck? Come find us:",
		"wechatGroup": "WeChat group",
		"wechatQrAlt": "WeChat group QR code"
	},
	Api: {
		"unknownError": "Unknown error",
		"unauthorized": "Unauthorized",
		"accessDenied": "Access denied",
		"requestFailed": "Request failed: {status} {statusText}",
		"success": "Success",
		"timedOut": "Request timed out; please retry"
	}
};
//#endregion
//#region src/canvas/i18n/messages/ja.json
var ja_default = {
	Workspace: {
		"edges": {
			"deleteConfirmTitle": "接続を削除しますか？",
			"deleteConfirmDescription": "この接続はどのノードにも接続されていません。削除しますか？",
			"delete": "削除",
			"cancel": "キャンセル"
		},
		"handles": {
			"text": "テキスト",
			"image": "画像",
			"image2": "画像 2",
			"images": "画像",
			"end_image": "終了画像",
			"video": "動画",
			"videos": "動画",
			"audio": "音声",
			"speech": "音声",
			"ref_audio": "参照音声",
			"ref_text": "参照テキスト",
			"document": "ドキュメント",
			"byFeature": { "image-image-gen-video": {
				"image": "開始フレーム",
				"end_image": "終了フレーム"
			} }
		},
		"modeSwitch": {
			"createMode": "作成モード",
			"executeMode": "実行モード",
			"appMode": "アプリモード",
			"upgradeRequired": "実行モードを解除するにはアップグレードが必要です"
		},
		"appView": {
			"inputsTitle": "入力",
			"outputsTitle": "結果",
			"run": "実行",
			"cancel": "停止",
			"stepProgress": "ステップ {current}/{total}",
			"completed": "完了",
			"failed": "実行に失敗しました",
			"emptyWorkflow": "キャンバスが空です。まずワークフローを作成してください",
			"invalidWorkflow": "ワークフローに循環があるため実行できません",
			"nothingToRun": "このワークフローには実行できるステップがありません",
			"noOutputsYet": "実行すると結果がここに表示されます",
			"dropFilesHere": "ここにファイルをドロップ、またはクリックしてアップロード",
			"copyText": "コピー",
			"download": "ダウンロード",
			"goToCreateMode": "作成モードへ",
			"textPlaceholder": "ここに入力…",
			"fieldType": {
				"text": "テキスト",
				"image": "画像",
				"audio": "音声",
				"video": "動画",
				"model": "3Dモデル",
				"file": "ファイル"
			}
		},
		"smartIsland": {
			"category": {
				"digitalHuman": "デジタルヒューマン",
				"artDesign": "アート＆デザイン",
				"videoAd": "動画＆広告",
				"docProcessing": "ドキュメント処理",
				"knowledgeLearning": "知識学習",
				"musicMV": "音楽/MV"
			},
			"enterWorkflowName": "ワークフロー名を入力",
			"saveSuccess": "保存しました！",
			"saveFailed": "保存に失敗しました。もう一度お試しください",
			"generateVideo": "動画生成",
			"generateAudio": "音声生成",
			"generateText": "テキスト生成",
			"generateImage": "画像生成",
			"generateMusic": "音楽生成",
			"generate3D": "3D生成",
			"detectPose": "ポーズ",
			"segmentBody": "身体分割",
			"estimateNormal": "法線",
			"extractForeground": "マッティング",
			"captureMotion": "モーションキャプチャ",
			"firstLastFrameVideo": "始点/終点フレーム動画",
			"confirmExecute": "実行を確認",
			"deployModalWorkers": "Modal ワーカーをデプロイ",
			"deployModalRunning": "Modal ワーカーをデプロイ中...",
			"deployModalSuccess": "Modal ワーカーのデプロイ完了",
			"deployModalFailed": "Modal のデプロイに失敗しました",
			"deployModalUnsupported": "この操作はデスクトップ版のみ対応しています",
			"saveAndExecute": "保存して実行",
			"cancel": "キャンセル",
			"executeConfirmTitle": "ワークフローの実行を確認",
			"executeConfirmDescSaved": "実行前にワークフローは最新の状態で保存されます。実行しますか？",
			"executeConfirmDescNew": "実行前にワークフローを保存してください。生成された素材は、将来の参照と管理のためにこのワークフローに関連付けられます。",
			"executeWorkflow": "ワークフローを実行",
			"saveWorkflow": "ワークフローを保存",
			"executing": "実行中...",
			"saving": "保存中...",
			"estimatedCost": "推定コスト：",
			"estimatedTime": "推定時間：",
			"workflowName": "ワークフロー名",
			"workflowNamePlaceholder": "ワークフロー名を入力",
			"workflowDescription": "ワークフローの説明",
			"workflowDescPlaceholder": "ワークフローの説明を入力（任意）",
			"tooltip3D": "3D",
			"tooltipDocument": "ドキュメント",
			"tooltipImage": "画像",
			"tooltipText": "テキスト",
			"tooltipVideo": "動画",
			"tooltipAudio": "音声",
			"tooltipLink": "リンク",
			"split": "分割",
			"splitText": "スマート分割",
			"speechRecognize": "音声認識",
			"separateAudio": "ボーカル分離",
			"separateSpeaker": "話者分離",
			"separateSound": "音を抽出",
			"denoise": "ノイズ除去",
			"convertVoice": "音声置換",
			"filter": "フィルター",
			"arrange": "配置",
			"concat": "結合",
			"describeReverse": "説明生成",
			"upscale": "HD高画質化",
			"removeSubtitle": "字幕削除",
			"removeWatermark": "透かし削除",
			"extractAudioTrack": "音声抽出",
			"removeVideoAudio": "音声を除去",
			"slice": "スライス",
			"firstFrame": "最初のフレーム",
			"lastFrame": "最後のフレーム",
			"editImage": "画像編集",
			"editVideo": "動画編集",
			"parseDocument": "ドキュメント解析",
			"extractContent": "コンテンツを抽出",
			"mergeGroup": "グループ化",
			"imageFusion": "画像合成",
			"imagesGenVideo": "参照から動画",
			"refsGenVideo": "マルチ参照動画",
			"rewriteText": "テキストリライト",
			"textToSpeech": "テキスト読み上げ",
			"textToSpeechClone": "音声生成（クローン）",
			"textToSpeechPreset": "音声生成（プリセット）",
			"textToSpeechInstruct": "音声生成（説明）",
			"motionControl": "モーション制御",
			"videoTransfer": "動画転送",
			"characterReplace": "キャラクター置換",
			"merge": "結合",
			"lipSync": "リップシンク",
			"lipDub": "吹き替え・口パク",
			"cloneVoice": "ボイスクローン",
			"speechGenVideo": "音声から動画生成",
			"repaintMusic": "リペイント",
			"coverMusic": "カバー",
			"extractStem": "ステム抽出",
			"addTrack": "トラック追加",
			"completeMusic": "編曲補完",
			"musicBrief": "音楽ブリーフ"
		},
		"toast": {
			"executing": "実行中...",
			"completed": "完了",
			"cancelled": "キャンセル済み",
			"cancelling": "キャンセル中...",
			"failed": "失敗",
			"nodes": "ノード",
			"cancelTask": "タスクをキャンセル",
			"totalDuration": "合計時間",
			"taskCompleted": "タスク完了",
			"taskFailed": "タスク失敗",
			"taskFailedWithMsg": "タスク失敗：{error}",
			"level": "レベル {level}"
		},
		"dialog": {
			"myWorkflows": "マイワークフロー",
			"noWorkflows": "ワークフローがありません",
			"savedWorkflowsHint": "保存したワークフローがここに表示されます",
			"noMore": "これ以上ワークフローはありません",
			"loadSuccess": "読み込みました！",
			"loadFailed": "読み込みに失敗しました。もう一度お試しください",
			"open": "開く"
		},
		"menu": {
			"save": "保存",
			"saveAs": "名前を付けて保存",
			"clear": "クリア",
			"confirmClear": "現在のワークフローをクリアしてもよろしいですか？この操作は元に戻せません。",
			"cleared": "ワークフローをクリアしました",
			"saveAsNew": "新規ワークフローとして保存",
			"saveWorkflow": "ワークフローを保存",
			"saveNew": "新規として保存",
			"name": "名前",
			"enterName": "ワークフロー名を入力",
			"descOptional": "説明（任意）",
			"enterDesc": "ワークフローの説明を入力",
			"saving": "保存中...",
			"update": "更新",
			"cancel": "キャンセル",
			"saveSuccess": "保存しました！",
			"saveFailed": "保存に失敗しました。もう一度お試しください",
			"exportJson": "JSON をエクスポート",
			"exportWorkflow": "ワークフローをエクスポート",
			"exportExecutable": "実行可能ファイルをエクスポート",
			"importJson": "JSON をインポート",
			"importJsonInvalid": "解析できません：JSON が無効か形式が違います。",
			"importJsonNoCanvas": "キャンバスデータがありません（originalFlow、flow、または nodes/edges が必要です）。",
			"importJsonSuccess": "ワークフローを読み込みました（ノード {nodes}、エッジ {edges}）",
			"importJsonReadFailed": "ファイルの読み込みに失敗しました",
			"exportJsonSuccess": "ダウンロードを開始しました",
			"exportJsonFailed": "エクスポートに失敗しました"
		},
		"nodes": {
			"base": {
				"moreActions": "その他のオプション",
				"execute": "実行",
				"cancelExecution": "実行をキャンセル",
				"removeComment": "コメントを削除",
				"commentPlaceholder": "ユーザー向けのコメントを追加...",
				"clickToAddComment": "クリックしてコメントを追加...",
				"addComment": "コメントを追加",
				"delete": "削除",
				"deleteConfirmTitle": "削除の確認",
				"deleteConfirmDescription": "このノードには {count} 個の下流ノードがあります。削除方法を選択してください。",
				"deleteNodeOnly": "このノードのみ削除",
				"deleteNodeAndChildren": "このノードとすべての下流ノードを削除（{count}個）",
				"deleteChildrenOnly": "下流ノードのみ削除（{count}個）",
				"run": "実行",
				"preview": "プレビュー",
				"lock": "ノードをロック",
				"unlock": "ノードのロックを解除",
				"startCombo": "コンボを開始",
				"addToCombo": "コンボに追加",
				"removeFromCombo": "コンボから削除",
				"edit": "編集",
				"cancel": "キャンセル",
				"confirm": "確認",
				"fullscreenEdit": "フルスクリーン編集",
				"startVoice": "音声入力を開始",
				"stopVoice": "音声入力を停止",
				"editText": "テキストを編集",
				"select": "選択",
				"selected": "選択済み",
				"modalNotDeployedTitle": "Modal が未デプロイです",
				"modalNotDeployedDescription": "機能「{feature}」には Modal App「{app}」が必要です。まだデプロイされていません。今すぐデプロイしますか？",
				"modalDeployHint": "1) MODAL_TOKEN_ID / MODAL_TOKEN_SECRET を設定。2) plugins/ があることを確認する。サーバーがプラグイン注釈をメモリ上でスキャンして登録情報を作成します。3) 本機で `python -m modal` が動くことを確認（例: pip install modal）。ワンクリックデプロイはその登録情報に沿って各プラグインの deploy.py に modal deploy をかける。",
				"modalDeployDismiss": "キャンセル",
				"modalDeployAction": "ダウンロード＆デプロイ",
				"modalDeploying": "Modal workers をデプロイ中…",
				"modalDeployDone": "デプロイ完了",
				"modalDeployError": "デプロイ失敗",
				"modalDeployingAction": "デプロイ中…",
				"modalDeployProgressHint": "{done}/{total} をデプロイ済み",
				"pluginImplementationTitle": "実装",
				"pluginModelTitle": "モデル",
				"pluginParamsTitle": "詳細設定",
				"pluginParamsReset": "リセット",
				"pluginParamDefault": "既定 {value}",
				"pluginImplementationLoading": "プラグイン実装一覧を読み込み中…",
				"pluginImplementationEmpty": "この機能向けのプラグイン実装がスキャン結果にありません。リポジトリの plugins/ に Modal または API プラグインを追加してください（README 参照）。ページを再読み込みしてください。",
				"pluginRegistryLoadError": "プラグイン登録情報を読み込めませんでした：{message}",
				"pluginSelectPlaceholder": "リポジトリを選択…",
				"missingImplTitle": "実装が未選択",
				"missingImplDescription": "このノードに利用可能なプラグインがインストールされていません（または未選択です）。実行前に、プラグインマネージャーからインストールするか、インストール済みのプラグインを選択してください。"
			},
			"common": {
				"enterInstructions": "指示を入力してください...",
				"manualInput": "入力",
				"library": "ライブラリ",
				"addText": "テキストを追加",
				"enterText": "テキスト内容を入力...",
				"descOptional": "説明（任意）",
				"fromUpstreamText": "上流テキストノードから",
				"videoDesc": "動画の説明",
				"aspectRatio": "アスペクト比",
				"resolution": "解像度",
				"duration": "長さ",
				"currentSize": "現在のサイズ：",
				"currentDuration": "現在の長さ：",
				"upscaleTierLabel": "拡大モード（長辺）",
				"upscaleTierHint": "縦横比は入力に合わせ、長辺のみをスケールします。",
				"upscaleTier1k": "1K · ~1920",
				"upscaleTier2k": "2K · ~2560",
				"upscaleTier4k": "4K · ~3840",
				"seconds": "秒",
				"minute": "分",
				"thinkingProcess": "思考プロセス",
				"performanceMode": "パフォーマンスモード",
				"ecoMode": "💰 エコモード",
				"proMode": "⚡ プロモード",
				"modelSelect": "モデル",
				"models": {
					"imageEdit": "FLUX2 Klein 画像編集",
					"imageGen": "Z-Image テキスト生成画像",
					"imageFusion": "FLUX2 Klein 画像融合",
					"imageGenVideo": "LTX2 画像から動画",
					"features": {
						"gen-text": "デフォルト（OpenRouter）",
						"gen-text-gemini": "Gemini",
						"gen-text-openai": "OpenAI",
						"split-text": "スマート分割",
						"combine-text": "テキスト結合",
						"combine-text-openai": "テキスト結合（OpenAI）",
						"combine-text-gemini": "テキスト結合（Gemini）",
						"image-fusion": "FLUX2 Klein 融合",
						"image-gen-text": "画像キャプション",
						"video-gen-text": "動画キャプション",
						"transcribe": "音声認識",
						"transcribe-timestamp": "音声認識（タイムスタンプ）",
						"concat-videos": "動画連結",
						"extract-audio": "音声抽出",
						"remove-video-audio": "音声除去（無音動画）",
						"merge-video-audio": "動画+音声合成",
						"get-first-frame": "先頭フレーム",
						"get-last-frame": "末尾フレーム",
						"parse-document": "文書解析",
						"split-video": "シーン分割",
						"link": "URL取得",
						"gen-video": "テキストから動画",
						"image-gen": "Z-Image 文生図",
						"gen-music": "ACE-Step 音楽",
						"text-gen-speech-preset": "音声合成（プリセット）",
						"image-gen-video": "画像から動画",
						"image-edit": "FLUX2 Klein 編集",
						"image-upscale": "画像アップスケール",
						"video-upscale": "動画アップスケール",
						"image-describe": "画像理解",
						"video-describe": "動画理解",
						"audio-describe": "音声理解",
						"music-repaint": "音楽リペイント",
						"music-cover": "音楽カバー",
						"music-extract": "ステム抽出",
						"music-lego": "トラック追加",
						"music-complete": "編曲補完",
						"music-brief": "音楽ブリーフ",
						"audio-image-gen-video": "音声+画像から動画",
						"speech-text-gen-video": "音声+テキストから動画",
						"video-image-gen-video-mix": "動画+画像ミックス",
						"video-image-gen-video-move": "動画+画像モーション",
						"image-image-gen-video": "画像+画像から動画",
						"text-gen-video": "テキストから動画",
						"image-gen-model": "画像から3D",
						"speech-video-gen-video": "動画+台詞吹き替え",
						"audio-video-lip-sync": "音声+動画リップシンク",
						"text-gen-speech-clone": "ボイスクローン",
						"text-gen-speech-instruct": "指示TTS",
						"video-image-move-animal": "動画+画像（動物）",
						"video-image-move": "動画+画像（人物）",
						"video-image-move-muti": "動画+画像（マルチ）",
						"video-image-move-animal-muti": "動画+画像（動物・マルチ）",
						"wan-animate-mix": "WAN アニメート",
						"subtitle-remove": "字幕除去",
						"remove-watermark": "透かし除去",
						"denoise-audio": "ノイズ除去",
						"separate-audio-track": "ボーカル/伴奏",
						"separate-speaker": "話者分離",
						"convert-voice": "声色変換",
						"drop-video": "クリップ抽出",
						"arrange-group": "バッチ配置"
					}
				},
				"voice": "音声",
				"selectVoice": "音声を選択",
				"previewVoice": "音声をプレビュー",
				"uploadVoice": "音声をアップロード",
				"recordVoice": "音声を録音",
				"voiceClone": "音声クローン",
				"voicePreset": "プリセットスタイル",
				"voiceDescribe": "カスタム説明",
				"gender": "性别",
				"selectGender": "性别を選択（任意）",
				"emotion": "感情",
				"style": "スタイル",
				"selectEmotion": "感情を選択（任意）",
				"selectStyle": "スタイルを選択（任意）",
				"voiceDescription": "音声の説明",
				"voiceDescriptionPlaceholder": "音声スタイルの説明を入力してください。例：優しく甘い女性の声、ゆっくり話す...",
				"referenceAudio": "参照音声",
				"noReferenceAudio": "参照音声をアップロードまたは録音してください（3秒以上）",
				"referenceText": "参照テキスト",
				"referenceTextPlaceholder": "任意：参照音声の書き起こしテキスト。クローン品質の向上に役立ちます",
				"referenceTextCloneHint": "空欄の場合は x-vector クローン。転写テキストを入力すると ICL 高品質クローンになります",
				"language": "言語",
				"inputText": "テキスト入力",
				"voiceOptions": {
					"female": "女性の声",
					"male": "男性の声"
				}
			},
			"titles": {
				"imageGenVideo": "画像から動画",
				"textGenVideo": "テキストから動画",
				"addText": "テキストを追加",
				"textGenText": "テキスト生成",
				"combineText": "テキスト結合",
				"textGenImage": "テキストから画像",
				"textGenSpeechClone": "音声生成（クローン）",
				"textGenSpeechCloneCompose": "ボイスクローン（テキスト+参考音声）",
				"imageGenVideoCompose": "画像から動画（テキスト+画像）",
				"textGenSpeechPreset": "音声生成（プリセット）",
				"textGenSpeechInstruct": "音声生成（説明）",
				"imageFusion": "画像合成",
				"videoUpscale": "動画高画質化",
				"videoGenText": "動画説明",
				"speechRecognize": "音声認識",
				"textGenMusic": "テキストから音楽",
				"speechGenVideo": "音声から動画",
				"convertVoice": "音声変換",
				"denoiseAudio": "音声ノイズ除去",
				"fileGenText": "ドキュメント解析",
				"linkGenText": "リンク抽出",
				"removeSubtitle": "字幕削除",
				"removeWatermark": "透かし削除",
				"separateAudio": "映像・音声の分離",
				"imageGenText": "画像説明",
				"audioDescribe": "音声理解",
				"imageGenImage": "画像編集",
				"videoEdit": "動画編集",
				"imageGenModel": "画像から3Dモデル",
				"imagePose": "ポーズ検出",
				"imageBodySeg": "身体部位セグメンテーション",
				"imageNormal": "表面法線",
				"imageMatting": "人物マッティング",
				"videoGenModel": "ビデオモーションキャプチャ",
				"imageUpscale": "HD高画質化",
				"separateTrack": "ボーカル抽出",
				"separateSpeaker": "話者分離",
				"separateSound": "音の分離",
				"speechImageGenVideo": "音声+画像から動画",
				"concatVideo": "動画結合",
				"mergeVideoAudio": "動画と音声を結合",
				"audioVideoLipSync": "動画リップシンク",
				"speechTextGenVideo": "音声+テキストから動画",
				"speechVideoGenVideo": "動画吹き替え・口パク",
				"imageImageGenVideo": "始点/終点フレーム動画",
				"imagesGenVideo": "複数画像から動画",
				"refsGenVideo": "マルチ参照から動画",
				"textAudioGenSpeech": "テキスト+音声から音声",
				"videoImageMix": "動画+画像ミックス",
				"videoImageMove": "動画+画像移動",
				"splitVideo": "動画分割",
				"splitText": "スマート分割",
				"getFirstFrame": "最初のフレーム",
				"getLastFrame": "最後のフレーム",
				"extractAudioTrack": "音声抽出",
				"removeVideoAudio": "音声除去（無音動画）",
				"musicRepaint": "音楽リペイント",
				"musicCover": "音楽カバー",
				"musicExtract": "ステム抽出",
				"musicLego": "トラック追加",
				"musicComplete": "編曲補完",
				"musicBrief": "音楽ブリーフ"
			},
			"options": {
				"portrait": "縦向き",
				"landscape": "横向き",
				"square": "正方形",
				"standard": "標準",
				"verticalStandard": "縦向き標準",
				"human": "人物",
				"animal": "動物",
				"single": "一人",
				"multi": "複数人"
			},
			"actions": {
				"generateVideo": "動画を生成",
				"generateImage": "画像を生成",
				"generateSpeech": "音声を生成",
				"startFusion": "合成を開始",
				"convertText": "テキストに変換",
				"startUpscale": "高画質化を開始",
				"describeVideo": "動画を説明",
				"recognizeSpeech": "音声を認識",
				"generateMusic": "音楽を生成",
				"generateAudio": "音声を生成",
				"startReplace": "置換を開始",
				"startDenoise": "ノイズ除去を開始",
				"parseDocument": "ドキュメントを解析",
				"extractContent": "コンテンツを抽出",
				"removeSubtitle": "字幕を削除",
				"removeWatermark": "透かしを削除",
				"compressVideo": "動画を圧縮",
				"extractAudio": "分離を開始",
				"describeImage": "画像を説明",
				"describeAudio": "音声を説明",
				"editImage": "画像を編集",
				"editVideo": "動画を編集",
				"generate3DModel": "3Dモデルを生成",
				"detectPose": "ポーズを検出",
				"segmentBody": "身体部位を分割",
				"estimateNormal": "法線を推定",
				"extractForeground": "前景を抽出",
				"captureMotion": "モーションをキャプチャ",
				"imageUpscale": "HD高画質化",
				"extractVocals": "ボーカルを抽出",
				"startSeparation": "分離を開始",
				"concatVideo": "動画を結合",
				"startMerge": "結合を開始",
				"lipDub": "吹き替え・口パク",
				"motionTrackGenerate": "軌跡で生成",
				"splitVideo": "動画を分割",
				"splitText": "スマート分割",
				"getFirstFrame": "最初のフレームを取得",
				"getLastFrame": "最後のフレームを取得",
				"extractAudioTrack": "音声を抽出",
				"removeVideoAudio": "音声を除去",
				"repaintMusic": "リペイント開始",
				"coverMusic": "カバー生成",
				"extractStem": "抽出開始",
				"separateSound": "分離開始",
				"addTrack": "トラック生成",
				"completeMusic": "補完開始",
				"musicBrief": "ブリーフ生成"
			},
			"params": {
				"prompt": "プロンプト",
				"model": "モデル",
				"modelDefault": "デフォルト (OpenRouter 無料ルーター)",
				"geminiModel": "Gemini モデル",
				"openaiModel": "OpenAI モデル",
				"style": "スタイル",
				"sampler": "サンプラー",
				"seed": "シード",
				"steps": "ステップ",
				"cfg": "CFGスケール"
			},
			"emotions": {
				"none": "なし",
				"happy": "嬉しい",
				"angry": "怒り",
				"sad": "悲しい",
				"fear": "恐怖",
				"surprised": "驚き",
				"confusion": "困惑",
				"empathy": "共感",
				"embarrass": "恥ずかしい",
				"excited": "興奮",
				"depressed": "落ち込み",
				"admiration": "感嘆",
				"coldness": "冷淡"
			},
			"genders": {
				"none": "なし",
				"male": "男性",
				"female": "女性"
			},
			"styles": {
				"none": "なし",
				"serious": "真剣",
				"arrogant": "傲慢",
				"child": "子供",
				"older": "年配",
				"girl": "少女",
				"pure": "純粋",
				"sister": "お姉さん",
				"sweet": "甘い",
				"exaggerated": "オーバー",
				"ethereal": "幻想的",
				"whisper": "囁き",
				"generous": "寛大",
				"recite": "朗読",
				"act_coy": "甘え",
				"warm": "温かい",
				"shy": "恥ずかしがり",
				"comfort": "癒し",
				"authority": "権威",
				"chat": "雑談",
				"radio": "ラジオ",
				"soulful": "ソウルフル",
				"gentle": "優しい",
				"story": "物語",
				"vivid": "生き生き",
				"program": "司会",
				"news": "ニュース",
				"advertising": "広告",
				"roar": "咆哮",
				"murmur": "つぶやき",
				"shout": "叫び",
				"deeply": "深い",
				"loudly": "大声"
			},
			"recorder": {
				"title": "音声を録音",
				"desc": "音声合成の参照用にオーディオクリップを録音",
				"start": "録音開始",
				"stop": "録音停止",
				"preview": "プレビュー",
				"retry": "やり直し",
				"uploading": "アップロード中...",
				"confirm": "確認",
				"cancel": "キャンセル",
				"recording": "録音中...",
				"recorded": "録音完了",
				"micError": "マイクにアクセスできません。権限を確認してください",
				"uploadError": "アップロードに失敗しました。もう一度お試しください",
				"uploadFailed": "アップロード失敗"
			},
			"imageFusion": {
				"loading": "読み込み中...",
				"imageReference": "画像参照",
				"imageReferenceHint": "画像をクリックしてプロンプトに参照を挿入",
				"fusionPrompt": "合成プロンプト",
				"fusionPromptPlaceholder": "合成の指示を入力してください。例：画像1のスタイルを維持、すべての画像の特徴を合成、画像2の色を強調、画像3の構図を組み合わせ...",
				"imageLabel": "画像"
			},
			"refsGenVideo": {
				"videoReference": "動画参照",
				"videoLabel": "動画",
				"audioReference": "音声参照",
				"audioLabel": "音声",
				"referenceHint": "画像は最大9枚、動画3本、音声3本（合計12ファイル）。音声は画像か動画と組み合わせてください。サムネイルをクリックするとプロンプトに参照を挿入できます。",
				"promptPlaceholder": "生成したい動画を記述してください。例：画像1のキャラクターが音声1の声で話し、動画1のシーンを引き継ぐ…"
			},
			"video": { "fileCount": "処理するファイル：" },
			"speechRecognize": { "withTimestamp": "タイムスタンプ付き" },
			"music": {
				"handleStyle": "スタイル",
				"handleLyric": "歌詞",
				"referenceAudio": "参照音声",
				"inputLyrics": "歌詞を入力",
				"fromUpstreamReadonly": "上流から（読み取り専用）",
				"lyricsPlaceholder": "歌詞入力未接続時に入力",
				"audioDuration": "音声の長さ",
				"currentDuration": "現在の長さ：",
				"seconds": "秒",
				"15s": "15秒",
				"30s": "30秒",
				"1min": "1分",
				"2min": "2分",
				"language": "言語",
				"keyscale": "キー",
				"bpm": "BPM",
				"auto": "自動"
			},
			"musicTasks": {
				"promptLabel": "キャプション",
				"promptPlaceholder": "音楽のスタイル、雰囲気、楽器などを記述...",
				"lyricsLabel": "歌詞",
				"lyricsPlaceholder": "歌詞（任意）...",
				"startTime": "開始（秒）",
				"endTime": "終了（秒）",
				"strength": "強度",
				"track": "トラック",
				"tracks": "追加するトラック",
				"instrumental": "インストゥルメンタル",
				"ideaPlaceholder": "作りたい曲を一文で記述...",
				"sourceAudio": "元の曲",
				"refAudio": "リファレンス音声",
				"trackOptions": {
					"vocals": "ボーカル",
					"backing_vocals": "コーラス",
					"drums": "ドラム",
					"percussion": "パーカッション",
					"bass": "ベース",
					"guitar": "ギター",
					"keyboard": "キーボード",
					"synth": "シンセ",
					"strings": "ストリングス",
					"brass": "ブラス",
					"woodwinds": "木管楽器",
					"fx": "効果音"
				}
			},
			"speechGenVideo": {
				"loading": "読み込み中...",
				"inputContent": "入力内容",
				"audio": "音声",
				"connectAudioHint": "音声ノードを接続するか、音声ファイルをアップロードしてください",
				"audioPromptLabel": "音声プロンプト",
				"audioPromptPlaceholder": "音声吹き替えのプロンプトを入力"
			},
			"convertVoice": {
				"voiceLabel": "音声：",
				"selectVoice": "音声を選択",
				"previewVoice": "音声をプレビュー",
				"uploadVoice": "音声をアップロード",
				"recordVoice": "音声を録音",
				"recordAudio": "音声を録音",
				"recordHint": "ここで音声を録音できます。",
				"recordNeedImpl": "音声録音機能は実装が必要です",
				"done": "完了"
			},
			"removeWatermark": { "hint": "Sora動画の透かしを自動検出して削除" },
			"videoGenText": {
				"promptLabel": "カスタムプロンプト",
				"promptPlaceholder": "デフォルトのプロンプトを使用する場合は空白のままにするか、カスタム指示を入力してください。例：動画の会話内容のみを説明..."
			},
			"imageGenText": {
				"promptLabel": "カスタムプロンプト",
				"promptPlaceholder": "デフォルトを使う場合は空白のまま。例：被写体・雰囲気・画像内の文字のみを説明..."
			},
			"audioDescribe": {
				"promptLabel": "カスタムプロンプト",
				"promptPlaceholder": "空欄でデフォルトプロンプトを使用。ジャンル・雰囲気・楽器・ボーカルの説明など、カスタム指示も入力可能…"
			},
			"textGenText": { "instructions": "指示" },
			"lipDub": {
				"targetDialogue": "目標台詞（プロンプト）",
				"targetDialoguePlaceholder": "キャラクター説明と目標言語の台詞を含む完全なプロンプト（自動翻訳なし）",
				"promptHint": "解像度は参照動画に合わせます。キャラクター＋目標言語の台詞を含む完全なプロンプト（自動翻訳なし）。長さは原動画に近づけ、話者1人のみ。"
			},
			"motionTrack": {
				"connectHint": "先頭フレーム画像、参考動画（動きを自動抽出して転写）、テキストプロンプト（見た目・スタイルのみ）を接続してください。",
				"controlVideo": "参考動画",
				"promptPlaceholder": "見た目とスタイルを記述。動きは参考動画から自動抽出されます。",
				"promptHint": "プロンプトは見た目・スタイルのみ。動きは参考動画から自動抽出されます。長さスライダーは参考動画の上限です。",
				"missing": "あと必要: {items}"
			},
			"lipSync": {
				"scenePrompt": "シーン説明（任意）",
				"scenePromptPlaceholder": "画面と話者を記述。口パクは接続した音声に同期します。台詞全文はここに書かないでください。",
				"audioDrivenHint": "口パクと音声は音声入力に従います。音声の長さは参照動画に近づけてください。出力は既定 720p。"
			},
			"imageEdit": {
				"performanceMode": "パフォーマンスモード",
				"ecoMode": "💰 エコ（FLUX.2 Klein 9B）",
				"proMode": "⚡ プロモード",
				"editInstruction": "編集指示",
				"fromUpstream": "（上流ノードから）",
				"editPlaceholder": "画像編集の指示を入力してください。例：コントラストを上げる、色温度を暖色に調整、ディテールを強調"
			},
			"videoEdit": {
				"editInstruction": "編集指示",
				"fromUpstream": "（上流ノードから）",
				"editPlaceholder": "動画編集の指示を入力してください。例：字幕を除去、水彩風スタイルを適用、空を夕焼けに変更",
				"connectTextVideo": "テキストノードと動画ノードを接続してください"
			},
			"compose": {
				"loading": "読み込み中...",
				"mediaFiles": "メディアファイル",
				"image": "画像",
				"audio": "音声",
				"video": "動画",
				"text": "テキスト",
				"noText": "テキストなし",
				"inputContent": "入力内容",
				"videoFiles": "動画ファイル",
				"connectImageAudio": "画像と音声ファイルを接続してください",
				"connectVideo": "少なくとも1つの動画ファイルを接続してください",
				"connectImageVideoAudio": "画像、動画、音声ファイルを接続してください",
				"connectTextAudio": "テキスト内容と音声ファイルを接続してください",
				"connectTextReferenceAudio": "テキストノードと音声ノード（参考音声）を接続してください",
				"connectTextImage": "テキストノードと画像ノードを接続してください",
				"connectTextVideoNode": "動画ノードとテキストノードを接続してください（目標台詞はプロンプトに記載）",
				"referenceAudio": "参考音声",
				"referenceTextOptional": "参照テキスト（任意・クローン品質向上）",
				"connectVideoAudio": "動画と音声ファイルを接続してください",
				"generatePromptLabel": "生成プロンプト",
				"generatePromptPlaceholder": "動画生成プロンプトを入力：内容、スタイル、アクションなどを説明（任意）",
				"inputText": "テキスト入力",
				"inputTextPlaceholder": "テキスト内容を入力",
				"describeVideo": "生成したい動画を説明...",
				"firstFrame": "最初のフレーム",
				"lastFrame": "最後のフレーム",
				"connectTwoImages": "2つの画像を接続してください（最初と最後のフレーム）",
				"inputData": "入力データ",
				"connectTextAudioNode": "テキストと音声ノードを接続してください",
				"emotion": "感情：",
				"style": "スタイル：",
				"selectEmotion": "感情スタイルを選択（任意）",
				"selectStyle": "音声スタイルを選択（任意）",
				"lipSync": "リップシンク",
				"connectImageVideo": "画像と動画ファイルを接続してください",
				"startSlice": "スライス開始"
			},
			"modal": {
				"audioPreview": "音声プレビュー",
				"audio": "音声",
				"audios": "音声（{count}）",
				"loading": "読み込み中...",
				"unavailable": "コンテンツを読み込めません",
				"more": "もっと見る",
				"moreOptions": "その他のオプション",
				"actions": "アクション",
				"textPreview": "テキストプレビュー",
				"text": "テキスト",
				"texts": "テキスト（{count}）",
				"imagePreview": "画像プレビュー",
				"image": "画像",
				"images": "画像（{count}）",
				"videoPreview": "動画プレビュー",
				"video": "動画",
				"videos": "動画（{count}）",
				"file": "ファイル",
				"files": "ファイル（{count}）",
				"link": "リンク",
				"links": "リンク（{count}）",
				"fullScreenPreview": "フルスクリーンプレビュー",
				"fullScreenWaterfall": "フルスクリーンウォーターフォールプレビュー",
				"model3DPreview": "3Dモデルプレビュー",
				"model3D": "3Dモデル",
				"models3D": "3Dモデル（{count}）",
				"resetView": "ビューをリセット",
				"playAnimation": "アニメーションを再生",
				"pauseAnimation": "アニメーションを一時停止",
				"downloadModel": "モデルをダウンロード",
				"loading3DModel": "3Dモデルを読み込み中...",
				"errorLoadingModel": "モデルの読み込みエラー",
				"dragToRotate": "左クリック+ドラッグで回転 | スクロールでズーム | リセットでセンタリング",
				"noModelLoaded": "3Dモデルが読み込まれていません",
				"loadingPreview": "プレビューを読み込み中...",
				"unsupportedFormat": "サポートされていない形式",
				"supportedFormats": "サポート形式",
				"download": "ダウンロード",
				"textEditor": "テキストエディター",
				"enterText": "テキストを入力してください..."
			},
			"add": {
				"addAudio": "音声を追加",
				"addImage": "画像を追加",
				"addVideo": "動画を追加",
				"addText": "テキストを追加",
				"addFile": "ファイルを追加",
				"addModel": "3Dモデルを追加",
				"addLink": "リンクを追加",
				"record": "録音",
				"recordVideo": "録画",
				"upload": "アップロード",
				"library": "ライブラリ",
				"recorded": "録音済み",
				"uploading": "アップロード中",
				"confirmAndUpload": "確認してアップロード",
				"dropToUpload": "ファイルをドロップしてアップロード",
				"dragOrClick": "ファイルをここにドラッグするか、クリックしてアップロード",
				"supportedAudioFormats": "MP3、WAV、OGG、M4Aをサポート",
				"supportedImageFormats": "一般的な画像形式をサポート",
				"supportedVideoFormats": "MP4、MOV、WEBMをサポート",
				"browseFiles": "ファイルを参照",
				"canvas": "キャンバス",
				"camera": "カメラ",
				"openCanvas": "キャンバスを開く",
				"canvasDrawing": "キャンバス描画",
				"canvasDrawingHint": "下のキャンバスに描画してから、完了をクリックして保存",
				"clearCanvas": "キャンバスをクリア",
				"finishAndSave": "完了して保存",
				"clickToStartCamera": "クリックしてカメラを起動",
				"takePhoto": "写真を撮る",
				"closeCamera": "カメラを閉じる",
				"cameraError": "カメラにアクセスできません",
				"cameraErrorHint": "確認してください：1. HTTPSまたはlocalhostを使用 2. カメラの権限を許可 3. カメラが他のアプリで使用されていない",
				"inputText": "テキスト入力",
				"textPlaceholder": "テキスト内容を入力してください...",
				"generate": "確認",
				"linkPlaceholder": "リンクを入力してください",
				"extractContent": "コンテンツを抽出",
				"cancel": "キャンセル",
				"startRecordingVideo": "動画録画を開始",
				"addDocument": "ドキュメントを追加",
				"supportedDocFormats": "複数のドキュメント形式をサポート",
				"dragModel": "3Dモデルファイルをここにドラッグするか、クリックしてアップロード",
				"supportedModelFormats": "サポート形式",
				"modelHint": "ヒント：以下の3Dファイル形式をサポート："
			},
			"batch": {
				"arrangeGroup": "グループを配置",
				"startArrange": "配置を開始",
				"describeRequirements": "要件を説明...",
				"groupCount": "グループ数",
				"allowDuplicate": "重複を許可",
				"videoFilter": "動画フィルター",
				"startFilter": "フィルターを開始"
			},
			"voice": {
				"timbre": "音声：",
				"selectTimbre": "音声を選択",
				"previewTimbre": "現在の音声をプレビュー",
				"uploadTimbre": "音声をアップロード",
				"recordTimbre": "音声を録音",
				"emotion": "感情：",
				"replaceTimbre": "音声を置換"
			},
			"separateSound": {
				"textLabel": "抽出したい音",
				"textPlaceholder": "例:犬の鳴き声、ピアノ、背景のサイレン"
			}
		}
	},
	Upload: {
		"failed": "アップロードに失敗しました。後で再試行してください",
		"fileFailed": "ファイル \"{name}\" のアップロードに失敗しました：{reason}",
		"partialFailed": "一部またはすべてのファイルのアップロードに失敗しました",
		"maxFilesExceeded": "最大 {max} 個のファイルまで許可されています",
		"fileTooLarge": "ファイル \"{name}\" が大きすぎます（最大 {max}MB）",
		"fileTypeNotAccepted": "ファイル \"{name}\" の種類はサポートされていません",
		"readImageFailed": "画像情報の読み取りに失敗しました",
		"readVideoFailed": "動画情報の読み取りに失敗しました"
	},
	LibInput: {
		"deleteSuccess": "素材を削除しました",
		"deleteFailed": "削除に失敗しました",
		"emptyText": "空のテキスト",
		"loadFailed": "素材の読み込みに失敗しました",
		"added": "素材を追加しました",
		"retry": "再試行",
		"emptyMaterial": "{type}素材がまだありません",
		"typeImage": "画像",
		"typeVideo": "動画",
		"typeAudio": "音声",
		"typeText": "テキスト",
		"typeFile": "ファイル",
		"typeModel": "モデル",
		"favoriteHint": "ノードの ❤️ アイコンをクリックして素材をお気に入りに追加",
		"portfolio": "ポートフォリオ"
	},
	Recorder: {
		"audioStart": "録音開始",
		"audioCancel": "キャンセル",
		"audioCancelTooltip": "録音をキャンセル",
		"audioFinish": "完了",
		"audioFinishTooltip": "録音を完了",
		"audioPermissionError": "マイクにアクセスできません。権限を許可してください。",
		"videoPermissionError": "カメラとマイクにアクセスできません。権限を許可してください。",
		"videoStart": "録画開始",
		"videoStop": "録画停止"
	},
	Languages: {
		"auto": "自動",
		"zh": "中国語",
		"ja": "日本語",
		"yue": "広東語",
		"en": "英語",
		"ko": "韓国語",
		"fr": "フランス語",
		"es": "スペイン語",
		"de": "ドイツ語",
		"ru": "ロシア語",
		"pt": "ポルトガル語",
		"it": "イタリア語"
	},
	Errors: {
		"somethingWentWrong": "問題が発生しました",
		"tryAgain": "再試行",
		"details": "詳細",
		"copy": "コピー",
		"copied": "コピーしました",
		"close": "閉じる",
		"openModalBilling": "Modal の請求設定を開く",
		"needHelp": "解決しない場合はこちらへ：",
		"wechatGroup": "WeChat グループ",
		"wechatQrAlt": "WeChat グループの QR コード"
	},
	Api: {
		"unknownError": "不明なエラー",
		"unauthorized": "認証されていません",
		"accessDenied": "アクセスが拒否されました",
		"requestFailed": "リクエスト失敗：{status} {statusText}",
		"success": "成功しました",
		"timedOut": "リクエストがタイムアウトしました。再試行してください"
	}
};
//#endregion
//#region src/canvas/i18n/messages/ko.json
var ko_default = {
	Workspace: {
		"edges": {
			"deleteConfirmTitle": "연결을 삭제하시겠습니까?",
			"deleteConfirmDescription": "이 연결은 어떤 노드에도 연결되어 있지 않습니다. 삭제하시겠습니까?",
			"delete": "삭제",
			"cancel": "취소"
		},
		"handles": {
			"text": "텍스트",
			"image": "이미지",
			"image2": "이미지 2",
			"images": "이미지",
			"end_image": "끝 이미지",
			"video": "비디오",
			"videos": "비디오",
			"audio": "오디오",
			"speech": "음성",
			"ref_audio": "참조 오디오",
			"ref_text": "참조 텍스트",
			"document": "문서",
			"byFeature": { "image-image-gen-video": {
				"image": "첫 프레임",
				"end_image": "마지막 프레임"
			} }
		},
		"modeSwitch": {
			"createMode": "생성 모드",
			"executeMode": "실행 모드",
			"appMode": "앱 모드",
			"upgradeRequired": "실행 모드를 사용하려면 업그레이드하세요"
		},
		"appView": {
			"inputsTitle": "입력",
			"outputsTitle": "결과",
			"run": "실행",
			"cancel": "중지",
			"stepProgress": "{current}/{total} 단계",
			"completed": "완료",
			"failed": "실행에 실패했습니다",
			"emptyWorkflow": "캔버스가 비어 있습니다. 먼저 워크플로를 만들어 주세요",
			"invalidWorkflow": "워크플로에 순환 연결이 있어 실행할 수 없습니다",
			"nothingToRun": "이 워크플로에는 실행할 단계가 없습니다",
			"noOutputsYet": "실행하면 결과가 여기에 표시됩니다",
			"dropFilesHere": "파일을 여기에 끌어다 놓거나 클릭해서 업로드",
			"copyText": "복사",
			"download": "다운로드",
			"goToCreateMode": "생성 모드로 이동",
			"textPlaceholder": "여기에 입력…",
			"fieldType": {
				"text": "텍스트",
				"image": "이미지",
				"audio": "오디오",
				"video": "비디오",
				"model": "3D 모델",
				"file": "파일"
			}
		},
		"smartIsland": {
			"category": {
				"digitalHuman": "디지털 휴먼",
				"artDesign": "아트 & 디자인",
				"videoAd": "비디오 & 광고",
				"docProcessing": "문서 처리",
				"knowledgeLearning": "지식 학습",
				"musicMV": "음악/MV"
			},
			"enterWorkflowName": "워크플로 이름 입력",
			"saveSuccess": "저장되었습니다!",
			"saveFailed": "저장에 실패했습니다. 다시 시도해 주세요",
			"generateVideo": "비디오 생성",
			"generateAudio": "오디오 생성",
			"generateText": "텍스트 생성",
			"generateImage": "이미지 생성",
			"generateMusic": "음악 생성",
			"generate3D": "3D 생성",
			"detectPose": "포즈",
			"segmentBody": "신체 분할",
			"estimateNormal": "노멀",
			"extractForeground": "매팅",
			"captureMotion": "모션 캡처",
			"firstLastFrameVideo": "첫/마지막 프레임 비디오",
			"confirmExecute": "실행 확인",
			"deployModalWorkers": "Modal 워커 배포",
			"deployModalRunning": "Modal 워커 배포 중...",
			"deployModalSuccess": "Modal 워커가 배포되었습니다",
			"deployModalFailed": "Modal 배포 실패",
			"deployModalUnsupported": "이 작업은 데스크톱 앱에서만 사용할 수 있습니다",
			"saveAndExecute": "저장 후 실행",
			"cancel": "취소",
			"executeConfirmTitle": "워크플로 실행 확인",
			"executeConfirmDescSaved": "실행 전에 워크플로가 최신 상태로 저장됩니다. 실행하시겠습니까?",
			"executeConfirmDescNew": "실행 전에 워크플로를 저장하세요. 생성된 자료는 향후 참조 및 관리를 위해 이 워크플로와 연결됩니다.",
			"executeWorkflow": "워크플로 실행",
			"saveWorkflow": "워크플로 저장",
			"executing": "실행 중...",
			"saving": "저장 중...",
			"estimatedCost": "예상 비용:",
			"estimatedTime": "예상 시간:",
			"workflowName": "워크플로 이름",
			"workflowNamePlaceholder": "워크플로 이름 입력",
			"workflowDescription": "워크플로 설명",
			"workflowDescPlaceholder": "워크플로 설명 입력(선택 사항)",
			"tooltip3D": "3D",
			"tooltipDocument": "문서",
			"tooltipImage": "이미지",
			"tooltipText": "텍스트",
			"tooltipVideo": "비디오",
			"tooltipAudio": "오디오",
			"tooltipLink": "링크",
			"split": "분할",
			"splitText": "스마트 분할",
			"speechRecognize": "음성 인식",
			"separateAudio": "보컬 분리",
			"separateSpeaker": "화자 분리",
			"separateSound": "소리 추출",
			"denoise": "노이즈 제거",
			"convertVoice": "음성 교체",
			"filter": "필터",
			"arrange": "배치",
			"concat": "연결",
			"describeReverse": "설명",
			"upscale": "고화질 업스케일",
			"removeSubtitle": "자막 제거",
			"removeWatermark": "워터마크 제거",
			"extractAudioTrack": "오디오 트랙 추출",
			"removeVideoAudio": "오디오 제거",
			"slice": "슬라이스",
			"firstFrame": "첫 프레임",
			"lastFrame": "마지막 프레임",
			"editImage": "이미지 편집",
			"editVideo": "비디오 편집",
			"parseDocument": "문서 파싱",
			"extractContent": "콘텐츠 추출",
			"mergeGroup": "그룹 병합",
			"imageFusion": "이미지 융합",
			"imagesGenVideo": "참조 이미지로 비디오 생성",
			"refsGenVideo": "옴니 참조 비디오",
			"rewriteText": "텍스트 재작성",
			"textToSpeech": "텍스트 음성 변환",
			"textToSpeechClone": "음성 생성(복제)",
			"textToSpeechPreset": "음성 생성(프리셋)",
			"textToSpeechInstruct": "음성 생성(설명)",
			"motionControl": "모션 제어",
			"videoTransfer": "비디오 트랜스퍼",
			"characterReplace": "캐릭터 교체",
			"merge": "병합",
			"lipSync": "립싱크",
			"lipDub": "립 더빙",
			"cloneVoice": "음성 복제",
			"speechGenVideo": "오디오로 비디오 생성",
			"repaintMusic": "리페인트",
			"coverMusic": "커버",
			"extractStem": "스템 추출",
			"addTrack": "트랙 추가",
			"completeMusic": "편곡 완성",
			"musicBrief": "음악 브리프"
		},
		"toast": {
			"executing": "실행 중...",
			"completed": "완료됨",
			"cancelled": "취소됨",
			"cancelling": "취소 중...",
			"failed": "실패",
			"nodes": "노드",
			"cancelTask": "작업 취소",
			"totalDuration": "총 소요 시간",
			"taskCompleted": "작업 완료",
			"taskFailed": "작업 실패",
			"taskFailedWithMsg": "작업 실패: {error}",
			"level": "레벨 {level}"
		},
		"dialog": {
			"myWorkflows": "내 워크플로",
			"noWorkflows": "워크플로 없음",
			"savedWorkflowsHint": "저장된 워크플로가 여기에 표시됩니다",
			"noMore": "더 이상 워크플로가 없습니다",
			"loadSuccess": "불러왔습니다!",
			"loadFailed": "불러오기에 실패했습니다. 다시 시도해 주세요",
			"open": "열기"
		},
		"menu": {
			"save": "저장",
			"saveAs": "다른 이름으로 저장",
			"clear": "지우기",
			"confirmClear": "현재 워크플로를 지우시겠습니까? 이 작업은 되돌릴 수 없습니다.",
			"cleared": "워크플로가 지워졌습니다",
			"saveAsNew": "새 워크플로로 저장",
			"saveWorkflow": "워크플로 저장",
			"saveNew": "새로 저장",
			"name": "이름",
			"enterName": "워크플로 이름 입력",
			"descOptional": "설명(선택 사항)",
			"enterDesc": "워크플로 설명 입력",
			"saving": "저장 중...",
			"update": "업데이트",
			"cancel": "취소",
			"saveSuccess": "저장되었습니다!",
			"saveFailed": "저장에 실패했습니다. 다시 시도해 주세요",
			"exportJson": "JSON 내보내기",
			"exportWorkflow": "워크플로 내보내기",
			"exportExecutable": "실행 파일 내보내기",
			"importJson": "JSON 가져오기",
			"importJsonInvalid": "파싱할 수 없습니다: 잘못된 JSON 또는 구조입니다.",
			"importJsonNoCanvas": "이 파일에는 캔버스 데이터가 없습니다(루트에 originalFlow, flow 또는 nodes/edges가 필요합니다).",
			"importJsonSuccess": "워크플로를 불러왔습니다(노드 {nodes}개, 엣지 {edges}개)",
			"importJsonReadFailed": "파일을 읽지 못했습니다",
			"exportJsonSuccess": "다운로드가 시작되었습니다",
			"exportJsonFailed": "내보내기에 실패했습니다. 다시 시도해 주세요"
		},
		"nodes": {
			"base": {
				"moreActions": "추가 옵션",
				"execute": "실행",
				"cancelExecution": "실행 취소",
				"removeComment": "댓글 제거",
				"commentPlaceholder": "사용자를 위한 댓글 추가...",
				"clickToAddComment": "클릭하여 댓글 추가...",
				"addComment": "댓글 추가",
				"delete": "삭제",
				"deleteConfirmTitle": "삭제 확인",
				"deleteConfirmDescription": "이 노드에는 {count}개의 다운스트림 노드가 있습니다. 삭제 방법을 선택하세요.",
				"deleteNodeOnly": "이 노드만 삭제",
				"deleteNodeAndChildren": "이 노드와 모든 다운스트림 노드 삭제({count})",
				"deleteChildrenOnly": "다운스트림 노드만 삭제({count})",
				"run": "실행",
				"preview": "미리보기",
				"lock": "노드 잠금",
				"unlock": "노드 잠금 해제",
				"startCombo": "콤보 시작",
				"addToCombo": "콤보에 추가",
				"removeFromCombo": "콤보에서 제거",
				"edit": "편집",
				"cancel": "취소",
				"confirm": "확인",
				"fullscreenEdit": "전체 화면 편집",
				"startVoice": "음성 입력 시작",
				"stopVoice": "음성 입력 중지",
				"editText": "텍스트 편집",
				"select": "선택",
				"selected": "선택됨",
				"modalNotDeployedTitle": "Modal 앱이 배포되지 않았습니다",
				"modalNotDeployedDescription": "\"{feature}\" 기능에는 Modal 앱 \"{app}\"이(가) 필요합니다. 아직 배포되지 않았습니다. 지금 배포하시겠습니까?",
				"modalDeployHint": "1) MODAL_TOKEN_ID / MODAL_TOKEN_SECRET를 설정합니다. 2) plugins/ 디렉터리가 있는지 확인합니다. 서버는 메모리에서 플러그인 주석을 스캔합니다. 3) `python -m modal`이 작동하는지 확인한 후(예: pip install modal) 원클릭 배포를 사용하여 해당 레지스트리의 각 플러그인 deploy.py에 대해 modal deploy를 실행합니다.",
				"modalDeployDismiss": "취소",
				"modalDeployAction": "다운로드 및 배포",
				"modalDeploying": "Modal 워커 배포 중…",
				"modalDeployDone": "배포가 완료되었습니다",
				"modalDeployError": "배포에 실패했습니다",
				"modalDeployingAction": "배포 중…",
				"modalDeployProgressHint": "{done}/{total} 배포됨",
				"pluginImplementationTitle": "구현",
				"pluginModelTitle": "모델",
				"pluginParamsTitle": "고급 설정",
				"pluginParamsReset": "초기화",
				"pluginParamDefault": "기본 {value}",
				"pluginImplementationLoading": "플러그인 구현을 불러오는 중…",
				"pluginImplementationEmpty": "이 기능에 대해 스캔된 플러그인 구현이 없습니다. 저장소의 plugins/ 디렉터리 아래에 Modal 또는 API 플러그인을 추가한 후(README 참조) 이 페이지를 새로 고치세요.",
				"pluginRegistryLoadError": "플러그인 레지스트리를 불러올 수 없습니다: {message}",
				"pluginSelectPlaceholder": "저장소 선택…",
				"missingImplTitle": "구현 누락",
				"missingImplDescription": "실행하기 전에 이 노드에서 플러그인 구현을 선택하세요."
			},
			"common": {
				"enterInstructions": "지시 사항 입력...",
				"manualInput": "입력",
				"library": "라이브러리",
				"addText": "텍스트 추가",
				"enterText": "텍스트 내용 입력...",
				"descOptional": "설명(선택 사항)",
				"fromUpstreamText": "업스트림 텍스트 노드에서",
				"videoDesc": "비디오 설명",
				"aspectRatio": "종횡비",
				"resolution": "해상도",
				"duration": "길이",
				"currentSize": "현재 크기:",
				"currentDuration": "현재 길이:",
				"upscaleTierLabel": "출력 등급(긴 변)",
				"upscaleTierHint": "종횡비는 입력을 따르며 긴 변만 확대됩니다.",
				"upscaleTier1k": "1K · ~1920",
				"upscaleTier2k": "2K · ~2560",
				"upscaleTier4k": "4K · ~3840",
				"seconds": "초",
				"minute": "분",
				"thinkingProcess": "사고 과정",
				"performanceMode": "성능 모드",
				"ecoMode": "💰 절약 모드",
				"proMode": "⚡ 프로 모드",
				"modelSelect": "모델",
				"models": {
					"imageEdit": "FLUX2 Klein 이미지 편집",
					"imageGen": "Z-Image 텍스트-이미지",
					"imageFusion": "FLUX2 Klein 이미지 융합",
					"imageGenVideo": "LTX2 이미지-비디오",
					"features": {
						"gen-text": "기본값(OpenRouter)",
						"gen-text-gemini": "Gemini",
						"gen-text-openai": "OpenAI",
						"split-text": "스마트 분할",
						"combine-text": "텍스트 병합",
						"combine-text-openai": "텍스트 병합(OpenAI)",
						"combine-text-gemini": "텍스트 병합(Gemini)",
						"image-fusion": "FLUX2 Klein 융합",
						"image-gen-text": "이미지 캡션",
						"video-gen-text": "비디오 캡션",
						"transcribe": "음성-텍스트 변환",
						"transcribe-timestamp": "음성-텍스트 변환(타임스탬프 포함)",
						"concat-videos": "비디오 연결",
						"extract-audio": "오디오 트랙 추출",
						"remove-video-audio": "오디오 제거(무음 비디오)",
						"merge-video-audio": "오디오 + 비디오 합성",
						"get-first-frame": "첫 프레임",
						"get-last-frame": "마지막 프레임",
						"parse-document": "문서-텍스트 변환",
						"split-video": "샷별 분할",
						"link": "URL에서 가져오기",
						"gen-video": "텍스트-비디오",
						"image-gen": "Z-Image 텍스트-이미지",
						"gen-music": "ACE-Step 음악",
						"text-gen-speech-preset": "TTS(프리셋 스타일)",
						"image-gen-video": "이미지-비디오",
						"image-edit": "FLUX2 Klein 편집",
						"image-upscale": "이미지 업스케일",
						"video-upscale": "비디오 업스케일",
						"image-describe": "이미지 이해",
						"video-describe": "비디오 이해",
						"audio-describe": "오디오 이해",
						"music-repaint": "음악 리페인트",
						"music-cover": "음악 커버",
						"music-extract": "스템 추출",
						"music-lego": "트랙 추가",
						"music-complete": "편곡 완성",
						"music-brief": "음악 브리프",
						"audio-image-gen-video": "음성+이미지-비디오",
						"speech-text-gen-video": "음성+텍스트-비디오",
						"video-image-gen-video-mix": "비디오+이미지 믹스",
						"video-image-gen-video-move": "비디오+이미지 무브",
						"image-image-gen-video": "이미지+이미지-비디오",
						"text-gen-video": "텍스트-비디오",
						"image-gen-model": "이미지-3D",
						"speech-video-gen-video": "비디오 립 더빙(텍스트+비디오)",
						"audio-video-lip-sync": "오디오+비디오 립싱크",
						"text-gen-speech-clone": "음성 복제 TTS",
						"text-gen-speech-instruct": "지시형 TTS",
						"video-image-move-animal": "비디오+이미지 무브(동물)",
						"video-image-move": "비디오+이미지 무브(사람)",
						"video-image-move-muti": "비디오+이미지 무브(다중)",
						"video-image-move-animal-muti": "비디오+이미지 무브(동물, 다중)",
						"wan-animate-mix": "WAN 애니메이트 믹스",
						"subtitle-remove": "자막 제거",
						"remove-watermark": "워터마크 제거",
						"denoise-audio": "오디오 노이즈 제거",
						"separate-audio-track": "보컬 / 반주",
						"separate-speaker": "화자 분리",
						"convert-voice": "음성 교체",
						"drop-video": "클립 필터링",
						"arrange-group": "배치 정렬"
					}
				},
				"voice": "음성",
				"selectVoice": "음성 선택",
				"previewVoice": "음성 미리듣기",
				"uploadVoice": "음성 업로드",
				"recordVoice": "음성 녹음",
				"voiceClone": "음성 복제",
				"voicePreset": "프리셋 스타일",
				"voiceDescribe": "사용자 정의 설명",
				"gender": "성별",
				"selectGender": "성별 선택(선택 사항)",
				"emotion": "감정",
				"style": "스타일",
				"selectEmotion": "감정 선택(선택 사항)",
				"selectStyle": "스타일 선택(선택 사항)",
				"voiceDescription": "음성 설명",
				"voiceDescriptionPlaceholder": "음성 스타일 설명을 입력하세요. 예: 부드럽고 달콤한 여성 목소리, 천천히 말하기...",
				"referenceAudio": "참조 오디오",
				"noReferenceAudio": "참조 클립을 업로드하거나 녹음하세요(3초 이상)",
				"referenceText": "참조 텍스트",
				"referenceTextPlaceholder": "선택 사항: 참조 오디오의 전사본 — 복제 품질을 향상시킵니다",
				"referenceTextCloneHint": "빠른 x-vector 복제는 비워 두고, 더 높은 품질의 ICL 복제를 위해서는 참조 전사본을 붙여넣으세요",
				"language": "언어",
				"inputText": "입력 텍스트",
				"voiceOptions": {
					"female": "여성 목소리",
					"male": "남성 목소리"
				}
			},
			"titles": {
				"imageGenVideo": "이미지-비디오",
				"textGenVideo": "텍스트-비디오",
				"addText": "텍스트 추가",
				"textGenText": "텍스트 생성",
				"combineText": "텍스트 결합",
				"textGenImage": "텍스트-이미지",
				"textGenSpeechClone": "음성 생성(복제)",
				"textGenSpeechCloneCompose": "음성 복제(텍스트 + 오디오)",
				"imageGenVideoCompose": "이미지-비디오(텍스트 + 이미지)",
				"textGenSpeechPreset": "음성 생성(프리셋)",
				"textGenSpeechInstruct": "음성 생성(설명)",
				"imageFusion": "이미지 융합",
				"videoUpscale": "비디오 업스케일",
				"videoGenText": "비디오 설명",
				"speechRecognize": "음성 인식",
				"textGenMusic": "텍스트-음악",
				"speechGenVideo": "오디오-비디오",
				"convertVoice": "음성 교체",
				"denoiseAudio": "오디오 노이즈 제거",
				"fileGenText": "문서 파서",
				"linkGenText": "링크 추출기",
				"removeSubtitle": "자막 제거",
				"removeWatermark": "워터마크 제거",
				"separateAudio": "오디오 & 비디오 분리",
				"imageGenText": "이미지 설명",
				"audioDescribe": "오디오 이해",
				"imageGenImage": "이미지 편집",
				"videoEdit": "비디오 편집",
				"imageGenModel": "이미지-3D 모델",
				"imagePose": "포즈 감지",
				"imageBodySeg": "신체 부위 분할",
				"imageNormal": "표면 노멀",
				"imageMatting": "인물 매팅",
				"videoGenModel": "비디오 모션 캡처",
				"imageUpscale": "고화질 업스케일",
				"separateTrack": "보컬 추출",
				"separateSpeaker": "화자 분리",
				"separateSound": "소리 분리",
				"speechImageGenVideo": "음성+이미지-비디오",
				"concatVideo": "비디오 연결",
				"mergeVideoAudio": "비디오 오디오 병합",
				"audioVideoLipSync": "비디오 립싱크",
				"speechTextGenVideo": "음성+텍스트-비디오",
				"speechVideoGenVideo": "비디오 립 더빙",
				"imageImageGenVideo": "첫/마지막 프레임 비디오",
				"imagesGenVideo": "이미지-비디오",
				"refsGenVideo": "옴니 참조 비디오",
				"textAudioGenSpeech": "텍스트+오디오-음성",
				"videoImageMix": "비디오+이미지 믹스",
				"videoImageMove": "비디오+이미지 무브",
				"splitVideo": "비디오 분할",
				"splitText": "스마트 분할",
				"getFirstFrame": "첫 프레임",
				"getLastFrame": "마지막 프레임",
				"extractAudioTrack": "오디오 트랙 추출",
				"removeVideoAudio": "오디오 제거(무음 비디오)",
				"musicRepaint": "음악 리페인트",
				"musicCover": "음악 커버",
				"musicExtract": "스템 추출",
				"musicLego": "트랙 추가",
				"musicComplete": "편곡 완성",
				"musicBrief": "음악 브리프"
			},
			"options": {
				"portrait": "세로",
				"landscape": "가로",
				"square": "정사각형",
				"standard": "표준",
				"verticalStandard": "세로 표준",
				"human": "사람",
				"animal": "동물",
				"single": "단일",
				"multi": "다중"
			},
			"actions": {
				"generateVideo": "비디오 생성",
				"generateImage": "이미지 생성",
				"generateSpeech": "음성 생성",
				"startFusion": "융합 시작",
				"convertText": "텍스트로 변환",
				"startUpscale": "업스케일 시작",
				"describeVideo": "비디오 설명",
				"recognizeSpeech": "음성 인식",
				"generateMusic": "음악 생성",
				"generateAudio": "오디오 생성",
				"startReplace": "교체 시작",
				"startDenoise": "노이즈 제거 시작",
				"parseDocument": "문서 파싱",
				"extractContent": "콘텐츠 추출",
				"removeSubtitle": "자막 제거",
				"removeWatermark": "워터마크 제거",
				"compressVideo": "비디오 압축",
				"extractAudio": "분리 시작",
				"describeImage": "이미지 설명",
				"describeAudio": "오디오 설명",
				"editImage": "이미지 편집",
				"editVideo": "비디오 편집",
				"generate3DModel": "3D 모델 생성",
				"detectPose": "포즈 감지",
				"segmentBody": "신체 부위 분할",
				"estimateNormal": "노멀 추정",
				"extractForeground": "전경 추출",
				"captureMotion": "모션 캡처",
				"imageUpscale": "고화질 업스케일",
				"extractVocals": "보컬 추출",
				"startSeparation": "분리 시작",
				"concatVideo": "비디오 연결",
				"startMerge": "병합 시작",
				"lipDub": "립 더빙",
				"motionTrackGenerate": "생성(모션 트랙)",
				"splitVideo": "비디오 분할",
				"splitText": "스마트 분할",
				"getFirstFrame": "첫 프레임 가져오기",
				"getLastFrame": "마지막 프레임 가져오기",
				"extractAudioTrack": "오디오 추출",
				"removeVideoAudio": "오디오 제거",
				"repaintMusic": "리페인트 시작",
				"coverMusic": "커버 생성",
				"extractStem": "분리 시작",
				"separateSound": "분리 시작",
				"addTrack": "트랙 생성",
				"completeMusic": "보완 시작",
				"musicBrief": "브리프 생성"
			},
			"params": {
				"prompt": "프롬프트",
				"model": "모델",
				"modelDefault": "기본값(OpenRouter 무료)",
				"geminiModel": "Gemini 모델",
				"openaiModel": "OpenAI 모델",
				"style": "스타일",
				"sampler": "샘플러",
				"seed": "시드",
				"steps": "스텝",
				"cfg": "CFG 스케일"
			},
			"emotions": {
				"none": "없음",
				"happy": "행복",
				"angry": "분노",
				"sad": "슬픔",
				"fear": "두려움",
				"surprised": "놀람",
				"confusion": "혼란",
				"empathy": "공감",
				"embarrass": "당황",
				"excited": "흥분",
				"depressed": "우울",
				"admiration": "감탄",
				"coldness": "냉담"
			},
			"genders": {
				"none": "없음",
				"male": "남성",
				"female": "여성"
			},
			"styles": {
				"none": "없음",
				"serious": "진지함",
				"arrogant": "거만함",
				"child": "어린이",
				"older": "노년",
				"girl": "소녀",
				"pure": "순수함",
				"sister": "성숙한 여성",
				"sweet": "달콤함",
				"exaggerated": "과장됨",
				"ethereal": "몽환적",
				"whisper": "속삭임",
				"generous": "관대함",
				"recite": "낭독",
				"act_coy": "교태",
				"warm": "따뜻함",
				"shy": "수줍음",
				"comfort": "위로",
				"authority": "권위적",
				"chat": "대화",
				"radio": "라디오",
				"soulful": "감성적",
				"gentle": "부드러움",
				"story": "이야기",
				"vivid": "생생함",
				"program": "진행자",
				"news": "뉴스",
				"advertising": "광고",
				"roar": "포효",
				"murmur": "중얼거림",
				"shout": "외침",
				"deeply": "깊음",
				"loudly": "큰 소리"
			},
			"recorder": {
				"title": "음성 녹음",
				"desc": "음성 합성을 위한 참조로 오디오 클립을 녹음하세요",
				"start": "녹음 시작",
				"stop": "녹음 중지",
				"preview": "미리듣기",
				"retry": "다시 시도",
				"uploading": "업로드 중...",
				"confirm": "확인",
				"cancel": "취소",
				"recording": "녹음 중...",
				"recorded": "녹음 완료",
				"micError": "마이크에 접근할 수 없습니다. 권한을 확인하세요",
				"uploadError": "업로드에 실패했습니다. 다시 시도해 주세요",
				"uploadFailed": "업로드 실패"
			},
			"imageFusion": {
				"loading": "불러오는 중...",
				"imageReference": "이미지 참조",
				"imageReferenceHint": "이미지를 클릭하여 프롬프트에 참조를 삽입하세요",
				"fusionPrompt": "융합 프롬프트",
				"fusionPromptPlaceholder": "융합 지시 사항을 입력하세요. 예: 이미지 1의 스타일 유지, 모든 이미지의 특징 융합, 이미지 2의 색상 강조, 이미지 3의 구도 결합...",
				"imageLabel": "이미지"
			},
			"refsGenVideo": {
				"videoReference": "비디오 참조",
				"videoLabel": "비디오",
				"audioReference": "오디오 참조",
				"audioLabel": "오디오",
				"referenceHint": "이미지 최대 9장, 비디오 3개, 오디오 3개(총 12개 파일). 오디오는 이미지나 비디오와 함께 사용해야 합니다. 썸네일을 클릭하면 프롬프트에 참조가 삽입됩니다.",
				"promptPlaceholder": "생성할 비디오를 설명하세요. 예: 이미지 1의 캐릭터가 오디오 1의 목소리로 말하며 비디오 1의 장면을 이어갑니다..."
			},
			"video": { "fileCount": "처리할 파일:" },
			"speechRecognize": { "withTimestamp": "타임스탬프 포함" },
			"music": {
				"handleStyle": "스타일",
				"handleLyric": "가사",
				"referenceAudio": "참조 오디오",
				"inputLyrics": "가사 입력",
				"audioDuration": "오디오 길이",
				"currentDuration": "현재 길이:",
				"seconds": "초",
				"15s": "15초",
				"30s": "30초",
				"1min": "1분",
				"2min": "2분",
				"styleSettings": "음악 스타일",
				"voiceGender": "음성",
				"genders": {
					"male": "남성",
					"female": "여성"
				},
				"timbre": "음색",
				"timbres": {
					"bright": "밝음",
					"dark": "어두움",
					"warm": "따뜻함",
					"soft": "부드러움",
					"rock": "록",
					"vocal": "보컬"
				},
				"genre": "장르",
				"genres": {
					"pop": "팝",
					"rock": "록",
					"electronic": "일렉트로닉",
					"hiphop": "힙합",
					"jazz": "재즈",
					"blues": "블루스",
					"classical": "클래식",
					"folk": "포크",
					"country": "컨트리",
					"rnb": "R&B",
					"kpop": "K-Pop",
					"reggae": "레게",
					"soul": "소울",
					"dance": "댄스",
					"rap": "랩",
					"classicRock": "클래식 록",
					"hardRock": "하드 록",
					"rockabilly": "로커빌리",
					"dancepop": "댄스 팝",
					"house": "하우스",
					"deephouse": "딥 하우스",
					"experimental": "실험적",
					"experimentalPop": "실험적 팝",
					"popPunk": "팝 펑크",
					"rockAndRoll": "로큰롤",
					"popRock": "팝 록"
				},
				"mood": "분위기",
				"moods": {
					"happy": "행복",
					"sad": "슬픔",
					"romantic": "로맨틱",
					"melancholic": "우울",
					"uplifting": "고양",
					"intense": "강렬함",
					"emotional": "감성적",
					"angry": "분노"
				},
				"instrumentAndRhythm": "악기 & 리듬",
				"instrument": "악기",
				"selectedCount": "{count}개 선택됨",
				"instruments": {
					"piano": "피아노",
					"guitar": "기타",
					"acousticGuitar": "어쿠스틱 기타",
					"electricGuitar": "일렉트릭 기타",
					"drums": "드럼",
					"synthesizer": "신디사이저",
					"violin": "바이올린",
					"saxophone": "색소폰",
					"bass": "베이스",
					"beats": "비트",
					"strings": "현악기",
					"cello": "첼로",
					"trumpet": "트럼펫",
					"harmonica": "하모니카",
					"banjo": "밴조",
					"fiddle": "피들",
					"brass": "금관악기",
					"doubleBass": "더블 베이스"
				},
				"bpm": "BPM",
				"slow": "느림",
				"moderate": "보통",
				"fast": "빠름",
				"songTitle": "곡 제목",
				"songTitlePlaceholder": "곡 제목 입력(선택 사항)",
				"noTagsSelected": "선택된 태그 없음",
				"tagsPlaceholder": "예: 여성, 팝, 밝음, 피아노, 행복, 120bpm",
				"tagsHint": "음악 스타일 태그를 쉼표로 구분하여 입력하세요. 예: 성별, 장르, 음색, 악기, 분위기, BPM",
				"fromUpstreamReadonly": "업스트림에서(읽기 전용)",
				"lyricsPlaceholder": "가사 입력이 연결되지 않은 경우 가사를 입력하세요",
				"language": "언어",
				"keyscale": "키 스케일",
				"auto": "자동"
			},
			"musicTasks": {
				"promptLabel": "캡션",
				"promptPlaceholder": "음악 스타일, 분위기, 악기 등을 설명하세요...",
				"lyricsLabel": "가사",
				"lyricsPlaceholder": "가사(선택)...",
				"startTime": "시작(초)",
				"endTime": "종료(초)",
				"strength": "강도",
				"track": "트랙",
				"tracks": "추가할 트랙",
				"instrumental": "연주곡",
				"ideaPlaceholder": "원하는 곡을 한 문장으로 설명하세요...",
				"sourceAudio": "원곡",
				"refAudio": "참조 오디오",
				"trackOptions": {
					"vocals": "보컬",
					"backing_vocals": "백킹 보컬",
					"drums": "드럼",
					"percussion": "퍼커션",
					"bass": "베이스",
					"guitar": "기타",
					"keyboard": "키보드",
					"synth": "신스",
					"strings": "스트링",
					"brass": "브라스",
					"woodwinds": "목관",
					"fx": "이펙트"
				}
			},
			"speechGenVideo": {
				"loading": "불러오는 중...",
				"inputContent": "입력 내용",
				"audio": "오디오",
				"connectAudioHint": "오디오 노드를 연결하거나 오디오 파일을 업로드하세요",
				"audioPromptLabel": "오디오 프롬프트",
				"audioPromptPlaceholder": "오디오 더빙 프롬프트 입력"
			},
			"convertVoice": {
				"voiceLabel": "음성:",
				"selectVoice": "음성 선택",
				"previewVoice": "음성 미리듣기",
				"uploadVoice": "음성 업로드",
				"recordVoice": "음성 녹음",
				"recordAudio": "오디오 녹음",
				"recordHint": "여기에서 오디오를 녹음할 수 있습니다.",
				"recordNeedImpl": "오디오 녹음 기능 구현이 필요합니다",
				"done": "완료"
			},
			"removeWatermark": { "hint": "Sora 비디오 워터마크를 자동으로 감지하고 제거합니다" },
			"videoGenText": {
				"promptLabel": "사용자 정의 프롬프트",
				"promptPlaceholder": "기본 프롬프트는 비워 두거나 사용자 정의 지시 사항을 입력하세요. 예: 비디오의 대화만 설명..."
			},
			"imageGenText": {
				"promptLabel": "사용자 정의 프롬프트",
				"promptPlaceholder": "기본 프롬프트는 비워 두거나 사용자 정의 지시 사항을 입력하세요. 예: 주요 피사체, 스타일 또는 이미지 내 텍스트 설명..."
			},
			"audioDescribe": {
				"promptLabel": "커스텀 프롬프트",
				"promptPlaceholder": "비워 두면 기본 프롬프트를 사용합니다. 장르, 분위기, 악기, 보컬 설명 등 커스텀 지시를 입력할 수도 있습니다…"
			},
			"textGenText": { "instructions": "지시 사항" },
			"lipDub": {
				"targetDialogue": "대상 대화(프롬프트)",
				"targetDialoguePlaceholder": "캐릭터 설명과 대상 언어 대사를 포함한 전체 프롬프트(모델은 번역하지 않습니다)",
				"promptHint": "출력 해상도는 참조 비디오를 따릅니다. 전체 프롬프트를 사용하세요(캐릭터 + 대상 언어 대사, 자동 번역 없음). 원본 클립 길이에 맞추고 단일 화자만 지원합니다."
			},
			"lipSync": {
				"scenePrompt": "장면 설명(선택 사항)",
				"scenePromptPlaceholder": "샷과 화자를 설명하세요. 립싱크는 연결된 오디오를 따릅니다 — 여기에 전체 대화를 붙여넣지 마세요.",
				"audioDrivenHint": "입 움직임과 오디오는 오디오 입력에서 가져옵니다. 오디오 길이를 참조 비디오에 가깝게 유지하세요. 기본 출력은 720p입니다."
			},
			"motionTrack": {
				"connectHint": "첫 프레임 이미지, 참조 비디오(모션이 자동으로 추적되어 전송됨), 텍스트 프롬프트(외형/스타일만)를 연결하세요.",
				"controlVideo": "참조 비디오",
				"promptPlaceholder": "외형과 스타일을 설명하세요 — 모션은 참조 비디오에서 추출됩니다.",
				"promptHint": "외형/스타일만 설명하세요. 모션은 참조 비디오에서 자동으로 추출됩니다. 길이 슬라이더는 참조 클립 길이를 제한합니다.",
				"missing": "추가로 필요함: {items}"
			},
			"imageEdit": {
				"performanceMode": "성능 모드",
				"ecoMode": "💰 절약(FLUX.2 Klein 9B)",
				"proMode": "⚡ 프로 모드",
				"editInstruction": "편집 지시 사항",
				"fromUpstream": "(업스트림 노드에서)",
				"editPlaceholder": "이미지 편집 지시 사항을 입력하세요. 예: 대비 증가, 색온도를 따뜻하게 조정, 디테일 강화"
			},
			"videoEdit": {
				"editInstruction": "편집 지시 사항",
				"fromUpstream": "(업스트림 노드에서)",
				"editPlaceholder": "비디오 편집 지시 사항을 입력하세요. 예: 자막 제거, 수채화 스타일 적용, 하늘을 노을로 변경",
				"connectTextVideo": "텍스트 노드와 비디오 노드를 연결하세요"
			},
			"compose": {
				"loading": "불러오는 중...",
				"mediaFiles": "미디어 파일",
				"image": "이미지",
				"audio": "오디오",
				"video": "비디오",
				"text": "텍스트",
				"noText": "텍스트 없음",
				"inputContent": "입력 내용",
				"videoFiles": "비디오 파일",
				"connectImageAudio": "이미지와 오디오 파일을 연결하세요",
				"connectVideo": "비디오 파일을 하나 이상 연결하세요",
				"connectImageVideoAudio": "이미지, 비디오, 오디오 파일을 연결하세요",
				"connectTextAudio": "텍스트 내용과 오디오 파일을 연결하세요",
				"connectTextReferenceAudio": "텍스트 노드와 오디오 노드(참조 음성)를 연결하세요",
				"connectTextImage": "텍스트 노드와 이미지 노드를 연결하세요",
				"connectTextVideoNode": "비디오 노드와 텍스트 노드를 연결하세요(대상 대화는 프롬프트에 입력)",
				"referenceAudio": "참조 오디오",
				"referenceTextOptional": "참조 전사본(선택 사항, 복제 품질 향상)",
				"connectVideoAudio": "비디오와 오디오 파일을 연결하세요",
				"generatePromptLabel": "프롬프트 생성",
				"generatePromptPlaceholder": "내용, 스타일, 동작 등을 설명하는 비디오 생성 프롬프트를 입력하세요(선택 사항)",
				"inputText": "입력 텍스트",
				"inputTextPlaceholder": "텍스트 내용 입력",
				"describeVideo": "생성하려는 비디오를 설명하세요...",
				"firstFrame": "첫 프레임",
				"lastFrame": "마지막 프레임",
				"connectTwoImages": "두 개의 이미지를 연결하세요(첫 프레임과 마지막 프레임)",
				"inputData": "입력 데이터",
				"connectTextAudioNode": "텍스트와 오디오 노드를 연결하세요",
				"emotion": "감정:",
				"style": "스타일:",
				"selectEmotion": "감정 스타일 선택(선택 사항)",
				"selectStyle": "음성 스타일 선택(선택 사항)",
				"lipSync": "립싱크",
				"connectImageVideo": "이미지와 비디오 파일을 연결하세요",
				"startSlice": "슬라이스 시작"
			},
			"modal": {
				"audioPreview": "오디오 미리듣기",
				"audio": "오디오",
				"audios": "오디오 ({count})",
				"loading": "불러오는 중...",
				"unavailable": "콘텐츠를 사용할 수 없습니다",
				"more": "더보기",
				"moreOptions": "추가 옵션",
				"actions": "작업",
				"textPreview": "텍스트 미리보기",
				"text": "텍스트",
				"texts": "텍스트 ({count})",
				"imagePreview": "이미지 미리보기",
				"image": "이미지",
				"images": "이미지 ({count})",
				"videoPreview": "비디오 미리보기",
				"video": "비디오",
				"videos": "비디오 ({count})",
				"file": "파일",
				"files": "파일 ({count})",
				"link": "링크",
				"links": "링크 ({count})",
				"fullScreenPreview": "전체 화면 미리보기",
				"fullScreenWaterfall": "전체 화면 워터폴 미리보기",
				"model3DPreview": "3D 모델 미리보기",
				"model3D": "3D 모델",
				"models3D": "3D 모델 ({count})",
				"resetView": "뷰 재설정",
				"playAnimation": "애니메이션 재생",
				"pauseAnimation": "애니메이션 일시정지",
				"downloadModel": "모델 다운로드",
				"loading3DModel": "3D 모델 불러오는 중...",
				"errorLoadingModel": "모델 불러오기 오류",
				"dragToRotate": "왼쪽 클릭 + 드래그로 회전 | 스크롤로 확대/축소 | 재설정 클릭으로 중앙 정렬",
				"noModelLoaded": "불러온 3D 모델이 없습니다",
				"loadingPreview": "미리보기 불러오는 중...",
				"unsupportedFormat": "지원되지 않는 형식",
				"supportedFormats": "지원됨",
				"download": "다운로드",
				"textEditor": "텍스트 편집기",
				"enterText": "텍스트 내용 입력..."
			},
			"add": {
				"addAudio": "오디오 추가",
				"addImage": "이미지 추가",
				"addVideo": "비디오 추가",
				"addText": "텍스트 추가",
				"addFile": "파일 추가",
				"addModel": "3D 모델 추가",
				"addLink": "링크 추가",
				"record": "녹음",
				"recordVideo": "비디오 녹화",
				"upload": "업로드",
				"library": "라이브러리",
				"recorded": "녹화됨",
				"uploading": "업로드 중",
				"confirmAndUpload": "확인 및 업로드",
				"dropToUpload": "파일을 놓아 업로드",
				"dragOrClick": "여기에 파일을 드래그하거나 클릭하여 업로드",
				"supportedAudioFormats": "MP3, WAV, OGG, M4A 지원",
				"supportedImageFormats": "일반적인 이미지 형식 지원",
				"supportedVideoFormats": "MP4, MOV, WEBM 지원",
				"browseFiles": "파일 찾아보기",
				"canvas": "캔버스",
				"camera": "카메라",
				"openCanvas": "캔버스 열기",
				"canvasDrawing": "캔버스 그리기",
				"canvasDrawingHint": "아래 캔버스에 그린 후 완료를 클릭하여 저장하세요",
				"clearCanvas": "캔버스 지우기",
				"finishAndSave": "완료 및 저장",
				"clickToStartCamera": "클릭하여 카메라 시작",
				"takePhoto": "사진 촬영",
				"closeCamera": "카메라 닫기",
				"cameraError": "카메라에 접근할 수 없습니다",
				"cameraErrorHint": "다음을 확인하세요: 1. HTTPS 또는 localhost 사용 2. 카메라 권한 허용 3. 카메라가 다른 앱에서 사용 중이 아님",
				"inputText": "입력 텍스트",
				"textPlaceholder": "텍스트 내용을 입력하세요...",
				"generate": "확인",
				"linkPlaceholder": "링크를 입력하세요",
				"extractContent": "콘텐츠 추출",
				"cancel": "취소",
				"startRecordingVideo": "비디오 녹화 시작",
				"addDocument": "문서 추가",
				"supportedDocFormats": "다양한 문서 형식 지원",
				"dragModel": "여기에 3D 모델 파일을 드래그하거나 클릭하여 업로드",
				"supportedModelFormats": "지원되는 형식",
				"modelHint": "팁: 다음 3D 파일 형식을 지원합니다:"
			},
			"batch": {
				"arrangeGroup": "그룹 배치",
				"startArrange": "배치 시작",
				"describeRequirements": "요구 사항을 설명하세요...",
				"groupCount": "그룹 수",
				"allowDuplicate": "중복 허용",
				"videoFilter": "비디오 필터",
				"startFilter": "필터 시작"
			},
			"voice": {
				"timbre": "음성:",
				"selectTimbre": "음성 선택",
				"previewTimbre": "현재 음성 미리듣기",
				"uploadTimbre": "음성 업로드",
				"recordTimbre": "음성 녹음",
				"emotion": "감정:",
				"replaceTimbre": "음성 교체"
			},
			"separateSound": {
				"textLabel": "추출할 소리",
				"textPlaceholder": "예: 개 짖는 소리, 피아노, 배경 사이렌"
			}
		}
	},
	Upload: {
		"failed": "업로드에 실패했습니다. 나중에 다시 시도해 주세요",
		"fileFailed": "파일 \"{name}\" 업로드에 실패했습니다: {reason}",
		"partialFailed": "일부 또는 모든 파일 업로드에 실패했습니다",
		"maxFilesExceeded": "최대 {max}개의 파일만 허용됩니다",
		"fileTooLarge": "파일 \"{name}\"이(가) 너무 큽니다(최대 {max}MB)",
		"fileTypeNotAccepted": "파일 \"{name}\" 유형이 허용되지 않습니다",
		"readImageFailed": "이미지 정보를 읽지 못했습니다",
		"readVideoFailed": "비디오 정보를 읽지 못했습니다"
	},
	LibInput: {
		"deleteSuccess": "자료가 삭제되었습니다",
		"deleteFailed": "삭제에 실패했습니다",
		"emptyText": "빈 텍스트",
		"loadFailed": "자료를 불러오지 못했습니다",
		"added": "자료가 추가되었습니다",
		"retry": "다시 시도",
		"emptyMaterial": "아직 {type} 자료가 없습니다",
		"typeImage": "이미지",
		"typeVideo": "비디오",
		"typeAudio": "오디오",
		"typeText": "텍스트",
		"typeFile": "파일",
		"typeModel": "모델",
		"favoriteHint": "노드의 ❤️ 아이콘을 클릭하여 자료를 즐겨찾기에 추가하세요",
		"portfolio": "포트폴리오"
	},
	Recorder: {
		"audioStart": "녹음 시작",
		"audioCancel": "취소",
		"audioCancelTooltip": "녹음 취소",
		"audioFinish": "완료",
		"audioFinishTooltip": "녹음 완료",
		"audioPermissionError": "마이크에 접근할 수 없습니다. 권한을 허용해 주세요.",
		"videoPermissionError": "카메라 또는 마이크에 접근할 수 없습니다. 권한을 허용해 주세요.",
		"videoStart": "녹화 시작",
		"videoStop": "녹화 중지"
	},
	Languages: {
		"auto": "자동",
		"zh": "중국어",
		"ja": "일본어",
		"yue": "광둥어",
		"en": "영어",
		"ko": "한국어",
		"fr": "프랑스어",
		"es": "스페인어",
		"de": "독일어",
		"ru": "러시아어",
		"pt": "포르투갈어",
		"it": "이탈리아어"
	},
	Errors: {
		"somethingWentWrong": "문제가 발생했습니다",
		"tryAgain": "다시 시도",
		"details": "세부 정보",
		"copy": "복사",
		"copied": "복사됨",
		"close": "닫기",
		"openModalBilling": "Modal 결제 설정 열기",
		"needHelp": "해결이 안 되면 커뮤니티로 오세요:",
		"wechatGroup": "WeChat 그룹",
		"wechatQrAlt": "WeChat 그룹 QR 코드"
	},
	Api: {
		"unknownError": "알 수 없는 오류",
		"unauthorized": "인증되지 않음",
		"accessDenied": "접근 거부됨",
		"requestFailed": "요청 실패: {status} {statusText}",
		"success": "성공",
		"timedOut": "요청 시간이 초과되었습니다. 다시 시도해 주세요"
	}
};
//#endregion
//#region src/canvas/i18n/messages/zh.json
var zh_default = {
	Workspace: {
		"edges": {
			"deleteConfirmTitle": "删除连线？",
			"deleteConfirmDescription": "这条连线没有连接到任何节点，是否删除它？",
			"delete": "删除",
			"cancel": "取消"
		},
		"handles": {
			"text": "文本",
			"image": "图片",
			"image2": "图片 2",
			"images": "图片",
			"end_image": "尾帧图片",
			"video": "视频",
			"videos": "视频",
			"audio": "音频",
			"speech": "语音",
			"ref_audio": "参考音频",
			"ref_text": "参考文本",
			"document": "文档",
			"byFeature": { "image-image-gen-video": {
				"image": "首帧图片",
				"end_image": "尾帧图片"
			} }
		},
		"modeSwitch": {
			"createMode": "创作模式",
			"executeMode": "执行模式",
			"appMode": "应用模式",
			"upgradeRequired": "升级订阅以解锁执行模式"
		},
		"appView": {
			"inputsTitle": "输入",
			"outputsTitle": "结果",
			"run": "运行",
			"cancel": "停止",
			"stepProgress": "第 {current}/{total} 步",
			"completed": "已完成",
			"failed": "运行失败",
			"emptyWorkflow": "画布还是空的，先去搭一个工作流吧",
			"invalidWorkflow": "工作流存在循环连接，无法运行",
			"nothingToRun": "这个工作流还没有可运行的步骤",
			"noOutputsYet": "运行后结果会显示在这里",
			"dropFilesHere": "拖拽文件到这里，或点击上传",
			"copyText": "复制",
			"download": "下载",
			"goToCreateMode": "去创作模式",
			"textPlaceholder": "在这里输入…",
			"fieldType": {
				"text": "文本",
				"image": "图片",
				"audio": "音频",
				"video": "视频",
				"model": "3D 模型",
				"file": "文件"
			}
		},
		"smartIsland": {
			"category": {
				"digitalHuman": "数字人",
				"artDesign": "艺术设计",
				"videoAd": "影视广告",
				"docProcessing": "文档处理",
				"knowledgeLearning": "知识学习",
				"musicMV": "音乐/MV"
			},
			"enterWorkflowName": "请输入工作流名称",
			"saveSuccess": "保存成功！",
			"saveFailed": "保存失败，请重试",
			"generateVideo": "生成视频",
			"generateAudio": "生成语音",
			"generateText": "生成文本",
			"generateImage": "生成图片",
			"generateMusic": "生成音乐",
			"generate3D": "生成3D",
			"detectPose": "姿态",
			"segmentBody": "人体分割",
			"estimateNormal": "法线",
			"extractForeground": "抠图",
			"captureMotion": "动作捕捉",
			"firstLastFrameVideo": "首尾帧生成视频",
			"confirmExecute": "确认执行",
			"deployModalWorkers": "部署 Modal Worker",
			"deployModalRunning": "正在部署 Modal worker...",
			"deployModalSuccess": "Modal worker 部署完成",
			"deployModalFailed": "Modal 部署失败",
			"deployModalUnsupported": "仅桌面版支持此操作",
			"saveAndExecute": "保存并执行",
			"cancel": "取消",
			"executeConfirmTitle": "确认执行工作流",
			"executeConfirmDescSaved": "执行前会保存当前工作流的最新状态，确认执行吗？",
			"executeConfirmDescNew": "执行前需要先保存工作流。保存后生成的素材将关联到此工作流，方便后续追溯和管理。",
			"executeWorkflow": "执行工作流",
			"saveWorkflow": "保存工作流",
			"executing": "执行中...",
			"saving": "保存中...",
			"estimatedCost": "预估费用：",
			"estimatedTime": "预估用时：",
			"workflowName": "工作流名称",
			"workflowNamePlaceholder": "请输入工作流名称",
			"workflowDescription": "工作流描述",
			"workflowDescPlaceholder": "请输入工作流描述（可选）",
			"tooltip3D": "3D",
			"tooltipDocument": "文档",
			"tooltipImage": "图片",
			"tooltipText": "文本",
			"tooltipVideo": "视频",
			"tooltipAudio": "音频",
			"tooltipLink": "链接",
			"split": "打散",
			"splitText": "智能拆分",
			"speechRecognize": "语音识别",
			"separateAudio": "人声分离",
			"separateSpeaker": "说话人分离",
			"separateSound": "提取声音",
			"denoise": "降噪",
			"convertVoice": "人声替换",
			"filter": "筛选",
			"arrange": "排列组合",
			"concat": "拼接",
			"describeReverse": "反推描述",
			"upscale": "高清放大",
			"removeSubtitle": "去字幕",
			"removeWatermark": "去水印",
			"extractAudioTrack": "提取音轨",
			"removeVideoAudio": "去除音频",
			"slice": "切片",
			"firstFrame": "首帧截图",
			"lastFrame": "尾帧截图",
			"editImage": "编辑图片",
			"editVideo": "编辑视频",
			"parseDocument": "解析文档",
			"extractContent": "提取内容",
			"mergeGroup": "合为一组",
			"imageFusion": "图片融合",
			"imagesGenVideo": "参考生视频",
			"refsGenVideo": "全参考生视频",
			"rewriteText": "文本改写",
			"textToSpeech": "转换为语音",
			"textToSpeechClone": "生成语音(克隆音色)",
			"textToSpeechPreset": "生成语音(预设音色)",
			"textToSpeechInstruct": "生成语音(描述音色)",
			"motionControl": "动作控制",
			"videoTransfer": "动作迁移",
			"characterReplace": "人物替换",
			"merge": "合并",
			"lipSync": "视频对口型",
			"lipDub": "配音对口型",
			"cloneVoice": "克隆语音",
			"speechGenVideo": "音频生成视频",
			"repaintMusic": "重绘片段",
			"coverMusic": "翻唱改风格",
			"extractStem": "分离音轨",
			"addTrack": "加轨",
			"completeMusic": "补全编曲",
			"musicBrief": "音乐企划"
		},
		"toast": {
			"executing": "执行中...",
			"completed": "执行完成",
			"cancelled": "已取消",
			"cancelling": "取消中...",
			"failed": "执行失败",
			"nodes": "节点",
			"cancelTask": "取消任务",
			"totalDuration": "总耗时",
			"taskCompleted": "任务完成",
			"taskFailed": "任务失败",
			"taskFailedWithMsg": "任务失败：{error}",
			"level": "层级 {level}"
		},
		"dialog": {
			"myWorkflows": "我的工作流",
			"noWorkflows": "暂无工作流",
			"savedWorkflowsHint": "保存的工作流将在这里展示",
			"noMore": "没有更多了",
			"loadSuccess": "加载成功！",
			"loadFailed": "加载失败，请重试",
			"open": "打开"
		},
		"menu": {
			"save": "保存",
			"saveAs": "另存为",
			"clear": "清空",
			"confirmClear": "确定要清空当前工作流吗？此操作无法撤销。",
			"cleared": "已清空工作流",
			"saveAsNew": "另存为新工作流",
			"saveWorkflow": "保存工作流",
			"saveNew": "保存为新工作流",
			"name": "名称",
			"enterName": "输入工作流名称",
			"descOptional": "描述（可选）",
			"enterDesc": "输入工作流描述",
			"saving": "保存中...",
			"update": "更新",
			"cancel": "取消",
			"saveSuccess": "保存成功！",
			"saveFailed": "保存失败，请重试",
			"exportJson": "导出 JSON",
			"exportWorkflow": "导出工作流",
			"exportExecutable": "导出可执行文件",
			"importJson": "导入 JSON",
			"importJsonInvalid": "无法解析：不是有效的 JSON 或结构不正确。",
			"importJsonNoCanvas": "该文件缺少画布数据（需要 originalFlow、flow 或根级 nodes/edges）。",
			"importJsonSuccess": "已载入当前工作流（{nodes} 个节点，{edges} 条连线）",
			"importJsonReadFailed": "读取文件失败",
			"exportJsonSuccess": "已开始下载",
			"exportJsonFailed": "导出失败，请重试"
		},
		"nodes": {
			"base": {
				"moreActions": "更多操作",
				"execute": "执行",
				"cancelExecution": "取消执行",
				"removeComment": "移除注释",
				"commentPlaceholder": "为用户添加注释...",
				"clickToAddComment": "点击添加注释...",
				"addComment": "添加注释",
				"delete": "删除",
				"deleteConfirmTitle": "确认删除",
				"deleteConfirmDescription": "该节点有 {count} 个下游节点，请选择删除方式。",
				"deleteNodeOnly": "仅删除当前节点",
				"deleteNodeAndChildren": "删除当前节点及所有下游节点（{count}个）",
				"deleteChildrenOnly": "仅删除所有下游节点（{count}个）",
				"run": "运行",
				"preview": "预览",
				"lock": "锁定节点",
				"unlock": "解锁节点",
				"startCombo": "开始组合",
				"addToCombo": "添加到组合",
				"removeFromCombo": "移出组合",
				"edit": "编辑",
				"cancel": "取消",
				"confirm": "确定",
				"fullscreenEdit": "全屏编辑",
				"startVoice": "开始语音输入",
				"stopVoice": "停止语音输入",
				"editText": "编辑文本",
				"select": "选择",
				"selected": "已选",
				"modalNotDeployedTitle": "未检测到 Modal 部署",
				"modalNotDeployedDescription": "功能「{feature}」需要 Modal App「{app}」。当前未检测到对应部署，是否现在下载/部署？",
				"modalDeployHint": "1) 配置 MODAL_TOKEN_ID / MODAL_TOKEN_SECRET；2) 确保本地存在 plugins/，服务端会在内存中扫描插件注解生成注册表；3) 确保本机可执行 `python -m modal`（如 pip install modal），再一键部署会根据该注册表对各个插件的 deploy.py 执行 modal deploy。完成后可点「重试」。",
				"modalDeployDismiss": "取消",
				"modalDeployAction": "下载并部署",
				"modalDeploying": "正在部署 Modal Workers…",
				"modalDeployDone": "部署完成",
				"modalDeployError": "部署失败",
				"modalDeployingAction": "部署中…",
				"modalDeployProgressHint": "已部署 {done}/{total}",
				"pluginImplementationTitle": "实现",
				"pluginModelTitle": "模型",
				"pluginParamsTitle": "高级参数",
				"pluginParamsReset": "重置",
				"pluginParamDefault": "默认 {value}",
				"pluginImplementationLoading": "正在加载插件实现列表…",
				"pluginImplementationEmpty": "未扫描到适用于该能力的插件实现。请在仓库的 plugins/ 目录下添加 Modal 或 API 插件（见 README），然后刷新页面。",
				"pluginRegistryLoadError": "无法加载插件注册表：{message}",
				"pluginSelectPlaceholder": "选择一个仓库…",
				"missingImplTitle": "未配置实现",
				"missingImplDescription": "该节点尚未安装可用插件（或未选择）。请先在插件管理中安装一个插件，或选择一个已安装的插件，然后再运行。"
			},
			"common": {
				"enterInstructions": "请输入指令...",
				"manualInput": "输入",
				"library": "素材库",
				"addText": "添加文本",
				"enterText": "输入文本内容...",
				"descOptional": "补充描述（可选）",
				"fromUpstreamText": "来自上游文本节点",
				"videoDesc": "视频描述",
				"aspectRatio": "宽高比",
				"resolution": "分辨率",
				"duration": "时长",
				"currentSize": "当前尺寸:",
				"currentDuration": "当前时长:",
				"upscaleTierLabel": "放大档位（输出长边）",
				"upscaleTierHint": "宽高比与输入一致，仅将长边放大至对应量级。",
				"upscaleTier1k": "1K · ~1920",
				"upscaleTier2k": "2K · ~2560",
				"upscaleTier4k": "4K · ~3840",
				"seconds": "秒",
				"minute": "分钟",
				"thinkingProcess": "思考过程",
				"performanceMode": "性能模式",
				"ecoMode": "💰 性价比模式",
				"proMode": "⚡ 高性能模式",
				"modelSelect": "模型",
				"models": {
					"imageEdit": "FLUX2 Klein 图片编辑",
					"imageGen": "Z-Image 文生图",
					"imageFusion": "FLUX2 Klein 图片融合",
					"imageGenVideo": "LTX2 图生视频",
					"features": {
						"gen-text": "默认（OpenRouter）",
						"gen-text-gemini": "Gemini",
						"gen-text-openai": "OpenAI",
						"split-text": "智能分段",
						"combine-text": "合并文本",
						"combine-text-openai": "合并文本（OpenAI）",
						"combine-text-gemini": "合并文本（Gemini）",
						"image-fusion": "FLUX2 Klein 融合",
						"image-gen-text": "图像描述",
						"video-gen-text": "视频描述",
						"transcribe": "语音转文字",
						"transcribe-timestamp": "语音转文字（带时间戳）",
						"concat-videos": "拼接视频",
						"extract-audio": "提取音轨",
						"remove-video-audio": "去除音频（无声视频）",
						"merge-video-audio": "音视频合并",
						"get-first-frame": "首帧",
						"get-last-frame": "尾帧",
						"parse-document": "文档解析",
						"split-video": "分镜切段",
						"link": "链接抓取",
						"gen-video": "文生视频",
						"image-gen": "Z-Image 文生图",
						"gen-music": "ACE-Step 音乐",
						"text-gen-speech-preset": "语音合成（预设风格）",
						"image-gen-video": "图生视频",
						"image-edit": "FLUX2 Klein 编辑",
						"image-upscale": "图像超分",
						"video-upscale": "视频超分",
						"image-describe": "图像理解",
						"video-describe": "视频理解",
						"audio-describe": "音频理解",
						"music-repaint": "音乐重绘",
						"music-cover": "音乐翻唱",
						"music-extract": "分离音轨",
						"music-lego": "添加乐轨",
						"music-complete": "补全编曲",
						"music-brief": "音乐企划",
						"audio-image-gen-video": "语音+图生视频",
						"speech-text-gen-video": "语音+文生视频",
						"video-image-gen-video-mix": "视频+图混合",
						"video-image-gen-video-move": "视频+图驱动",
						"image-image-gen-video": "图+图生视频",
						"text-gen-video": "文生视频",
						"image-gen-model": "图生3D",
						"speech-video-gen-video": "视频+台词配音对口型",
						"audio-video-lip-sync": "音频+视频对口型",
						"text-gen-speech-clone": "音色克隆",
						"text-gen-speech-instruct": "指令语音",
						"video-image-move-animal": "视频+图驱动（动物）",
						"video-image-move": "视频+图驱动（人物）",
						"video-image-move-muti": "视频+图驱动（多人）",
						"video-image-move-animal-muti": "视频+图驱动（动物，多人）",
						"wan-animate-mix": "WAN 混合动画",
						"subtitle-remove": "去字幕",
						"remove-watermark": "去水印",
						"denoise-audio": "降噪",
						"separate-audio-track": "人声伴奏分离",
						"separate-speaker": "说话人分离",
						"convert-voice": "音色替换",
						"drop-video": "筛选片段",
						"arrange-group": "批量编排"
					}
				},
				"voice": "音色",
				"selectVoice": "请选择音色",
				"previewVoice": "试听当前音色",
				"uploadVoice": "上传音色",
				"recordVoice": "录制音色",
				"voiceClone": "声音克隆",
				"voicePreset": "预设风格",
				"voiceDescribe": "自定义描述",
				"gender": "性别",
				"selectGender": "选择性别（可选）",
				"emotion": "情感",
				"style": "风格",
				"selectEmotion": "选择情感风格（可选）",
				"selectStyle": "选择语音风格（可选）",
				"voiceDescription": "声音描述",
				"voiceDescriptionPlaceholder": "请输入声音风格描述，例如：温柔甜美的女声，语速稍慢...",
				"referenceAudio": "参考音频",
				"noReferenceAudio": "请上传或录制一段参考音频（≥3 秒）",
				"referenceText": "参考文本",
				"referenceTextPlaceholder": "可选：参考音频的转写文本，有助于提升克隆质量",
				"referenceTextCloneHint": "不填则使用 x-vector 快速克隆；填写参考音频转写文本可启用更高质量的 ICL 克隆",
				"language": "语言",
				"inputText": "输入文本",
				"voiceOptions": {
					"female": "普通女声",
					"male": "普通男声"
				}
			},
			"titles": {
				"imageGenVideo": "图片生成视频",
				"textGenVideo": "文本生成视频",
				"addText": "添加文本",
				"textGenText": "文本生成文本",
				"combineText": "合并文本",
				"textGenImage": "文本生成图片",
				"textGenSpeechClone": "生成语音(克隆音色)",
				"textGenSpeechCloneCompose": "音色克隆（文本+参考音频）",
				"imageGenVideoCompose": "图生视频（文本+图片）",
				"textGenSpeechPreset": "生成语音(预设音色)",
				"textGenSpeechInstruct": "生成语音(描述音色)",
				"imageFusion": "图片融合",
				"videoUpscale": "视频高清放大",
				"videoGenText": "反推描述",
				"speechRecognize": "语音识别",
				"textGenMusic": "文本生成音乐",
				"speechGenVideo": "音频生成视频",
				"convertVoice": "人声替换",
				"denoiseAudio": "音频降噪",
				"fileGenText": "文档解析",
				"linkGenText": "链接提取",
				"removeSubtitle": "消除字幕",
				"removeWatermark": "去除水印",
				"separateAudio": "音视频分离",
				"imageGenText": "反推描述",
				"audioDescribe": "音频理解",
				"imageGenImage": "图片编辑",
				"videoEdit": "视频编辑",
				"imageGenModel": "图片生成3D模型",
				"imagePose": "姿态检测",
				"imageBodySeg": "人体部位分割",
				"imageNormal": "表面法线",
				"imageMatting": "人像抠图",
				"videoGenModel": "视频动作捕捉",
				"imageUpscale": "高清放大",
				"separateTrack": "人声提取",
				"separateSpeaker": "说话人分离",
				"separateSound": "声音分离",
				"speechImageGenVideo": "语音图片生成视频",
				"concatVideo": "视频拼接",
				"mergeVideoAudio": "音视频合并",
				"audioVideoLipSync": "视频对口型",
				"speechTextGenVideo": "语音文本生成视频",
				"speechVideoGenVideo": "视频配音对口型",
				"imageImageGenVideo": "首尾帧生成视频",
				"imagesGenVideo": "多图参考生视频",
				"refsGenVideo": "全模态参考生视频",
				"textAudioGenSpeech": "文本音频生成语音",
				"videoImageMix": "视频图片混合",
				"videoImageMove": "视频图片迁移",
				"splitVideo": "视频分割",
				"splitText": "智能拆分",
				"getFirstFrame": "首帧截图",
				"getLastFrame": "尾帧截图",
				"extractAudioTrack": "提取音轨",
				"removeVideoAudio": "去除音频（无声视频）",
				"musicRepaint": "音乐重绘",
				"musicCover": "音乐翻唱",
				"musicExtract": "分离音轨",
				"musicLego": "添加乐轨",
				"musicComplete": "补全编曲",
				"musicBrief": "音乐企划"
			},
			"options": {
				"portrait": "竖屏",
				"landscape": "宽屏",
				"square": "正方形",
				"standard": "标准",
				"verticalStandard": "竖标",
				"human": "人物",
				"animal": "动物",
				"single": "单人",
				"multi": "多人"
			},
			"actions": {
				"generateVideo": "生成视频",
				"generateImage": "生成图片",
				"generateSpeech": "生成语音",
				"startFusion": "开始融合",
				"convertText": "转换文本",
				"startUpscale": "开始放大",
				"describeVideo": "描述视频",
				"recognizeSpeech": "识别语音",
				"generateMusic": "生成音乐",
				"generateAudio": "生成音频",
				"startReplace": "开始替换",
				"startDenoise": "开始降噪",
				"parseDocument": "解析文档",
				"extractContent": "提取内容",
				"removeSubtitle": "消除字幕",
				"removeWatermark": "去除水印",
				"compressVideo": "压缩视频",
				"extractAudio": "开始分离",
				"describeImage": "描述图片",
				"describeAudio": "描述音频",
				"editImage": "编辑图片",
				"editVideo": "编辑视频",
				"generate3DModel": "生成3D模型",
				"detectPose": "检测姿态",
				"segmentBody": "分割人体部位",
				"estimateNormal": "估计法线",
				"extractForeground": "提取前景",
				"captureMotion": "捕捉动作",
				"imageUpscale": "高清放大",
				"extractVocals": "提取人声",
				"startSeparation": "开始分离",
				"concatVideo": "拼接视频",
				"startMerge": "开始合并",
				"lipDub": "配音对口型",
				"motionTrackGenerate": "按轨迹生成",
				"splitVideo": "分割视频",
				"splitText": "智能拆分",
				"getFirstFrame": "截取首帧",
				"getLastFrame": "截取尾帧",
				"extractAudioTrack": "提取音轨",
				"removeVideoAudio": "去除音频",
				"repaintMusic": "开始重绘",
				"coverMusic": "生成翻唱",
				"extractStem": "开始分离",
				"separateSound": "开始分离",
				"addTrack": "生成乐轨",
				"completeMusic": "开始补全",
				"musicBrief": "生成企划"
			},
			"params": {
				"prompt": "提示词",
				"model": "模型",
				"modelDefault": "默认 (OpenRouter 免费路由)",
				"geminiModel": "Gemini 模型",
				"openaiModel": "OpenAI 模型",
				"style": "风格",
				"sampler": "采样器",
				"seed": "种子",
				"steps": "步数",
				"cfg": "提示词相关性"
			},
			"emotions": {
				"none": "无",
				"happy": "开心",
				"angry": "生气",
				"sad": "伤心",
				"fear": "恐惧",
				"surprised": "惊讶",
				"confusion": "困惑",
				"empathy": "共情",
				"embarrass": "尴尬",
				"excited": "兴奋",
				"depressed": "氮丧",
				"admiration": "敬佩",
				"coldness": "冷淡"
			},
			"genders": {
				"none": "无",
				"male": "男声",
				"female": "女声"
			},
			"styles": {
				"none": "无",
				"serious": "严肃",
				"arrogant": "傲慢",
				"child": "童声",
				"older": "老年",
				"girl": "少女",
				"pure": "纯真",
				"sister": "御姐",
				"sweet": "甜美",
				"exaggerated": "夸张",
				"ethereal": "空灵",
				"whisper": "耳语",
				"generous": "豪爽",
				"recite": "朗诵",
				"act_coy": "撒娇",
				"warm": "温暖",
				"shy": "害羞",
				"comfort": "安慰",
				"authority": "权威",
				"chat": "闲聊",
				"radio": "电台",
				"soulful": "深情",
				"gentle": "温柔",
				"story": "故事",
				"vivid": "生动",
				"program": "主持",
				"news": "新闻",
				"advertising": "广告",
				"roar": "咆哮",
				"murmur": "低语",
				"shout": "呐喊",
				"deeply": "低沉",
				"loudly": "高亢"
			},
			"recorder": {
				"title": "录制音色",
				"desc": "录制一段音频作为语音合成的参考音色",
				"start": "开始录制",
				"stop": "停止录制",
				"preview": "试听",
				"retry": "重录",
				"uploading": "上传中...",
				"confirm": "确认使用",
				"cancel": "取消",
				"recording": "正在录制...",
				"recorded": "录制完成",
				"micError": "无法访问麦克风，请确保已授予权限",
				"uploadError": "上传失败，请重试",
				"uploadFailed": "上传失败"
			},
			"imageFusion": {
				"loading": "加载中...",
				"imageReference": "图片引用",
				"imageReferenceHint": "点击图片可在提示词中插入引用",
				"fusionPrompt": "融合提示词",
				"fusionPromptPlaceholder": "输入融合指令，例如：保持图1的风格、融合所有图片的特征、突出图2的色彩、结合图3的构图等",
				"imageLabel": "图"
			},
			"refsGenVideo": {
				"videoReference": "视频引用",
				"videoLabel": "视频",
				"audioReference": "音频引用",
				"audioLabel": "音频",
				"referenceHint": "最多 9 张图、3 段视频、3 段音频（共 12 个文件）；音频需搭配图片或视频。点击缩略图可在提示词中插入引用。",
				"promptPlaceholder": "描述想要的视频，例如：图1的角色用音频1的声音说话，场景延续视频1……"
			},
			"video": { "fileCount": "处理文件数:" },
			"speechRecognize": { "withTimestamp": "带时间戳" },
			"music": {
				"handleStyle": "曲风",
				"handleLyric": "歌词",
				"referenceAudio": "参考音频",
				"inputLyrics": "输入歌词",
				"audioDuration": "音频时长",
				"currentDuration": "当前时长:",
				"seconds": "秒",
				"15s": "15秒",
				"30s": "30秒",
				"1min": "1分钟",
				"2min": "2分钟",
				"styleSettings": "音乐风格",
				"voiceGender": "声音",
				"genders": {
					"male": "男声",
					"female": "女声"
				},
				"timbre": "音色",
				"timbres": {
					"bright": "明亮",
					"dark": "深沉",
					"warm": "温暖",
					"soft": "柔和",
					"rock": "摇滚",
					"vocal": "人声"
				},
				"genre": "流派",
				"genres": {
					"pop": "流行",
					"rock": "摇滚",
					"electronic": "电子",
					"hiphop": "嘻哈",
					"jazz": "爵士",
					"blues": "蓝调",
					"classical": "古典",
					"folk": "民谣",
					"country": "乡村",
					"rnb": "R&B",
					"kpop": "K-Pop",
					"reggae": "雷鬼",
					"soul": "灵魂乐",
					"dance": "舞曲",
					"rap": "说唱",
					"classicRock": "经典摇滚",
					"hardRock": "硬摇滚",
					"rockabilly": "乡村摇滚",
					"dancepop": "舞曲流行",
					"house": "浩室",
					"deephouse": "深浩室",
					"experimental": "实验音乐",
					"experimentalPop": "实验流行",
					"popPunk": "流行朋克",
					"rockAndRoll": "摇滚乐",
					"popRock": "流行摇滚"
				},
				"mood": "情感",
				"moods": {
					"happy": "欢快",
					"sad": "悲伤",
					"romantic": "浪漫",
					"melancholic": "忧郁",
					"uplifting": "振奋",
					"intense": "激烈",
					"emotional": "感性",
					"angry": "愤怒"
				},
				"instrumentAndRhythm": "乐器与节奏",
				"instrument": "乐器",
				"selectedCount": "已选 {count} 个",
				"instruments": {
					"piano": "钢琴",
					"guitar": "吉他",
					"acousticGuitar": "木吉他",
					"electricGuitar": "电吉他",
					"drums": "鼓",
					"synthesizer": "合成器",
					"violin": "小提琴",
					"saxophone": "萨克斯",
					"bass": "贝斯",
					"beats": "节拍采样",
					"strings": "弦乐",
					"cello": "大提琴",
					"trumpet": "小号",
					"harmonica": "口琴",
					"banjo": "班卓琴",
					"fiddle": "民间小提琴",
					"brass": "铜管乐器",
					"doubleBass": "低音提琴"
				},
				"bpm": "节拍速度",
				"slow": "慢",
				"moderate": "中等",
				"fast": "快",
				"songTitle": "歌曲名称",
				"songTitlePlaceholder": "输入歌曲名称（可选）",
				"noTagsSelected": "未选择标签",
				"tagsPlaceholder": "例如：female, pop, bright, piano, happy, 120bpm",
				"tagsHint": "输入音乐风格标签，用逗号分隔，如性别、流派、音色、乐器、情感、BPM等",
				"fromUpstreamReadonly": "来自上游（只读）",
				"lyricsPlaceholder": "输入歌词（未连接歌词输入时）",
				"language": "语言",
				"keyscale": "调式",
				"auto": "自动"
			},
			"musicTasks": {
				"promptLabel": "描述",
				"promptPlaceholder": "描述音乐风格、情绪、乐器…",
				"lyricsLabel": "歌词",
				"lyricsPlaceholder": "歌词（可选）…",
				"startTime": "开始（秒）",
				"endTime": "结束（秒）",
				"strength": "强度",
				"track": "乐轨",
				"tracks": "要添加的乐轨",
				"instrumental": "纯音乐",
				"ideaPlaceholder": "用一句话描述你想要的歌曲…",
				"sourceAudio": "原曲",
				"refAudio": "参考音频",
				"trackOptions": {
					"vocals": "人声",
					"backing_vocals": "和声",
					"drums": "鼓",
					"percussion": "打击乐",
					"bass": "贝斯",
					"guitar": "吉他",
					"keyboard": "键盘",
					"synth": "合成器",
					"strings": "弦乐",
					"brass": "铜管",
					"woodwinds": "木管",
					"fx": "音效"
				}
			},
			"speechGenVideo": {
				"loading": "加载中...",
				"inputContent": "输入内容",
				"audio": "音频",
				"connectAudioHint": "请连接音频节点或上传音频文件",
				"audioPromptLabel": "配音提示词",
				"audioPromptPlaceholder": "输入音频配音提示词"
			},
			"convertVoice": {
				"voiceLabel": "音色：",
				"selectVoice": "请选择音色",
				"previewVoice": "试听当前音色",
				"uploadVoice": "上传音色",
				"recordVoice": "录制音色",
				"recordAudio": "录制音频",
				"recordHint": "您可以在这里录制音频。",
				"recordNeedImpl": "音频录制功能需要实现",
				"done": "完成"
			},
			"removeWatermark": { "hint": "自动识别并去除 Sora 视频水印" },
			"videoGenText": {
				"promptLabel": "自定义提示词",
				"promptPlaceholder": "留空使用默认提示词，或输入自定义指令，例如：只描述视频中的对话内容..."
			},
			"imageGenText": {
				"promptLabel": "自定义提示词",
				"promptPlaceholder": "留空使用默认提示词，或输入自定义指令，例如：只描述画面主体、风格、文字内容..."
			},
			"audioDescribe": {
				"promptLabel": "自定义提示词",
				"promptPlaceholder": "留空使用默认提示词，或输入自定义指令，例如：描述这段音频的曲风、情绪、乐器或人声…"
			},
			"textGenText": { "instructions": "指令" },
			"lipDub": {
				"targetDialogue": "目标台词（提示词）",
				"targetDialoguePlaceholder": "写入完整提示词，需包含角色描述与要说的目标语言台词（模型不会自动翻译）",
				"promptHint": "输出分辨率跟随参考视频。台词宜写完整提示词（含角色与目标语言对白，模型不自动翻译），长度接近原片；仅支持单说话人。"
			},
			"motionTrack": {
				"connectHint": "请连接首帧图片、参考视频（自动提取并迁移其运动），以及文本提示词（仅描述外观/风格）。",
				"controlVideo": "参考视频",
				"promptPlaceholder": "描述外观与风格；运动从参考视频中自动提取。",
				"promptHint": "提示词仅描述外观与风格；运动会从参考视频自动提取。时长滑块用于裁剪参考视频长度。",
				"missing": "还需提供：{items}"
			},
			"lipSync": {
				"scenePrompt": "场景描述（可选）",
				"scenePromptPlaceholder": "描述画面与说话人，例如：一位女性对着镜头说话。口型由连接的音频驱动，不要在这里写台词全文。",
				"audioDrivenHint": "口型与配音由音频轨决定；音频时长宜与参考视频接近。输出默认 720p。"
			},
			"imageEdit": {
				"performanceMode": "性能模式",
				"ecoMode": "💰 性价比（FLUX.2 Klein 9B）",
				"proMode": "⚡ 高性能模式",
				"editInstruction": "编辑指令",
				"fromUpstream": "（来自上游节点）",
				"editPlaceholder": "输入图片编辑指令，例如：提高对比度、调整色温为暖色、增强细节等"
			},
			"videoEdit": {
				"editInstruction": "编辑指令",
				"fromUpstream": "（来自上游节点）",
				"editPlaceholder": "输入视频编辑指令，例如：去除字幕、应用水彩风格、将天空替换为日落",
				"connectTextVideo": "请连接文本节点与视频节点"
			},
			"compose": {
				"loading": "加载中...",
				"mediaFiles": "媒体文件",
				"image": "图片",
				"audio": "音频",
				"video": "视频",
				"text": "文本",
				"noText": "无文本",
				"inputContent": "输入内容",
				"videoFiles": "视频文件",
				"connectImageAudio": "请确保已连接一张图片和一个音频文件",
				"connectVideo": "请连接至少一个视频文件",
				"connectImageVideoAudio": "请确保已连接一张图片、一个视频和一个音频文件",
				"connectTextAudio": "请确保已连接文本内容和音频文件",
				"connectTextReferenceAudio": "请连接文本节点与音频节点（参考音色）",
				"connectTextImage": "请连接文本节点与图片节点",
				"connectTextVideoNode": "请连接视频节点与文本节点（目标台词写入提示词）",
				"referenceAudio": "参考音频",
				"referenceTextOptional": "参考文本（可选，提升克隆质量）",
				"connectVideoAudio": "请确保已连接视频和音频文件",
				"generatePromptLabel": "生成提示词",
				"generatePromptPlaceholder": "输入视频生成提示词，描述要生成的视频内容、风格、动作等（可选）",
				"inputText": "输入文本",
				"inputTextPlaceholder": "输入文本内容",
				"describeVideo": "请描述您想生成的视频内容...",
				"firstFrame": "首帧",
				"lastFrame": "尾帧",
				"connectTwoImages": "请确保已连接两张图片（首帧和尾帧）",
				"inputData": "输入数据",
				"connectTextAudioNode": "请连接文本和音频节点",
				"emotion": "情感：",
				"style": "风格：",
				"selectEmotion": "选择情感风格（可选）",
				"selectStyle": "选择语音风格（可选）",
				"lipSync": "对口型",
				"connectImageVideo": "请确保已连接一张图片和一个视频文件",
				"startSlice": "开始切片"
			},
			"modal": {
				"audioPreview": "音频预览",
				"audio": "音频",
				"audios": "音频 ({count})",
				"loading": "加载中...",
				"unavailable": "内容无法加载",
				"more": "更多",
				"moreOptions": "更多选项",
				"actions": "操作",
				"textPreview": "文本预览",
				"text": "文本",
				"texts": "文本 ({count})",
				"imagePreview": "图片预览",
				"image": "图片",
				"images": "图片 ({count})",
				"videoPreview": "视频预览",
				"video": "视频",
				"videos": "视频 ({count})",
				"file": "文件",
				"files": "文件 ({count})",
				"link": "链接",
				"links": "链接 ({count})",
				"fullScreenPreview": "全屏预览",
				"fullScreenWaterfall": "全屏瀑布流预览",
				"model3DPreview": "3D模型预览",
				"model3D": "3D模型",
				"models3D": "3D模型 ({count})",
				"resetView": "重置视图",
				"playAnimation": "播放动画",
				"pauseAnimation": "暂停动画",
				"downloadModel": "下载模型",
				"loading3DModel": "正在加载3D模型...",
				"errorLoadingModel": "模型加载出错",
				"dragToRotate": "左键拖拽旋转 | 滚轮缩放 | 点击重置居中",
				"noModelLoaded": "未加载3D模型",
				"loadingPreview": "加载预览中...",
				"unsupportedFormat": "不支持的格式",
				"supportedFormats": "支持的格式",
				"download": "下载",
				"textEditor": "编辑文本",
				"enterText": "请输入文本内容..."
			},
			"add": {
				"addAudio": "添加音频",
				"addImage": "添加图片",
				"addVideo": "添加视频",
				"addText": "添加文本",
				"addFile": "添加文件",
				"addModel": "添加3D模型",
				"addLink": "添加链接",
				"record": "录音",
				"recordVideo": "录像",
				"upload": "上传",
				"library": "素材库",
				"recorded": "已录制",
				"uploading": "上传中",
				"confirmAndUpload": "确认并上传",
				"dropToUpload": "释放文件以上传",
				"dragOrClick": "拖拽文件到此处或点击上传",
				"supportedAudioFormats": "支持 MP3, WAV, OGG, M4A 格式",
				"supportedImageFormats": "支持常见图片格式",
				"supportedVideoFormats": "支持 MP4, MOV, WEBM 格式",
				"browseFiles": "浏览文件",
				"canvas": "涂鸦",
				"camera": "相机",
				"openCanvas": "打开涂鸦板",
				"canvasDrawing": "涂鸦绘制",
				"canvasDrawingHint": "在下面的画板上绘制，然后点击完成保存",
				"clearCanvas": "清空画布",
				"finishAndSave": "完成并保存",
				"clickToStartCamera": "点击启动相机",
				"takePhoto": "拍照",
				"closeCamera": "关闭相机",
				"cameraError": "无法访问摄像头",
				"cameraErrorHint": "请确保：1. 使用HTTPS或localhost 2. 允许摄像头权限 3. 摄像头未被其他应用占用",
				"inputText": "输入文本",
				"textPlaceholder": "请输入文本内容...",
				"generate": "确认",
				"linkPlaceholder": "请输入链接",
				"extractContent": "提取内容",
				"cancel": "取消",
				"startRecordingVideo": "开始录制视频",
				"addDocument": "添加文档",
				"supportedDocFormats": "支持多种文档格式",
				"dragModel": "拖拽3D模型文件到此处或点击上传",
				"supportedModelFormats": "支持格式",
				"modelHint": "提示: 支持以下3D文件格式："
			},
			"batch": {
				"arrangeGroup": "排列组合",
				"startArrange": "开始排列",
				"describeRequirements": "请描述您的要求...",
				"groupCount": "分组数量",
				"allowDuplicate": "允许重复",
				"videoFilter": "视频筛选",
				"startFilter": "开始筛选"
			},
			"voice": {
				"timbre": "音色：",
				"selectTimbre": "请选择音色",
				"previewTimbre": "试听当前音色",
				"uploadTimbre": "上传音色",
				"recordTimbre": "录制音色",
				"emotion": "情感：",
				"replaceTimbre": "替换音色"
			},
			"separateSound": {
				"textLabel": "要提取的声音",
				"textPlaceholder": "如:狗叫、钢琴、背景里的警笛"
			}
		}
	},
	Upload: {
		"failed": "上传失败，请稍后重试",
		"fileFailed": "文件 \"{name}\" 上传失败：{reason}",
		"partialFailed": "部分或全部文件上传失败",
		"maxFilesExceeded": "最多允许 {max} 个文件",
		"fileTooLarge": "文件 \"{name}\" 过大（上限 {max}MB）",
		"fileTypeNotAccepted": "文件 \"{name}\" 类型不被支持",
		"readImageFailed": "读取图片信息失败",
		"readVideoFailed": "读取视频信息失败"
	},
	LibInput: {
		"deleteSuccess": "素材已删除",
		"deleteFailed": "删除失败",
		"emptyText": "空文本",
		"loadFailed": "加载素材失败",
		"added": "已添加素材",
		"retry": "重试",
		"emptyMaterial": "暂无{type}素材",
		"typeImage": "图片",
		"typeVideo": "视频",
		"typeAudio": "音频",
		"typeText": "文本",
		"typeFile": "文件",
		"typeModel": "模型",
		"favoriteHint": "点击节点上的 ❤️ 图标收藏素材",
		"portfolio": "作品集"
	},
	Recorder: {
		"audioStart": "开始录音",
		"audioCancel": "取消",
		"audioCancelTooltip": "取消录音",
		"audioFinish": "完成",
		"audioFinishTooltip": "完成录音",
		"audioPermissionError": "无法访问麦克风。请确保已授予权限。",
		"videoPermissionError": "无法访问摄像头和麦克风。请确保已授予权限。",
		"videoStart": "开始录制",
		"videoStop": "停止录制"
	},
	Languages: {
		"auto": "自动",
		"zh": "中文",
		"ja": "日本語",
		"yue": "粤语",
		"en": "英语",
		"ko": "韩语",
		"fr": "法语",
		"es": "西班牙语",
		"de": "德语",
		"ru": "俄语",
		"pt": "葡萄牙语",
		"it": "意大利语"
	},
	Errors: {
		"somethingWentWrong": "出现错误",
		"tryAgain": "重试",
		"details": "详情",
		"copy": "复制",
		"copied": "已复制",
		"close": "关闭",
		"openModalBilling": "打开 Modal 账单设置",
		"needHelp": "解决不了？来社群里找我们：",
		"wechatGroup": "微信群",
		"wechatQrAlt": "微信群二维码"
	},
	Api: {
		"unknownError": "未知错误",
		"unauthorized": "未授权",
		"accessDenied": "拒绝访问",
		"requestFailed": "请求失败：{status} {statusText}",
		"success": "操作成功",
		"timedOut": "请求超时，请重试"
	}
};
//#endregion
//#region src/canvas/i18n/messages.ts
/**
* Message catalogs the canvas ships with. A host merges the catalog for its
* locale into its own messages (`{ ...canvasMessages[locale], ...own }`) and
* provides them through `use-intl`'s `IntlProvider` (or next-intl's
* `NextIntlClientProvider`, which is the same context).
*/
const CANVAS_LOCALES = [
	"en",
	"zh",
	"ja",
	"ko"
];
const canvasMessages = {
	en: en_default,
	zh: zh_default,
	ja: ja_default,
	ko: ko_default
};
function isCanvasLocale(value) {
	return CANVAS_LOCALES.includes(value);
}
//#endregion
//#region src/canvas/i18n/client.ts
/**
* Client-side translator for non-React contexts (utilities, lib code,
* thrown errors that surface via toast). React components should keep
* using `useTranslations()` from `use-intl` instead.
*
* The locale is whatever the host last announced via `setCanvasLocale`
* (`CanvasProvider` does this from its `locale` prop); it defaults to "en".
*/
let currentLocale = "en";
/** Announce the active UI locale (called by `CanvasProvider`). */
function setCanvasLocale(locale) {
	if (isCanvasLocale(locale)) currentLocale = locale;
}
function getCanvasLocale() {
	return currentLocale;
}
function getClientTranslator(namespace) {
	return createTranslator({
		locale: currentLocale,
		messages: canvasMessages[currentLocale],
		namespace
	});
}
//#endregion
//#region src/canvas/host.tsx
/**
* Canvas host configuration.
*
* The canvas talks to a TongFlow-compatible HTTP API (`/api/task/create`,
* `/api/task/wait` SSE, `/api/upload`, `/api/plugins/registry`, …) and
* resolves asset `file_key`s to URLs. A host points it at that API through
* `configureCanvasHost` / `<CanvasProvider>`; by default everything is
* same-origin, which is what the TongFlow app itself uses.
*/
const host = { apiBaseUrl: "" };
/** Replace the host configuration (merges with the current one). */
function configureCanvasHost(config) {
	Object.assign(host, config);
}
function getCanvasHost() {
	return host;
}
/** Absolute (or origin-relative) URL for an API path such as `/api/task/create`. */
function apiUrl(path) {
	return `${host.apiBaseUrl}${path}`;
}
/** The fetch implementation the canvas should use. */
function hostFetch(input, init) {
	return (host.fetch ?? fetch)(input, init);
}
/** URL for an asset `file_key` (private uploads endpoint by default). */
function assetUrl(fileKey) {
	if (/^https?:\/\//.test(fileKey)) return fileKey;
	return host.resolveAssetUrl ? host.resolveAssetUrl(fileKey) : `${host.apiBaseUrl}/api/uploads/${fileKey}`;
}
/**
* Applies host configuration + locale for the canvas subtree. Message
* catalogs are NOT provided here — wrap the tree in `use-intl`'s
* `IntlProvider` (or next-intl's provider) with `canvasMessages[locale]`
* merged into your messages.
*/
function CanvasProvider({ locale, children, ...config }) {
	if (Object.keys(config).length > 0) configureCanvasHost(config);
	if (locale) setCanvasLocale(locale);
	useEffect(() => {
		if (locale) setCanvasLocale(locale);
	}, [locale]);
	return /* @__PURE__ */ jsx(Fragment, { children });
}
//#endregion
//#region src/canvas/lib/file/url.ts
/**
* File URL utilities - local disk backend.
*
* Files are saved under `data/uploads/` (see `src/handlers/file-utils.ts`)
* and served via `GET /api/uploads/[...path]`.
*/
const getFileUrl = (fileKey) => {
	if (!fileKey || typeof fileKey !== "string") return "";
	if (fileKey.startsWith("http://") || fileKey.startsWith("https://")) return fileKey;
	return assetUrl(fileKey.startsWith("/") ? fileKey.slice(1) : fileKey);
};
const getPrivateFileUrl = (fileKey) => getFileUrl(fileKey);
const getSharedFileUrl = (_materialId, fileKey) => getFileUrl(fileKey);
const getMaterialFileUrls = (material) => {
	return ((typeof material.content === "string" ? JSON.parse(material.content) : material.content).fileKeys || []).map((key) => getFileUrl(key));
};
//#endregion
//#region src/canvas/lib/file/url-cache.ts
/**
* Simple URL cache for file keys.
*/
var FileUrlCache = class {
	cache = /* @__PURE__ */ new Map();
	get(fileKey) {
		return this.cache.get(fileKey);
	}
	set(fileKey, url) {
		this.cache.set(fileKey, url);
	}
	has(fileKey) {
		return this.cache.has(fileKey);
	}
	delete(fileKey) {
		this.cache.delete(fileKey);
	}
	clear() {
		this.cache.clear();
	}
};
let globalCache = null;
function getGlobalFileCache() {
	if (!globalCache) globalCache = new FileUrlCache();
	return globalCache;
}
//#endregion
//#region src/canvas/hooks/use-file-async-loader.ts
/**
* useFileAsyncLoader Hook
*
* Integrates cache and queue, provides local file async loading hooks.
*/
function useFileAsyncLoader(fileKey, options = {}) {
	const { priority = "normal" } = options;
	const [url, setUrl] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const taskIdRef = useRef(null);
	const cache = getGlobalFileCache();
	const queue = getGlobalFileQueue();
	useEffect(() => {
		if (!fileKey) {
			setUrl(null);
			setIsLoading(false);
			return;
		}
		const cachedUrl = cache.get(fileKey);
		if (cachedUrl) {
			setUrl(cachedUrl);
			setIsLoading(false);
			return;
		}
		const fileUrl = useFileUrl(fileKey);
		if (taskIdRef.current) queue.cancel(taskIdRef.current);
		const taskId = `loader-${fileKey}-${Date.now()}-${Math.random()}`;
		taskIdRef.current = taskId;
		setIsLoading(true);
		setError(null);
		queue.enqueue({
			id: taskId,
			fileKey,
			url: fileUrl,
			priority,
			retryCount: 0,
			maxRetries: 2,
			timestamp: Date.now(),
			onSuccess: (loadedUrl) => {
				setUrl(loadedUrl);
				setIsLoading(false);
			},
			onError: (err) => {
				setUrl(fileUrl);
				setError(err);
				setIsLoading(false);
			}
		});
		return () => {
			if (taskIdRef.current) {
				queue.cancel(taskIdRef.current);
				taskIdRef.current = null;
			}
		};
	}, [
		fileKey,
		priority,
		cache,
		queue
	]);
	return {
		url,
		isLoading,
		error
	};
}
function useFileAsyncLoaderBatch(fileKeys, options = {}) {
	const { priority = "normal", onProgress } = options;
	const [urls, setUrls] = useState(/* @__PURE__ */ new Map());
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState(/* @__PURE__ */ new Map());
	const [progress, setProgress] = useState({
		loaded: 0,
		total: fileKeys.length
	});
	const taskIdsRef = useRef(/* @__PURE__ */ new Map());
	const cache = getGlobalFileCache();
	const queue = getGlobalFileQueue();
	useEffect(() => {
		if (fileKeys.length === 0) {
			setUrls(/* @__PURE__ */ new Map());
			setIsLoading(false);
			return;
		}
		const newUrls = /* @__PURE__ */ new Map();
		const tasksToEnqueue = [];
		let loadedCount = 0;
		for (const fileKey of fileKeys) {
			const cachedUrl = cache.get(fileKey);
			if (cachedUrl) {
				newUrls.set(fileKey, cachedUrl);
				loadedCount += 1;
			} else {
				const fileUrl = useFileUrl(fileKey);
				const taskId = `batch-loader-${fileKey}-${Date.now()}-${Math.random()}`;
				taskIdsRef.current.set(fileKey, taskId);
				tasksToEnqueue.push({
					id: taskId,
					fileKey,
					url: fileUrl,
					priority,
					retryCount: 0,
					maxRetries: 2,
					timestamp: Date.now(),
					onSuccess: (loadedUrl) => {
						newUrls.set(fileKey, loadedUrl);
						setUrls(new Map(newUrls));
						updateProgress(newUrls.size, fileKeys.length);
					},
					onError: (err) => {
						newUrls.set(fileKey, fileUrl);
						setUrls(new Map(newUrls));
						setErrors((prev) => new Map(prev).set(fileKey, err));
						updateProgress(newUrls.size, fileKeys.length);
					}
				});
			}
		}
		const updateProgress = (loaded, total) => {
			setProgress({
				loaded,
				total
			});
			onProgress?.(loaded, total);
		};
		if (newUrls.size === fileKeys.length) {
			setUrls(newUrls);
			setIsLoading(false);
			updateProgress(fileKeys.length, fileKeys.length);
		} else {
			setIsLoading(true);
			setUrls(newUrls);
			updateProgress(loadedCount, fileKeys.length);
			if (tasksToEnqueue.length > 0) queue.enqueueBatch(tasksToEnqueue);
		}
		return () => {
			for (const taskId of taskIdsRef.current.values()) queue.cancel(taskId);
			taskIdsRef.current.clear();
		};
	}, [
		fileKeys.length,
		priority,
		cache,
		queue,
		onProgress
	]);
	return {
		urls,
		isLoading,
		errors,
		progress
	};
}
function useFileUrl(fileKey) {
	const cache = getGlobalFileCache();
	if (fileKey) {
		const cachedUrl = cache.get(fileKey);
		if (cachedUrl) return cachedUrl;
	}
	if (fileKey) {
		const url = getFileUrl(fileKey);
		cache.set(fileKey, url);
		return url;
	}
	return "";
}
function useFileLoaderStats() {
	const [stats] = useState(() => getGlobalFileQueue().getStats());
	return stats;
}
//#endregion
//#region src/canvas/hooks/use-flow.ts
/**
* React Flow state wrapper built on Zustand.
*
* The document model (nodes / edges / meta, graph mutations, undo/redo,
* combo state) is the headless `createFlowSlice` from the `tongflow`
* package; this hook layers the React Flow callbacks on top and persists the
* canvas to localStorage.
*/
function createDebounce(callback, delay) {
	let timeoutId = null;
	return (...args) => {
		if (timeoutId) clearTimeout(timeoutId);
		timeoutId = setTimeout(() => {
			callback(...args);
			timeoutId = null;
		}, delay);
	};
}
const debouncedSaveNodes = createDebounce((nodes) => {
	localStorage.setItem("nodes", JSON.stringify(nodes));
}, 500);
const debouncedSaveEdges = createDebounce((edges) => {
	localStorage.setItem("edges", JSON.stringify(edges));
}, 500);
const debouncedSaveWorkflowMeta = createDebounce((meta) => {
	localStorage.setItem("workflowMeta", JSON.stringify(meta));
}, 500);
const useFlow = create()((set, get) => ({
	...createFlowSlice(set, get, { createId: v4 }),
	onSelectionChange: ({ nodes }) => {
		get().setSelectedNodes(nodes);
	},
	onNodesChange: (changes) => {
		if (changes.some((c) => c.type === "remove")) {
			get().commitHistory("remove");
			queueMicrotask(get().resetHistoryCoalescing);
		}
		if (changes.some((c) => c.type === "position" && c.dragging === true)) get().cancelLayoutSettleWatch();
		else {
			const measured = changes.filter((c) => c.type === "dimensions").map((c) => c.id);
			if (measured.length > 0) get().notifyNodesMeasured(measured);
		}
		const nodes = applyNodeChanges(changes, get().nodes);
		const removed = new Set(changes.filter((c) => c.type === "remove").map((c) => c.id));
		set({
			nodes,
			edges: removed.size > 0 ? get().edges.filter((e) => !removed.has(e.source) && !removed.has(e.target)) : get().edges
		});
	},
	onEdgesChange: (changes) => {
		if (changes.some((c) => c.type === "remove")) {
			get().commitHistory("remove");
			queueMicrotask(get().resetHistoryCoalescing);
		}
		set({ edges: applyEdgeChanges(changes, get().edges) });
	},
	onConnect: (connection) => {
		get().commitHistory();
		set({ edges: addEdge({
			...connection,
			id: v4(),
			type: "custom-edge"
		}, get().edges) });
	}
}));
useFlow.subscribe((state, prev) => {
	if (state.nodes !== prev.nodes) debouncedSaveNodes(state.nodes);
	if (state.edges !== prev.edges) debouncedSaveEdges(state.edges);
	if (state.workflowId !== prev.workflowId || state.workflowName !== prev.workflowName || state.workflowDescription !== prev.workflowDescription) debouncedSaveWorkflowMeta({
		id: state.workflowId,
		name: state.workflowId ? state.workflowName : "",
		description: state.workflowDescription
	});
});
//#endregion
//#region src/canvas/ui/error-toast.tsx
function ErrorToastCard({ toastId, title, message, detail, footer }) {
	const t = getClientTranslator("Errors");
	const [expanded, setExpanded] = useState(false);
	const [copied, setCopied] = useState(false);
	const copy = () => {
		const parts = [
			title,
			message,
			detail
		].filter(Boolean);
		navigator.clipboard?.writeText(parts.join("\n\n")).then(() => setCopied(true), () => {});
	};
	return /* @__PURE__ */ jsx("div", {
		className: "pointer-events-auto w-[380px] max-w-[90vw] rounded-lg border border-red-200 bg-white shadow-lg dark:border-red-900/60 dark:bg-neutral-900",
		children: /* @__PURE__ */ jsxs("div", {
			className: "flex items-start gap-2 p-3",
			children: [
				/* @__PURE__ */ jsx(AlertCircle, { className: "mt-0.5 h-4 w-4 shrink-0 text-red-500" }),
				/* @__PURE__ */ jsxs("div", {
					className: "min-w-0 flex-1",
					children: [
						title && /* @__PURE__ */ jsx("p", {
							className: "break-words text-sm font-medium text-red-600 dark:text-red-400",
							children: title
						}),
						/* @__PURE__ */ jsx("p", {
							className: "break-words text-sm text-neutral-700 dark:text-neutral-200",
							children: message
						}),
						/* @__PURE__ */ jsxs("div", {
							className: "mt-1.5 flex items-center gap-3 text-xs",
							children: [detail && /* @__PURE__ */ jsx("button", {
								type: "button",
								onClick: () => setExpanded((v) => !v),
								className: "text-neutral-500 transition-colors hover:text-neutral-800 dark:hover:text-neutral-200",
								children: t("details")
							}), /* @__PURE__ */ jsx("button", {
								type: "button",
								onClick: copy,
								className: "text-neutral-500 transition-colors hover:text-neutral-800 dark:hover:text-neutral-200",
								children: copied ? t("copied") : t("copy")
							})]
						}),
						expanded && detail && /* @__PURE__ */ jsx("pre", {
							className: "mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-neutral-100 p-2 font-mono text-[11px] leading-relaxed text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300",
							children: detail
						}),
						footer
					]
				}),
				/* @__PURE__ */ jsx("button", {
					type: "button",
					"aria-label": t("close"),
					onClick: () => toast.dismiss(toastId),
					className: "-mr-1 -mt-1 rounded p-1 text-neutral-400 transition-colors hover:text-neutral-700 dark:hover:text-neutral-200",
					children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
				})
			]
		})
	});
}
/**
* Persistent error toast: stays until the user dismisses it (✕), with an
* optional expandable detail panel and copy button. Use for any real error
* (API/network failures, task/workflow failures) so they don't flash by.
*/
function showErrorToast({ title, message, detail, id, footer }) {
	return toast.custom((tInst) => /* @__PURE__ */ jsx(ErrorToastCard, {
		toastId: tInst.id,
		title,
		message,
		detail,
		footer
	}), {
		duration: Number.POSITIVE_INFINITY,
		id
	});
}
//#endregion
//#region src/canvas/lib/api/client.ts
/**
* Get error message
*/
function getErrorMessage(error) {
	if (error instanceof Error) return error.message;
	if (typeof error === "string") return error;
	if (typeof error === "object" && error !== null && "message" in error && typeof error.message === "string") return error.message;
	return getClientTranslator("Api")("unknownError");
}
/**
* Extract error information from an API response
* Priority: error > errorMessage > message
*/
function extractErrorFromResponse(data) {
	if (typeof data !== "object" || data === null) return null;
	const response = data;
	if (typeof response.error === "string" && response.error.trim()) return response.error.trim();
	if (typeof response.errorMessage === "string" && response.errorMessage.trim()) return response.errorMessage.trim();
	if (typeof response.message === "string" && response.message.trim() && !response.success) return response.message.trim();
	return null;
}
/**
* Unified fetch wrapper function
*
* @example
* // Simple GET request
* const data = await apiClient('/api/users');
*
* @example
* // POST request with success message
* const result = await apiClient('/api/users', {
*   method: 'POST',
*   body: JSON.stringify({ name: 'John' }),
*   showSuccessToast: true,
*   successMessage: 'Created successfully!',
* });
*
* @example
* // Custom error handling
* const data = await apiClient('/api/users', {
*   errorMessage: 'Failed to load users',
* });
*/
async function apiClient(url, options = {}) {
	const { showErrorToast: shouldShowErrorToast = true, showSuccessToast = false, successMessage, errorMessage, parseJson = true, timeout = 3e4, ...fetchOptions } = options;
	const t = getClientTranslator("Api");
	let errorToastShown = false;
	try {
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeout);
		let response;
		try {
			response = await hostFetch(apiUrl(url), {
				...fetchOptions,
				signal: controller.signal
			});
		} finally {
			clearTimeout(timeoutId);
		}
		let data;
		if (parseJson) try {
			data = await response.json();
		} catch {
			data = null;
		}
		else data = await response.text();
		if (!response.ok) {
			if (response.status === 401 || response.status === 403) {
				const authErrorMsg = response.status === 401 ? t("unauthorized") : t("accessDenied");
				if (response.status === 401 && typeof window !== "undefined" && !window.dispatchEvent(new CustomEvent("tf:unauthorized", { cancelable: true }))) errorToastShown = true;
				else if (shouldShowErrorToast) {
					showErrorToast({ message: authErrorMsg });
					errorToastShown = true;
				}
				const error = new Error(authErrorMsg);
				error.status = response.status;
				throw error;
			}
			const backendError = extractErrorFromResponse(data);
			const errorMsg = errorMessage || backendError || t("requestFailed", {
				status: response.status,
				statusText: response.statusText
			});
			if (shouldShowErrorToast) {
				showErrorToast({ message: errorMsg });
				errorToastShown = true;
			}
			const error = new Error(errorMsg);
			error.status = response.status;
			throw error;
		}
		if (showSuccessToast) {
			const message = successMessage || t("success");
			toast.success(message);
		}
		return data;
	} catch (error) {
		if (error instanceof Error && error.name === "AbortError") {
			const msg = errorMessage || t("timedOut");
			if (shouldShowErrorToast && !errorToastShown) {
				showErrorToast({ message: msg });
				errorToastShown = true;
			}
			throw new Error(msg);
		}
		if (errorToastShown) throw error;
		const msg = errorMessage || getErrorMessage(error);
		if (shouldShowErrorToast) showErrorToast({ message: msg });
		throw error;
	}
}
/**
* Convenience method: GET request
*/
async function apiGet(url, options = {}) {
	return apiClient(url, {
		...options,
		method: "GET"
	});
}
/**
* Convenience method: POST request
*/
async function apiPost(url, body, options = {}) {
	return apiClient(url, {
		...options,
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...options.headers
		},
		body: typeof body === "string" ? body : JSON.stringify(body)
	});
}
/**
* Convenience method: PUT request
*/
async function apiPut(url, body, options = {}) {
	return apiClient(url, {
		...options,
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			...options.headers
		},
		body: typeof body === "string" ? body : JSON.stringify(body)
	});
}
/**
* Convenience method: DELETE request
*/
async function apiDelete(url, options = {}) {
	return apiClient(url, {
		...options,
		method: "DELETE"
	});
}
/**
* Convenience method: PATCH request
*/
async function apiPatch(url, body, options = {}) {
	return apiClient(url, {
		...options,
		method: "PATCH",
		headers: {
			"Content-Type": "application/json",
			...options.headers
		},
		body: typeof body === "string" ? body : JSON.stringify(body)
	});
}
//#endregion
//#region src/canvas/lib/api/material.ts
/**
* Material API client — local library
*/
async function createMaterial(data) {
	return await apiPost("/api/material", data);
}
async function listMaterials(type) {
	return await apiGet(type ? `/api/material?type=${type}` : "/api/material");
}
async function deleteMaterial(id) {
	await apiDelete(`/api/material?id=${id}`);
}
async function toggleFavorite(id) {
	return await apiPatch(`/api/material?id=${id}`);
}
async function traceMaterialWorkflow(materialId) {
	return await apiGet(`/api/material/${materialId}/workflow`);
}
async function saveFromTask(data) {
	try {
		return await apiPost("/api/material/save-from-task", data);
	} catch (error) {
		logger.warn("[Frontend] save-from-task failed (webhook backup exists):", error);
		return {
			success: false,
			error: "Request failed"
		};
	}
}
//#endregion
//#region src/canvas/lib/utils.ts
function cn(...inputs) {
	return twMerge(clsx(inputs));
}
//#endregion
//#region src/canvas/ui/button.tsx
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", {
	variants: {
		variant: {
			default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
			destructive: "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
			outline: "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
			secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
			ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
			link: "text-primary underline-offset-4 hover:underline"
		},
		size: {
			default: "h-9 px-4 py-2 has-[>svg]:px-3",
			sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
			lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
			icon: "size-9"
		}
	},
	defaultVariants: {
		variant: "default",
		size: "default"
	}
});
function Button({ className, variant, size, asChild = false, ...props }) {
	return /* @__PURE__ */ jsx(asChild ? Slot : "button", {
		"data-slot": "button",
		className: cn(buttonVariants({
			variant,
			size,
			className
		})),
		...props
	});
}
//#endregion
//#region src/canvas/ui/card.tsx
function Card({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card",
		className: cn("bg-card text-card-foreground flex flex-col gap-6 rounded-xl border py-6 shadow-sm", className),
		...props
	});
}
function CardHeader({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-header",
		className: cn("@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6", className),
		...props
	});
}
function CardTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-title",
		className: cn("leading-none font-semibold", className),
		...props
	});
}
function CardDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-description",
		className: cn("text-muted-foreground text-sm", className),
		...props
	});
}
function CardAction({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-action",
		className: cn("col-start-2 row-span-2 row-start-1 self-start justify-self-end", className),
		...props
	});
}
function CardContent({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-content",
		className: cn("px-6", className),
		...props
	});
}
function CardFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "card-footer",
		className: cn("flex items-center px-6 [.border-t]:pt-6", className),
		...props
	});
}
//#endregion
//#region src/canvas/ui/skeleton.tsx
function Skeleton({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		className: cn("animate-pulse rounded-md bg-muted", className),
		...props
	});
}
//#endregion
//#region src/canvas/components/lib-input.tsx
const resourceTypeToMaterialType = {
	TEXT: "text",
	IMAGE: "image",
	VIDEO: "video",
	AUDIO: "audio",
	FILE: "file",
	MODEL: "model"
};
const materialTypeToNodeType = {
	image: "imageNode",
	video: "videoNode",
	audio: "audioNode",
	text: "textNode",
	file: "fileNode",
	model: "modelNode"
};
const MaterialTypeIcon = {
	image: /* @__PURE__ */ jsx(Image$1, { className: "h-6 w-6" }),
	video: /* @__PURE__ */ jsx(Video, { className: "h-6 w-6" }),
	audio: /* @__PURE__ */ jsx(Music, { className: "h-6 w-6" }),
	text: /* @__PURE__ */ jsx(FileText, { className: "h-6 w-6" }),
	file: /* @__PURE__ */ jsx(File$1, { className: "h-6 w-6" }),
	model: /* @__PURE__ */ jsx(Box, { className: "h-6 w-6" })
};
const MaterialItem = memo(({ material, thumbnailUrl, isLoading, onSelect, onDelete }) => {
	const t = useTranslations("LibInput");
	const [isDeleting, setIsDeleting] = useState(false);
	const handleDelete = async (e) => {
		e.stopPropagation();
		if (isDeleting) return;
		setIsDeleting(true);
		try {
			await deleteMaterial(material.id);
			onDelete(material.id);
			toast.success(t("deleteSuccess"));
		} catch (error) {
			logger.error("Failed to delete material:", error);
			showErrorToast({ message: t("deleteFailed") });
		} finally {
			setIsDeleting(false);
		}
	};
	const renderThumbnail = () => {
		if (material.type === "text") {
			const previewText = (material.content.texts || [])[0]?.substring(0, 60) || t("emptyText");
			return /* @__PURE__ */ jsxs("div", {
				className: "h-full w-full p-2 text-xs text-muted-foreground overflow-hidden",
				children: [previewText, previewText.length >= 60 && "..."]
			});
		}
		if (isLoading) return /* @__PURE__ */ jsx("div", {
			className: "h-full w-full flex items-center justify-center",
			children: /* @__PURE__ */ jsx(RefreshCw, { className: "h-4 w-4 animate-spin text-muted-foreground" })
		});
		if (thumbnailUrl && (material.type === "image" || material.type === "video")) return /* @__PURE__ */ jsx("img", {
			src: thumbnailUrl,
			alt: material.name,
			className: "h-full w-full object-cover"
		});
		return /* @__PURE__ */ jsx("div", {
			className: "h-full w-full flex items-center justify-center text-muted-foreground",
			children: MaterialTypeIcon[material.type]
		});
	};
	return /* @__PURE__ */ jsxs("div", {
		className: cn("relative aspect-square rounded-lg border border-border bg-card overflow-hidden cursor-pointer group", "hover:border-primary hover:shadow-md transition-all"),
		onClick: () => onSelect(material),
		children: [
			renderThumbnail(),
			/* @__PURE__ */ jsx(Button, {
				size: "icon",
				variant: "destructive",
				className: cn("absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity", isDeleting && "opacity-100"),
				onClick: handleDelete,
				disabled: isDeleting,
				children: isDeleting ? /* @__PURE__ */ jsx(RefreshCw, { className: "h-3 w-3 animate-spin" }) : /* @__PURE__ */ jsx(Trash2, { className: "h-3 w-3" })
			}),
			/* @__PURE__ */ jsx("div", {
				className: "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2",
				children: /* @__PURE__ */ jsx("p", {
					className: "text-xs text-white truncate",
					children: material.name
				})
			})
		]
	});
});
MaterialItem.displayName = "MaterialItem";
const LibInput = ({ resourceType }) => {
	const t = useTranslations("LibInput");
	const expands = useFlow((s) => s.expands);
	const id = useNodeId();
	const [materials, setMaterials] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const materialType = resourceTypeToMaterialType[resourceType];
	const { urls: thumbnailUrls, isLoading: thumbnailsLoading } = useFileAsyncLoaderBatch(materials.filter((m) => m.thumbnail && (m.type === "image" || m.type === "video")).map((m) => m.thumbnail), { priority: "normal" });
	const loadMaterials = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await listMaterials(materialType);
			setMaterials(response.materials);
		} catch (err) {
			logger.error("Failed to load materials:", err);
			setError(t("loadFailed"));
		} finally {
			setIsLoading(false);
		}
	}, [materialType, t]);
	useEffect(() => {
		loadMaterials();
	}, [loadMaterials]);
	const handleSelect = useCallback((material) => {
		if (!id) return;
		const nodeType = materialTypeToNodeType[material.type];
		const nodeData = material.type === "text" ? { texts: material.content.texts } : { fileKeys: material.content.fileKeys };
		expands(id, [{
			type: nodeType,
			data: nodeData
		}]);
		toast.success(t("added"));
	}, [
		id,
		expands,
		t
	]);
	const handleDelete = useCallback((deletedId) => {
		setMaterials((prev) => prev.filter((m) => m.id !== deletedId));
	}, []);
	if (isLoading) return /* @__PURE__ */ jsx(Card, {
		className: "p-3",
		children: /* @__PURE__ */ jsx("div", {
			className: "grid grid-cols-3 gap-2",
			children: [...Array(6)].map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "aspect-square rounded-lg" }, i))
		})
	});
	if (error) return /* @__PURE__ */ jsx(Card, {
		className: "p-3",
		children: /* @__PURE__ */ jsxs("div", {
			className: "text-center space-y-2",
			children: [/* @__PURE__ */ jsx("p", {
				className: "text-sm text-destructive",
				children: error
			}), /* @__PURE__ */ jsxs(Button, {
				size: "sm",
				variant: "outline",
				onClick: loadMaterials,
				children: [/* @__PURE__ */ jsx(RefreshCw, { className: "h-4 w-4 mr-2" }), t("retry")]
			})]
		})
	});
	if (materials.length === 0) return /* @__PURE__ */ jsx(Card, {
		className: "p-6",
		children: /* @__PURE__ */ jsxs("div", {
			className: "text-center space-y-2",
			children: [
				/* @__PURE__ */ jsx("div", {
					className: "flex justify-center text-muted-foreground",
					children: MaterialTypeIcon[materialType]
				}),
				/* @__PURE__ */ jsx("p", {
					className: "text-sm text-muted-foreground",
					children: t("emptyMaterial", { type: resourceType === "IMAGE" ? t("typeImage") : resourceType === "VIDEO" ? t("typeVideo") : resourceType === "AUDIO" ? t("typeAudio") : resourceType === "TEXT" ? t("typeText") : resourceType === "FILE" ? t("typeFile") : t("typeModel") })
				}),
				/* @__PURE__ */ jsx("p", {
					className: "text-xs text-muted-foreground",
					children: t("favoriteHint")
				})
			]
		})
	});
	return /* @__PURE__ */ jsxs(Card, {
		className: "p-3",
		children: [/* @__PURE__ */ jsxs("div", {
			className: "flex items-center justify-between mb-3",
			children: [/* @__PURE__ */ jsxs("p", {
				className: "text-sm font-medium",
				children: [
					t("portfolio"),
					" (",
					materials.length,
					")"
				]
			}), /* @__PURE__ */ jsx(Button, {
				size: "icon",
				variant: "ghost",
				className: "h-6 w-6",
				onClick: loadMaterials,
				disabled: isLoading,
				children: /* @__PURE__ */ jsx(RefreshCw, { className: cn("h-4 w-4", isLoading && "animate-spin") })
			})]
		}), /* @__PURE__ */ jsx("div", {
			className: "grid grid-cols-3 gap-2 max-h-[300px] overflow-y-auto",
			children: materials.map((material) => /* @__PURE__ */ jsx(MaterialItem, {
				material,
				thumbnailUrl: material.thumbnail ? thumbnailUrls.get(material.thumbnail) : void 0,
				isLoading: material.thumbnail ? thumbnailsLoading && !thumbnailUrls.has(material.thumbnail) : false,
				onSelect: handleSelect,
				onDelete: handleDelete
			}, material.id))
		})]
	});
};
//#endregion
//#region src/canvas/ui/dialog.tsx
function Dialog({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Root, {
		"data-slot": "dialog",
		...props
	});
}
function DialogTrigger({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Trigger, {
		"data-slot": "dialog-trigger",
		...props
	});
}
function DialogPortal({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Portal, {
		"data-slot": "dialog-portal",
		...props
	});
}
function DialogClose({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Close, {
		"data-slot": "dialog-close",
		...props
	});
}
function DialogOverlay({ className, ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Overlay, {
		"data-slot": "dialog-overlay",
		className: cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50", className),
		...props
	});
}
function DialogContent({ className, children, showCloseButton = true, ...props }) {
	return /* @__PURE__ */ jsxs(DialogPortal, {
		"data-slot": "dialog-portal",
		children: [/* @__PURE__ */ jsx(DialogOverlay, {}), /* @__PURE__ */ jsxs(DialogPrimitive.Content, {
			"data-slot": "dialog-content",
			className: cn("bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg", className),
			...props,
			children: [children, showCloseButton && /* @__PURE__ */ jsxs(DialogPrimitive.Close, {
				"data-slot": "dialog-close",
				className: "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
				children: [/* @__PURE__ */ jsx(XIcon, {}), /* @__PURE__ */ jsx("span", {
					className: "sr-only",
					children: "Close"
				})]
			})]
		})]
	});
}
function DialogHeader({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "dialog-header",
		className: cn("flex flex-col gap-2 text-center sm:text-left", className),
		...props
	});
}
function DialogFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "dialog-footer",
		className: cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className),
		...props
	});
}
function DialogTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Title, {
		"data-slot": "dialog-title",
		className: cn("text-lg leading-none font-semibold", className),
		...props
	});
}
function DialogDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Description, {
		"data-slot": "dialog-description",
		className: cn("text-muted-foreground text-sm", className),
		...props
	});
}
//#endregion
//#region src/canvas/components/speaker-voice-recorder.tsx
const SpeakerVoiceRecorder = ({ trigger, onChange }) => {
	const t = useTranslations("Workspace.nodes.recorder");
	const [isRecording, setIsRecording] = useState(false);
	const [recordedBlob, setRecordedBlob] = useState(null);
	const [recordingTime, setRecordingTime] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState(null);
	const [open, setOpen] = useState(false);
	const mediaRecorderRef = useRef(null);
	const audioChunksRef = useRef([]);
	const timerRef = useRef(null);
	const audioPreviewRef = useRef(null);
	const startRecording = async () => {
		try {
			setError(null);
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			const mediaRecorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4" });
			mediaRecorderRef.current = mediaRecorder;
			audioChunksRef.current = [];
			mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) audioChunksRef.current.push(event.data);
			};
			mediaRecorder.onstop = () => {
				const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
				setRecordedBlob(audioBlob);
				stream.getTracks().forEach((track) => track.stop());
			};
			mediaRecorder.start(100);
			setIsRecording(true);
			setRecordingTime(0);
			timerRef.current = setInterval(() => {
				setRecordingTime((prev) => prev + 1);
			}, 1e3);
		} catch (err) {
			logger.error("Failed to start recording:", err);
			setError(t("micError"));
		}
	};
	const stopRecording = () => {
		if (mediaRecorderRef.current && isRecording) {
			mediaRecorderRef.current.stop();
			setIsRecording(false);
			if (timerRef.current) {
				clearInterval(timerRef.current);
				timerRef.current = null;
			}
		}
	};
	const playPreview = () => {
		if (recordedBlob) {
			if (audioPreviewRef.current) audioPreviewRef.current.pause();
			const audio = new Audio(URL.createObjectURL(recordedBlob));
			audioPreviewRef.current = audio;
			audio.play();
		}
	};
	const uploadRecording = async () => {
		if (!recordedBlob) return;
		setIsUploading(true);
		setError(null);
		try {
			const extension = recordedBlob.type.includes("webm") ? "webm" : "mp4";
			const fileName = `voice_${Date.now()}.${extension}`;
			const file = new File([recordedBlob], fileName, { type: recordedBlob.type });
			const { getPresignedUploadUrl } = await Promise.resolve().then(() => upload_exports);
			const { fileKey } = await getPresignedUploadUrl(file);
			onChange(fileKey);
			setOpen(false);
			setRecordedBlob(null);
			setRecordingTime(0);
		} catch (err) {
			logger.error("Upload failed:", err);
			setError(t("uploadError"));
		} finally {
			setIsUploading(false);
		}
	};
	const formatTime = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};
	const resetRecording = () => {
		setRecordedBlob(null);
		setRecordingTime(0);
		setError(null);
	};
	return /* @__PURE__ */ jsxs(Dialog, {
		open,
		onOpenChange: setOpen,
		children: [/* @__PURE__ */ jsx(DialogTrigger, {
			asChild: true,
			children: trigger
		}), /* @__PURE__ */ jsxs(DialogContent, {
			className: "sm:max-w-md",
			children: [
				/* @__PURE__ */ jsxs(DialogHeader, { children: [/* @__PURE__ */ jsx(DialogTitle, { children: t("title") }), /* @__PURE__ */ jsx(DialogDescription, { children: t("desc") })] }),
				/* @__PURE__ */ jsxs("div", {
					className: "flex flex-col items-center gap-4 py-4",
					children: [
						/* @__PURE__ */ jsxs("div", {
							className: "flex flex-col items-center gap-2",
							children: [
								/* @__PURE__ */ jsx("div", {
									className: `w-20 h-20 rounded-full flex items-center justify-center transition-all ${isRecording ? "bg-red-100 dark:bg-red-900/30 animate-pulse" : recordedBlob ? "bg-green-100 dark:bg-green-900/30" : "bg-muted"}`,
									children: isRecording ? /* @__PURE__ */ jsx("div", { className: "w-6 h-6 bg-red-500 rounded-sm" }) : recordedBlob ? /* @__PURE__ */ jsx("svg", {
										className: "w-8 h-8 text-green-600",
										fill: "none",
										viewBox: "0 0 24 24",
										stroke: "currentColor",
										children: /* @__PURE__ */ jsx("path", {
											strokeLinecap: "round",
											strokeLinejoin: "round",
											strokeWidth: 2,
											d: "M5 13l4 4L19 7"
										})
									}) : /* @__PURE__ */ jsx("svg", {
										className: "w-8 h-8 text-muted-foreground",
										fill: "none",
										viewBox: "0 0 24 24",
										stroke: "currentColor",
										children: /* @__PURE__ */ jsx("path", {
											strokeLinecap: "round",
											strokeLinejoin: "round",
											strokeWidth: 2,
											d: "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
										})
									})
								}),
								/* @__PURE__ */ jsx("span", {
									className: "text-2xl font-mono tabular-nums",
									children: formatTime(recordingTime)
								}),
								isRecording && /* @__PURE__ */ jsxs("span", {
									className: "text-sm text-red-500 flex items-center gap-1",
									children: [/* @__PURE__ */ jsx("span", { className: "w-2 h-2 bg-red-500 rounded-full animate-pulse" }), t("recording")]
								}),
								recordedBlob && !isRecording && /* @__PURE__ */ jsx("span", {
									className: "text-sm text-green-600",
									children: t("recorded")
								})
							]
						}),
						error && /* @__PURE__ */ jsx("p", {
							className: "text-sm text-red-500",
							children: error
						}),
						/* @__PURE__ */ jsxs("div", {
							className: "flex gap-2",
							children: [
								!isRecording && !recordedBlob && /* @__PURE__ */ jsx(Button, {
									onClick: startRecording,
									variant: "default",
									children: t("start")
								}),
								isRecording && /* @__PURE__ */ jsx(Button, {
									onClick: stopRecording,
									variant: "destructive",
									children: t("stop")
								}),
								recordedBlob && !isRecording && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(Button, {
									onClick: playPreview,
									variant: "outline",
									children: t("preview")
								}), /* @__PURE__ */ jsx(Button, {
									onClick: resetRecording,
									variant: "outline",
									children: t("retry")
								})] })
							]
						})
					]
				}),
				/* @__PURE__ */ jsxs(DialogFooter, {
					className: "sm:justify-between",
					children: [/* @__PURE__ */ jsx(DialogClose, {
						asChild: true,
						children: /* @__PURE__ */ jsx(Button, {
							type: "button",
							variant: "ghost",
							children: t("cancel")
						})
					}), /* @__PURE__ */ jsx(Button, {
						type: "button",
						onClick: uploadRecording,
						disabled: !recordedBlob || isRecording || isUploading,
						children: isUploading ? t("uploading") : t("confirm")
					})]
				})
			]
		})]
	});
};
//#endregion
//#region src/canvas/constants/qwen-speakers.ts
const QWEN_SPEAKERS = [
	{
		value: "Vivian",
		gender: "female",
		langKey: "zh",
		language: "Chinese"
	},
	{
		value: "Serena",
		gender: "female",
		langKey: "zh",
		language: "Chinese"
	},
	{
		value: "Uncle_Fu",
		gender: "male",
		langKey: "zh",
		language: "Chinese"
	},
	{
		value: "Dylan",
		gender: "male",
		langKey: "zh",
		language: "Chinese"
	},
	{
		value: "Eric",
		gender: "male",
		langKey: "zh",
		language: "Chinese"
	},
	{
		value: "Ryan",
		gender: "male",
		langKey: "en",
		language: "English"
	},
	{
		value: "Aiden",
		gender: "male",
		langKey: "en",
		language: "English"
	},
	{
		value: "Ono_Anna",
		gender: "female",
		langKey: "ja",
		language: "Japanese"
	},
	{
		value: "Sohee",
		gender: "female",
		langKey: "ko",
		language: "Korean"
	}
];
const DEFAULT_QWEN_SPEAKER = QWEN_SPEAKERS[0];
//#endregion
//#region src/canvas/ui/select.tsx
function Select({ ...props }) {
	return /* @__PURE__ */ jsx(SelectPrimitive.Root, {
		"data-slot": "select",
		...props
	});
}
function SelectGroup({ ...props }) {
	return /* @__PURE__ */ jsx(SelectPrimitive.Group, {
		"data-slot": "select-group",
		...props
	});
}
function SelectValue({ ...props }) {
	return /* @__PURE__ */ jsx(SelectPrimitive.Value, {
		"data-slot": "select-value",
		...props
	});
}
function SelectTrigger({ className, size = "default", children, ...props }) {
	return /* @__PURE__ */ jsxs(SelectPrimitive.Trigger, {
		"data-slot": "select-trigger",
		"data-size": size,
		className: cn("border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className),
		...props,
		children: [children, /* @__PURE__ */ jsx(SelectPrimitive.Icon, {
			asChild: true,
			children: /* @__PURE__ */ jsx(ChevronDownIcon, { className: "size-4 opacity-50" })
		})]
	});
}
function SelectContent({ className, children, position = "popper", ...props }) {
	return /* @__PURE__ */ jsx(SelectPrimitive.Portal, { children: /* @__PURE__ */ jsxs(SelectPrimitive.Content, {
		"data-slot": "select-content",
		className: cn("bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border shadow-md", position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1", className),
		position,
		...props,
		children: [
			/* @__PURE__ */ jsx(SelectScrollUpButton, {}),
			/* @__PURE__ */ jsx(SelectPrimitive.Viewport, {
				className: cn("p-1", position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"),
				children
			}),
			/* @__PURE__ */ jsx(SelectScrollDownButton, {})
		]
	}) });
}
function SelectLabel({ className, ...props }) {
	return /* @__PURE__ */ jsx(SelectPrimitive.Label, {
		"data-slot": "select-label",
		className: cn("text-muted-foreground px-2 py-1.5 text-xs", className),
		...props
	});
}
function SelectItem({ className, children, endSlot, ...props }) {
	return /* @__PURE__ */ jsxs(SelectPrimitive.Item, {
		"data-slot": "select-item",
		className: cn("focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2", className),
		...props,
		children: [
			/* @__PURE__ */ jsx("span", {
				className: "absolute right-2 flex size-3.5 items-center justify-center",
				children: /* @__PURE__ */ jsx(SelectPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon, { className: "size-4" }) })
			}),
			/* @__PURE__ */ jsx(SelectPrimitive.ItemText, { children }),
			endSlot
		]
	});
}
function SelectSeparator({ className, ...props }) {
	return /* @__PURE__ */ jsx(SelectPrimitive.Separator, {
		"data-slot": "select-separator",
		className: cn("bg-border pointer-events-none -mx-1 my-1 h-px", className),
		...props
	});
}
function SelectScrollUpButton({ className, ...props }) {
	return /* @__PURE__ */ jsx(SelectPrimitive.ScrollUpButton, {
		"data-slot": "select-scroll-up-button",
		className: cn("flex cursor-default items-center justify-center py-1", className),
		...props,
		children: /* @__PURE__ */ jsx(ChevronUpIcon, { className: "size-4" })
	});
}
function SelectScrollDownButton({ className, ...props }) {
	return /* @__PURE__ */ jsx(SelectPrimitive.ScrollDownButton, {
		"data-slot": "select-scroll-down-button",
		className: cn("flex cursor-default items-center justify-center py-1", className),
		...props,
		children: /* @__PURE__ */ jsx(ChevronDownIcon, { className: "size-4" })
	});
}
//#endregion
//#region src/canvas/edges/custom-edge.tsx
const CustomEdge = ({ id, source, target, sourceHandleId: sourceHandle, targetHandleId: targetHandle, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style }) => {
	const [edgePath, labelX, labelY] = getBezierPath({
		sourceX,
		sourceY,
		targetX,
		targetY,
		sourcePosition,
		targetPosition
	});
	const edgeStyle = {
		...style,
		strokeWidth: 3,
		stroke: "#94a3b8",
		strokeLinecap: "round"
	};
	const { getNodes } = useReactFlow();
	const t = useTranslations("Workspace.handles");
	const options = useMemo(() => getEdgeTargetOptions({
		id,
		source,
		target,
		sourceHandle,
		targetHandle
	}, getNodes()), [
		id,
		source,
		target,
		sourceHandle,
		targetHandle,
		getNodes
	]);
	const onSelect = useCallback((newHandle) => {
		if (newHandle === targetHandle) return;
		const { edges, setEdges } = useFlow.getState();
		const occupant = options.find((o) => o.handleId === newHandle)?.single && edges.find((e) => e.id !== id && e.target === target && e.targetHandle === newHandle);
		setEdges(edges.map((e) => {
			if (e.id === id) return {
				...e,
				targetHandle: newHandle
			};
			if (occupant && e.id === occupant.id) return {
				...e,
				targetHandle
			};
			return e;
		}));
	}, [
		id,
		target,
		targetHandle,
		options
	]);
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(BaseEdge, {
		id,
		path: edgePath,
		style: edgeStyle
	}), options.length >= 2 && /* @__PURE__ */ jsx(EdgeLabelRenderer, { children: /* @__PURE__ */ jsx("div", {
		className: "nodrag nopan absolute",
		style: {
			transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
			pointerEvents: "all"
		},
		children: /* @__PURE__ */ jsxs(Select, {
			value: targetHandle ?? void 0,
			onValueChange: onSelect,
			children: [/* @__PURE__ */ jsx(SelectTrigger, {
				size: "sm",
				className: "h-6 bg-white px-2 text-xs shadow-sm",
				children: /* @__PURE__ */ jsx(SelectValue, {})
			}), /* @__PURE__ */ jsx(SelectContent, { children: options.map((o) => {
				const perNode = `byFeature.${o.feature}.${o.field}`;
				const label = t.has(perNode) ? t(perNode) : t.has(o.field) ? t(o.field) : o.field;
				return /* @__PURE__ */ jsx(SelectItem, {
					value: o.handleId,
					className: "text-xs",
					children: label
				}, o.handleId);
			}) })]
		})
	}) })] });
};
CustomEdge.displayName = "CustomEdge";
//#endregion
//#region src/canvas/lib/upload/validation.ts
/**
* Client file validation utilities
*
* Validate file size before upload
*/
/**
* Read image dimensions
*/
function getImageDimensions(file) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		const url = URL.createObjectURL(file);
		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve({
				width: img.naturalWidth,
				height: img.naturalHeight
			});
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error(getClientTranslator("Upload")("readImageFailed")));
		};
		img.src = url;
	});
}
/**
* Read video metadata
*/
function getVideoMetadata(file) {
	return new Promise((resolve, reject) => {
		const video = document.createElement("video");
		const url = URL.createObjectURL(file);
		video.preload = "metadata";
		video.onloadedmetadata = () => {
			URL.revokeObjectURL(url);
			resolve({
				width: video.videoWidth,
				height: video.videoHeight,
				duration: video.duration
			});
		};
		video.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error(getClientTranslator("Upload")("readVideoFailed")));
		};
		video.src = url;
	});
}
function isImageFile(file) {
	return file.type.startsWith("image/");
}
function isVideoFile(file) {
	return file.type.startsWith("video/");
}
/**
* Validate whether the file meets upload limits
*/
async function validateFile(file) {
	if (isImageFile(file)) try {
		const { width, height } = await getImageDimensions(file);
		return {
			allowed: true,
			fileInfo: {
				size: file.size,
				width,
				height
			}
		};
	} catch {
		return {
			allowed: true,
			fileInfo: { size: file.size }
		};
	}
	if (isVideoFile(file)) try {
		const { width, height, duration } = await getVideoMetadata(file);
		return {
			allowed: true,
			fileInfo: {
				size: file.size,
				width,
				height,
				duration
			}
		};
	} catch {
		return {
			allowed: true,
			fileInfo: { size: file.size }
		};
	}
	return {
		allowed: true,
		fileInfo: { size: file.size }
	};
}
var UploadValidationError = class extends Error {
	code = "UPLOAD_VALIDATION_ERROR";
	maxAllowed;
	fileInfo;
	constructor(result) {
		super(result.message || "File does not meet upload requirements");
		this.maxAllowed = result.maxAllowed;
		this.fileInfo = result.fileInfo;
	}
};
//#endregion
//#region src/canvas/lib/api/upload.ts
/**
* File upload API - simplified for open-source version.
* Uploads directly to local server storage.
*/
var upload_exports = /* @__PURE__ */ __exportAll({
	UploadValidationError: () => UploadValidationError,
	getPresignedUploadUrl: () => getPresignedUploadUrl
});
/**
* Upload file to local server.
* Returns a response compatible with the presigned URL interface.
*/
async function getPresignedUploadUrl(file) {
	const validation = await validateFile(file);
	if (!validation.allowed) throw new UploadValidationError(validation);
	const formData = new FormData();
	formData.append("file", file);
	const data = await apiClient("/api/upload", {
		method: "POST",
		body: formData,
		timeout: 6e5
	});
	return {
		uploadUrl: "",
		fileKey: data.fileKey,
		url: data.url,
		expiresIn: 0
	};
}
//#endregion
//#region src/canvas/hooks/use-upload.ts
/**
* File upload hook - simplified for open-source version
* Uploads files directly to local server
*/
function useUpload(options) {
	const t = useTranslations("Upload");
	const [state, setState] = useState({
		isUploading: false,
		progress: 0,
		error: null,
		uploadedFiles: []
	});
	const upload = useCallback(async (file) => {
		setState({
			isUploading: true,
			progress: 0,
			error: null,
			uploadedFiles: []
		});
		try {
			setState((prev) => ({
				...prev,
				progress: 30
			}));
			const data = await getPresignedUploadUrl(file);
			setState((prev) => ({
				...prev,
				progress: 100
			}));
			const response = {
				url: data.url,
				key: data.fileKey,
				size: file.size
			};
			setState((prev) => ({
				...prev,
				isUploading: false,
				progress: 100,
				uploadedFiles: [response]
			}));
			options?.onSuccess?.(response);
			return response;
		} catch (err) {
			const error = err instanceof Error ? err : /* @__PURE__ */ new Error("Upload failed");
			setState((prev) => ({
				...prev,
				isUploading: false,
				error
			}));
			if (err instanceof UploadValidationError) showErrorToast({ message: err.message });
			else showErrorToast({ message: t("failed") });
			options?.onError?.(error);
			return null;
		}
	}, [options, t]);
	const reset = useCallback(() => {
		setState({
			isUploading: false,
			progress: 0,
			error: null,
			uploadedFiles: []
		});
	}, []);
	return {
		...state,
		upload,
		reset
	};
}
function useMultipleUpload(options) {
	const t = useTranslations("Upload");
	const [state, setState] = useState({
		isUploading: false,
		progress: 0,
		error: null,
		uploadedFiles: []
	});
	const upload = useCallback(async (files) => {
		if (files.length === 0) return [];
		setState({
			isUploading: true,
			progress: 0,
			error: null,
			uploadedFiles: []
		});
		try {
			const results = [];
			let completed = 0;
			for (const file of files) {
				try {
					const data = await getPresignedUploadUrl(file);
					const response = {
						url: data.url,
						key: data.fileKey,
						size: file.size
					};
					results.push(response);
					options?.onFileComplete?.(response, completed);
				} catch (err) {
					logger.error(`Failed to upload file ${file.name}:`, err);
					if (err instanceof UploadValidationError) showErrorToast({ message: t("fileFailed", {
						name: file.name,
						reason: err.message
					}) });
				}
				completed++;
				const progress = Math.round(completed / files.length * 100);
				setState((prev) => ({
					...prev,
					progress
				}));
				options?.onProgress?.(progress);
			}
			if (results.length === 0) {
				const error = new Error(t("failed"));
				setState((prev) => ({
					...prev,
					isUploading: false,
					error
				}));
				options?.onError?.(error);
				return [];
			}
			setState((prev) => ({
				...prev,
				isUploading: false,
				progress: 100,
				uploadedFiles: results
			}));
			options?.onSuccess?.(results);
			return results;
		} catch (err) {
			const error = err instanceof Error ? err : /* @__PURE__ */ new Error("Upload failed");
			setState((prev) => ({
				...prev,
				isUploading: false,
				error
			}));
			if (err instanceof UploadValidationError) showErrorToast({ message: err.message });
			else showErrorToast({ message: t("partialFailed") });
			options?.onError?.(error);
			return [];
		}
	}, [options, t]);
	const reset = useCallback(() => {
		setState({
			isUploading: false,
			progress: 0,
			error: null,
			uploadedFiles: []
		});
	}, []);
	return {
		...state,
		upload,
		reset
	};
}
function useDropzone(options) {
	const tUpload = useTranslations("Upload");
	const [isDragActive, setIsDragActive] = useState(false);
	const [error, setError] = useState(null);
	const validateFiles = useCallback((files) => {
		setError(null);
		if (options?.maxFiles && files.length > options.maxFiles) {
			setError(tUpload("maxFilesExceeded", { max: options.maxFiles }));
			return [];
		}
		const validFiles = [];
		for (const file of files) {
			if (options?.maxSize && file.size > options.maxSize) {
				setError(tUpload("fileTooLarge", {
					name: file.name,
					max: Math.round(options.maxSize / 1024 / 1024)
				}));
				continue;
			}
			if (options?.accept) {
				if (!options.accept.split(",").map((t) => t.trim()).some((type) => {
					if (type.endsWith("/*")) return file.type.startsWith(`${type.split("/")[0]}/`);
					return type === file.type;
				})) {
					setError(tUpload("fileTypeNotAccepted", { name: file.name }));
					continue;
				}
			}
			validFiles.push(file);
		}
		return validFiles;
	}, [options, tUpload]);
	const handleDragEnter = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragActive(true);
	}, []);
	const handleDragLeave = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragActive(false);
	}, []);
	const handleDragOver = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);
	const handleDrop = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragActive(false);
		const files = Array.from(e.dataTransfer.files);
		const validFiles = validateFiles(files);
		if (validFiles.length > 0) options?.onDrop?.(validFiles);
	}, [validateFiles, options]);
	const handleFileInput = useCallback((e) => {
		const files = Array.from(e.target.files || []);
		const validFiles = validateFiles(files);
		if (validFiles.length > 0) options?.onDrop?.(validFiles);
	}, [validateFiles, options]);
	return {
		isDragActive,
		error,
		handleDragEnter,
		handleDragLeave,
		handleDragOver,
		handleDrop,
		handleFileInput,
		getRootProps: () => ({
			onDragEnter: handleDragEnter,
			onDragLeave: handleDragLeave,
			onDragOver: handleDragOver,
			onDrop: handleDrop
		}),
		getInputProps: () => ({
			type: "file",
			onChange: handleFileInput,
			accept: options?.accept,
			multiple: (options?.maxFiles ?? 1) > 1
		})
	};
}
//#endregion
//#region src/canvas/ui/tooltip.tsx
function TooltipProvider({ delayDuration = 0, ...props }) {
	return /* @__PURE__ */ jsx(TooltipPrimitive.Provider, {
		"data-slot": "tooltip-provider",
		delayDuration,
		...props
	});
}
function Tooltip({ ...props }) {
	return /* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsx(TooltipPrimitive.Root, {
		"data-slot": "tooltip",
		...props
	}) });
}
function TooltipTrigger({ ...props }) {
	return /* @__PURE__ */ jsx(TooltipPrimitive.Trigger, {
		"data-slot": "tooltip-trigger",
		...props
	});
}
function TooltipContent({ className, sideOffset = 0, children, ...props }) {
	return /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsxs(TooltipPrimitive.Content, {
		"data-slot": "tooltip-content",
		sideOffset,
		className: cn("bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance", className),
		...props,
		children: [children, /* @__PURE__ */ jsx(TooltipPrimitive.Arrow, { className: "bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" })]
	}) });
}
//#endregion
//#region src/canvas/ui/audio-recorder.tsx
let recorder;
let recordingChunks = [];
let timerTimeout;
const padWithLeadingZeros = (num, length) => {
	return String(num).padStart(length, "0");
};
const AudioRecorderWithVisualizer = ({ className, timerClassName: _timerClassName, onRecord }) => {
	const t = useTranslations("Recorder");
	const [isRecording, setIsRecording] = useState(false);
	const [_isRecordingFinished, setIsRecordingFinished] = useState(false);
	const [timer, setTimer] = useState(0);
	const [currentRecord, setCurrentRecord] = useState({
		id: -1,
		name: "",
		file: null
	});
	const hours = Math.floor(timer / 3600);
	const minutes = Math.floor(timer % 3600 / 60);
	const seconds = timer % 60;
	const [_hourLeft, _hourRight] = useMemo(() => padWithLeadingZeros(hours, 2).split(""), [hours]);
	const [_minuteLeft, _minuteRight] = useMemo(() => padWithLeadingZeros(minutes, 2).split(""), [minutes]);
	const [_secondLeft, _secondRight] = useMemo(() => padWithLeadingZeros(seconds, 2).split(""), [seconds]);
	const mediaRecorderRef = useRef({
		stream: null,
		analyser: null,
		mediaRecorder: null,
		audioContext: null
	});
	const canvasRef = useRef(null);
	const animationRef = useRef(null);
	const mimeType = MediaRecorder.isTypeSupported("audio/mpeg") ? "audio/mpeg" : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/wav";
	function startRecording() {
		if (navigator.mediaDevices?.getUserMedia) navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
			setIsRecording(true);
			const AudioContext = window.AudioContext;
			const audioCtx = new AudioContext();
			const analyser = audioCtx.createAnalyser();
			audioCtx.createMediaStreamSource(stream).connect(analyser);
			mediaRecorderRef.current = {
				stream,
				analyser,
				mediaRecorder: null,
				audioContext: audioCtx
			};
			const options = { mimeType };
			mediaRecorderRef.current.mediaRecorder = new MediaRecorder(stream, options);
			mediaRecorderRef.current.mediaRecorder.start();
			recordingChunks = [];
			recorder = new MediaRecorder(stream);
			recorder.start();
			recorder.ondataavailable = (e) => {
				recordingChunks.push(e.data);
			};
		}).catch((error) => {
			showErrorToast({ message: t("audioPermissionError") });
			logger.error(error);
		});
	}
	function stopRecording() {
		recorder.onstop = () => {
			const recordBlob = new Blob(recordingChunks, { type: mimeType });
			setCurrentRecord({
				...currentRecord,
				file: window.URL.createObjectURL(recordBlob)
			});
			recordingChunks = [];
			const fileName = `recording-${Date.now()}.${mimeType.split("/")[1]}`;
			onRecord(new File([recordBlob], fileName, { type: mimeType }));
		};
		recorder.stop();
		setIsRecording(false);
		setIsRecordingFinished(true);
		setTimer(0);
		clearTimeout(timerTimeout);
	}
	function resetRecording() {
		const { mediaRecorder, stream, analyser, audioContext } = mediaRecorderRef.current;
		if (mediaRecorder) {
			mediaRecorder.onstop = () => {
				recordingChunks = [];
			};
			mediaRecorder.stop();
		} else logger.error("recorder instance is null");
		if (analyser) analyser.disconnect();
		if (stream) stream.getTracks().forEach((track) => track.stop());
		if (audioContext) audioContext.close();
		setIsRecording(false);
		setIsRecordingFinished(true);
		setTimer(0);
		clearTimeout(timerTimeout);
		cancelAnimationFrame(animationRef.current || 0);
		const canvas = canvasRef.current;
		if (canvas) {
			const canvasCtx = canvas.getContext("2d");
			if (canvasCtx) {
				const WIDTH = canvas.width;
				const HEIGHT = canvas.height;
				canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);
			}
		}
	}
	const handleSubmit = () => {
		stopRecording();
	};
	useEffect(() => {
		if (isRecording) timerTimeout = setTimeout(() => {
			setTimer(timer + 1);
		}, 1e3);
		return () => clearTimeout(timerTimeout);
	}, [isRecording, timer]);
	useEffect(() => {
		if (!canvasRef.current) return;
		const canvas = canvasRef.current;
		const canvasCtx = canvas.getContext("2d");
		const WIDTH = canvas.width;
		const HEIGHT = canvas.height;
		const drawWaveform = (dataArray) => {
			if (!canvasCtx) return;
			canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);
			canvasCtx.fillStyle = "#939393";
			const barWidth = 1;
			const maxBarHeight = HEIGHT / 2.5;
			const numBars = Math.floor(WIDTH / 2);
			for (let i = 0; i < numBars; i++) {
				const barHeight = (dataArray[i] / 128) ** 8 * maxBarHeight;
				const x = 2 * i;
				const y = HEIGHT / 2 - barHeight / 2;
				canvasCtx.fillRect(x, y, barWidth, barHeight);
			}
		};
		const visualizeVolume = () => {
			if (!mediaRecorderRef.current?.stream?.getAudioTracks()[0]?.getSettings().sampleRate) return;
			const bufferLength = mediaRecorderRef.current?.stream?.getAudioTracks()[0]?.getSettings().sampleRate / 100;
			const dataArray = new Uint8Array(bufferLength);
			const draw = () => {
				if (!isRecording) {
					cancelAnimationFrame(animationRef.current || 0);
					return;
				}
				animationRef.current = requestAnimationFrame(draw);
				mediaRecorderRef.current?.analyser?.getByteTimeDomainData(dataArray);
				drawWaveform(dataArray);
			};
			draw();
		};
		if (isRecording) visualizeVolume();
		else {
			if (canvasCtx) canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);
			cancelAnimationFrame(animationRef.current || 0);
		}
		return () => {
			cancelAnimationFrame(animationRef.current || 0);
		};
	}, [isRecording]);
	return /* @__PURE__ */ jsx("div", {
		className: cn("w-full", className),
		children: !isRecording ? /* @__PURE__ */ jsx("div", {
			className: "w-full flex flex-col items-center justify-center gap-3 py-8",
			children: /* @__PURE__ */ jsxs(Button, {
				onClick: () => startRecording(),
				size: "lg",
				className: "flex items-center gap-2",
				children: [/* @__PURE__ */ jsx(Mic, { className: "h-5 w-5" }), t("audioStart")]
			})
		}) : /* @__PURE__ */ jsxs("div", {
			className: "w-full space-y-3",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "relative w-full h-32 bg-muted rounded-lg overflow-hidden border",
				children: [/* @__PURE__ */ jsx("canvas", {
					ref: canvasRef,
					className: "h-full w-full",
					width: 800,
					height: 128
				}), /* @__PURE__ */ jsxs("div", {
					className: "absolute top-3 left-3 flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-full text-sm font-medium",
					children: [/* @__PURE__ */ jsx("div", { className: "h-2 w-2 rounded-full bg-white animate-pulse" }), /* @__PURE__ */ jsxs("span", {
						className: "font-mono",
						children: [
							padWithLeadingZeros(minutes, 2),
							":",
							padWithLeadingZeros(seconds, 2)
						]
					})]
				})]
			}), /* @__PURE__ */ jsxs("div", {
				className: "flex gap-2",
				children: [/* @__PURE__ */ jsxs(Tooltip, { children: [/* @__PURE__ */ jsx(TooltipTrigger, {
					asChild: true,
					children: /* @__PURE__ */ jsxs(Button, {
						onClick: resetRecording,
						size: "sm",
						variant: "outline",
						className: "flex-1",
						children: [/* @__PURE__ */ jsx(Trash, { className: "h-4 w-4 mr-2" }), t("audioCancel")]
					})
				}), /* @__PURE__ */ jsx(TooltipContent, { children: /* @__PURE__ */ jsx("span", { children: t("audioCancelTooltip") }) })] }), /* @__PURE__ */ jsxs(Tooltip, { children: [/* @__PURE__ */ jsx(TooltipTrigger, {
					asChild: true,
					children: /* @__PURE__ */ jsxs(Button, {
						onClick: handleSubmit,
						size: "sm",
						className: "flex-1",
						children: [/* @__PURE__ */ jsx(StopCircle, { className: "h-4 w-4 mr-2" }), t("audioFinish")]
					})
				}), /* @__PURE__ */ jsx(TooltipContent, { children: /* @__PURE__ */ jsx("span", { children: t("audioFinishTooltip") }) })] })]
			})]
		})
	});
};
//#endregion
//#region src/canvas/ui/tabs.tsx
function Tabs({ className, ...props }) {
	return /* @__PURE__ */ jsx(TabsPrimitive.Root, {
		"data-slot": "tabs",
		className: cn("flex flex-col gap-2", className),
		...props
	});
}
function TabsList({ className, ...props }) {
	return /* @__PURE__ */ jsx(TabsPrimitive.List, {
		"data-slot": "tabs-list",
		className: cn("bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px]", className),
		...props
	});
}
function TabsTrigger({ className, ...props }) {
	return /* @__PURE__ */ jsx(TabsPrimitive.Trigger, {
		"data-slot": "tabs-trigger",
		className: cn("data-[state=active]:bg-background dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className),
		...props
	});
}
function TabsContent({ className, ...props }) {
	return /* @__PURE__ */ jsx(TabsPrimitive.Content, {
		"data-slot": "tabs-content",
		className: cn("flex-1 outline-none", className),
		...props
	});
}
//#endregion
//#region src/canvas/ui/alert-dialog.tsx
function AlertDialog({ ...props }) {
	return /* @__PURE__ */ jsx(AlertDialogPrimitive.Root, {
		"data-slot": "alert-dialog",
		...props
	});
}
function AlertDialogTrigger({ ...props }) {
	return /* @__PURE__ */ jsx(AlertDialogPrimitive.Trigger, {
		"data-slot": "alert-dialog-trigger",
		...props
	});
}
function AlertDialogPortal({ ...props }) {
	return /* @__PURE__ */ jsx(AlertDialogPrimitive.Portal, {
		"data-slot": "alert-dialog-portal",
		...props
	});
}
function AlertDialogOverlay({ className, ...props }) {
	return /* @__PURE__ */ jsx(AlertDialogPrimitive.Overlay, {
		"data-slot": "alert-dialog-overlay",
		className: cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50", className),
		...props
	});
}
function AlertDialogContent({ className, ...props }) {
	return /* @__PURE__ */ jsxs(AlertDialogPortal, { children: [/* @__PURE__ */ jsx(AlertDialogOverlay, {}), /* @__PURE__ */ jsx(AlertDialogPrimitive.Content, {
		"data-slot": "alert-dialog-content",
		className: cn("bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg", className),
		...props
	})] });
}
function AlertDialogHeader({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "alert-dialog-header",
		className: cn("flex flex-col gap-2 text-center sm:text-left", className),
		...props
	});
}
function AlertDialogFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "alert-dialog-footer",
		className: cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className),
		...props
	});
}
function AlertDialogTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx(AlertDialogPrimitive.Title, {
		"data-slot": "alert-dialog-title",
		className: cn("text-lg font-semibold", className),
		...props
	});
}
function AlertDialogDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx(AlertDialogPrimitive.Description, {
		"data-slot": "alert-dialog-description",
		className: cn("text-muted-foreground text-sm", className),
		...props
	});
}
function AlertDialogAction({ className, ...props }) {
	return /* @__PURE__ */ jsx(AlertDialogPrimitive.Action, {
		className: cn(buttonVariants(), className),
		...props
	});
}
function AlertDialogCancel({ className, ...props }) {
	return /* @__PURE__ */ jsx(AlertDialogPrimitive.Cancel, {
		className: cn(buttonVariants({ variant: "outline" }), className),
		...props
	});
}
//#endregion
//#region src/canvas/nodes/base/node-combo-button.tsx
function NodeComboButton() {
	const nodeId = useNodeId();
	const t = useTranslations("Workspace.nodes.base");
	const comboMode = useFlow((s) => s.comboMode);
	const isInCombo = useFlow((s) => nodeId ? s.isInCombo(nodeId) : false);
	const toggleCombo = useFlow((s) => s.toggleCombo);
	const nodeType = useStore((state) => {
		return state.nodeLookup.get(nodeId ?? "")?.type;
	});
	if (!comboMode || !isModalityNode(nodeType)) return null;
	return /* @__PURE__ */ jsx("div", {
		className: "absolute -bottom-3 left-1/2 -translate-x-1/2 z-[100] nodrag",
		children: /* @__PURE__ */ jsxs("button", {
			type: "button",
			onClick: (e) => {
				e.stopPropagation();
				if (nodeId) toggleCombo(nodeId);
			},
			className: cn("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 shadow-md", "border-2 cursor-pointer", isInCombo ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90" : "bg-background text-muted-foreground border-muted-foreground/30 hover:border-primary hover:text-primary"),
			children: [isInCombo ? /* @__PURE__ */ jsx(CheckCircle, { className: "h-3.5 w-3.5" }) : /* @__PURE__ */ jsx(Circle, { className: "h-3.5 w-3.5" }), /* @__PURE__ */ jsx("span", { children: isInCombo ? t("selected") : t("select") })]
		})
	});
}
//#endregion
//#region src/canvas/nodes/base/node-comment-box.tsx
function NodeCommentBox() {
	const nodeId = useNodeId();
	const { updateNodeData } = useReactFlow();
	const t = useTranslations("Workspace.nodes.base");
	const comment = useStore(useCallback((state) => {
		return coerceBaseNodeData(state.nodeLookup.get(nodeId ?? "")?.data).comment;
	}, [nodeId]));
	const [isEditing, setIsEditing] = useState(false);
	const [localComment, setLocalComment] = useState(comment ?? "");
	useEffect(() => {
		setLocalComment(comment ?? "");
	}, [comment]);
	const handleBlur = useCallback(() => {
		setIsEditing(false);
		if (nodeId && localComment !== comment) updateNodeData(nodeId, { comment: localComment || void 0 });
	}, [
		nodeId,
		localComment,
		comment,
		updateNodeData
	]);
	const handleKeyDown = useCallback((e) => {
		if (e.key === "Escape") {
			setLocalComment(comment ?? "");
			setIsEditing(false);
		}
	}, [comment]);
	const handleRemove = useCallback(() => {
		if (nodeId) {
			updateNodeData(nodeId, { comment: void 0 });
			setLocalComment("");
		}
	}, [nodeId, updateNodeData]);
	if (comment === void 0) return null;
	return /* @__PURE__ */ jsx("div", {
		className: cn("absolute -top-2 left-0 right-0 -translate-y-full", "nodrag"),
		children: /* @__PURE__ */ jsxs("div", {
			className: cn("relative rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 mb-2", "shadow-sm"),
			children: [/* @__PURE__ */ jsx("button", {
				type: "button",
				onClick: handleRemove,
				className: "absolute -right-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-200",
				title: t("removeComment"),
				children: /* @__PURE__ */ jsx(X, { className: "h-3 w-3" })
			}), isEditing ? /* @__PURE__ */ jsx("textarea", {
				value: localComment,
				onChange: (e) => setLocalComment(e.target.value),
				onBlur: handleBlur,
				onKeyDown: handleKeyDown,
				className: "w-full min-h-[40px] resize-none rounded border-0 bg-transparent text-sm text-amber-800 dark:text-amber-200 placeholder-amber-400 dark:placeholder-amber-500 focus:outline-none focus:ring-0",
				placeholder: t("commentPlaceholder"),
				autoFocus: true
			}) : /* @__PURE__ */ jsx("div", {
				onClick: () => setIsEditing(true),
				className: cn("min-h-[24px] cursor-text text-sm text-amber-800 dark:text-amber-200 whitespace-pre-wrap", !localComment && "text-amber-400 dark:text-amber-500 italic"),
				children: localComment || t("clickToAddComment")
			})]
		})
	});
}
//#endregion
//#region src/canvas/ui/dropdown-menu.tsx
function DropdownMenu({ ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Root, {
		"data-slot": "dropdown-menu",
		...props
	});
}
function DropdownMenuPortal({ ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, {
		"data-slot": "dropdown-menu-portal",
		...props
	});
}
function DropdownMenuTrigger({ ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Trigger, {
		"data-slot": "dropdown-menu-trigger",
		...props
	});
}
function DropdownMenuContent({ className, sideOffset = 4, ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.Content, {
		"data-slot": "dropdown-menu-content",
		sideOffset,
		className: cn("bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-h-(--radix-dropdown-menu-content-available-height) min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border p-1 shadow-md", className),
		...props
	}) });
}
function DropdownMenuGroup({ ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Group, {
		"data-slot": "dropdown-menu-group",
		...props
	});
}
function DropdownMenuItem({ className, inset, variant = "default", ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Item, {
		"data-slot": "dropdown-menu-item",
		"data-inset": inset,
		"data-variant": variant,
		className: cn("focus:bg-accent focus:text-accent-foreground data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:bg-destructive/10 dark:data-[variant=destructive]:focus:bg-destructive/20 data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive [&_svg:not([class*='text-'])]:text-muted-foreground relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className),
		...props
	});
}
function DropdownMenuCheckboxItem({ className, children, checked, ...props }) {
	return /* @__PURE__ */ jsxs(DropdownMenuPrimitive.CheckboxItem, {
		"data-slot": "dropdown-menu-checkbox-item",
		className: cn("focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className),
		checked,
		...props,
		children: [/* @__PURE__ */ jsx("span", {
			className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center",
			children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CheckIcon, { className: "size-4" }) })
		}), children]
	});
}
function DropdownMenuRadioGroup({ ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.RadioGroup, {
		"data-slot": "dropdown-menu-radio-group",
		...props
	});
}
function DropdownMenuRadioItem({ className, children, ...props }) {
	return /* @__PURE__ */ jsxs(DropdownMenuPrimitive.RadioItem, {
		"data-slot": "dropdown-menu-radio-item",
		className: cn("focus:bg-accent focus:text-accent-foreground relative flex cursor-default items-center gap-2 rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className),
		...props,
		children: [/* @__PURE__ */ jsx("span", {
			className: "pointer-events-none absolute left-2 flex size-3.5 items-center justify-center",
			children: /* @__PURE__ */ jsx(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx(CircleIcon, { className: "size-2 fill-current" }) })
		}), children]
	});
}
function DropdownMenuLabel({ className, inset, ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Label, {
		"data-slot": "dropdown-menu-label",
		"data-inset": inset,
		className: cn("px-2 py-1.5 text-sm font-medium data-[inset]:pl-8", className),
		...props
	});
}
function DropdownMenuSeparator({ className, ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Separator, {
		"data-slot": "dropdown-menu-separator",
		className: cn("bg-border -mx-1 my-1 h-px", className),
		...props
	});
}
function DropdownMenuShortcut({ className, ...props }) {
	return /* @__PURE__ */ jsx("span", {
		"data-slot": "dropdown-menu-shortcut",
		className: cn("text-muted-foreground ml-auto text-xs tracking-widest", className),
		...props
	});
}
function DropdownMenuSub({ ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.Sub, {
		"data-slot": "dropdown-menu-sub",
		...props
	});
}
function DropdownMenuSubTrigger({ className, inset, children, ...props }) {
	return /* @__PURE__ */ jsxs(DropdownMenuPrimitive.SubTrigger, {
		"data-slot": "dropdown-menu-sub-trigger",
		"data-inset": inset,
		className: cn("focus:bg-accent focus:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[inset]:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4", className),
		...props,
		children: [children, /* @__PURE__ */ jsx(ChevronRightIcon, { className: "ml-auto size-4" })]
	});
}
function DropdownMenuSubContent({ className, ...props }) {
	return /* @__PURE__ */ jsx(DropdownMenuPrimitive.SubContent, {
		"data-slot": "dropdown-menu-sub-content",
		className: cn("bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 min-w-[8rem] origin-(--radix-dropdown-menu-content-transform-origin) overflow-hidden rounded-md border p-1 shadow-lg", className),
		...props
	});
}
//#endregion
//#region src/canvas/nodes/base/node-header.tsx
/**
* Recursively collect all downstream node IDs starting from a given node.
*/
function getDescendantNodeIds(nodeId, edges) {
	const descendants = /* @__PURE__ */ new Set();
	const queue = [nodeId];
	while (queue.length > 0) {
		const current = queue.shift();
		for (const edge of edges) if (edge.source === current && !descendants.has(edge.target)) {
			descendants.add(edge.target);
			queue.push(edge.target);
		}
	}
	return descendants;
}
/**
* A container for a consistent header layout intended to be used inside the
* `<BaseNodeShell />` component.
*/
const NodeHeader = forwardRef(({ className, ...props }, ref) => {
	return /* @__PURE__ */ jsx("header", {
		ref,
		...props,
		className: cn("flex items-center justify-between gap-2 px-3 py-2", className)
	});
});
NodeHeader.displayName = "NodeHeader";
/**
* The title text for the node. To maintain a native application feel, the title
* text is not selectable.
*/
const NodeHeaderTitle = forwardRef(({ className, asChild, ...props }, ref) => {
	return /* @__PURE__ */ jsx(asChild ? Slot : "h3", {
		ref,
		...props,
		className: cn(className, "select-none flex-1 font-semibold")
	});
});
NodeHeaderTitle.displayName = "NodeHeaderTitle";
const NodeHeaderIcon = forwardRef(({ className, ...props }, ref) => {
	return /* @__PURE__ */ jsx("span", {
		ref,
		...props,
		className: cn(className, "[&>*]:size-5")
	});
});
NodeHeaderIcon.displayName = "NodeHeaderIcon";
/**
* A container for right-aligned action buttons in the node header.
*/
const NodeHeaderActions = forwardRef(({ className, ...props }, ref) => {
	return /* @__PURE__ */ jsx("div", {
		ref,
		...props,
		className: cn("ml-auto flex items-center gap-1 justify-self-end", className)
	});
});
NodeHeaderActions.displayName = "NodeHeaderActions";
/**
* A thin wrapper around the `<Button />` component with a fixed sized suitable
* for icons.
*
* Because the `<NodeHeaderAction />` component is intended to render icons, it's
* important to provide a meaningful and accessible `label` prop that describes
* the action.
*/
const NodeHeaderAction = forwardRef(({ className, label, title, ...props }, ref) => {
	return /* @__PURE__ */ jsx(Button, {
		ref,
		variant: "ghost",
		"aria-label": label,
		title: title ?? label,
		className: cn(className, "nodrag size-6 p-1"),
		...props
	});
});
NodeHeaderAction.displayName = "NodeHeaderAction";
/**
* Renders a header action that opens a dropdown menu when clicked. The dropdown
* trigger is a button with a menu icon. The trigger's content can be changed
* by using the `trigger` prop.
*
* Any children passed to the `<NodeHeaderMenuAction />` component will be rendered
* inside the dropdown menu. By default, a delete action and comment action are
* automatically included. Set `showDelete={false}` or `showComment={false}` to hide them.
*/
const NodeHeaderMenuAction = forwardRef(({ trigger, children, showDelete = true, showComment = true, ...props }, ref) => {
	const id = useNodeId();
	const { updateNodeData } = useReactFlow();
	const edges = useFlow((s) => s.edges);
	const t = useTranslations("Workspace.nodes.base");
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const comment = useStore(useCallback((state) => {
		return coerceBaseNodeData(state.nodeLookup.get(id ?? "")?.data).comment;
	}, [id]));
	const descendantCount = useMemo(() => {
		if (!id) return 0;
		return getDescendantNodeIds(id, edges).size;
	}, [id, edges]);
	const handleDeleteNode = useCallback(() => {
		if (!id) return;
		useFlow.getState().removeNode(id);
		setShowDeleteDialog(false);
	}, [id]);
	const handleDeleteNodeAndChildren = useCallback(() => {
		if (!id) return;
		const { edges: curEdges, nodes, setNodes, setEdges, commitHistory } = useFlow.getState();
		commitHistory();
		const descendants = getDescendantNodeIds(id, curEdges);
		const toRemove = /* @__PURE__ */ new Set([id, ...descendants]);
		setNodes(nodes.filter((node) => !toRemove.has(node.id)));
		setEdges(curEdges.filter((edge) => !toRemove.has(edge.source) && !toRemove.has(edge.target)));
		setShowDeleteDialog(false);
	}, [id]);
	const handleDeleteChildren = useCallback(() => {
		if (!id) return;
		const { edges: curEdges, nodes, setNodes, setEdges, commitHistory } = useFlow.getState();
		commitHistory();
		const descendants = getDescendantNodeIds(id, curEdges);
		setNodes(nodes.filter((node) => !descendants.has(node.id)));
		setEdges(curEdges.filter((edge) => !descendants.has(edge.source) && !descendants.has(edge.target)));
		setShowDeleteDialog(false);
	}, [id]);
	const handleToggleComment = useCallback(() => {
		if (!id) return;
		if (comment === void 0) updateNodeData(id, { comment: "" });
	}, [
		id,
		comment,
		updateNodeData
	]);
	const hasComment = comment !== void 0;
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs(DropdownMenu, { children: [/* @__PURE__ */ jsx(DropdownMenuTrigger, {
		asChild: true,
		children: /* @__PURE__ */ jsx(NodeHeaderAction, {
			ref,
			...props,
			children: trigger ?? /* @__PURE__ */ jsx(MenuIcon, {})
		})
	}), /* @__PURE__ */ jsxs(DropdownMenuContent, { children: [
		children,
		showComment && !hasComment && /* @__PURE__ */ jsxs(Fragment, { children: [children && /* @__PURE__ */ jsx(DropdownMenuSeparator, {}), /* @__PURE__ */ jsxs(DropdownMenuItem, {
			onClick: handleToggleComment,
			children: [/* @__PURE__ */ jsx(MessageSquare, { className: "mr-2 h-4 w-4" }), t("addComment")]
		})] }),
		showDelete && /* @__PURE__ */ jsxs(Fragment, { children: [(children || showComment && !hasComment) && /* @__PURE__ */ jsx(DropdownMenuSeparator, {}), /* @__PURE__ */ jsxs(DropdownMenuItem, {
			onClick: () => descendantCount > 0 ? setShowDeleteDialog(true) : handleDeleteNode(),
			children: [/* @__PURE__ */ jsx(Trash, { className: "mr-2 h-4 w-4" }), t("delete")]
		})] })
	] })] }), /* @__PURE__ */ jsx(AlertDialog, {
		open: showDeleteDialog,
		onOpenChange: setShowDeleteDialog,
		children: /* @__PURE__ */ jsxs(AlertDialogContent, { children: [/* @__PURE__ */ jsxs(AlertDialogHeader, { children: [/* @__PURE__ */ jsx(AlertDialogTitle, { children: t("deleteConfirmTitle") }), /* @__PURE__ */ jsx(AlertDialogDescription, { children: t("deleteConfirmDescription", { count: descendantCount }) })] }), /* @__PURE__ */ jsxs(AlertDialogFooter, {
			className: "flex-col gap-2 sm:flex-col",
			children: [
				/* @__PURE__ */ jsxs(Button, {
					variant: "destructive",
					onClick: handleDeleteNode,
					className: "w-full",
					children: [/* @__PURE__ */ jsx(Trash, { className: "mr-2 h-4 w-4" }), t("deleteNodeOnly")]
				}),
				/* @__PURE__ */ jsxs(Button, {
					variant: "destructive",
					onClick: handleDeleteNodeAndChildren,
					className: "w-full",
					children: [/* @__PURE__ */ jsx(Trash2, { className: "mr-2 h-4 w-4" }), t("deleteNodeAndChildren", { count: descendantCount })]
				}),
				/* @__PURE__ */ jsxs(Button, {
					variant: "outline",
					onClick: handleDeleteChildren,
					className: "w-full",
					children: [/* @__PURE__ */ jsx(Trash, { className: "mr-2 h-4 w-4" }), t("deleteChildrenOnly", { count: descendantCount })]
				}),
				/* @__PURE__ */ jsx(AlertDialogCancel, {
					className: "w-full mt-0",
					children: t("cancel")
				})
			]
		})] })
	})] });
});
NodeHeaderMenuAction.displayName = "NodeHeaderMenuAction";
const NodeHeaderRunAction = ({ onClick }) => {
	const t = useTranslations("Workspace.nodes.base");
	return /* @__PURE__ */ jsx("div", {
		className: "flex items-center gap-2",
		children: /* @__PURE__ */ jsx(NodeHeaderAction, {
			onClick,
			variant: "ghost",
			label: t("run"),
			children: /* @__PURE__ */ jsx(Play, {})
		})
	});
};
NodeHeaderRunAction.displayName = "NodeHeaderRunAction";
const NodeHeaderComboAction = ({ onClick }) => {
	const nodeId = useNodeId();
	const comboMode = useFlow((s) => s.comboMode);
	const isInCombo = useFlow((s) => nodeId ? s.isInCombo(nodeId) : false);
	const toggleCombo = useFlow((s) => s.toggleCombo);
	const t = useTranslations("Workspace.nodes.base");
	const handleComboClick = useCallback(() => {
		if (!nodeId) return;
		toggleCombo(nodeId);
		onClick?.();
	}, [
		nodeId,
		toggleCombo,
		onClick
	]);
	return /* @__PURE__ */ jsxs(NodeHeaderAction, {
		onClick: handleComboClick,
		variant: "ghost",
		label: comboMode ? isInCombo ? t("removeFromCombo") : t("addToCombo") : t("startCombo"),
		className: cn("transition-all duration-200", !comboMode && "!size-8 !p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground", comboMode && isInCombo && "!size-8 !p-1.5 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm", comboMode && !isInCombo && "!size-8 !p-1.5 bg-background border-2 border-muted hover:border-primary hover:text-primary hover:bg-accent/50 text-muted-foreground"),
		children: [
			!comboMode && /* @__PURE__ */ jsx(Layers, { className: "size-full" }),
			comboMode && isInCombo && /* @__PURE__ */ jsx(CheckCircle, { className: "size-full" }),
			comboMode && !isInCombo && /* @__PURE__ */ jsx(Circle, { className: "size-full" })
		]
	});
};
NodeHeaderComboAction.displayName = "NodeHeaderComboAction";
const NodeHeaderPreviewAction = ({ onClick }) => {
	const t = useTranslations("Workspace.nodes.base");
	return /* @__PURE__ */ jsx(NodeHeaderAction, {
		onClick,
		variant: "ghost",
		label: t("preview"),
		children: /* @__PURE__ */ jsx(Eye, {})
	});
};
NodeHeaderPreviewAction.displayName = "NodeHeaderPreviewAction";
const NodeHeaderLockAction = ({ locked, onToggle }) => {
	const t = useTranslations("Workspace.nodes.base");
	return /* @__PURE__ */ jsx(NodeHeaderAction, {
		onClick: onToggle,
		variant: "ghost",
		label: locked ? t("unlock") : t("lock"),
		className: cn(locked && "text-amber-500"),
		children: locked ? /* @__PURE__ */ jsx(Lock, { className: "fill-current" }) : /* @__PURE__ */ jsx(Unlock, {})
	});
};
NodeHeaderLockAction.displayName = "NodeHeaderLockAction";
//#endregion
//#region src/canvas/nodes/base/node-loading-overlay.tsx
function NodeLoadingOverlay({ loading, elapsedSeconds, progressLabel, onCancel }) {
	const t = useTranslations("Workspace.nodes.base");
	if (!loading) return null;
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("div", {
		className: "pointer-events-none absolute -inset-[1px] z-50 rounded-[inherit]",
		style: {
			padding: "3px",
			background: "conic-gradient(from var(--angle, 0deg), transparent 0%, transparent 75%, #ef4444 78%, #f97316 82%, #eab308 86%, #22c55e 90%, #3b82f6 94%, #8b5cf6 98%, transparent 100%)",
			mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
			WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
			WebkitMaskComposite: "xor",
			maskComposite: "exclude",
			animation: "rotate-border 4s linear infinite"
		}
	}), /* @__PURE__ */ jsxs("div", {
		className: "absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/80 dark:bg-gray-800/80 group/loading",
		children: [
			progressLabel && /* @__PURE__ */ jsx("div", {
				className: cn("mb-2 max-w-[80%] truncate px-2 text-center text-xs", "bg-clip-text text-transparent", "animate-[shimmer_2.4s_linear_infinite]"),
				style: {
					backgroundImage: "linear-gradient(90deg, rgb(107 114 128) 0%, rgb(107 114 128) 45%, rgb(229 231 235) 50%, rgb(107 114 128) 55%, rgb(107 114 128) 100%)",
					backgroundSize: "200% 100%"
				},
				title: progressLabel,
				children: progressLabel
			}),
			onCancel ? /* @__PURE__ */ jsxs("button", {
				type: "button",
				onClick: (e) => {
					e.stopPropagation();
					onCancel();
				},
				title: t("cancelExecution"),
				"aria-label": t("cancelExecution"),
				className: "nodrag group/cancel relative flex h-12 w-12 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-red-500/10 dark:hover:bg-red-500/15",
				children: [/* @__PURE__ */ jsx(Loader2, { className: "h-8 w-8 animate-spin text-blue-500 transition-opacity duration-150 group-hover/cancel:opacity-0" }), /* @__PURE__ */ jsx(Square, { className: "absolute h-4 w-4 fill-red-500 text-red-500 opacity-0 transition-opacity duration-150 group-hover/cancel:opacity-100" })]
			}) : /* @__PURE__ */ jsx(Loader2, { className: "h-8 w-8 animate-spin text-blue-500" }),
			/* @__PURE__ */ jsxs("div", {
				className: "mt-1 text-lg font-semibold text-gray-700 dark:text-gray-300",
				children: [elapsedSeconds, "s"]
			})
		]
	})] });
}
//#endregion
//#region src/canvas/hooks/use-plugins-registry.ts
let fetchPromise = null;
const usePluginsRegistryStore = create(() => ({
	registry: null,
	isLoaded: false,
	isLoading: false,
	error: null
}));
async function loadRegistry() {
	if (usePluginsRegistryStore.getState().isLoaded || fetchPromise) return fetchPromise ?? Promise.resolve();
	usePluginsRegistryStore.setState({ isLoading: true });
	fetchPromise = (async () => {
		try {
			const res = await hostFetch(apiUrl("/api/plugins/registry"), {
				cache: "no-store",
				credentials: "same-origin"
			});
			if (!res.ok) {
				const j = await res.json();
				throw new Error(j.error || `HTTP ${res.status}`);
			}
			const payload = await res.json();
			usePluginsRegistryStore.setState({
				registry: payload,
				isLoaded: true,
				isLoading: false,
				error: null
			});
		} catch (e) {
			usePluginsRegistryStore.setState({
				isLoaded: false,
				isLoading: false,
				error: e instanceof Error ? e : new Error(String(e))
			});
		} finally {
			fetchPromise = null;
		}
	})();
	return fetchPromise;
}
function usePluginsRegistry() {
	const registry = usePluginsRegistryStore((s) => s.registry);
	const isLoaded = usePluginsRegistryStore((s) => s.isLoaded);
	const isLoading = usePluginsRegistryStore((s) => s.isLoading);
	const error = usePluginsRegistryStore((s) => s.error);
	useEffect(() => {
		loadRegistry();
	}, []);
	return {
		registry,
		isLoaded,
		isLoading,
		error
	};
}
/**
* Force refresh registry from the server (e.g. after install/update/remove).
*/
async function refreshPluginsRegistry() {
	usePluginsRegistryStore.setState({ isLoaded: false });
	await loadRegistry();
}
function dedupeIds(list) {
	const seen = /* @__PURE__ */ new Set();
	return list.map((s) => s.trim()).filter((s) => Boolean(s)).filter((s) => {
		if (seen.has(s)) return false;
		seen.add(s);
		return true;
	});
}
/** Plugin directory names registered for a single ABI `nodeSlot`. */
function useNodePluginIds(nodeSlot) {
	return dedupeIds(usePluginsRegistryStore((s) => s.registry)?.nodePluginMap?.[nodeSlot] ?? []);
}
const useLiveModelsStore = create(() => ({ byPlugin: {} }));
const MODEL_CATALOG_TTL_MS = 6e5;
const catalogInflight = /* @__PURE__ */ new Map();
/**
* Fetch (or refresh, once the TTL elapsed) a plugin's live model catalog. Safe
* to call eagerly — it is a no-op for plugins without a catalog and while a
* fresh result is cached.
*/
async function loadPluginModelCatalog(pluginId) {
	const catalog = usePluginsRegistryStore.getState().registry?.plugins?.[pluginId]?.modelCatalog;
	if (!catalog) return;
	const cached = useLiveModelsStore.getState().byPlugin[pluginId];
	if (cached && Date.now() - cached.fetchedAt < MODEL_CATALOG_TTL_MS) return;
	const inflight = catalogInflight.get(pluginId);
	if (inflight) return inflight;
	const job = (async () => {
		try {
			const res = catalog.authEnv ? await hostFetch(apiUrl(`/api/plugins/model-catalog?pluginId=${encodeURIComponent(pluginId)}`), {
				cache: "no-store",
				credentials: "same-origin"
			}) : await fetch(catalog.url, {
				method: "GET",
				cache: "no-store"
			});
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			const bySlot = filterModelCatalog(catalog, await res.json());
			useLiveModelsStore.setState((s) => ({ byPlugin: {
				...s.byPlugin,
				[pluginId]: {
					fetchedAt: Date.now(),
					bySlot
				}
			} }));
		} catch (e) {
			console.warn(`[tongflow] model catalog for ${pluginId} unavailable:`, e);
		} finally {
			catalogInflight.delete(pluginId);
		}
	})();
	catalogInflight.set(pluginId, job);
	return job;
}
/**
* Synchronous read of a plugin's model ids for one slot: the static shortlist
* (declared order, first = default) followed by any live-catalog extras.
*/
function getNodePluginModels(nodeSlot, pluginId) {
	const declared = usePluginsRegistryStore.getState().registry?.plugins?.[pluginId]?.methodsByNodeSlot?.[nodeSlot]?.models ?? [];
	const live = useLiveModelsStore.getState().byPlugin[pluginId]?.bySlot[nodeSlot] ?? [];
	return dedupeIds([...declared, ...live]);
}
/**
* Model ids a plugin offers for one ABI `nodeSlot` (empty for single-model
* plugins — the model dropdown is hidden in that case): the static shortlist
* plus live-catalog extras, which this hook fetches on first use.
*/
function useNodePluginModels(nodeSlot, pluginId) {
	const registry = usePluginsRegistryStore((s) => s.registry);
	const declared = registry?.plugins?.[pluginId]?.methodsByNodeSlot?.[nodeSlot]?.models ?? [];
	const hasCatalog = Boolean(registry?.plugins?.[pluginId]?.modelCatalog);
	const live = useLiveModelsStore((s) => s.byPlugin[pluginId]?.bySlot[nodeSlot]);
	useEffect(() => {
		if (hasCatalog) loadPluginModelCatalog(pluginId);
	}, [hasCatalog, pluginId]);
	return dedupeIds([...declared, ...live ?? []]);
}
/**
* Synchronous read of the params a plugin offers for one slot given the
* selected model, in declaration order.
*/
function getNodePluginParams(nodeSlot, pluginId, model) {
	const registry = usePluginsRegistryStore.getState().registry;
	return visiblePluginParams(registry?.plugins?.[pluginId]?.methodsByNodeSlot?.[nodeSlot]?.params, model);
}
/** Reactive form of {@link getNodePluginParams}. */
function useNodePluginParams(nodeSlot, pluginId, model) {
	const declared = usePluginsRegistryStore((s) => s.registry?.plugins?.[pluginId]?.methodsByNodeSlot?.[nodeSlot]?.params);
	return useMemo(() => visiblePluginParams(declared, model), [declared, model]);
}
/** Presentation metadata (name/description/icon) for a single plugin. */
function usePluginMeta(pluginId) {
	const p = usePluginsRegistryStore((s) => s.registry)?.plugins?.[pluginId];
	return {
		name: p?.name,
		description: p?.description,
		icon: p?.icon
	};
}
/**
* Union of plugin ids for several slots (e.g. `transcribe` + `transcribe_timestamp`).
*/
function useNodePluginIdsUnion(nodeSlots) {
	const registry = usePluginsRegistryStore((s) => s.registry);
	const out = [];
	for (const slot of nodeSlots) for (const id of registry?.nodePluginMap?.[slot] ?? []) out.push(id);
	return dedupeIds(out);
}
//#endregion
//#region src/canvas/ui/label.tsx
function Label({ className, ...props }) {
	return /* @__PURE__ */ jsx(LabelPrimitive.Root, {
		"data-slot": "label",
		className: cn("flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50", className),
		...props
	});
}
//#endregion
//#region src/canvas/nodes/base/node-plugin-select.tsx
/** Monogram fallback shown when a plugin has no icon (or it fails to load). */
function PluginMonogram({ label }) {
	const letter = label.trim().charAt(0).toUpperCase() || "?";
	return /* @__PURE__ */ jsx("span", {
		className: "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-muted text-[9px] font-medium text-muted-foreground",
		style: {
			width: 16,
			height: 16
		},
		children: letter
	});
}
/** Plugin icon with a graceful monogram fallback on missing/broken src. */
function PluginIcon({ icon, label }) {
	const [failed, setFailed] = useState(false);
	if (!icon || failed) return /* @__PURE__ */ jsx(PluginMonogram, { label });
	return /* @__PURE__ */ jsx("img", {
		src: icon,
		alt: "",
		width: 16,
		height: 16,
		className: "h-4 w-4 shrink-0 rounded-sm object-contain",
		onError: () => setFailed(true)
	});
}
/**
* Shared plugin implementation selector (`plugins/<id>` directory name from registry).
*/
function NodePluginSelect({ value, onValueChange, options, title, onOpenChange }) {
	const t = useTranslations("Workspace.nodes.base");
	return /* @__PURE__ */ jsx(Card, {
		className: "p-3",
		children: /* @__PURE__ */ jsxs("div", {
			className: "space-y-2",
			children: [/* @__PURE__ */ jsx(Label, {
				className: "text-sm font-medium text-muted-foreground",
				children: title ?? t("pluginImplementationTitle")
			}), /* @__PURE__ */ jsxs(Select, {
				value,
				onValueChange,
				onOpenChange,
				children: [/* @__PURE__ */ jsx(SelectTrigger, {
					className: "w-full",
					size: "sm",
					children: /* @__PURE__ */ jsx(SelectValue, { placeholder: t("pluginSelectPlaceholder") })
				}), /* @__PURE__ */ jsx(SelectContent, { children: options.map((opt) => /* @__PURE__ */ jsxs(SelectItem, {
					value: opt.value,
					endSlot: opt.description ? /* @__PURE__ */ jsxs(Tooltip, { children: [/* @__PURE__ */ jsx(TooltipTrigger, {
						asChild: true,
						onClick: (e) => e.stopPropagation(),
						onPointerDown: (e) => e.stopPropagation(),
						children: /* @__PURE__ */ jsx("span", {
							className: "ml-auto flex items-center text-muted-foreground/70 hover:text-muted-foreground",
							children: /* @__PURE__ */ jsx(HelpCircle, { className: "size-3.5" })
						})
					}), /* @__PURE__ */ jsx(TooltipContent, {
						side: "right",
						className: "text-xs",
						style: { maxWidth: 256 },
						children: opt.description
					})] }) : void 0,
					children: [/* @__PURE__ */ jsx(PluginIcon, {
						icon: opt.icon,
						label: opt.label
					}), /* @__PURE__ */ jsx("span", {
						className: "truncate",
						children: opt.label
					})]
				}, opt.value)) })]
			})]
		})
	});
}
//#endregion
//#region src/canvas/nodes/base/node-plugin-id-select.tsx
function pluginDisplayName(pluginId) {
	const parts = pluginId.split("-").filter(Boolean);
	return (parts[0] === "tongflow" ? parts.slice(1) : parts).join("-");
}
function useResolvedPluginId(nodeSlot, data, dataKey = "pluginId") {
	usePluginsRegistry();
	const pluginOptions = useNodePluginIds(nodeSlot);
	const current = String(data[dataKey] ?? data.pluginRepo ?? "").trim();
	return {
		current,
		resolved: (current || pluginOptions[0] || "").trim(),
		pluginOptions
	};
}
/**
* Plugin implementation selector for a fixed ABI `nodeSlot`.
* Options come from scanned registry: `nodePluginMap[nodeSlot]`.
*/
function NodePluginIdSelect({ nodeSlot, data, dataKey = "pluginId" }) {
	const id = useNodeId();
	const updates = useFlow((s) => s.updates);
	const t = useTranslations("Workspace.nodes.base");
	const isLoading = usePluginsRegistryStore((s) => s.isLoading);
	const isLoaded = usePluginsRegistryStore((s) => s.isLoaded);
	const loadError = usePluginsRegistryStore((s) => s.error);
	const pluginsMeta = usePluginsRegistryStore((s) => s.registry?.plugins);
	const { resolved, pluginOptions } = useResolvedPluginId(nodeSlot, data, dataKey);
	const options = useMemo(() => pluginOptions.map((pid) => {
		const meta = pluginsMeta?.[pid];
		return {
			value: pid,
			label: meta?.name || pluginDisplayName(pid),
			description: meta?.description,
			icon: meta?.icon
		};
	}), [pluginOptions, pluginsMeta]);
	const title = /* @__PURE__ */ jsx(Label, {
		className: "text-sm font-medium text-muted-foreground",
		children: t("pluginImplementationTitle")
	});
	if (loadError) return /* @__PURE__ */ jsx(Card, {
		className: "p-3 border-destructive/40",
		children: /* @__PURE__ */ jsxs("div", {
			className: "space-y-2",
			children: [title, /* @__PURE__ */ jsx("p", {
				className: "text-xs text-destructive leading-snug",
				children: t("pluginRegistryLoadError", { message: loadError.message })
			})]
		})
	});
	if (isLoading || !isLoaded) return /* @__PURE__ */ jsx(Card, {
		className: "p-3",
		children: /* @__PURE__ */ jsxs("div", {
			className: "space-y-2",
			children: [title, /* @__PURE__ */ jsx("p", {
				className: "text-xs text-muted-foreground",
				children: t("pluginImplementationLoading")
			})]
		})
	});
	if (options.length === 0) return /* @__PURE__ */ jsx(Card, {
		className: "p-3 border-dashed border-border",
		children: /* @__PURE__ */ jsxs("div", {
			className: "space-y-2",
			children: [title, /* @__PURE__ */ jsx("p", {
				className: "text-xs text-muted-foreground leading-snug",
				children: t("pluginImplementationEmpty")
			})]
		})
	});
	return /* @__PURE__ */ jsx(NodePluginSelect, {
		value: resolved,
		onValueChange: (value) => updates(id, {
			...data,
			[dataKey]: value
		}),
		options
	});
}
//#endregion
//#region src/canvas/nodes/base/node-plugin-model-select.tsx
/**
* Model selector for router-style plugins that declare per-slot model lists
* (`TONGFLOW_SLOT_MODELS`, optionally extended live via
* `TONGFLOW_MODEL_CATALOG` — re-checked each time the dropdown opens). Renders
* nothing when the active plugin declares no models, so single-model plugins
* are visually unchanged. The selection is stored as `data.pluginModel` and
* travels top-level (like `pluginId`) through the create-task API and workflow
* export.
*/
function NodePluginModelSelect({ nodeSlot, data }) {
	const id = useNodeId();
	const updates = useFlow((s) => s.updates);
	const t = useTranslations("Workspace.nodes.base");
	const { resolved: pluginId } = useResolvedPluginId(nodeSlot, data);
	const models = useNodePluginModels(nodeSlot, pluginId);
	const current = String(data.pluginModel ?? "").trim();
	const resolved = models.includes(current) ? current : models[0] ?? "";
	useEffect(() => {
		if (resolved === current) return;
		updates(id, {
			...data,
			pluginModel: resolved
		}, { history: false });
	}, [
		id,
		data,
		current,
		resolved,
		updates
	]);
	const options = useMemo(() => models.map((m) => ({
		value: m,
		label: m
	})), [models]);
	if (options.length === 0) return null;
	return /* @__PURE__ */ jsx(NodePluginSelect, {
		value: resolved,
		onValueChange: (value) => updates(id, {
			...data,
			pluginModel: value
		}),
		options,
		title: t("pluginModelTitle"),
		onOpenChange: (open) => {
			if (open) loadPluginModelCatalog(pluginId);
		}
	});
}
//#endregion
//#region src/canvas/ui/input.tsx
function Input({ className, type, ...props }) {
	return /* @__PURE__ */ jsx("input", {
		type,
		"data-slot": "input",
		className: cn("file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm", "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]", "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", className),
		...props
	});
}
//#endregion
//#region src/canvas/ui/switch.tsx
function Switch({ className, ...props }) {
	return /* @__PURE__ */ jsx(SwitchPrimitive.Root, {
		"data-slot": "switch",
		className: cn("peer data-[state=checked]:bg-primary data-[state=unchecked]:bg-input focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] dark:data-[state=unchecked]:bg-input/80 inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all outline-none disabled:cursor-not-allowed disabled:opacity-50", className),
		...props,
		children: /* @__PURE__ */ jsx(SwitchPrimitive.Thumb, {
			"data-slot": "switch-thumb",
			className: cn("bg-background pointer-events-none block size-4 rounded-full ring-0 shadow-sm transition-transform data-[state=checked]:translate-x-[calc(100%-2px)] data-[state=unchecked]:translate-x-0")
		})
	});
}
//#endregion
//#region src/canvas/nodes/base/node-plugin-params.tsx
function readParams(data) {
	const raw = data.pluginParams;
	return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
}
/**
* Collapsed "Advanced" section listing the run-time knobs the active plugin
* declares for this slot (`TONGFLOW_SLOT_PARAMS`, optionally gated per router
* model). Renders nothing for plugins that declare none. Choices live in
* `data.pluginParams` as a diff against the declared defaults — an unset key
* means "plugin default" — and travel top-level as `params` through the
* create-task API and the workflow export, never inside the ABI prompt.
*/
function NodePluginParams({ nodeSlot, data }) {
	const id = useNodeId();
	const updates = useFlow((s) => s.updates);
	const t = useTranslations("Workspace.nodes.base");
	const [open, setOpen] = useState(false);
	const { resolved: pluginId } = useResolvedPluginId(nodeSlot, data);
	const models = useNodePluginModels(nodeSlot, pluginId);
	const currentModel = String(data.pluginModel ?? "").trim();
	const entries = useNodePluginParams(nodeSlot, pluginId, models.includes(currentModel) ? currentModel : models[0] ?? void 0);
	const values = useMemo(() => readParams(data), [data]);
	useEffect(() => {
		const normalized = normalizePluginParams(entries, values);
		if (samePluginParams(normalized, data.pluginParams)) return;
		if (Object.keys(normalized).length === 0 && data.pluginParams === void 0) return;
		const next = { ...data };
		if (Object.keys(normalized).length === 0) delete next.pluginParams;
		else next.pluginParams = normalized;
		updates(id, next, { history: false });
	}, [
		id,
		data,
		entries,
		values,
		updates
	]);
	const setParam = useCallback((name, value) => {
		const next = { ...values };
		if (value === void 0) delete next[name];
		else next[name] = value;
		const normalized = normalizePluginParams(entries, next);
		const nextData = { ...data };
		if (Object.keys(normalized).length === 0) delete nextData.pluginParams;
		else nextData.pluginParams = normalized;
		updates(id, nextData);
	}, [
		id,
		data,
		entries,
		values,
		updates
	]);
	const reset = useCallback(() => {
		const nextData = { ...data };
		delete nextData.pluginParams;
		updates(id, nextData);
	}, [
		id,
		data,
		updates
	]);
	if (entries.length === 0) return null;
	const changed = Object.keys(values).length;
	return /* @__PURE__ */ jsx(Card, {
		className: "p-3",
		children: /* @__PURE__ */ jsxs("div", {
			className: "space-y-2",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center gap-1",
				children: [/* @__PURE__ */ jsxs("button", {
					type: "button",
					onClick: () => setOpen((o) => !o),
					className: "nodrag flex flex-1 items-center gap-1 text-left text-sm font-medium text-muted-foreground hover:text-foreground",
					"aria-expanded": open,
					children: [
						/* @__PURE__ */ jsx(ChevronRight, { className: `size-3.5 transition-transform ${open ? "rotate-90" : ""}` }),
						/* @__PURE__ */ jsx("span", { children: t("pluginParamsTitle") }),
						changed > 0 && /* @__PURE__ */ jsx("span", {
							className: "ml-1 rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary",
							children: changed
						})
					]
				}), changed > 0 && /* @__PURE__ */ jsx("button", {
					type: "button",
					onClick: reset,
					title: t("pluginParamsReset"),
					"aria-label": t("pluginParamsReset"),
					className: "nodrag rounded p-1 text-muted-foreground hover:text-foreground",
					children: /* @__PURE__ */ jsx(RotateCcw, { className: "size-3.5" })
				})]
			}), open && /* @__PURE__ */ jsx("div", {
				className: "space-y-2",
				children: entries.map((entry) => /* @__PURE__ */ jsx(ParamRow, {
					entry,
					value: values[entry.name],
					onChange: (v) => setParam(entry.name, v)
				}, entry.name))
			})]
		})
	});
}
function ParamRow({ entry, value, onChange }) {
	const t = useTranslations("Workspace.nodes.base");
	const { name, spec } = entry;
	const label = spec.label ?? name;
	const inline = spec.type === "boolean";
	const changed = value !== void 0 && spec.default !== void 0;
	return /* @__PURE__ */ jsxs("div", {
		className: inline ? "flex items-center justify-between gap-2" : "space-y-1",
		children: [/* @__PURE__ */ jsxs(Label, {
			className: "flex items-baseline gap-1.5 text-xs text-muted-foreground",
			title: spec.description,
			children: [/* @__PURE__ */ jsx("span", { children: label }), changed && /* @__PURE__ */ jsx("span", {
				className: "text-[10px] text-muted-foreground/60",
				children: t("pluginParamDefault", { value: String(spec.default) })
			})]
		}), /* @__PURE__ */ jsx(ParamControl, {
			spec,
			value,
			onChange
		})]
	});
}
function ParamControl({ spec, value, onChange }) {
	const effective = value === void 0 ? spec.default : value;
	switch (spec.type) {
		case "select": {
			const options = spec.options ?? [];
			return /* @__PURE__ */ jsxs(Select, {
				value: effective === void 0 ? "" : String(effective),
				onValueChange: (v) => {
					onChange(options.find((o) => String(o) === v));
				},
				children: [/* @__PURE__ */ jsx(SelectTrigger, {
					className: "nodrag w-full",
					size: "sm",
					children: /* @__PURE__ */ jsx(SelectValue, {})
				}), /* @__PURE__ */ jsx(SelectContent, { children: options.map((o) => /* @__PURE__ */ jsx(SelectItem, {
					value: String(o),
					children: String(o)
				}, String(o))) })]
			});
		}
		case "boolean": return /* @__PURE__ */ jsx(Switch, {
			className: "nodrag",
			checked: effective === true,
			onCheckedChange: (checked) => onChange(checked)
		});
		case "number":
		case "integer": return /* @__PURE__ */ jsx(Input, {
			type: "number",
			className: "nodrag h-8",
			value: typeof effective === "number" ? String(effective) : "",
			min: spec.min,
			max: spec.max,
			step: spec.step ?? (spec.type === "integer" ? 1 : "any"),
			onChange: (e) => {
				const raw = e.target.value.trim();
				if (raw === "") {
					onChange(void 0);
					return;
				}
				const n = Number(raw);
				if (!Number.isFinite(n)) return;
				onChange(spec.type === "integer" ? Math.round(n) : n);
			}
		});
		case "text": return /* @__PURE__ */ jsx(Input, {
			type: "text",
			className: "nodrag h-8",
			value: typeof effective === "string" ? effective : "",
			onChange: (e) => {
				const v = e.target.value;
				onChange(v === "" ? void 0 : v);
			}
		});
		default: return null;
	}
}
//#endregion
//#region src/canvas/nodes/base/node-thinking-bubble.tsx
/**
* A transient, auto-scrolling bubble anchored to the right of a node that
* streams a model's reasoning ("thinking") while it runs. Positioned outside
* the node body so it never occludes node content, and non-interactive so it
* doesn't interfere with canvas panning or node selection.
*/
function NodeThinkingBubble({ text, visible }) {
	const scrollRef = useRef(null);
	useEffect(() => {
		const el = scrollRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [text]);
	if (!visible || !text) return null;
	return /* @__PURE__ */ jsx("div", {
		className: cn("nodrag pointer-events-none absolute left-full top-0 z-50 ml-3", "w-64 animate-in fade-in-0 duration-200"),
		children: /* @__PURE__ */ jsxs("div", {
			className: "rounded-lg border border-gray-200 bg-white/95 shadow-lg dark:border-gray-700 dark:bg-gray-800/95",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center gap-1.5 border-b border-gray-100 px-3 py-1.5 text-xs font-medium text-gray-500 dark:border-gray-700 dark:text-gray-400",
				children: [/* @__PURE__ */ jsx(Brain, { className: "h-3.5 w-3.5 animate-pulse" }), "Thinking"]
			}), /* @__PURE__ */ jsx("div", {
				ref: scrollRef,
				className: "max-h-48 overflow-y-auto whitespace-pre-wrap px-3 py-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400",
				children: text
			})]
		})
	});
}
//#endregion
//#region src/canvas/nodes/base/base-node-shell.tsx
/**
* Pure UI shell for canvas nodes (headers, plugin picker, execute affordances,
* loading overlay, comments). Execution wiring lives in `AbiNodeShell` or callers
* that pass explicit props.
*/
const BaseNodeShell = forwardRef(({ className, selected, count, data, children, overlay, title, icon, headerActions, loading = false, elapsedSeconds = 0, progressLabel = null, thinkingText = null, isExecuteMode = false, onExecute, onCancel, executeLabel, executeIcon, executeDisabled, isInputNode, feature, showPluginSelect = true, missingPluginOpen = false, setMissingPluginOpen, ...props }, ref) => {
	const nodeId = useNodeId();
	const t = useTranslations("Workspace.nodes.base");
	const comboMode = useFlow((s) => s.comboMode);
	const isInCombo = useFlow((s) => nodeId ? s.isInCombo(nodeId) : false);
	const nodeType = useStore((state) => {
		return state.nodeLookup.get(nodeId ?? "")?.type;
	});
	const renderPluginSelect = showPluginSelect && !!feature;
	const renderExecuteButton = !!onExecute && (!isExecuteMode || !!isInputNode);
	return /* @__PURE__ */ jsxs("div", {
		className: "relative",
		children: [
			setMissingPluginOpen && /* @__PURE__ */ jsx(AlertDialog, {
				open: missingPluginOpen,
				onOpenChange: setMissingPluginOpen,
				children: /* @__PURE__ */ jsxs(AlertDialogContent, { children: [/* @__PURE__ */ jsxs(AlertDialogHeader, { children: [/* @__PURE__ */ jsx(AlertDialogTitle, { children: t("missingImplTitle") }), /* @__PURE__ */ jsx(AlertDialogDescription, { children: t("missingImplDescription") })] }), /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [/* @__PURE__ */ jsx(AlertDialogCancel, { children: t("cancel") }), /* @__PURE__ */ jsx(AlertDialogAction, { children: t("confirm") })] })] })
			}),
			/* @__PURE__ */ jsx(NodeCommentBox, {}),
			/* @__PURE__ */ jsx("div", {
				className: "relative",
				children: /* @__PURE__ */ jsxs("div", {
					ref,
					...props,
					className: cn("relative min-h-20 min-w-64 max-w-96 rounded-lg bg-white shadow-lg border border-gray-200", "dark:bg-gray-800 dark:border-gray-700", selected && !loading && !isInCombo && "ring-2 ring-blue-500 shadow-xl", comboMode && isModalityNode(nodeType) && isInCombo && "ring-2 ring-primary shadow-lg shadow-primary/20", className),
					children: [
						/* @__PURE__ */ jsx(NodeLoadingOverlay, {
							loading,
							elapsedSeconds,
							progressLabel,
							onCancel
						}),
						/* @__PURE__ */ jsx(NodeThinkingBubble, {
							text: thinkingText,
							visible: loading
						}),
						count !== void 0 && count > 1 && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("div", {
							className: "absolute inset-0 -z-10 rounded-lg bg-white shadow-sm dark:bg-gray-800",
							style: {
								top: "4px",
								left: "4px",
								right: "-4px",
								bottom: "-4px"
							}
						}), count > 2 && /* @__PURE__ */ jsx("div", {
							className: "absolute inset-0 -z-20 rounded-lg bg-white shadow-sm dark:bg-gray-800",
							style: {
								top: "8px",
								left: "8px",
								right: "-8px",
								bottom: "-8px"
							}
						})] }),
						count !== void 0 && count > 1 && /* @__PURE__ */ jsx("div", {
							className: "absolute -right-2 -top-2 z-20 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-blue-500 px-2 text-xs font-semibold text-white shadow-md",
							children: count
						}),
						title && /* @__PURE__ */ jsxs(NodeHeader, { children: [
							icon && /* @__PURE__ */ jsx(NodeHeaderIcon, { children: icon }),
							/* @__PURE__ */ jsx(NodeHeaderTitle, {
								className: "flex items-center gap-2",
								children: title
							}),
							/* @__PURE__ */ jsxs(NodeHeaderActions, { children: [headerActions, /* @__PURE__ */ jsx(NodeHeaderMenuAction, { label: t("moreActions") })] })
						] }),
						renderPluginSelect && /* @__PURE__ */ jsxs("div", {
							className: "p-4 pb-0 space-y-2",
							children: [
								/* @__PURE__ */ jsx(NodePluginIdSelect, {
									nodeSlot: feature,
									data: data ?? { feature }
								}),
								/* @__PURE__ */ jsx(NodePluginModelSelect, {
									nodeSlot: feature,
									data: data ?? { feature }
								}),
								/* @__PURE__ */ jsx(NodePluginParams, {
									nodeSlot: feature,
									data: data ?? { feature }
								})
							]
						}),
						/* @__PURE__ */ jsx("div", {
							className: "relative z-0",
							children
						}),
						renderExecuteButton && /* @__PURE__ */ jsx("div", {
							className: "p-4 pt-0",
							children: /* @__PURE__ */ jsx(Button, {
								onClick: onExecute,
								disabled: !!executeDisabled || loading,
								className: "w-full h-10",
								children: /* @__PURE__ */ jsxs("div", {
									className: "flex items-center justify-center gap-2",
									children: [executeIcon ?? /* @__PURE__ */ jsx(Wand2, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", { children: executeLabel ?? t("execute") })]
								})
							})
						}),
						overlay && /* @__PURE__ */ jsx("div", {
							className: "absolute inset-0 z-[60]",
							children: overlay
						}),
						/* @__PURE__ */ jsx(NodeComboButton, {})
					]
				})
			})
		]
	});
});
BaseNodeShell.displayName = "BaseNodeShell";
//#endregion
//#region src/canvas/nodes/add/add-audio-node.tsx
const RecordTab = () => {
	const t = useTranslations("Workspace.nodes.add");
	const expands = useFlow((s) => s.expands);
	const id = useNodeId();
	const [file, setFile] = useState();
	const { upload, isUploading, progress, error } = useUpload({
		onSuccess: (info) => {
			if (id) {
				expands(id, [{
					type: "audioNode",
					data: { fileKeys: [info.key] }
				}]);
				setFile(void 0);
			}
		},
		onError: (error) => {
			logger.error("Upload failed:", error);
		}
	});
	const onFinish = async () => {
		if (!file) return;
		upload(file);
	};
	return /* @__PURE__ */ jsxs("div", {
		className: "w-full space-y-3",
		children: [
			/* @__PURE__ */ jsx(TooltipProvider, { children: /* @__PURE__ */ jsx(AudioRecorderWithVisualizer, { onRecord: (file) => {
				setFile(file);
			} }) }),
			file && !isUploading && /* @__PURE__ */ jsx("div", {
				className: "p-3 bg-muted rounded-lg",
				children: /* @__PURE__ */ jsxs("p", {
					className: "text-sm text-muted-foreground truncate",
					children: [
						t("recorded"),
						": ",
						file.name
					]
				})
			}),
			isUploading && /* @__PURE__ */ jsxs("div", {
				className: "space-y-2",
				children: [/* @__PURE__ */ jsx("div", {
					className: "w-full bg-muted rounded-full h-2 overflow-hidden",
					children: /* @__PURE__ */ jsx("div", {
						className: "bg-primary h-full transition-all duration-300",
						style: { width: `${progress}%` }
					})
				}), /* @__PURE__ */ jsxs("p", {
					className: "text-xs text-center text-muted-foreground",
					children: [
						t("uploading"),
						" ",
						Math.round(progress),
						"%"
					]
				})]
			}),
			error && /* @__PURE__ */ jsx("div", {
				className: "p-3 bg-destructive/10 border border-destructive/20 rounded-lg",
				children: /* @__PURE__ */ jsx("p", {
					className: "text-sm text-destructive",
					children: error.message
				})
			}),
			file && !isUploading && /* @__PURE__ */ jsx(Button, {
				onClick: onFinish,
				disabled: !file || isUploading,
				size: "sm",
				className: "w-full",
				children: t("confirmAndUpload")
			})
		]
	});
};
const UploadTab$4 = () => {
	const t = useTranslations("Workspace.nodes.add");
	const expands = useFlow((s) => s.expands);
	const id = useNodeId();
	const [isDragActive, setIsDragActive] = useState(false);
	const { upload, isUploading, progress } = useMultipleUpload({
		onSuccess: (responses) => {
			if (id && responses.length > 0) {
				const fileKeys = responses.map((r) => r.key);
				expands(id, [{
					type: "audioNode",
					data: { fileKeys }
				}]);
			}
		},
		onError: (error) => {
			logger.error("Upload failed:", error);
		}
	});
	const doUpload = async (files) => {
		if (!id || files.length === 0) return;
		await upload(files);
	};
	const handleDrop = async (e) => {
		e.preventDefault();
		setIsDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) await doUpload(Array.from(e.dataTransfer.files));
	};
	const handleFileChange = async (e) => {
		if (e.target.files && e.target.files.length > 0) await doUpload(Array.from(e.target.files));
	};
	return /* @__PURE__ */ jsx("div", {
		className: "w-full",
		children: /* @__PURE__ */ jsx("div", {
			onDrop: handleDrop,
			onDragOver: (e) => {
				e.preventDefault();
				setIsDragActive(true);
			},
			onDragLeave: () => setIsDragActive(false),
			onPointerDown: (e) => e.stopPropagation(),
			className: `w-full h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors cursor-pointer nodrag ${isDragActive ? "border-primary bg-primary/10" : "border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50"}`,
			children: isUploading ? /* @__PURE__ */ jsxs("div", {
				className: "w-full px-8 space-y-3",
				children: [/* @__PURE__ */ jsx("div", {
					className: "w-full bg-muted rounded-full h-2 overflow-hidden",
					children: /* @__PURE__ */ jsx("div", {
						className: "bg-primary h-full transition-all duration-300",
						style: { width: `${progress}%` }
					})
				}), /* @__PURE__ */ jsxs("p", {
					className: "text-sm text-center text-muted-foreground font-medium",
					children: [
						t("uploading"),
						" ",
						Math.round(progress),
						"%"
					]
				})]
			}) : /* @__PURE__ */ jsxs(Fragment, { children: [
				/* @__PURE__ */ jsx(Upload, { className: "h-10 w-10 mb-3 text-muted-foreground" }),
				/* @__PURE__ */ jsx("p", {
					className: "text-sm font-medium text-foreground mb-1",
					children: isDragActive ? t("dropToUpload") : t("dragOrClick")
				}),
				/* @__PURE__ */ jsx("p", {
					className: "text-xs text-muted-foreground",
					children: t("supportedAudioFormats")
				}),
				/* @__PURE__ */ jsx("label", {
					htmlFor: `audio-upload-${id}`,
					children: /* @__PURE__ */ jsx(Button, {
						variant: "outline",
						size: "sm",
						className: "mt-3",
						asChild: true,
						children: /* @__PURE__ */ jsx("span", { children: t("browseFiles") })
					})
				}),
				/* @__PURE__ */ jsx("input", {
					id: `audio-upload-${id}`,
					type: "file",
					multiple: true,
					accept: "audio/*,.mp3,.wav,.ogg,.m4a",
					onChange: handleFileChange,
					className: "hidden"
				})
			] })
		})
	});
};
const LibraryTab$5 = () => {
	return /* @__PURE__ */ jsx("div", {
		className: "w-full",
		children: /* @__PURE__ */ jsx(LibInput, { resourceType: "AUDIO" })
	});
};
const AddAudioNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.add");
	const id = useNodeId();
	const updates = useFlow((s) => s.updates);
	data?.activeTab;
	const handleTabChange = (value) => {
		if (id) updates(id, {
			...data,
			activeTab: value
		});
	};
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		className: "min-w-[480px]",
		data,
		title: t("addAudio"),
		icon: /* @__PURE__ */ jsx(Music, { className: "h-5 w-5" }),
		isInputNode: true,
		showPluginSelect: false,
		children: [/* @__PURE__ */ jsx(Handle, {
			type: "source",
			position: Position.Right,
			id: "out:audioNode"
		}), /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-2",
			children: /* @__PURE__ */ jsxs(Tabs, {
				defaultValue: data?.activeTab || "upload",
				className: "w-full",
				onValueChange: handleTabChange,
				children: [/* @__PURE__ */ jsxs(TabsList, {
					className: "grid grid-cols-3 gap-2 bg-transparent h-auto p-0",
					children: [
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "upload",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Upload, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("upload")
							})]
						}, "upload"),
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "record",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Mic, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("record")
							})]
						}, "record"),
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "library",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Library, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("library")
							})]
						}, "library")
					]
				}), /* @__PURE__ */ jsxs("div", {
					className: "mt-2",
					children: [
						/* @__PURE__ */ jsx(TabsContent, {
							value: "upload",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(UploadTab$4, {})
						}),
						/* @__PURE__ */ jsx(TabsContent, {
							value: "record",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(RecordTab, {})
						}),
						/* @__PURE__ */ jsx(TabsContent, {
							value: "library",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(LibraryTab$5, {})
						})
					]
				})]
			})
		})]
	});
};
var add_audio_node_default = memo(AddAudioNode);
//#endregion
//#region src/canvas/ui/progress.tsx
function Progress({ className, value, ...props }) {
	return /* @__PURE__ */ jsx(ProgressPrimitive.Root, {
		"data-slot": "progress",
		className: cn("bg-primary/20 relative h-2 w-full overflow-hidden rounded-full", className),
		...props,
		children: /* @__PURE__ */ jsx(ProgressPrimitive.Indicator, {
			"data-slot": "progress-indicator",
			className: "bg-primary h-full w-full flex-1 transition-all",
			style: { transform: `translateX(-${100 - (value || 0)}%)` }
		})
	});
}
//#endregion
//#region src/canvas/nodes/add/add-file-node.tsx
const UploadTab$3 = () => {
	const t = useTranslations("Workspace.nodes.add");
	const { expands } = useFlow();
	const id = useNodeId();
	const [isDragActive, setIsDragActive] = useState(false);
	const { upload, isUploading, progress } = useMultipleUpload({
		onSuccess: (responses) => {
			if (id && responses.length > 0) {
				const fileKeys = responses.map((r) => r.key);
				expands(id, [{
					type: "fileNode",
					data: { fileKeys }
				}]);
			}
		},
		onError: (error) => {
			logger.error("Upload failed:", error);
		}
	});
	const handleFileChange = async (e) => {
		if (e.target.files && e.target.files.length > 0) await upload(Array.from(e.target.files));
	};
	const handleDrop = async (e) => {
		e.preventDefault();
		setIsDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) await upload(Array.from(e.dataTransfer.files));
	};
	return /* @__PURE__ */ jsx("div", {
		onDrop: handleDrop,
		onDragOver: (e) => {
			e.preventDefault();
			setIsDragActive(true);
		},
		onDragLeave: () => setIsDragActive(false),
		onPointerDown: (e) => e.stopPropagation(),
		className: cn("w-full h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors cursor-pointer nodrag", isDragActive ? "border-primary bg-primary/10" : "border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50"),
		children: isUploading ? /* @__PURE__ */ jsxs("div", {
			className: "w-full px-8 space-y-3",
			children: [/* @__PURE__ */ jsx(Progress, { value: progress }), /* @__PURE__ */ jsxs("p", {
				className: "text-sm text-center text-muted-foreground font-medium",
				children: [
					t("uploading"),
					" ",
					Math.round(progress),
					"%"
				]
			})]
		}) : /* @__PURE__ */ jsxs(Fragment, { children: [
			/* @__PURE__ */ jsx(Upload, { className: "h-10 w-10 mb-3 text-muted-foreground" }),
			/* @__PURE__ */ jsx("p", {
				className: "text-sm font-medium text-foreground mb-1",
				children: isDragActive ? t("dropToUpload") : t("dragOrClick")
			}),
			/* @__PURE__ */ jsx("p", {
				className: "text-xs text-muted-foreground",
				children: t("supportedDocFormats")
			}),
			/* @__PURE__ */ jsx("label", {
				htmlFor: `file-upload-${id}`,
				children: /* @__PURE__ */ jsx(Button, {
					variant: "outline",
					size: "sm",
					className: "mt-3",
					asChild: true,
					children: /* @__PURE__ */ jsx("span", { children: t("browseFiles") })
				})
			}),
			/* @__PURE__ */ jsx("input", {
				id: `file-upload-${id}`,
				type: "file",
				multiple: true,
				accept: ".pdf,.doc,.docx,.txt,.xlsx,.xls,.pptx,.ppt,.odt,.pages,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.oasis.opendocument.text",
				onChange: handleFileChange,
				className: "hidden"
			})
		] })
	});
};
const LibraryTab$4 = () => {
	return /* @__PURE__ */ jsx("div", {
		className: "w-full",
		children: /* @__PURE__ */ jsx(LibInput, { resourceType: "FILE" })
	});
};
const AddFileNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.add");
	const id = useNodeId();
	const updates = useFlow((s) => s.updates);
	const activeTab = data?.activeTab || "upload";
	const handleTabChange = (value) => {
		if (id) updates(id, {
			...data,
			activeTab: value
		});
	};
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		className: "min-w-[360px]",
		data,
		title: t("addDocument"),
		icon: /* @__PURE__ */ jsx(FileText, { className: "h-5 w-5" }),
		isInputNode: true,
		showPluginSelect: false,
		children: [/* @__PURE__ */ jsx(Handle, {
			type: "source",
			position: Position.Right,
			id: "out:fileNode"
		}), /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-2",
			children: /* @__PURE__ */ jsxs(Tabs, {
				value: activeTab,
				className: "w-full",
				onValueChange: handleTabChange,
				children: [/* @__PURE__ */ jsxs(TabsList, {
					className: "grid grid-cols-2 gap-2 bg-transparent h-auto p-0",
					children: [/* @__PURE__ */ jsxs(TabsTrigger, {
						value: "upload",
						className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
						children: [/* @__PURE__ */ jsx(Upload, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
							className: "text-xs font-medium",
							children: t("upload")
						})]
					}), /* @__PURE__ */ jsxs(TabsTrigger, {
						value: "library",
						className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
						children: [/* @__PURE__ */ jsx(Library, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
							className: "text-xs font-medium",
							children: t("library")
						})]
					})]
				}), /* @__PURE__ */ jsxs("div", {
					className: "mt-2",
					children: [/* @__PURE__ */ jsx(TabsContent, {
						value: "upload",
						className: "mt-0",
						children: /* @__PURE__ */ jsx(UploadTab$3, {})
					}), /* @__PURE__ */ jsx(TabsContent, {
						value: "library",
						className: "mt-0",
						children: /* @__PURE__ */ jsx(LibraryTab$4, {})
					})]
				})]
			})
		})]
	});
};
AddFileNode.displayName = "AddFileNode";
var add_file_node_default = memo(AddFileNode);
//#endregion
//#region src/canvas/ui/whiteboard.tsx
const WhiteBoard = forwardRef(({ onChange }, ref) => {
	const canvasRef = useRef(null);
	const isDrawing = useRef(false);
	const lastX = useRef(0);
	const lastY = useRef(0);
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		const rect = canvas.parentElement?.getBoundingClientRect();
		if (rect) {
			canvas.width = rect.width;
			canvas.height = rect.height;
		} else {
			canvas.width = 800;
			canvas.height = 300;
		}
		ctx.fillStyle = "white";
		ctx.fillRect(0, 0, canvas.width, canvas.height);
	}, []);
	const getCanvasDataUrl = () => {
		return canvasRef.current?.toDataURL("image/png") || null;
	};
	const handleMouseDown = (e) => {
		e.stopPropagation();
		isDrawing.current = true;
		const rect = canvasRef.current?.getBoundingClientRect();
		if (rect) {
			lastX.current = e.clientX - rect.left;
			lastY.current = e.clientY - rect.top;
		}
	};
	const handleMouseMove = (e) => {
		e.stopPropagation();
		if (!isDrawing.current || !canvasRef.current) return;
		const ctx = canvasRef.current.getContext("2d");
		if (!ctx) return;
		const rect = canvasRef.current.getBoundingClientRect();
		const currentX = e.clientX - rect.left;
		const currentY = e.clientY - rect.top;
		ctx.strokeStyle = "#000";
		ctx.lineWidth = 2;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
		ctx.beginPath();
		ctx.moveTo(lastX.current, lastY.current);
		ctx.lineTo(currentX, currentY);
		ctx.stroke();
		lastX.current = currentX;
		lastY.current = currentY;
		onChange(getCanvasDataUrl() || void 0);
	};
	const handleMouseUp = (e) => {
		e.stopPropagation();
		isDrawing.current = false;
	};
	const handleMouseLeave = () => {
		isDrawing.current = false;
	};
	const handleTouchStart = (e) => {
		e.stopPropagation();
		if (e.touches.length !== 1) return;
		isDrawing.current = true;
		const rect = canvasRef.current?.getBoundingClientRect();
		if (rect) {
			lastX.current = e.touches[0].clientX - rect.left;
			lastY.current = e.touches[0].clientY - rect.top;
		}
	};
	const handleTouchMove = (e) => {
		e.stopPropagation();
		if (!isDrawing.current || !canvasRef.current || e.touches.length !== 1) return;
		const ctx = canvasRef.current.getContext("2d");
		if (!ctx) return;
		const rect = canvasRef.current.getBoundingClientRect();
		const currentX = e.touches[0].clientX - rect.left;
		const currentY = e.touches[0].clientY - rect.top;
		ctx.strokeStyle = "#000";
		ctx.lineWidth = 2;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
		ctx.beginPath();
		ctx.moveTo(lastX.current, lastY.current);
		ctx.lineTo(currentX, currentY);
		ctx.stroke();
		lastX.current = currentX;
		lastY.current = currentY;
		onChange(getCanvasDataUrl() || void 0);
	};
	const handleTouchEnd = (e) => {
		e.stopPropagation();
		isDrawing.current = false;
	};
	useImperativeHandle(ref, () => ({
		clear: () => {
			if (canvasRef.current) {
				const ctx = canvasRef.current.getContext("2d");
				if (ctx) {
					ctx.fillStyle = "white";
					ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
					onChange(void 0);
				}
			}
		},
		getDataURL: getCanvasDataUrl
	}));
	return /* @__PURE__ */ jsx("canvas", {
		ref: canvasRef,
		onMouseDown: handleMouseDown,
		onMouseMove: handleMouseMove,
		onMouseUp: handleMouseUp,
		onMouseLeave: handleMouseLeave,
		onTouchStart: handleTouchStart,
		onTouchMove: handleTouchMove,
		onTouchEnd: handleTouchEnd,
		className: "w-full h-full bg-white cursor-crosshair",
		style: {
			display: "block",
			pointerEvents: "auto"
		}
	});
});
WhiteBoard.displayName = "WhiteBoard";
//#endregion
//#region src/canvas/nodes/add/add-image-node.tsx
const UploadTab$2 = () => {
	const t = useTranslations("Workspace.nodes.add");
	const expands = useFlow((s) => s.expands);
	const id = useNodeId();
	const [isDragActive, setIsDragActive] = useState(false);
	const { upload, isUploading, progress } = useMultipleUpload({
		onSuccess: (responses) => {
			if (id && responses.length > 0) {
				const fileKeys = responses.map((r) => r.key);
				expands(id, [{
					type: "imageNode",
					data: { fileKeys }
				}]);
			}
		},
		onError: (error) => {
			logger.error("Upload failed:", error);
		}
	});
	const handleFileChange = async (e) => {
		if (e.target.files && e.target.files.length > 0) await upload(Array.from(e.target.files));
	};
	const handleDrop = async (e) => {
		e.preventDefault();
		setIsDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) await upload(Array.from(e.dataTransfer.files));
	};
	return /* @__PURE__ */ jsx("div", {
		className: "w-full",
		children: /* @__PURE__ */ jsx("div", {
			onDrop: handleDrop,
			onDragOver: (e) => {
				e.preventDefault();
				setIsDragActive(true);
			},
			onDragLeave: () => setIsDragActive(false),
			onPointerDown: (e) => e.stopPropagation(),
			className: cn("w-full h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors nodrag", isDragActive ? "border-primary bg-primary/10" : "border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50"),
			children: isUploading ? /* @__PURE__ */ jsxs("div", {
				className: "w-full px-8 space-y-3",
				children: [/* @__PURE__ */ jsx("div", {
					className: "w-full bg-muted rounded-full h-2 overflow-hidden",
					children: /* @__PURE__ */ jsx("div", {
						className: "bg-primary h-full transition-all duration-300",
						style: { width: `${progress}%` }
					})
				}), /* @__PURE__ */ jsxs("p", {
					className: "text-sm text-center text-muted-foreground font-medium",
					children: [
						t("uploading"),
						" ",
						Math.round(progress),
						"%"
					]
				})]
			}) : /* @__PURE__ */ jsxs(Fragment, { children: [
				/* @__PURE__ */ jsx(Upload, { className: "h-10 w-10 mb-3 text-muted-foreground" }),
				/* @__PURE__ */ jsx("p", {
					className: "text-sm font-medium text-foreground mb-1",
					children: isDragActive ? t("dropToUpload") : t("dragOrClick")
				}),
				/* @__PURE__ */ jsx("p", {
					className: "text-xs text-muted-foreground",
					children: t("supportedImageFormats")
				}),
				/* @__PURE__ */ jsx("label", {
					htmlFor: `file-upload-${id}`,
					children: /* @__PURE__ */ jsx(Button, {
						variant: "outline",
						size: "sm",
						className: "mt-3",
						asChild: true,
						children: /* @__PURE__ */ jsx("span", { children: t("browseFiles") })
					})
				}),
				/* @__PURE__ */ jsx("input", {
					id: `file-upload-${id}`,
					type: "file",
					multiple: true,
					accept: "image/*",
					onChange: handleFileChange,
					className: "hidden"
				})
			] })
		})
	});
};
const CanvasTab = () => {
	const t = useTranslations("Workspace.nodes.add");
	const expands = useFlow((s) => s.expands);
	const id = useNodeId();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [dataUrl, setDataUrl] = useState("");
	const whiteBoardRef = useRef(null);
	const { upload, isUploading } = useUpload({
		onSuccess: (response) => {
			if (id) {
				expands(id, [{
					type: "imageNode",
					data: { fileKeys: [response.key] }
				}]);
				whiteBoardRef.current?.clear();
				setDataUrl("");
				setIsDialogOpen(false);
			}
		},
		onError: (error) => {
			logger.error("Error uploading canvas drawing:", error);
		}
	});
	const dataURLtoBlob = (dataURL) => {
		const arr = dataURL.split(",");
		const mime = arr[0]?.match(/:(.*?);/)?.[1] ?? "image/png";
		const bstr = atob(arr[1] ?? "");
		let n = bstr.length;
		const u8arr = new Uint8Array(n);
		while (n--) u8arr[n] = bstr.charCodeAt(n);
		return new Blob([u8arr], { type: mime });
	};
	const onFinish = async () => {
		if (!dataUrl || !id) return;
		const blob = dataURLtoBlob(dataUrl);
		const file = new File([blob], `canvas-drawing-${Date.now()}.png`, { type: "image/png" });
		await upload(file);
	};
	const clearCanvas = () => {
		whiteBoardRef.current?.clear();
		setDataUrl("");
	};
	const handleCanvasDialogEvents = (e) => {
		e.stopPropagation();
	};
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs(Button, {
		onClick: () => setIsDialogOpen(true),
		className: "w-full",
		children: [/* @__PURE__ */ jsx(Pencil, { className: "mr-2 h-4 w-4" }), t("openCanvas")]
	}), /* @__PURE__ */ jsx(Dialog, {
		open: isDialogOpen,
		onOpenChange: setIsDialogOpen,
		children: /* @__PURE__ */ jsxs(DialogContent, {
			className: "max-w-2xl",
			onMouseDown: handleCanvasDialogEvents,
			onTouchStart: handleCanvasDialogEvents,
			children: [
				/* @__PURE__ */ jsxs(DialogHeader, { children: [/* @__PURE__ */ jsx(DialogTitle, { children: t("canvasDrawing") }), /* @__PURE__ */ jsx(DialogDescription, { children: t("canvasDrawingHint") })] }),
				/* @__PURE__ */ jsx("div", {
					className: "w-full h-96 border rounded-lg overflow-hidden bg-white",
					children: /* @__PURE__ */ jsx(WhiteBoard, {
						ref: whiteBoardRef,
						onChange: (dataUrl) => setDataUrl(dataUrl ?? "")
					})
				}),
				/* @__PURE__ */ jsxs(DialogFooter, {
					className: "flex gap-2",
					children: [/* @__PURE__ */ jsx(Button, {
						variant: "outline",
						onClick: clearCanvas,
						disabled: isUploading,
						children: t("clearCanvas")
					}), /* @__PURE__ */ jsx(Button, {
						onClick: onFinish,
						disabled: !dataUrl || isUploading,
						children: isUploading ? `${t("uploading")}...` : t("finishAndSave")
					})]
				})
			]
		})
	})] });
};
const LibraryTab$3 = () => {
	return /* @__PURE__ */ jsx("div", {
		className: "w-full",
		children: /* @__PURE__ */ jsx(LibInput, { resourceType: "IMAGE" })
	});
};
const CameraTab$1 = () => {
	const t = useTranslations("Workspace.nodes.add");
	const expands = useFlow((s) => s.expands);
	const id = useNodeId();
	const [stream, setStream] = useState(null);
	const [error, setError] = useState("");
	const [videoReady, setVideoReady] = useState(false);
	const videoRef = React.useRef(null);
	const { upload, isUploading } = useUpload({
		onSuccess: (response) => {
			if (id) {
				expands(id, [{
					type: "imageNode",
					data: { fileKeys: [response.key] }
				}]);
				stopCamera();
			}
		},
		onError: (error) => {
			logger.error("Failed to upload photo:", error);
		}
	});
	React.useEffect(() => {
		return () => {
			if (stream) stream.getTracks().forEach((track) => track.stop());
		};
	}, [stream]);
	React.useEffect(() => {
		if (stream && videoRef.current) videoRef.current.srcObject = stream;
	}, [stream]);
	const startCamera = async () => {
		try {
			setError("");
			const mediaStream = await navigator.mediaDevices.getUserMedia({
				video: {
					width: { ideal: 1280 },
					height: { ideal: 720 },
					facingMode: "user"
				},
				audio: false
			});
			setStream(mediaStream);
		} catch (err) {
			logger.error("Error accessing camera:", err);
			setError(err instanceof Error ? err.message : t("cameraError"));
		}
	};
	const stopCamera = () => {
		if (stream) {
			stream.getTracks().forEach((track) => track.stop());
			setStream(null);
			setVideoReady(false);
			if (videoRef.current) videoRef.current.srcObject = null;
		}
	};
	const takePhoto = async () => {
		if (videoRef.current && videoRef.current.videoWidth > 0) {
			const canvas = document.createElement("canvas");
			canvas.width = videoRef.current.videoWidth;
			canvas.height = videoRef.current.videoHeight;
			const ctx = canvas.getContext("2d");
			if (!ctx) return;
			ctx.drawImage(videoRef.current, 0, 0);
			const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
			if (blob) {
				const file = new File([blob], `camera-photo-${Date.now()}.png`, { type: "image/png" });
				upload(file);
			}
		}
	};
	return /* @__PURE__ */ jsxs("div", {
		className: "w-full flex flex-col items-center gap-4",
		children: [error && /* @__PURE__ */ jsxs("div", {
			className: "w-full p-3 bg-red-50 border border-red-200 rounded-lg",
			children: [/* @__PURE__ */ jsx("p", {
				className: "text-red-600 text-sm",
				children: error
			}), /* @__PURE__ */ jsx("p", {
				className: "text-red-500 text-xs mt-1",
				children: t("cameraErrorHint")
			})]
		}), stream ? /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("div", {
			className: "w-full border rounded-lg overflow-hidden bg-gray-100",
			children: /* @__PURE__ */ jsx("video", {
				ref: videoRef,
				autoPlay: true,
				playsInline: true,
				muted: true,
				className: "w-full h-64 object-cover",
				onLoadedMetadata: () => {
					if (videoRef.current) setVideoReady(true);
				},
				onCanPlay: () => {
					setVideoReady(true);
				}
			})
		}), /* @__PURE__ */ jsxs("div", {
			className: "flex gap-2",
			children: [/* @__PURE__ */ jsx(Button, {
				onClick: takePhoto,
				disabled: !videoReady || isUploading,
				children: isUploading ? `${t("uploading")}...` : t("takePhoto")
			}), /* @__PURE__ */ jsx(Button, {
				variant: "outline",
				onClick: stopCamera,
				children: t("closeCamera")
			})]
		})] }) : /* @__PURE__ */ jsxs(Button, {
			onClick: startCamera,
			className: "w-full h-32 flex flex-col gap-2",
			children: [/* @__PURE__ */ jsx(Camera, { className: "h-8 w-8" }), /* @__PURE__ */ jsx("span", { children: t("clickToStartCamera") })]
		})]
	});
};
const AddImageNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.add");
	const id = useNodeId();
	const updates = useFlow((s) => s.updates);
	data?.activeTab;
	logger.debug("AddImageNode");
	const handleTabChange = (value) => {
		if (id) updates(id, {
			...data,
			activeTab: value
		});
	};
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		className: "min-w-[480px]",
		data,
		title: t("addImage"),
		icon: /* @__PURE__ */ jsx(Image$1, { className: "h-5 w-5" }),
		isInputNode: true,
		showPluginSelect: false,
		children: [/* @__PURE__ */ jsx(Handle, {
			type: "source",
			position: Position.Right,
			id: "out:imageNode"
		}), /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-2",
			children: /* @__PURE__ */ jsxs(Tabs, {
				defaultValue: data?.activeTab || "upload",
				className: "w-full",
				onValueChange: handleTabChange,
				children: [/* @__PURE__ */ jsxs(TabsList, {
					className: "grid grid-cols-4 gap-2 bg-transparent h-auto p-0",
					children: [
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "upload",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Upload, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("upload")
							})]
						}, "upload"),
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "camera",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Camera, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("camera")
							})]
						}, "camera"),
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "canvas",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Pencil, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("canvas")
							})]
						}, "canvas"),
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "library",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Library, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("library")
							})]
						}, "library")
					]
				}), /* @__PURE__ */ jsxs("div", {
					className: "mt-2",
					children: [
						/* @__PURE__ */ jsx(TabsContent, {
							value: "upload",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(UploadTab$2, {})
						}, "upload"),
						/* @__PURE__ */ jsx(TabsContent, {
							value: "camera",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(CameraTab$1, {})
						}, "camera"),
						/* @__PURE__ */ jsx(TabsContent, {
							value: "canvas",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(CanvasTab, {})
						}, "canvas"),
						/* @__PURE__ */ jsx(TabsContent, {
							value: "library",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(LibraryTab$3, {})
						}, "library")
					]
				})]
			})
		})]
	});
};
AddImageNode.displayName = "AddImageNode";
var add_image_node_default = memo(AddImageNode);
//#endregion
//#region src/canvas/hooks/use-node-data.ts
/**
* Hook for managing multiple states of a node
* @param initialState Initial state object
* @param nodeData Node's data object
* @returns [state, updateState] Similar to useState interface, but supports object updates
*/
function useNodeState(initialState, nodeData) {
	const id = useNodeId();
	const flowUpdates = useFlow((s) => s.updates);
	const [state, setState] = useState(() => ({
		...initialState,
		...nodeData
	}));
	const stateRef = useRef(state);
	stateRef.current = state;
	useEffect(() => {
		setState((prev) => ({
			...prev,
			...nodeData
		}));
	}, [nodeData]);
	return [state, useCallback((updates) => {
		const prev = stateRef.current;
		const newState = typeof updates === "function" ? updates(prev) : {
			...prev,
			...updates
		};
		stateRef.current = newState;
		setState(newState);
		if (id) flowUpdates(id, newState);
	}, [id, flowUpdates])];
}
//#endregion
//#region src/canvas/nodes/add/add-link-node.tsx
const AddLinkNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.add");
	const id = useNodeId();
	const expands = useFlow((s) => s.expands);
	const updateNode = useFlow((s) => s.updates);
	const edges = useFlow((s) => s.edges);
	const [state, updateState] = useNodeState({ urls: [] }, data);
	const urls = state.urls;
	const [input, setInput] = useState("");
	const downstreamId = useMemo(() => edges.find((e) => e.source === id)?.target, [edges, id]);
	const downstreamData = useNodesData(downstreamId ? [downstreamId] : []);
	const setUrls = (next) => {
		updateState({ urls: next });
		if (downstreamId) updateNode(downstreamId, {
			...downstreamData[0]?.data,
			texts: next
		});
	};
	const handleAddUrl = () => {
		const match = input.trim().match(/https?:\/\/[^\s]+/);
		if (!match) return;
		setUrls([...urls, match[0]]);
		setInput("");
	};
	const handleRemove = (index) => {
		setUrls(urls.filter((_, i) => i !== index));
	};
	const handleCreate = () => {
		if (!urls.length || !id || downstreamId) return;
		expands(id, [{
			type: "linkNode",
			data: { texts: urls }
		}]);
	};
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		className: "min-w-[480px]",
		data,
		title: t("addLink"),
		icon: /* @__PURE__ */ jsx(Link, { className: "h-5 w-5" }),
		isInputNode: true,
		showPluginSelect: false,
		children: [/* @__PURE__ */ jsx(Handle, {
			type: "source",
			position: Position.Right,
			id: "out:linkNode"
		}), /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-2",
			children: [
				urls.length > 0 && /* @__PURE__ */ jsx("div", {
					className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
					children: urls.map((url, idx) => /* @__PURE__ */ jsxs(Card, {
						className: "p-3 relative rounded-lg border hover:shadow-sm transition-all",
						children: [/* @__PURE__ */ jsx("button", {
							className: "absolute top-2 right-2 text-muted-foreground hover:text-destructive transition-colors",
							onClick: () => handleRemove(idx),
							children: /* @__PURE__ */ jsx(Trash, { size: 14 })
						}), /* @__PURE__ */ jsx("div", {
							className: "pr-6",
							children: /* @__PURE__ */ jsx("a", {
								href: url,
								target: "_blank",
								rel: "noreferrer",
								className: "text-primary text-xs hover:underline break-all line-clamp-2",
								children: url
							})
						})]
					}, idx))
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "flex gap-2 items-center nodrag",
					onPointerDown: (e) => e.stopPropagation(),
					children: [/* @__PURE__ */ jsx(Input, {
						placeholder: t("linkPlaceholder"),
						value: input,
						onChange: (e) => setInput(e.target.value),
						className: "flex-1 h-10",
						onKeyDown: (e) => {
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault();
								handleAddUrl();
							}
						}
					}), /* @__PURE__ */ jsx(Button, {
						variant: "outline",
						size: "default",
						onClick: handleAddUrl,
						className: "h-10 px-3",
						children: /* @__PURE__ */ jsx(Plus, { size: 16 })
					})]
				}),
				/* @__PURE__ */ jsx(Button, {
					onClick: handleCreate,
					disabled: !urls.length || !!downstreamId,
					className: "w-full h-10",
					children: t("addLink")
				})
			]
		})]
	});
};
AddLinkNode.displayName = "AddLinkNode";
var add_link_node_default = memo(AddLinkNode);
//#endregion
//#region src/canvas/nodes/add/add-model-node.tsx
const LibraryTab$2 = () => {
	return /* @__PURE__ */ jsx("div", {
		className: "space-y-3",
		children: /* @__PURE__ */ jsx(Card, {
			className: "p-3",
			children: /* @__PURE__ */ jsx(LibInput, { resourceType: "MODEL" })
		})
	});
};
const UploadTab$1 = () => {
	const t = useTranslations("Workspace.nodes.add");
	const { expands } = useFlow();
	const id = useNodeId();
	const [isDragActive, setIsDragActive] = useState(false);
	const { upload, isUploading, progress } = useMultipleUpload({
		onSuccess: (responses) => {
			if (id && responses.length > 0) {
				const response = responses[0];
				expands(id, [{
					type: "modelNode",
					data: { fileKeys: [response.key] }
				}]);
			}
		},
		onError: (error) => {
			logger.error("Upload failed:", error);
		}
	});
	const handleFileChange = async (e) => {
		if (e.target.files && e.target.files.length > 0) await upload(Array.from(e.target.files));
	};
	const handleDrop = async (e) => {
		e.preventDefault();
		setIsDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) await upload(Array.from(e.dataTransfer.files));
	};
	const supportedFormats = [
		".glb",
		".gltf",
		".obj",
		".fbx",
		".stl",
		".dae",
		".ply",
		".spz",
		".splat",
		".usdz"
	];
	return /* @__PURE__ */ jsxs("div", {
		className: "space-y-3",
		children: [/* @__PURE__ */ jsx(Card, {
			className: "p-3",
			children: /* @__PURE__ */ jsx("div", {
				onDrop: handleDrop,
				onDragOver: (e) => {
					e.preventDefault();
					setIsDragActive(true);
				},
				onDragLeave: () => setIsDragActive(false),
				onPointerDown: (e) => e.stopPropagation(),
				className: `w-full h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors cursor-pointer nodrag ${isDragActive ? "border-primary bg-primary/10" : "border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50"}`,
				children: isUploading ? /* @__PURE__ */ jsxs("div", {
					className: "w-full px-8 space-y-3",
					children: [/* @__PURE__ */ jsx(Progress, { value: progress }), /* @__PURE__ */ jsxs("p", {
						className: "text-sm text-center text-muted-foreground font-medium",
						children: [
							t("uploading"),
							" ",
							Math.round(progress),
							"%"
						]
					})]
				}) : /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(Upload, { className: "h-10 w-10 mb-3 text-muted-foreground" }),
					/* @__PURE__ */ jsx("p", {
						className: "text-sm font-medium text-foreground mb-1",
						children: isDragActive ? t("dropToUpload") : t("dragModel")
					}),
					/* @__PURE__ */ jsxs("p", {
						className: "text-xs text-muted-foreground",
						children: [
							t("supportedModelFormats"),
							":",
							" ",
							supportedFormats.join(", ")
						]
					}),
					/* @__PURE__ */ jsx("label", {
						htmlFor: `model-upload-${id}`,
						children: /* @__PURE__ */ jsx(Button, {
							variant: "outline",
							size: "sm",
							className: "mt-3",
							asChild: true,
							children: /* @__PURE__ */ jsx("span", { children: t("browseFiles") })
						})
					}),
					/* @__PURE__ */ jsx("input", {
						id: `model-upload-${id}`,
						type: "file",
						accept: supportedFormats.map((f) => `*${f}`).join(","),
						onChange: handleFileChange,
						className: "hidden"
					})
				] })
			})
		}), /* @__PURE__ */ jsx(Card, {
			className: "p-3",
			children: /* @__PURE__ */ jsxs("p", {
				className: "text-xs text-muted-foreground",
				children: [
					/* @__PURE__ */ jsx("strong", { children: t("modelHint") }),
					/* @__PURE__ */ jsx("br", {}),
					"GLB, glTF, OBJ, FBX, STL, DAE, PLY, SPZ, SPLAT, USDZ"
				]
			})
		})]
	});
};
const AddModelNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.add");
	const id = useNodeId();
	const updates = useFlow((s) => s.updates);
	data?.activeTab;
	const handleTabChange = (value) => {
		if (id) updates(id, {
			...data,
			activeTab: value
		});
	};
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		className: "min-w-[480px]",
		data,
		title: t("addModel"),
		icon: /* @__PURE__ */ jsx(Box, { className: "h-5 w-5" }),
		isInputNode: true,
		showPluginSelect: false,
		children: [/* @__PURE__ */ jsx(Handle, {
			type: "source",
			position: Position.Right,
			id: "out:modelNode"
		}), /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-2",
			children: /* @__PURE__ */ jsxs(Tabs, {
				defaultValue: data?.activeTab || "upload",
				className: "w-full",
				onValueChange: handleTabChange,
				children: [/* @__PURE__ */ jsxs(TabsList, {
					className: "grid grid-cols-2 gap-2 bg-transparent h-auto p-0",
					children: [/* @__PURE__ */ jsxs(TabsTrigger, {
						value: "upload",
						className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
						children: [/* @__PURE__ */ jsx(Upload, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
							className: "text-xs font-medium",
							children: t("upload")
						})]
					}, "upload"), /* @__PURE__ */ jsxs(TabsTrigger, {
						value: "library",
						className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
						children: [/* @__PURE__ */ jsx(Library, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
							className: "text-xs font-medium",
							children: t("library")
						})]
					}, "library")]
				}), /* @__PURE__ */ jsxs("div", {
					className: "mt-2",
					children: [/* @__PURE__ */ jsx(TabsContent, {
						value: "upload",
						className: "mt-0",
						children: /* @__PURE__ */ jsx(UploadTab$1, {})
					}, "upload"), /* @__PURE__ */ jsx(TabsContent, {
						value: "library",
						className: "mt-0",
						children: /* @__PURE__ */ jsx(LibraryTab$2, {})
					}, "library")]
				})]
			})
		})]
	});
};
AddModelNode.displayName = "AddModelNode";
memo(AddModelNode);
//#endregion
//#region src/canvas/lib/api/task.ts
/**
* Task API client
*/
/**
* Create task
*/
async function createTask(config) {
	return await apiPost("/api/task/create", config);
}
/**
* Get task list
*/
async function listTasks(page = 1, pageSize = 20) {
	return await apiGet(`/api/task/list?page=${page}&pageSize=${pageSize}`);
}
/**
* Update task status (frontend safety net, for single tasks)
* Do not save assets; only update task status
*/
async function updateTaskStatus(request) {
	return await apiPost("/api/task/update-status", request);
}
//#endregion
//#region src/canvas/lib/task/api-url.ts
/**
* Request URLs for task SSE (wait) and stop, served by the TongFlow API.
*/
function getTaskWaitUrl(taskId, reconnect) {
	const params = new URLSearchParams({ taskId });
	if (reconnect) params.set("reconnect", "true");
	return apiUrl(`/api/task/wait?${params.toString()}`);
}
function getTaskStopUrl() {
	return apiUrl("/api/task/stop");
}
//#endregion
//#region src/canvas/lib/task/sse-events.ts
const SSE_TASK_MESSAGE_EVENT = "sse-task-message";
const TASK_CANCEL_REQUEST_EVENT = "task-cancel-request";
function emitSSETaskMessage(message) {
	if (typeof window !== "undefined") {
		logger.debug("[emitSSETaskMessage] Emitting:", message);
		const event = new CustomEvent(SSE_TASK_MESSAGE_EVENT, { detail: message });
		window.dispatchEvent(event);
	}
}
function emitSSEConnected(taskId) {
	emitSSETaskMessage({
		id: taskId,
		status: "SSE_CONNECTED",
		nodeId: null
	});
}
function emitTaskCancelRequest(taskId) {
	if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(TASK_CANCEL_REQUEST_EVENT, { detail: { taskId } }));
}
//#endregion
//#region src/canvas/hooks/use-task.ts
/**
* Task management hook.
* Uses SSE (Server-Sent Events) for realtime task updates.
*/
function emitTaskProgressFromSSE(payload) {
	const nodeId = payload.nodeId != null && payload.nodeId !== "" ? String(payload.nodeId) : null;
	emitSSETaskMessage({
		id: payload.id,
		status: payload.status,
		nodeId,
		data: payload.data
	});
}
const WORKSPACE_MODE_KEY = "workspace-mode";
const SSE_DEFAULT_MAX_RETRIES = 10;
const SSE_DEFAULT_RETRY_DELAY = 2e3;
const SSE_MAX_RETRY_DELAY = 3e4;
const useTaskStore = create((set, get) => ({
	tasks: /* @__PURE__ */ new Map(),
	taskNodeMap: /* @__PURE__ */ new Map(),
	nodeHandlers: /* @__PURE__ */ new Map(),
	workspaceMode: "create",
	workflowExecutionStatus: "idle",
	currentExecutionLevel: -1,
	nodeExecutionStatusMap: /* @__PURE__ */ new Map(),
	setWorkspaceMode: (mode) => {
		if (typeof window !== "undefined") localStorage.setItem(WORKSPACE_MODE_KEY, mode);
		set({ workspaceMode: mode });
	},
	setWorkflowExecutionStatus: (status) => {
		set({ workflowExecutionStatus: status });
	},
	setCurrentExecutionLevel: (level) => {
		set({ currentExecutionLevel: level });
	},
	setNodeExecutionStatus: (nodeId, status) => {
		set((state) => {
			const newMap = new Map(state.nodeExecutionStatusMap);
			newMap.set(nodeId, status);
			return { nodeExecutionStatusMap: newMap };
		});
	},
	clearNodeExecutionStatus: () => {
		set({
			nodeExecutionStatusMap: /* @__PURE__ */ new Map(),
			currentExecutionLevel: -1
		});
	},
	setTask: (taskId, task) => set((state) => {
		const newTasks = new Map(state.tasks);
		newTasks.set(String(taskId), task);
		return { tasks: newTasks };
	}),
	getTask: (taskId) => get().tasks.get(String(taskId)),
	removeTask: (taskId) => set((state) => {
		const newTasks = new Map(state.tasks);
		const taskIdStr = String(taskId);
		newTasks.delete(taskIdStr);
		const newTaskNodeMap = new Map(state.taskNodeMap);
		newTaskNodeMap.delete(taskIdStr);
		return {
			tasks: newTasks,
			taskNodeMap: newTaskNodeMap
		};
	}),
	clearCompletedTasks: () => set((state) => {
		const newTasks = /* @__PURE__ */ new Map();
		const newTaskNodeMap = new Map(state.taskNodeMap);
		for (const [taskId, task] of state.tasks) if (task.status === "PENDING" || task.status === "PROCESSING") newTasks.set(taskId, task);
		else newTaskNodeMap.delete(taskId);
		logger.debug(`[Task Store] Cleared completed tasks, ${state.tasks.size - newTasks.size} tasks removed`);
		return {
			tasks: newTasks,
			taskNodeMap: newTaskNodeMap
		};
	}),
	getActiveTasks: () => {
		return Array.from(get().tasks.values()).filter((task) => task.status === "PENDING" || task.status === "PROCESSING");
	},
	hasActiveTasks: () => {
		return Array.from(get().tasks.values()).some((task) => task.status === "PENDING" || task.status === "PROCESSING");
	},
	trackTaskToNode: (taskId, nodeId) => {
		set((state) => {
			const newTaskNodeMap = new Map(state.taskNodeMap);
			newTaskNodeMap.set(taskId, nodeId);
			logger.debug(`[Task Store] Task ${taskId} tracked to node ${nodeId}`);
			return { taskNodeMap: newTaskNodeMap };
		});
	},
	getTaskNodeId: (taskId) => get().taskNodeMap.get(taskId),
	registerNodeHandler: (nodeId, handler) => {
		set((state) => {
			const newNodeHandlers = new Map(state.nodeHandlers);
			const handlers = newNodeHandlers.get(nodeId) || [];
			handlers.push(handler);
			newNodeHandlers.set(nodeId, handlers);
			logger.debug(`[Task Store] Registered handler for node ${nodeId}, total handlers: ${handlers.length}`);
			return { nodeHandlers: newNodeHandlers };
		});
	},
	unregisterNodeHandler: (nodeId, handler) => {
		set((state) => {
			const newNodeHandlers = new Map(state.nodeHandlers);
			const handlers = newNodeHandlers.get(nodeId) || [];
			const index = handlers.indexOf(handler);
			if (index > -1) handlers.splice(index, 1);
			if (handlers.length === 0) newNodeHandlers.delete(nodeId);
			else newNodeHandlers.set(nodeId, handlers);
			logger.debug(`[Task Store] Unregistered handler for node ${nodeId}, remaining handlers: ${handlers.length}`);
			return { nodeHandlers: newNodeHandlers };
		});
	},
	routeTaskToNode: (task) => {
		const state = get();
		const nodeId = state.taskNodeMap.get(task.id) ?? (task.nodeId ? String(task.nodeId) : void 0);
		if (nodeId) {
			const handlers = state.nodeHandlers.get(nodeId) || [];
			logger.debug(`[Task Router] Routing task ${task.id} (status: ${task.status}) to node ${nodeId}, handlers: ${handlers.length}`);
			handlers.forEach((handler) => {
				try {
					handler(task);
				} catch (error) {
					logger.error(`[Task Router] Error calling handler for node ${nodeId}:`, error);
				}
			});
		} else logger.warn(`[Task Router] No node found for task ${task.id}, storing in global store only`);
	}
}));
/**
* Subscribe to task updates over SSE (Server-Sent Events).
* Handles connection lifecycle, reconnect, and parsing.
*/
function useTaskSubscription(taskId, options) {
	const t = useTranslations("Workspace.toast");
	const { setTask, routeTaskToNode } = useTaskStore();
	const [status, setStatus] = useState("disconnected");
	const eventSourceRef = useRef(null);
	const reconnectTimeoutRef = useRef(null);
	const reconnectAttemptsRef = useRef(0);
	const maxRetries = options?.maxRetries ?? SSE_DEFAULT_MAX_RETRIES;
	const baseRetryDelay = options?.retryDelay ?? SSE_DEFAULT_RETRY_DELAY;
	useEffect(() => {
		if (!taskId) {
			setStatus("disconnected");
			return;
		}
		let isSubscribed = true;
		const connect = () => {
			if (!isSubscribed) return;
			try {
				setStatus(reconnectAttemptsRef.current > 0 ? "reconnecting" : "connecting");
				const eventSource = new EventSource(getTaskWaitUrl(taskId));
				eventSourceRef.current = eventSource;
				eventSource.onopen = () => {
					if (!isSubscribed) return;
					setStatus("connected");
					reconnectAttemptsRef.current = 0;
					emitSSEConnected(taskId);
					if (options?.onStatusChange) options.onStatusChange("connected");
				};
				eventSource.onmessage = (event) => {
					if (!isSubscribed) return;
					try {
						const message = JSON.parse(event.data);
						logger.debug(`[SSE] Received message:`, message);
						emitTaskProgressFromSSE(message);
						const taskStatus = mapSSEStatusToTaskStatus(message.status);
						const msgNodeId = message.nodeId;
						const internalTask = {
							id: message.id,
							status: taskStatus,
							progress: message.progress || 0,
							data: message.data,
							error: message.error || message.data?.error,
							nodeId: (msgNodeId != null && msgNodeId !== "" ? String(msgNodeId) : void 0) ?? useTaskStore.getState().getTaskNodeId(message.id)
						};
						setTask(internalTask.id, internalTask);
						logger.debug(`[SSE] Task updated: ${internalTask.id} (${internalTask.status})`);
						if (taskStatus === "COMPLETED") toast.success(t("taskCompleted"));
						if (isTerminalStatus(message.status)) updateTaskStatus({
							taskId: message.id,
							status: message.status,
							data: message.data
						}).catch((error) => {
							logger.error("[SSE] Failed to update task status (frontend backup):", error);
						});
						routeTaskToNode(internalTask);
						if (options?.onTaskUpdate) options.onTaskUpdate(internalTask);
						if (isTerminalStatus(message.status)) {
							isSubscribed = false;
							if (reconnectTimeoutRef.current) {
								clearTimeout(reconnectTimeoutRef.current);
								reconnectTimeoutRef.current = null;
							}
							eventSource.close();
							eventSourceRef.current = null;
							setStatus("disconnected");
							if (options?.onStatusChange) options.onStatusChange("disconnected");
						}
					} catch (error) {
						logger.error("[SSE] Error parsing message:", error, event.data);
					}
				};
				eventSource.addEventListener("close", () => {
					logger.debug("[SSE] Server sent close event");
					if (!isSubscribed) return;
					isSubscribed = false;
					eventSource.close();
					eventSourceRef.current = null;
					setStatus("disconnected");
					if (options?.onStatusChange) options.onStatusChange("disconnected");
				});
				eventSource.onerror = (error) => {
					if (!isSubscribed) return;
					logger.error("[SSE] Connection error:", error);
					eventSource.close();
					eventSourceRef.current = null;
					if (reconnectAttemptsRef.current < maxRetries) {
						reconnectAttemptsRef.current++;
						const delay = Math.min(baseRetryDelay * 2 ** (reconnectAttemptsRef.current - 1), SSE_MAX_RETRY_DELAY);
						logger.debug(`[SSE] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxRetries})`);
						setStatus("reconnecting");
						if (options?.onStatusChange) options.onStatusChange("reconnecting");
						reconnectTimeoutRef.current = setTimeout(() => {
							if (isSubscribed) connect();
						}, delay);
					} else {
						logger.error("[SSE] Max reconnection attempts reached");
						setStatus("error");
						if (options?.onStatusChange) options.onStatusChange("error");
						if (options?.onError) options.onError(/* @__PURE__ */ new Error("Max reconnection attempts reached"));
					}
				};
			} catch (error) {
				logger.error("[SSE] Failed to create EventSource:", error);
				setStatus("error");
				if (options?.onStatusChange) options.onStatusChange("error");
				if (options?.onError) options.onError(error);
			}
		};
		connect();
		return () => {
			isSubscribed = false;
			if (eventSourceRef.current) {
				eventSourceRef.current.close();
				eventSourceRef.current = null;
			}
			if (reconnectTimeoutRef.current) {
				clearTimeout(reconnectTimeoutRef.current);
				reconnectTimeoutRef.current = null;
			}
			setStatus("disconnected");
		};
	}, [
		taskId,
		setTask,
		routeTaskToNode,
		maxRetries,
		baseRetryDelay,
		options
	]);
	return { status };
}
/**
* Create a task and automatically attach SSE streaming.
*/
function useCreateTask(options) {
	const { setTask, trackTaskToNode } = useTaskStore();
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [createdTaskId, setCreatedTaskId] = useState(null);
	useTaskSubscription(createdTaskId ?? void 0, options);
	return {
		createTask: useCallback(async (config) => {
			setIsLoading(true);
			setError(null);
			try {
				const { taskId } = await createTask(config);
				setCreatedTaskId(taskId);
				setTask(taskId, {
					id: taskId,
					status: "PENDING",
					progress: 0,
					data: config.prompt,
					nodeId: config.nodeId
				});
				trackTaskToNode(taskId, config.nodeId);
				logger.debug(`[useCreateTask] Task ${taskId} created from node ${config.nodeId}`);
				return taskId;
			} catch (err) {
				const error = err instanceof Error ? err : /* @__PURE__ */ new Error("Failed to create task");
				setError(error);
				throw error;
			} finally {
				setIsLoading(false);
			}
		}, [setTask, trackTaskToNode]),
		isLoading,
		error
	};
}
/**
* Manage many tasks concurrently.
* Opens one SSE channel per task; loading stops when streams finish or drop.
*/
function useBatchTaskManager(config, options) {
	const t = useTranslations("Workspace.toast");
	const { setTask, trackTaskToNode, routeTaskToNode } = useTaskStore();
	const [isLoading, setIsLoading] = useState(false);
	const [completedTasks, setCompletedTasks] = useState([]);
	const [totalTasks, setTotalTasks] = useState(0);
	const [currentBatchTaskIds, setCurrentBatchTaskIds] = useState(/* @__PURE__ */ new Set());
	const eventSourcesRef = useRef(/* @__PURE__ */ new Map());
	const activeConnectionsRef = useRef(/* @__PURE__ */ new Set());
	/** Shared cancel fallback timeout between SSE handlers and cancelTasks */
	const batchCancelTimeoutRef = useRef(null);
	const reconnectAttemptsRef = useRef(/* @__PURE__ */ new Map());
	const reconnectTimeoutsRef = useRef(/* @__PURE__ */ new Map());
	const createBatchTasks = useCallback(async (taskConfigs) => {
		setIsLoading(true);
		setCompletedTasks([]);
		setTotalTasks(taskConfigs.length);
		activeConnectionsRef.current.clear();
		if (batchCancelTimeoutRef.current != null) {
			clearTimeout(batchCancelTimeoutRef.current);
			batchCancelTimeoutRef.current = null;
		}
		try {
			const taskIds = [];
			const createPromises = taskConfigs.map(async (taskConfig) => {
				const { taskId } = await createTask(taskConfig);
				taskIds.push(taskId);
				setTask(taskId, {
					id: taskId,
					status: "PENDING",
					progress: 0,
					data: taskConfig.prompt,
					nodeId: taskConfig.nodeId
				});
				trackTaskToNode(taskId, taskConfig.nodeId);
				const connectTaskSSE = (tid) => {
					const es = new EventSource(getTaskWaitUrl(tid));
					es.onopen = () => {
						logger.debug(`[SSE Batch] Connected for task ${tid}`);
						reconnectAttemptsRef.current.set(tid, 0);
						activeConnectionsRef.current.add(tid);
						emitSSEConnected(tid);
					};
					es.onmessage = (event) => {
						try {
							const message = JSON.parse(event.data);
							logger.debug(`[SSE Batch] Message for task ${tid}:`, message);
							emitTaskProgressFromSSE(message);
							const taskStatus = mapSSEStatusToTaskStatus(message.status);
							const msgNodeId = message.nodeId;
							const internalTask = {
								id: message.id,
								status: taskStatus,
								progress: message.progress || 0,
								data: message.data,
								error: message.error,
								nodeId: (msgNodeId != null && msgNodeId !== "" ? String(msgNodeId) : void 0) ?? useTaskStore.getState().getTaskNodeId(message.id)
							};
							setTask(internalTask.id, internalTask);
							routeTaskToNode(internalTask);
							if (taskStatus === "COMPLETED") toast.success(t("taskCompleted"));
							else if (taskStatus === "CANCELLED") {
								if (batchCancelTimeoutRef.current != null) {
									clearTimeout(batchCancelTimeoutRef.current);
									batchCancelTimeoutRef.current = null;
								}
							}
							if (isTerminalStatus(message.status)) {
								updateTaskStatus({
									taskId: message.id,
									status: message.status,
									data: message.data
								}).catch((err) => {
									logger.error("[SSE Batch] Failed to update task status (frontend backup):", err);
								});
								const pending = reconnectTimeoutsRef.current.get(tid);
								if (pending != null) {
									clearTimeout(pending);
									reconnectTimeoutsRef.current.delete(tid);
								}
								reconnectAttemptsRef.current.set(tid, Number.POSITIVE_INFINITY);
								es.close();
								eventSourcesRef.current.delete(tid);
								activeConnectionsRef.current.delete(tid);
								if (activeConnectionsRef.current.size === 0) {
									setIsLoading(false);
									if (taskStatus === "CANCELLED") setCurrentBatchTaskIds(/* @__PURE__ */ new Set());
								}
							}
							if (options?.onTaskUpdate) options.onTaskUpdate(internalTask);
						} catch (err) {
							logger.error(`[SSE Batch] Error parsing message:`, err);
						}
					};
					es.addEventListener("close", () => {
						logger.debug(`[SSE Batch] Server sent close event for task ${tid}`);
						es.close();
						eventSourcesRef.current.delete(tid);
						activeConnectionsRef.current.delete(tid);
						if (activeConnectionsRef.current.size === 0) {
							setIsLoading(false);
							logger.debug("[SSE Batch] All connections closed, loading stopped");
						}
					});
					es.onerror = () => {
						es.close();
						eventSourcesRef.current.delete(tid);
						const attempts = (reconnectAttemptsRef.current.get(tid) ?? 0) + 1;
						if (attempts <= SSE_DEFAULT_MAX_RETRIES) {
							reconnectAttemptsRef.current.set(tid, attempts);
							const delay = Math.min(SSE_DEFAULT_RETRY_DELAY * 2 ** (attempts - 1), SSE_MAX_RETRY_DELAY);
							logger.debug(`[SSE Batch] Reconnecting task ${tid} in ${delay}ms (attempt ${attempts}/${SSE_DEFAULT_MAX_RETRIES})`);
							const timeoutId = setTimeout(() => {
								reconnectTimeoutsRef.current.delete(tid);
								connectTaskSSE(tid);
							}, delay);
							reconnectTimeoutsRef.current.set(tid, timeoutId);
						} else {
							logger.error(`[SSE Batch] Max reconnection attempts reached for task ${tid}`);
							activeConnectionsRef.current.delete(tid);
							if (activeConnectionsRef.current.size === 0) setIsLoading(false);
							if (options?.onError) options.onError(/* @__PURE__ */ new Error(`Max reconnection attempts reached for task ${tid}`));
						}
					};
					eventSourcesRef.current.set(tid, es);
				};
				connectTaskSSE(taskId);
				return taskId;
			});
			const createdTaskIds = await Promise.all(createPromises);
			setCurrentBatchTaskIds(new Set(createdTaskIds));
			return createdTaskIds;
		} catch (error) {
			logger.error("Failed to create batch tasks:", error);
			setIsLoading(false);
			throw error;
		}
	}, [
		setTask,
		trackTaskToNode,
		routeTaskToNode,
		options
	]);
	useEffect(() => {
		if (currentBatchTaskIds.size === 0) return;
		const interval = setInterval(() => {
			const tasks = useTaskStore.getState().tasks;
			const completed = Array.from(currentBatchTaskIds).map((id) => tasks.get(id)).filter((task) => task !== void 0).filter((task) => task.status === "COMPLETED" || task.status === "FAILED");
			setCompletedTasks(completed);
			if (config?.onProgress) config.onProgress(completed.length, totalTasks);
			if (completed.length === totalTasks && config?.onBatchComplete) {
				config.onBatchComplete(completed);
				setCurrentBatchTaskIds(/* @__PURE__ */ new Set());
			}
		}, 500);
		return () => clearInterval(interval);
	}, [
		currentBatchTaskIds,
		totalTasks,
		config
	]);
	const cancelTasks = useCallback(async () => {
		if (currentBatchTaskIds.size === 0) return;
		const taskIds = Array.from(currentBatchTaskIds);
		logger.debug("[BatchTaskManager] Cancelling tasks:", taskIds);
		for (const taskId of taskIds) emitSSETaskMessage({
			id: taskId,
			status: TaskStatus.RUNNING,
			nodeId: null,
			data: { message: t("cancelling") }
		});
		const stopPromises = taskIds.map(async (taskId) => {
			try {
				const response = await hostFetch(getTaskStopUrl(), {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ taskId })
				});
				logger.debug(`[BatchTaskManager] Stop request sent for task: ${taskId}`);
				return response.ok;
			} catch (error) {
				logger.error(`[BatchTaskManager] Failed to send stop request for task ${taskId}:`, error);
				return false;
			}
		});
		await Promise.all(stopPromises);
		if (batchCancelTimeoutRef.current != null) {
			clearTimeout(batchCancelTimeoutRef.current);
			batchCancelTimeoutRef.current = null;
		}
		const timeoutId = setTimeout(() => {
			batchCancelTimeoutRef.current = null;
			logger.debug("[BatchTaskManager] Timeout waiting for CANCELLED message, emitting manually");
			for (const taskId of taskIds) emitSSETaskMessage({
				id: taskId,
				status: TaskStatus.CANCELLED,
				nodeId: null,
				data: { message: t("cancelled") }
			});
			eventSourcesRef.current.forEach((eventSource) => {
				eventSource.close();
			});
			eventSourcesRef.current.clear();
			activeConnectionsRef.current.clear();
			setIsLoading(false);
			setCurrentBatchTaskIds(/* @__PURE__ */ new Set());
		}, 1e4);
		batchCancelTimeoutRef.current = timeoutId;
	}, [currentBatchTaskIds, t]);
	useEffect(() => {
		const handleCancelRequest = () => {
			if (currentBatchTaskIds.size > 0) {
				logger.debug("[BatchTaskManager] Received cancel request from Toast");
				cancelTasks();
			}
		};
		window.addEventListener(TASK_CANCEL_REQUEST_EVENT, handleCancelRequest);
		return () => {
			window.removeEventListener(TASK_CANCEL_REQUEST_EVENT, handleCancelRequest);
		};
	}, [currentBatchTaskIds, cancelTasks]);
	useEffect(() => {
		return () => {
			if (batchCancelTimeoutRef.current != null) {
				clearTimeout(batchCancelTimeoutRef.current);
				batchCancelTimeoutRef.current = null;
			}
			eventSourcesRef.current.forEach((eventSource) => {
				eventSource.close();
			});
			eventSourcesRef.current.clear();
			activeConnectionsRef.current.clear();
			reconnectTimeoutsRef.current.forEach(clearTimeout);
			reconnectTimeoutsRef.current.clear();
			reconnectAttemptsRef.current.clear();
		};
	}, []);
	return {
		createBatchTasks,
		cancelTasks,
		isLoading,
		completedTasks,
		progress: totalTasks > 0 ? completedTasks.length / totalTasks * 100 : 0
	};
}
/**
* Convenience hook bridging the Zustand store with routing helpers.
*
* SSE handling is encapsulated by useCreateTask; consumers rarely subscribe manually.
*/
function useTask() {
	return {
		tasks: useTaskStore((state) => state.tasks),
		setTask: useTaskStore((state) => state.setTask),
		getTask: useTaskStore((state) => state.getTask),
		removeTask: useTaskStore((state) => state.removeTask),
		clearCompletedTasks: useTaskStore((state) => state.clearCompletedTasks),
		getActiveTasks: useTaskStore((state) => state.getActiveTasks),
		hasActiveTasks: useTaskStore((state) => state.hasActiveTasks),
		trackTaskToNode: useTaskStore((state) => state.trackTaskToNode),
		getTaskNodeId: useTaskStore((state) => state.getTaskNodeId),
		registerNodeHandler: useTaskStore((state) => state.registerNodeHandler),
		unregisterNodeHandler: useTaskStore((state) => state.unregisterNodeHandler),
		routeTaskToNode: useTaskStore((state) => state.routeTaskToNode)
	};
}
/**
* Subscribe handlers for updates emitted by tasks created from nodeId.
*/
function useNodeTaskUpdate(nodeId, handler) {
	const { registerNodeHandler, unregisterNodeHandler } = useTaskStore();
	const handlerRef = useRef(handler);
	useEffect(() => {
		handlerRef.current = handler;
	}, [handler]);
	useEffect(() => {
		if (!nodeId) return;
		const wrappedHandler = (task) => {
			handlerRef.current(task);
		};
		logger.debug(`[useNodeTaskUpdate] Registering task update handler for node: ${nodeId}`);
		registerNodeHandler(nodeId, wrappedHandler);
		return () => {
			logger.debug(`[useNodeTaskUpdate] Unregistering task update handler for node: ${nodeId}`);
			unregisterNodeHandler(nodeId, wrappedHandler);
		};
	}, [
		nodeId,
		registerNodeHandler,
		unregisterNodeHandler
	]);
}
//#endregion
//#region src/canvas/ui/textarea.tsx
function Textarea({ className, ...props }) {
	return /* @__PURE__ */ jsx("textarea", {
		"data-slot": "textarea",
		className: cn("border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm", className),
		...props
	});
}
//#endregion
//#region src/canvas/nodes/base/node-textarea.tsx
/**
* Shared node Textarea component with voice input support
*
* @example
* // Labelled usage example
* <NodeTextarea
*   label="Edit instructions"
*   icon={Sparkles}
*   placeholder="Enter image editing instructions..."
*   value={editText}
*   onChange={(value) => setState({ editText: value })}
*   rows={4}
* />
*
* @example
* // Minimal usage example
* <NodeTextarea
*   placeholder="Enter your instructions..."
*   value={prompt}
*   onChange={(value) => setState({ prompt: value })}
*   rows={6}
* />
*
* @example
* // Disable dictation helper
* <NodeTextarea
*   placeholder="Enter your instructions..."
*   value={prompt}
*   onChange={(value) => setState({ prompt: value })}
*   enableVoiceInput={false}
* />
*/
const NodeTextarea = forwardRef(({ label, icon: Icon, showCard = true, cardClassName, labelClassName, className, value, onChange, rows = 4, enableVoiceInput = true, voiceLang = "zh-CN", enableFullscreen = true, fullscreenTitle, ...props }, ref) => {
	const t = useTranslations("Workspace.nodes.base");
	const defaultTitle = t("editText");
	const title = fullscreenTitle || defaultTitle;
	const [isListening, setIsListening] = useState(false);
	const [interimText, setInterimText] = useState("");
	const [speechSupported, setSpeechSupported] = useState(false);
	const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
	const [fullscreenValue, setFullscreenValue] = useState("");
	const recognitionRef = useRef(null);
	const textareaRef = useRef(null);
	const fullscreenTextareaRef = useRef(null);
	const cursorPositionRef = useRef(null);
	const isFullscreenRef = useRef(false);
	const [localValue, setLocalValue] = useState(value || "");
	const isTypingRef = useRef(false);
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	useEffect(() => {
		if (!isTypingRef.current) setLocalValue(value || "");
	}, [value]);
	const flushToParent = useCallback((val) => {
		isTypingRef.current = false;
		onChangeRef.current?.(val);
	}, []);
	const debouncedFlush = useMemo(() => {
		let timer;
		return (val) => {
			clearTimeout(timer);
			timer = setTimeout(() => flushToParent(val), 300);
		};
	}, [flushToParent]);
	const valueRef = useRef(localValue);
	useEffect(() => {
		valueRef.current = localValue;
	}, [localValue]);
	const fullscreenValueRef = useRef(fullscreenValue);
	useEffect(() => {
		fullscreenValueRef.current = fullscreenValue;
	}, [fullscreenValue]);
	const setRefs = useCallback((element) => {
		textareaRef.current = element;
		if (typeof ref === "function") ref(element);
		else if (ref) ref.current = element;
	}, [ref]);
	useEffect(() => {
		const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
		setSpeechSupported(!!SpeechRecognitionAPI);
	}, []);
	const saveCursorPosition = useCallback(() => {
		if (isFullscreenRef.current && fullscreenTextareaRef.current) cursorPositionRef.current = fullscreenTextareaRef.current.selectionStart;
		else if (textareaRef.current) cursorPositionRef.current = textareaRef.current.selectionStart;
	}, []);
	const insertTextAtCursor = useCallback((text) => {
		const currentValue = isFullscreenRef.current ? fullscreenValueRef.current || "" : valueRef.current || "";
		const cursorPos = cursorPositionRef.current;
		if (cursorPos !== null && cursorPos >= 0) {
			const newValue = currentValue.slice(0, cursorPos) + text + currentValue.slice(cursorPos);
			if (isFullscreenRef.current) {
				setFullscreenValue(newValue);
				fullscreenValueRef.current = newValue;
			} else {
				setLocalValue(newValue);
				valueRef.current = newValue;
				isTypingRef.current = true;
				debouncedFlush(newValue);
			}
			cursorPositionRef.current = cursorPos + text.length;
		} else {
			const newValue = currentValue + text;
			if (isFullscreenRef.current) {
				setFullscreenValue(newValue);
				fullscreenValueRef.current = newValue;
			} else {
				setLocalValue(newValue);
				valueRef.current = newValue;
				isTypingRef.current = true;
				debouncedFlush(newValue);
			}
			cursorPositionRef.current = currentValue.length + text.length;
		}
	}, [debouncedFlush]);
	const startListening = useCallback(() => {
		const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
		if (!SpeechRecognitionAPI) return;
		saveCursorPosition();
		const recognition = new SpeechRecognitionAPI();
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.lang = voiceLang;
		recognition.onstart = () => {
			setIsListening(true);
			setInterimText("");
		};
		recognition.onresult = (event) => {
			let currentInterim = "";
			let newFinal = "";
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i];
				if (result.isFinal) newFinal += result[0].transcript;
				else currentInterim += result[0].transcript;
			}
			if (newFinal) insertTextAtCursor(newFinal);
			setInterimText(currentInterim);
		};
		recognition.onerror = () => {
			setIsListening(false);
			setInterimText("");
		};
		recognition.onend = () => {
			setIsListening(false);
			setInterimText("");
		};
		recognitionRef.current = recognition;
		recognition.start();
	}, [
		voiceLang,
		saveCursorPosition,
		insertTextAtCursor
	]);
	const stopListening = useCallback(() => {
		if (recognitionRef.current) {
			recognitionRef.current.stop();
			recognitionRef.current = null;
		}
		setIsListening(false);
		setInterimText("");
	}, []);
	const toggleListening = useCallback(() => {
		if (isListening) stopListening();
		else startListening();
	}, [
		isListening,
		startListening,
		stopListening
	]);
	useEffect(() => {
		return () => {
			if (recognitionRef.current) recognitionRef.current.abort();
		};
	}, []);
	const handleChange = (e) => {
		isTypingRef.current = true;
		setLocalValue(e.target.value);
		debouncedFlush(e.target.value);
	};
	const handleSelect = () => {
		saveCursorPosition();
	};
	const openFullscreen = useCallback(() => {
		setFullscreenValue(localValue || "");
		setIsFullscreenOpen(true);
		isFullscreenRef.current = true;
	}, [localValue]);
	const closeFullscreen = useCallback(() => {
		setIsFullscreenOpen(false);
		isFullscreenRef.current = false;
		if (isListening) stopListening();
	}, [isListening, stopListening]);
	const saveAndCloseFullscreen = useCallback(() => {
		onChange?.(fullscreenValue);
		setLocalValue(fullscreenValue);
		setIsFullscreenOpen(false);
		isFullscreenRef.current = false;
		if (isListening) stopListening();
	}, [
		fullscreenValue,
		onChange,
		isListening,
		stopListening
	]);
	const handleFullscreenChange = useCallback((e) => {
		setFullscreenValue(e.target.value);
	}, []);
	const showVoiceButton = enableVoiceInput && speechSupported;
	const showFullscreenButton = enableFullscreen;
	const hasButtons = showVoiceButton || showFullscreenButton;
	const VoiceButton = ({ className: btnClassName }) => /* @__PURE__ */ jsx(Button, {
		type: "button",
		variant: isListening ? "destructive" : "ghost",
		size: "icon",
		className: cn("h-8 w-8", isListening && "animate-pulse", btnClassName),
		onClick: toggleListening,
		title: isListening ? t("stopVoice") : t("startVoice"),
		children: isListening ? /* @__PURE__ */ jsx(MicOff, { className: "h-4 w-4" }) : /* @__PURE__ */ jsx(Mic, { className: "h-4 w-4" })
	});
	const handleInlineBlur = useCallback((_e) => {
		if (localValue !== value) {
			isTypingRef.current = false;
			onChange?.(localValue);
		}
	}, [
		localValue,
		value,
		onChange
	]);
	const textareaElement = /* @__PURE__ */ jsxs("div", {
		className: "flex flex-col gap-1.5 w-full",
		children: [/* @__PURE__ */ jsxs("div", {
			className: "relative",
			children: [/* @__PURE__ */ jsx(Textarea, {
				...props,
				ref: setRefs,
				rows,
				value: localValue,
				onChange: handleChange,
				onBlur: (e) => {
					handleInlineBlur(e);
					if (typeof props.onBlur === "function") props.onBlur(e);
				},
				onSelect: handleSelect,
				onClick: handleSelect,
				onKeyUp: handleSelect,
				className: cn("resize-none max-h-40 overflow-y-auto", hasButtons && "pr-12", className)
			}), hasButtons && /* @__PURE__ */ jsxs("div", {
				className: "absolute right-2 bottom-2 flex gap-1",
				children: [showFullscreenButton && /* @__PURE__ */ jsx(Button, {
					type: "button",
					variant: "ghost",
					size: "icon",
					className: "h-8 w-8",
					onClick: openFullscreen,
					title: t("fullscreenEdit"),
					children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
				}), showVoiceButton && /* @__PURE__ */ jsx(VoiceButton, {})]
			})]
		}), isListening && interimText && /* @__PURE__ */ jsx("div", {
			className: "text-xs text-muted-foreground italic px-1 animate-pulse truncate min-h-[16px] w-full",
			children: interimText
		})]
	});
	const fullscreenDialog = /* @__PURE__ */ jsx(Dialog, {
		open: isFullscreenOpen,
		onOpenChange: (open) => {
			if (!open) closeFullscreen();
		},
		children: /* @__PURE__ */ jsxs(DialogContent, {
			className: "w-[90vw] h-[90vh] max-w-none flex flex-col",
			"aria-describedby": void 0,
			children: [
				/* @__PURE__ */ jsx(DialogHeader, {
					className: "flex-shrink-0",
					children: /* @__PURE__ */ jsxs(DialogTitle, {
						className: "flex items-center gap-2",
						children: [Icon && /* @__PURE__ */ jsx(Icon, { className: "h-5 w-5" }), label || title]
					})
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "flex-1 min-h-0 flex flex-col gap-2",
					children: [/* @__PURE__ */ jsxs("div", {
						className: "flex-1 relative min-h-0 overflow-hidden",
						children: [/* @__PURE__ */ jsx(Textarea, {
							ref: fullscreenTextareaRef,
							value: fullscreenValue,
							onChange: handleFullscreenChange,
							onSelect: handleSelect,
							onClick: handleSelect,
							onKeyUp: handleSelect,
							placeholder: props.placeholder,
							className: cn("resize-none h-full w-full overflow-y-auto", showVoiceButton && "pr-12")
						}), showVoiceButton && /* @__PURE__ */ jsx("div", {
							className: "absolute right-3 bottom-3",
							children: /* @__PURE__ */ jsx(VoiceButton, {})
						})]
					}), isListening && interimText && /* @__PURE__ */ jsx("div", {
						className: "text-sm text-muted-foreground italic truncate animate-pulse min-h-[20px] px-2 flex-shrink-0",
						children: interimText
					})]
				}),
				/* @__PURE__ */ jsxs(DialogFooter, {
					className: "flex-shrink-0",
					children: [/* @__PURE__ */ jsx(Button, {
						variant: "outline",
						onClick: closeFullscreen,
						children: t("cancel")
					}), /* @__PURE__ */ jsx(Button, {
						onClick: saveAndCloseFullscreen,
						children: t("confirm")
					})]
				})
			]
		})
	});
	const content = label ? /* @__PURE__ */ jsxs("div", {
		className: "space-y-2",
		children: [/* @__PURE__ */ jsxs(Label, {
			className: cn("flex items-center gap-2 text-sm font-medium text-muted-foreground", labelClassName),
			children: [Icon && /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4" }), label]
		}), textareaElement]
	}) : textareaElement;
	if (!showCard) return /* @__PURE__ */ jsxs(Fragment, { children: [content, fullscreenDialog] });
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(Card, {
		className: cn("p-3 nodrag", cardClassName),
		onPointerDown: (e) => e.stopPropagation(),
		children: content
	}), fullscreenDialog] });
});
NodeTextarea.displayName = "NodeTextarea";
//#endregion
//#region src/canvas/nodes/add/add-text-node.tsx
const ManualInputTab = ({ data, locked }) => {
	const expands = useFlow((s) => s.expands);
	const edges = useFlow((s) => s.edges);
	const updateNode = useFlow((s) => s.updates);
	const id = useNodeId();
	const t = useTranslations("Workspace.nodes");
	const [state, updateState] = useNodeState({ manualValue: "" }, data);
	const { manualValue } = state;
	const downstreamNodeId = useMemo(() => edges.find((e) => e.source === id), [edges, id])?.target;
	const downstreamNode = useNodesData(downstreamNodeId ? [downstreamNodeId] : [])[0];
	const addTextNode = () => {
		if (!manualValue.trim() || !id) return;
		if (!downstreamNodeId) expands(id, [{
			type: "textNode",
			data: { texts: [manualValue] }
		}]);
	};
	const handleBlur = () => {
		if (!manualValue.trim() || !downstreamNodeId) return;
		updateNode(downstreamNodeId, {
			...downstreamNode?.data,
			texts: [manualValue]
		});
	};
	return /* @__PURE__ */ jsxs("div", {
		className: "w-full space-y-2",
		children: [/* @__PURE__ */ jsx(NodeTextarea, {
			rows: 6,
			placeholder: t("common.enterText"),
			value: manualValue,
			onChange: (value) => {
				updateState({ manualValue: value });
			},
			className: "min-h-[120px] max-h-[240px] overflow-y-auto scrollbar-thin",
			disabled: locked,
			onBlur: handleBlur
		}), /* @__PURE__ */ jsx(Button, {
			onClick: addTextNode,
			disabled: !manualValue.trim() || locked || !!downstreamNodeId,
			className: "w-full h-10",
			children: t("common.addText")
		})]
	});
};
const LibraryTab$1 = ({ locked }) => {
	return /* @__PURE__ */ jsxs("div", {
		className: "w-full relative",
		children: [/* @__PURE__ */ jsx(LibInput, { resourceType: "TEXT" }), locked && /* @__PURE__ */ jsx("div", { className: "absolute inset-0 bg-white/50 dark:bg-gray-800/50 cursor-not-allowed" })]
	});
};
const AddTextNode = ({ selected, data }) => {
	const id = useNodeId();
	const nodeData = data;
	const { activeTab = "manual", locked = false } = nodeData;
	const updates = useFlow((s) => s.updates);
	const workspaceMode = useTaskStore((state) => state.workspaceMode);
	const t = useTranslations("Workspace.nodes");
	const tBase = useTranslations("Workspace.nodes.base");
	const handleToggleLock = useCallback(() => {
		if (id) updates(id, {
			...data,
			locked: !locked
		});
	}, [
		id,
		data,
		locked,
		updates
	]);
	const handleTabChange = (value) => {
		if (locked) return;
		if (id) updates(id, {
			...data,
			activeTab: value
		});
	};
	const lockAction = workspaceMode === "create" ? /* @__PURE__ */ jsx(NodeHeaderAction, {
		onClick: handleToggleLock,
		variant: "ghost",
		label: locked ? tBase("unlock") : tBase("lock"),
		className: locked ? "text-amber-500" : "",
		children: locked ? /* @__PURE__ */ jsx(Lock, { className: "h-4 w-4 fill-current" }) : /* @__PURE__ */ jsx(Unlock, { className: "h-4 w-4" })
	}) : null;
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.addText"),
		icon: /* @__PURE__ */ jsx(Type, { className: "h-5 w-5" }),
		headerActions: lockAction,
		isInputNode: true,
		showPluginSelect: false,
		children: [/* @__PURE__ */ jsx(Handle, {
			type: "source",
			position: Position.Right,
			id: "out:textNode"
		}), /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-2",
			children: /* @__PURE__ */ jsxs(Tabs, {
				value: activeTab,
				className: "w-full",
				onValueChange: handleTabChange,
				children: [/* @__PURE__ */ jsxs(TabsList, {
					className: "w-full grid grid-cols-2 gap-2 bg-transparent h-auto p-0",
					children: [/* @__PURE__ */ jsxs(TabsTrigger, {
						value: "manual",
						className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
						children: [/* @__PURE__ */ jsx(Edit3, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
							className: "text-xs font-medium",
							children: t("common.manualInput")
						})]
					}, "manual"), /* @__PURE__ */ jsxs(TabsTrigger, {
						value: "library",
						className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
						children: [/* @__PURE__ */ jsx(Library, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
							className: "text-xs font-medium",
							children: t("common.library")
						})]
					}, "library")]
				}), /* @__PURE__ */ jsxs("div", {
					className: "mt-2",
					children: [/* @__PURE__ */ jsx(TabsContent, {
						value: "manual",
						className: "mt-0",
						children: /* @__PURE__ */ jsx(ManualInputTab, {
							data: nodeData,
							locked
						})
					}, "manual"), /* @__PURE__ */ jsx(TabsContent, {
						value: "library",
						className: "mt-0",
						children: /* @__PURE__ */ jsx(LibraryTab$1, { locked })
					}, "library")]
				})]
			})
		})]
	});
};
AddTextNode.displayName = "AddTextNode";
var add_text_node_default = memo(AddTextNode);
//#endregion
//#region src/canvas/ui/video-recorder.tsx
const VideoRecorder = ({ onRecord, className }) => {
	const t = useTranslations("Recorder");
	const [status, setStatus] = useState("idle");
	const [mediaBlobUrl, setMediaBlobUrl] = useState("");
	const [recordingTime, setRecordingTime] = useState(0);
	const [previewStream, setPreviewStream] = useState(null);
	const mediaRecorderRef = useRef(null);
	const chunksRef = useRef([]);
	const timerRef = useRef(null);
	const cleanup = useCallback(() => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
		if (previewStream) previewStream.getTracks().forEach((track) => track.stop());
	}, [previewStream]);
	const startRecording = useCallback(async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: {
					width: { ideal: 1280 },
					height: { ideal: 720 }
				},
				audio: true
			});
			setPreviewStream(stream);
			const mimeType = MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "video/mp4";
			const mediaRecorder = new MediaRecorder(stream, {
				mimeType,
				videoBitsPerSecond: 25e5
			});
			chunksRef.current = [];
			mediaRecorder.ondataavailable = (event) => {
				if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
			};
			mediaRecorder.onstop = () => {
				const blob = new Blob(chunksRef.current, { type: mimeType });
				const url = URL.createObjectURL(blob);
				setMediaBlobUrl(url);
				onRecord(url);
				stream.getTracks().forEach((track) => track.stop());
				setPreviewStream(null);
			};
			mediaRecorderRef.current = mediaRecorder;
			mediaRecorder.start(1e3);
			setStatus("recording");
			setRecordingTime(0);
			timerRef.current = setInterval(() => {
				setRecordingTime((prev) => prev + 1);
			}, 1e3);
		} catch (error) {
			logger.error("Failed to start recording:", error);
			showErrorToast({ message: t("videoPermissionError") });
		}
	}, [onRecord, t]);
	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current && status === "recording") {
			mediaRecorderRef.current.stop();
			setStatus("stopped");
			if (timerRef.current) {
				clearInterval(timerRef.current);
				timerRef.current = null;
			}
		}
	}, [status]);
	useEffect(() => {
		return () => {
			cleanup();
			if (mediaBlobUrl) URL.revokeObjectURL(mediaBlobUrl);
		};
	}, [cleanup, mediaBlobUrl]);
	const formatTime = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};
	return /* @__PURE__ */ jsx("div", {
		className: cn("relative w-full", className),
		children: /* @__PURE__ */ jsxs("div", {
			className: "relative w-full aspect-video bg-black rounded-lg overflow-hidden",
			children: [
				status === "idle" && /* @__PURE__ */ jsx("div", {
					className: "absolute inset-0 flex items-center justify-center",
					children: /* @__PURE__ */ jsxs(Button, {
						size: "lg",
						onClick: startRecording,
						className: "flex items-center gap-2",
						children: [/* @__PURE__ */ jsx(Camera, { className: "h-5 w-5" }), t("videoStart")]
					})
				}),
				status === "recording" && /* @__PURE__ */ jsxs(Fragment, { children: [
					/* @__PURE__ */ jsx(VideoPreview, { stream: previewStream }),
					/* @__PURE__ */ jsxs("div", {
						className: "absolute top-4 left-4 flex items-center gap-2 bg-red-500 text-white px-3 py-1.5 rounded-full text-sm font-medium",
						children: [/* @__PURE__ */ jsx(Circle, { className: "h-3 w-3 fill-current animate-pulse" }), formatTime(recordingTime)]
					}),
					/* @__PURE__ */ jsx("div", {
						className: "absolute bottom-4 left-1/2 -translate-x-1/2",
						children: /* @__PURE__ */ jsxs(Button, {
							size: "lg",
							variant: "destructive",
							onClick: stopRecording,
							className: "flex items-center gap-2 shadow-lg",
							children: [/* @__PURE__ */ jsx(Square, { className: "h-4 w-4" }), t("videoStop")]
						})
					})
				] }),
				status === "stopped" && mediaBlobUrl && /* @__PURE__ */ jsx("div", {
					className: "w-full h-full",
					children: /* @__PURE__ */ jsx("video", {
						src: mediaBlobUrl,
						controls: true,
						autoPlay: true,
						loop: true,
						className: "w-full h-full object-contain"
					})
				})
			]
		})
	});
};
const VideoPreview = ({ stream }) => {
	const videoRef = useRef(null);
	useEffect(() => {
		if (videoRef.current && stream) videoRef.current.srcObject = stream;
	}, [stream]);
	if (!stream) return null;
	return /* @__PURE__ */ jsx("video", {
		ref: videoRef,
		autoPlay: true,
		muted: true,
		playsInline: true,
		className: "w-full h-full object-cover"
	});
};
//#endregion
//#region src/canvas/nodes/add/add-video-node.tsx
const UploadTab = () => {
	const t = useTranslations("Workspace.nodes.add");
	const expands = useFlow((s) => s.expands);
	const id = useNodeId();
	const [isDragActive, setIsDragActive] = useState(false);
	const { upload, isUploading, progress } = useMultipleUpload({
		onSuccess: (responses) => {
			if (id && responses.length > 0) {
				const fileKeys = responses.map((r) => r.key);
				expands(id, [{
					type: "videoNode",
					data: { fileKeys }
				}]);
			}
		},
		onError: (error) => {
			logger.error("Upload failed:", error);
		}
	});
	const doUpload = async (files) => {
		if (!id || files.length === 0) return;
		await upload(files);
	};
	const handleDrop = async (e) => {
		e.preventDefault();
		setIsDragActive(false);
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) await doUpload(Array.from(e.dataTransfer.files));
	};
	const handleFileChange = async (e) => {
		if (e.target.files && e.target.files.length > 0) await doUpload(Array.from(e.target.files));
	};
	return /* @__PURE__ */ jsx("div", {
		className: "w-full",
		children: /* @__PURE__ */ jsx("div", {
			onDrop: handleDrop,
			onDragOver: (e) => {
				e.preventDefault();
				setIsDragActive(true);
			},
			onDragLeave: () => setIsDragActive(false),
			onPointerDown: (e) => e.stopPropagation(),
			className: `w-full h-48 border-2 border-dashed rounded-lg flex flex-col items-center justify-center transition-colors cursor-pointer nodrag ${isDragActive ? "border-primary bg-primary/10" : "border-muted-foreground/25 hover:border-primary/50 hover:bg-accent/50"}`,
			children: isUploading ? /* @__PURE__ */ jsxs("div", {
				className: "w-full px-8 space-y-3",
				children: [/* @__PURE__ */ jsx("div", {
					className: "w-full bg-muted rounded-full h-2 overflow-hidden",
					children: /* @__PURE__ */ jsx("div", {
						className: "bg-primary h-full transition-all duration-300",
						style: { width: `${progress}%` }
					})
				}), /* @__PURE__ */ jsxs("p", {
					className: "text-sm text-center text-muted-foreground font-medium",
					children: [
						t("uploading"),
						" ",
						Math.round(progress),
						"%"
					]
				})]
			}) : /* @__PURE__ */ jsxs(Fragment, { children: [
				/* @__PURE__ */ jsx(Upload, { className: "h-10 w-10 mb-3 text-muted-foreground" }),
				/* @__PURE__ */ jsx("p", {
					className: "text-sm font-medium text-foreground mb-1",
					children: isDragActive ? t("dropToUpload") : t("dragOrClick")
				}),
				/* @__PURE__ */ jsx("p", {
					className: "text-xs text-muted-foreground",
					children: t("supportedVideoFormats")
				}),
				/* @__PURE__ */ jsx("label", {
					htmlFor: `video-upload-${id}`,
					children: /* @__PURE__ */ jsx(Button, {
						variant: "outline",
						size: "sm",
						className: "mt-3",
						asChild: true,
						children: /* @__PURE__ */ jsx("span", { children: t("browseFiles") })
					})
				}),
				/* @__PURE__ */ jsx("input", {
					id: `video-upload-${id}`,
					type: "file",
					multiple: true,
					accept: "video/*",
					onChange: handleFileChange,
					className: "hidden"
				})
			] })
		})
	});
};
const CameraTab = () => {
	const t = useTranslations("Workspace.nodes.add");
	const expands = useFlow((s) => s.expands);
	const id = useNodeId();
	const [blobUrl, setBlobUrl] = useState("");
	const [isRecording, setIsRecording] = useState(false);
	const { upload, isUploading, progress } = useUpload({
		onSuccess: (info) => {
			if (id) {
				expands(id, [{
					type: "videoNode",
					data: { fileKeys: [info.key] }
				}]);
				setIsRecording(false);
				setBlobUrl("");
			}
		},
		onError: (error) => {
			logger.error("Upload failed:", error);
		}
	});
	const onFinish = async () => {
		if (!blobUrl) return;
		try {
			const blob = await (await fetch(blobUrl)).blob();
			const file = new File([blob], `recording-${Date.now()}.mp4`, { type: "video/mp4" });
			upload(file);
		} catch (error) {
			logger.error("Upload failed:", error);
		}
	};
	const handleCancel = () => {
		setIsRecording(false);
		setBlobUrl("");
	};
	return /* @__PURE__ */ jsx("div", {
		className: "w-full",
		children: !isRecording ? /* @__PURE__ */ jsxs(Button, {
			onClick: () => setIsRecording(true),
			variant: "outline",
			className: "w-full h-32 flex flex-col gap-2 border-2 border-dashed hover:border-primary hover:bg-primary/5 transition-colors",
			children: [/* @__PURE__ */ jsx(Video, { className: "h-8 w-8 text-muted-foreground" }), /* @__PURE__ */ jsx("span", {
				className: "text-sm",
				children: t("recordVideo")
			})]
		}) : /* @__PURE__ */ jsxs("div", {
			className: "w-full space-y-3",
			children: [
				/* @__PURE__ */ jsx("div", {
					className: "w-full bg-muted rounded-lg overflow-hidden",
					children: /* @__PURE__ */ jsx(VideoRecorder, { onRecord: (blobUrl) => setBlobUrl(blobUrl ?? "") })
				}),
				isUploading && /* @__PURE__ */ jsxs("div", {
					className: "w-full space-y-2",
					children: [/* @__PURE__ */ jsx("div", {
						className: "w-full bg-muted rounded-full h-2 overflow-hidden",
						children: /* @__PURE__ */ jsx("div", {
							className: "bg-primary h-full transition-all duration-300",
							style: { width: `${progress}%` }
						})
					}), /* @__PURE__ */ jsxs("p", {
						className: "text-xs text-center text-muted-foreground",
						children: [
							t("uploading"),
							" ",
							Math.round(progress),
							"%"
						]
					})]
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "flex gap-2",
					children: [/* @__PURE__ */ jsx(Button, {
						variant: "outline",
						size: "sm",
						className: "flex-1",
						onClick: handleCancel,
						disabled: isUploading,
						children: t("cancel")
					}), /* @__PURE__ */ jsx(Button, {
						size: "sm",
						className: "flex-1",
						onClick: onFinish,
						disabled: !blobUrl || isUploading,
						children: isUploading ? `${t("uploading")}...` : t("generate")
					})]
				})
			]
		})
	});
};
const LibraryTab = () => {
	return /* @__PURE__ */ jsx("div", {
		className: "w-full",
		children: /* @__PURE__ */ jsx(LibInput, { resourceType: "VIDEO" })
	});
};
const AddVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.add");
	const id = useNodeId();
	const updates = useFlow((s) => s.updates);
	data?.activeTab;
	logger.debug("AddVideoNode data:", data);
	const handleTabChange = (value) => {
		if (id) updates(id, {
			...data,
			activeTab: value
		});
	};
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		className: "min-w-[480px]",
		data,
		title: t("addVideo"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		isInputNode: true,
		showPluginSelect: false,
		children: [/* @__PURE__ */ jsx(Handle, {
			type: "source",
			position: Position.Right,
			id: "out:videoNode"
		}), /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-2",
			children: /* @__PURE__ */ jsxs(Tabs, {
				defaultValue: data?.activeTab || "upload",
				className: "w-full",
				onValueChange: handleTabChange,
				children: [/* @__PURE__ */ jsxs(TabsList, {
					className: "grid grid-cols-3 gap-2 bg-transparent h-auto p-0",
					children: [
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "upload",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Upload, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("upload")
							})]
						}, "upload"),
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "camera",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Video, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("recordVideo")
							})]
						}, "camera"),
						/* @__PURE__ */ jsxs(TabsTrigger, {
							value: "library",
							className: "h-9 flex flex-row items-center justify-center gap-2 data-[state=active]:bg-secondary",
							children: [/* @__PURE__ */ jsx(Library, { className: "h-4 w-4" }), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: t("library")
							})]
						}, "library")
					]
				}), /* @__PURE__ */ jsxs("div", {
					className: "mt-2",
					children: [
						/* @__PURE__ */ jsx(TabsContent, {
							value: "upload",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(UploadTab, {})
						}),
						/* @__PURE__ */ jsx(TabsContent, {
							value: "camera",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(CameraTab, {})
						}),
						/* @__PURE__ */ jsx(TabsContent, {
							value: "library",
							className: "mt-0",
							children: /* @__PURE__ */ jsx(LibraryTab, {})
						})
					]
				})]
			})
		})]
	});
};
var add_video_node_default = memo(AddVideoNode);
//#endregion
//#region src/canvas/hooks/use-node-abi-spec.ts
/**
* Resolved ABI spec for the node this hook is called from.
*
* The spec is looked up by the node's React Flow type in the static
* `NODE_TYPE_SOURCE_SPEC` registry — the same table the exporter, connection
* validator and agent tools read headlessly — so a component never carries its
* own `sourceSpec`. A type missing from the table (should not happen for a
* registered ABI node) falls back to the bare ABI topology of `feature`.
*/
/** React Flow type of the current node (undefined outside a node). */
function useNodeType() {
	const nodeId = useNodeId();
	return useStore((state) => state.nodeLookup.get(nodeId ?? "")?.type);
}
function useNodeAbiSpec(feature) {
	const nodeType = useNodeType();
	return useMemo(() => resolvedSpecForNodeType(nodeType) ?? resolveSpec(feature, {}), [nodeType, feature]);
}
//#endregion
//#region src/canvas/hooks/use-abi-form.ts
/**
* ABI-driven form hook.
*
* Replaces hand-written `useNodeState` for executable nodes. Reads which
* fields are "config" vs "handle" from the node type's resolved ABI spec
* (static registry) and syncs the config subset back to React Flow `node.data`.
*
* Two binding flavors:
*  - `bind(field)`  — for controls taking `{ value, onChange: (v) => void }`
*                     (NodeTextarea, AspectRatioPicker, custom selects)
*  - `register(field)` — for native inputs `{ value, onChange: e => void }`
*/
function useAbiForm(feature) {
	const spec = useNodeAbiSpec(feature);
	const nodeId = useNodeId();
	const flowUpdates = useFlow((s) => s.updates);
	const data = useFlow(useCallback((s) => s.nodes.find((n) => n.id === nodeId)?.data, [nodeId]));
	const [state, setState] = useState(() => pickConfig(spec, data));
	const dataRef = useRef(data);
	dataRef.current = data;
	const stateRef = useRef(state);
	stateRef.current = state;
	useEffect(() => {
		setState(pickConfig(spec, data));
	}, [data]);
	const writeBack = useCallback((next, opts) => {
		if (!nodeId) return;
		flowUpdates(nodeId, {
			...dataRef.current ?? {},
			...next
		}, opts);
	}, [nodeId, flowUpdates]);
	const set = useCallback((field, value, opts) => {
		const next = {
			...stateRef.current,
			[field]: value
		};
		stateRef.current = next;
		setState(next);
		writeBack(next, opts);
	}, [writeBack]);
	return {
		feature,
		spec,
		state,
		set,
		patch: useCallback((partial, opts) => {
			const next = {
				...stateRef.current,
				...partial
			};
			stateRef.current = next;
			setState(next);
			writeBack(next, opts);
		}, [writeBack]),
		bind: useCallback((field) => ({
			value: state[field],
			onChange: (value) => set(field, value)
		}), [state, set]),
		register: useCallback((field) => ({
			value: state[field] ?? "",
			onChange: (e) => {
				set(field, e.target.value);
			}
		}), [state, set])
	};
}
function pickConfig(spec, data) {
	if (!data) return {};
	const out = {};
	for (const [field, resolved] of Object.entries(spec.fields)) {
		const isManualHandle = resolved.kind === "handle" && resolved.manual === true;
		if ((resolved.kind === "config" || isManualHandle) && field in data) out[field] = data[field];
	}
	return out;
}
//#endregion
//#region src/canvas/lib/task/payload.ts
function extractItemValue(item, itemValuePath) {
	if (item == null) return void 0;
	if (itemValuePath && typeof item === "object") {
		const v = item[itemValuePath];
		if (v == null) return void 0;
		const s = String(v);
		return s && s !== "undefined" ? s : void 0;
	}
	if (typeof item === "string") return item || void 0;
	const s = String(item);
	return s && s !== "undefined" ? s : void 0;
}
function computeOutputView(routes, payload) {
	const view = {};
	if (!payload) return view;
	for (const route of routes) {
		const raw = payload[route.sourceField];
		if (raw == null) continue;
		const values = [];
		if (Array.isArray(raw)) {
			if (route.isArrayOfArrays) for (const inner of raw) {
				if (!Array.isArray(inner)) continue;
				for (const item of inner) {
					const v = extractItemValue(item, route.itemValuePath);
					if (v) values.push(v);
				}
			}
			else for (const item of raw) {
				const v = extractItemValue(item, route.itemValuePath);
				if (v) values.push(v);
			}
		} else {
			const v = extractItemValue(raw, route.itemValuePath);
			if (v) values.push(v);
		}
		if (values.length === 0) continue;
		view[route.sourceField] = {
			sourceField: route.sourceField,
			nodeType: route.nodeType,
			dataField: route.dataField,
			expandEach: route.expandEach,
			values
		};
	}
	return view;
}
function applyResolvedOutputRoutes(nodeId, payload, routes, expands) {
	for (const route of routes) {
		const raw = payload?.[route.sourceField];
		if (raw == null) continue;
		if (Array.isArray(raw)) {
			if (route.isArrayOfArrays) {
				for (const innerArr of raw) {
					if (!Array.isArray(innerArr)) continue;
					const keys = innerArr.map((item) => route.itemValuePath && typeof item === "object" && item !== null ? String(item[route.itemValuePath]) : String(item)).filter(Boolean);
					if (keys.length) expands(nodeId, [{
						type: route.nodeType,
						data: { [route.dataField]: keys }
					}]);
				}
				continue;
			}
			if (route.expandEach) {
				const items = [];
				for (const item of raw) {
					const value = route.itemValuePath && typeof item === "object" && item !== null ? String(item[route.itemValuePath]) : String(item);
					if (value) items.push({
						type: route.nodeType,
						data: { [route.dataField]: [value] }
					});
				}
				if (items.length) expands(nodeId, items);
			} else {
				const values = raw.map((item) => route.itemValuePath && typeof item === "object" && item !== null ? String(item[route.itemValuePath]) : String(item)).filter(Boolean);
				if (values.length) expands(nodeId, [{
					type: route.nodeType,
					data: { [route.dataField]: values }
				}]);
			}
		} else if (typeof raw === "object" && raw !== null && route.itemValuePath) {
			const value = String(raw[route.itemValuePath]);
			if (value && value !== "undefined") expands(nodeId, [{
				type: route.nodeType,
				data: { [route.dataField]: [value] }
			}]);
		} else if (typeof raw === "string" && raw) expands(nodeId, [{
			type: route.nodeType,
			data: { [route.dataField]: [raw] }
		}]);
	}
}
/** SSE data is sometimes a JSON string; Modal may wrap content in markdown or nested result. */
function normalizeTaskPayloadData(data) {
	if (data == null) return void 0;
	if (typeof data === "object" && !Array.isArray(data)) return data;
	if (typeof data === "string") try {
		const p = JSON.parse(data);
		if (typeof p === "object" && p !== null && !Array.isArray(p)) return p;
	} catch {
		return;
	}
}
//#endregion
//#region src/canvas/hooks/use-node-plugin-resolver.ts
/**
* Resolves the active plugin for a node given its ABI feature/nodeSlot.
*
* Responsibilities:
* - Ensures the plugins registry is fetched (so `nodePluginMap` populates on canvas).
* - Reads pluginOptions from the scanned registry (`nodePluginMap[feature]`).
* - Persists a default `pluginId` into node data before paint so execution hooks
*   always see a value (avoids run-before-effect race).
* - Provides `resolveActivePluginId()` so the execution hook can hand the id
*   to the create-task API as a top-level field.
*/
function useNodePluginResolver(feature) {
	usePluginsRegistry();
	const nodeId = useNodeId();
	const { updateNodeData, getNode } = useReactFlow();
	const pluginOptions = useNodePluginIds(feature ?? "");
	const defaultPluginIdFromRegistry = (pluginOptions[0] ?? "").trim();
	useEffect(() => {
		if (!nodeId || !feature) return;
		if (pluginOptions.length === 0) return;
		if (!defaultPluginIdFromRegistry) return;
		const d = getNode(nodeId)?.data;
		const current = (typeof d?.pluginId === "string" ? d.pluginId : "").trim();
		if (current && pluginOptions.includes(current)) return;
		updateNodeData(nodeId, { pluginId: defaultPluginIdFromRegistry });
	}, [
		nodeId,
		feature,
		defaultPluginIdFromRegistry,
		getNode,
		updateNodeData,
		pluginOptions
	]);
	const resolveActivePluginId = useCallback(() => {
		if (!nodeId) return defaultPluginIdFromRegistry;
		const nodeData = getNode(nodeId)?.data ?? void 0;
		const fromData = (typeof nodeData?.pluginId === "string" ? nodeData.pluginId : "").trim();
		return (!fromData ? "" : pluginOptions.length === 0 ? fromData : pluginOptions.includes(fromData) ? fromData : "") || defaultPluginIdFromRegistry;
	}, [
		nodeId,
		getNode,
		defaultPluginIdFromRegistry,
		pluginOptions
	]);
	/**
	* Model for router-style plugins: the node's `pluginModel` when it is one
	* of the active plugin's models (declared shortlist or live catalog), else
	* that plugin's default (first declared model). `undefined` when the
	* plugin declares none — the create-task API then omits the field entirely.
	*/
	const resolveActiveModel = useCallback(() => {
		if (!feature) return void 0;
		const pluginId = resolveActivePluginId();
		if (!pluginId) return void 0;
		const models = getNodePluginModels(feature, pluginId);
		if (models.length === 0) return void 0;
		const n = nodeId ? getNode(nodeId) : void 0;
		const fromData = String((n?.data)?.pluginModel ?? "").trim();
		return models.includes(fromData) ? fromData : models[0];
	}, [
		feature,
		nodeId,
		getNode,
		resolveActivePluginId
	]);
	return {
		pluginOptions,
		defaultPluginIdFromRegistry,
		resolveActivePluginId,
		resolveActiveModel,
		resolveActiveParams: useCallback(() => {
			if (!feature) return void 0;
			const pluginId = resolveActivePluginId();
			if (!pluginId) return void 0;
			const entries = getNodePluginParams(feature, pluginId, resolveActiveModel());
			if (entries.length === 0) return void 0;
			const raw = ((nodeId ? getNode(nodeId) : void 0)?.data)?.pluginParams;
			const params = normalizePluginParams(entries, raw && typeof raw === "object" && !Array.isArray(raw) ? raw : void 0);
			return Object.keys(params).length > 0 ? params : void 0;
		}, [
			feature,
			nodeId,
			getNode,
			resolveActivePluginId,
			resolveActiveModel
		])
	};
}
//#endregion
//#region src/canvas/hooks/use-abi-execution.ts
/**
* ABI-driven node execution hook.
*
* Responsibilities:
*  - Resolve the node's ABI spec from the static node-type registry
*  - Sync `feature` into `node.data` (so persisted workflows know the ABI slot)
*  - Build prompts via `resolve.ts` (handle-driven upstream collection)
*  - ajv pre-flight validation before submitting tasks
*  - Wire SSE task updates to ABI output routing (resolveAbiOutputMappings)
*/
function useAbiExecution(opts) {
	const { feature, form, disabled, onTaskUpdate, transformPrompts } = opts;
	const nodeId = useNodeId();
	const { updateNodeData } = useReactFlow();
	const storeApi = useStoreApi();
	const flowUpdates = useFlow((s) => s.updates);
	const nodeType = useNodeType();
	const [elapsedSeconds, setElapsedSeconds] = useState(0);
	const [progressLabel, setProgressLabel] = useState(null);
	const [thinkingText, setThinkingText] = useState(null);
	const [missingPluginOpen, setMissingPluginOpen] = useState(false);
	const spec = useNodeAbiSpec(feature);
	const specRef = useRef(spec);
	specRef.current = spec;
	const hasSyncedRef = useRef(false);
	useEffect(() => {
		if (!nodeId) return;
		let cancelled = false;
		queueMicrotask(() => {
			if (cancelled) return;
			const { nodes } = storeApi.getState();
			const data = nodes.find((n) => n.id === nodeId)?.data ?? {};
			if (!(!hasSyncedRef.current && data.feature !== feature)) return;
			hasSyncedRef.current = true;
			flowUpdates(nodeId, {
				...data,
				feature
			}, { history: false });
		});
		return () => {
			cancelled = true;
		};
	}, [
		nodeId,
		feature,
		storeApi,
		flowUpdates
	]);
	const incomingEdgeSig = useFlow((s) => nodeId ? s.edges.filter((e) => e.target === nodeId).map((e) => `${e.id}|${e.sourceHandle ?? ""}|${e.targetHandle ?? ""}`).join(";") : "");
	useEffect(() => {
		if (!nodeId) return;
		const flowState = useFlow.getState();
		const targetType = flowState.nodes.find((n) => n.id === nodeId)?.type;
		const spec = specRef.current;
		const isLiveTargetHandle = (handle) => {
			const field = parseTargetHandleId(handle);
			return !!field && spec.fields[field]?.kind === "handle";
		};
		const needsHealing = (e) => e.target === nodeId && (!e.sourceHandle || !e.targetHandle || !isLiveTargetHandle(e.targetHandle));
		if (!flowState.edges.some(needsHealing)) return;
		const usedTargetHandles = new Set(flowState.edges.filter((e) => e.target === nodeId && isLiveTargetHandle(e.targetHandle)).map((e) => e.targetHandle));
		let mutated = false;
		const patched = flowState.edges.map((edge) => {
			if (edge.target !== nodeId) return edge;
			const liveTarget = isLiveTargetHandle(edge.targetHandle);
			if (edge.sourceHandle && liveTarget) return edge;
			const sourceType = flowState.nodes.find((n) => n.id === edge.source)?.type;
			const { sourceHandle, targetHandle } = resolveEdgeHandles({
				sourceType,
				targetType,
				usedTargetHandles,
				targetSpec: spec
			});
			if (!sourceHandle && !targetHandle) return edge;
			const nextTargetHandle = liveTarget ? edge.targetHandle : targetHandle;
			if (nextTargetHandle) usedTargetHandles.add(nextTargetHandle);
			mutated = true;
			return {
				...edge,
				...edge.sourceHandle ? {} : sourceHandle ? { sourceHandle } : {},
				...liveTarget ? {} : targetHandle ? { targetHandle } : {}
			};
		});
		if (mutated) flowState.setEdges(patched);
	}, [nodeId, incomingEdgeSig]);
	const isExecuteMode = useTaskStore((s) => s.workspaceMode) === "execute";
	const nodeExecutionStatus = useTaskStore(useCallback((s) => s.nodeExecutionStatusMap.get(nodeId ?? ""), [nodeId]));
	const expands = useFlow((s) => s.expands);
	const { isLoading: taskLoading, createBatchTasks, cancelTasks } = useBatchTaskManager();
	const loading = taskLoading || nodeExecutionStatus === "running";
	const { pluginOptions, resolveActivePluginId, resolveActiveModel, resolveActiveParams } = useNodePluginResolver(feature);
	const { isLoaded: pluginsRegistryLoaded } = usePluginsRegistry();
	const handleTaskUpdate = useCallback(async (task) => {
		if (!nodeId) return;
		if (onTaskUpdate) {
			if (await onTaskUpdate(task)) return;
		}
		if (task?.status === "PROCESSING" || task?.status === "PENDING") {
			const d = task.data;
			if (d?.thinking === true) {
				if (typeof d.message === "string") setThinkingText(d.message);
			} else {
				const label = d?.label || d?.feature || d?.message;
				if (label) setProgressLabel(String(label));
			}
		}
		if (task?.status === "COMPLETED") {
			const payload = normalizeTaskPayloadData(task?.data) ?? task?.data;
			const abiNode = getAbiNodeBySlot(feature);
			const routes = abiNode ? resolveAbiOutputMappings(abiNode) : [];
			if (routes.length > 0) {
				applyResolvedOutputRoutes(nodeId, payload, routes, expands);
				useFlow.getState().autoLayout([nodeId], { history: false });
			}
			return;
		}
		if (task?.status === "FAILED") logger.error(`[useAbiExecution] Task failed for node ${nodeId}:`, task);
	}, [
		nodeId,
		feature,
		expands,
		onTaskUpdate
	]);
	useNodeTaskUpdate(nodeId ?? "", handleTaskUpdate);
	useEffect(() => {
		if (!loading) {
			setElapsedSeconds(0);
			setProgressLabel(null);
			setThinkingText(null);
			return;
		}
		const interval = setInterval(() => {
			setElapsedSeconds((p) => p + 1);
		}, 1e3);
		return () => clearInterval(interval);
	}, [loading]);
	return {
		run: useCallback(async () => {
			if (!nodeId) return;
			const spec = specRef.current;
			const { nodes, edges } = storeApi.getState();
			const handleValues = collectHandleValues(nodeId, spec, nodes, edges);
			const configValues = form?.state ?? {};
			const built = buildPrompts({
				spec,
				configValues,
				handleValues
			});
			const prompts = transformPrompts ? transformPrompts(built) : built;
			for (const prompt of prompts) {
				const missing = promptMissingRequired(spec, prompt);
				if (missing) {
					logger.warn(`[useAbiExecution] Refusing to run ${feature}: required input "${missing}" is empty (node ${nodeId})`, {
						prompt,
						handleValues,
						configValues
					});
					return;
				}
			}
			if (prompts.length === 0) {
				const incomingEdges = edges.filter((e) => e.target === nodeId);
				const upstreamSnapshot = incomingEdges.map((e) => {
					const src = nodes.find((n) => n.id === e.source);
					return {
						edgeId: e.id,
						source: e.source,
						sourceType: src?.type,
						sourceData: src?.data,
						sourceHandle: e.sourceHandle,
						targetHandle: e.targetHandle
					};
				});
				logger.warn(`[useAbiExecution] No prompts built for ${feature}; node ${nodeId} has no upstream handle values for batch field`, {
					spec,
					handleValues,
					configValues,
					incomingEdgeCount: incomingEdges.length,
					incomingEdges: upstreamSnapshot
				});
				return;
			}
			const pluginId = resolveActivePluginId();
			if (!pluginId) {
				setMissingPluginOpen(true);
				return;
			}
			if (pluginsRegistryLoaded && !pluginOptions.includes(pluginId)) {
				setMissingPluginOpen(true);
				return;
			}
			updateNodeData(nodeId, {
				feature,
				prompt: prompts.length === 1 ? prompts[0] : prompts
			});
			const model = resolveActiveModel();
			const params = resolveActiveParams();
			try {
				const taskConfigs = prompts.map((prompt) => ({
					feature,
					pluginId,
					...model ? { model } : {},
					...params ? { params } : {},
					prompt,
					nodeId
				}));
				await createBatchTasks(taskConfigs);
			} catch (error) {
				logger.error(`[useAbiExecution] Failed to create tasks:`, error);
			}
		}, [
			nodeId,
			feature,
			form,
			storeApi,
			updateNodeData,
			createBatchTasks,
			resolveActivePluginId,
			resolveActiveModel,
			resolveActiveParams,
			pluginOptions,
			pluginsRegistryLoaded,
			transformPrompts
		]),
		cancel: cancelTasks,
		canCancel: taskLoading,
		loading,
		elapsedSeconds,
		progressLabel,
		thinkingText,
		canRun: !!nodeId && !disabled && !loading && nodeType !== void 0,
		feature,
		isExecuteMode,
		missingPluginOpen,
		setMissingPluginOpen,
		pluginOptions
	};
}
//#endregion
//#region src/canvas/nodes/base/abi-handles.tsx
/**
* Render ReactFlow `<Handle>` components for an ABI feature.
*
* Inputs:  one target Handle per `kind:"handle"` field (id = `in:<field>`)
* Outputs: one source Handle per ABI output route (id = `out:<field>`)
*
* Layout: all handles emit from the vertical center of the node's left
* (targets) and right (sources) edges, regardless of how many there are.
* Callers can override layout via `style` or a `layout` prop later if needed.
*/
function distributeHandles(targets, sources) {
	const out = [];
	targets.forEach((id) => out.push({
		id,
		role: "target",
		offset: .5
	}));
	sources.forEach((id) => out.push({
		id,
		role: "source",
		offset: .5
	}));
	return out;
}
function getHandleIds(spec) {
	const { topology } = spec;
	const targets = [];
	for (const field of topology.inputOrder) if (spec.fields[field]?.kind === "handle") targets.push(targetHandleId(field));
	return {
		targets,
		sources: topology.outputs.map((o) => sourceHandleId(o.field))
	};
}
function AbiHandles({ feature, targetPosition = Position.Left, sourcePosition = Position.Right, handleClassName }) {
	const spec = useNodeAbiSpec(feature);
	const handles = useMemo(() => {
		const { targets, sources } = getHandleIds(spec);
		return distributeHandles(targets, sources);
	}, [spec]);
	return /* @__PURE__ */ jsx(Fragment, { children: handles.map((h) => /* @__PURE__ */ jsx(Handle, {
		type: h.role,
		position: h.role === "target" ? targetPosition : sourcePosition,
		id: h.id,
		className: handleClassName,
		style: { top: `${(h.offset * 100).toFixed(2)}%` }
	}, `${h.role}:${h.id}`)) });
}
//#endregion
//#region src/canvas/nodes/base/abi-node-shell.tsx
function AbiNodeShell({ feature, form, title, icon, headerActions, executeLabel, executeIcon, executeDisabled, isInputNode, selected, count, className, children, overlay, autoHandles = true, showPluginSelect = true, data, onTaskUpdate, transformPrompts }) {
	const exec = useAbiExecution({
		feature,
		form,
		disabled: executeDisabled,
		onTaskUpdate,
		transformPrompts
	});
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		count,
		data: data ?? form.state,
		overlay,
		className,
		title,
		icon,
		headerActions,
		loading: exec.loading,
		elapsedSeconds: exec.elapsedSeconds,
		progressLabel: exec.progressLabel,
		thinkingText: exec.thinkingText,
		isExecuteMode: exec.isExecuteMode,
		onExecute: exec.run,
		onCancel: exec.canCancel ? exec.cancel : void 0,
		executeLabel,
		executeIcon,
		executeDisabled: executeDisabled || !exec.canRun,
		isInputNode,
		feature,
		showPluginSelect,
		missingPluginOpen: exec.missingPluginOpen,
		setMissingPluginOpen: exec.setMissingPluginOpen,
		children: [children, autoHandles && /* @__PURE__ */ jsx(AbiHandles, { feature })]
	});
}
//#endregion
//#region src/canvas/nodes/batch/arrange-text.tsx
const ArrangeTextNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.batch");
	const form = useAbiForm("arrange-group");
	const infos = data.infos ?? [];
	const groupCount = form.state.groupCount ?? 3;
	const duplicatable = form.state.duplicatable ?? true;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "arrange-group",
		form,
		selected,
		data,
		title: t("arrangeGroup"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("startArrange"),
		executeDisabled: !infos?.length,
		children: /* @__PURE__ */ jsxs(Card, {
			className: "p-5 space-y-4 nodrag",
			onPointerDown: (e) => e.stopPropagation(),
			children: [
				/* @__PURE__ */ jsx(NodeTextarea, {
					showCard: false,
					rows: 6,
					placeholder: t("describeRequirements"),
					...form.bind("query")
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "flex items-center justify-between",
					children: [/* @__PURE__ */ jsx(Label, {
						htmlFor: "groupCount",
						children: t("groupCount")
					}), /* @__PURE__ */ jsx(Input, {
						id: "groupCount",
						type: "number",
						min: 1,
						value: groupCount,
						onChange: (e) => form.set("groupCount", Number(e.target.value)),
						className: "w-24"
					})]
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "flex items-center justify-between",
					children: [/* @__PURE__ */ jsx(Label, {
						htmlFor: "duplicatable",
						children: t("allowDuplicate")
					}), /* @__PURE__ */ jsx("input", {
						id: "duplicatable",
						type: "checkbox",
						checked: duplicatable,
						onChange: (e) => form.set("duplicatable", e.target.checked),
						className: "w-4 h-4 rounded border-gray-300"
					})]
				})
			]
		})
	});
};
var arrange_text_default = memo(ArrangeTextNode);
//#endregion
//#region src/canvas/nodes/batch/drop-video.tsx
const DropVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.batch");
	const form = useAbiForm("drop-video");
	const fileKeys = data.fileKeys;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "drop-video",
		form,
		selected,
		data,
		title: t("videoFilter"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("startFilter"),
		executeDisabled: !fileKeys?.length,
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx(NodeTextarea, {
				cardClassName: "p-5",
				rows: 6,
				placeholder: t("describeRequirements"),
				...form.bind("query")
			})
		})
	});
};
var drop_video_default = memo(DropVideoNode);
//#endregion
//#region src/canvas/nodes/base/media-thumbnail.tsx
const MediaThumbnail = memo(({ fileKey, label, type, loadingText = "...", onClick }) => {
	const { url } = useFileAsyncLoader(fileKey, { priority: "high" });
	const colors = {
		image: {
			bg: "bg-purple-100",
			text: "text-purple-700"
		},
		video: {
			bg: "bg-orange-100",
			text: "text-orange-700"
		},
		audio: {
			bg: "bg-blue-100",
			text: "text-blue-700"
		}
	}[type];
	return /* @__PURE__ */ jsxs("div", {
		className: "flex flex-col items-center gap-1.5",
		onClick,
		children: [/* @__PURE__ */ jsxs("div", {
			className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100 transition-colors",
			children: [type === "image" ? url ? /* @__PURE__ */ jsx("img", {
				src: url,
				alt: label,
				className: "w-full h-full object-cover"
			}) : /* @__PURE__ */ jsx(Placeholder, { text: loadingText }) : type === "video" ? url ? /* @__PURE__ */ jsx("video", {
				src: url,
				className: "w-full h-full object-cover"
			}) : /* @__PURE__ */ jsx(Placeholder, { text: loadingText }) : /* @__PURE__ */ jsx("div", {
				className: "flex items-center justify-center h-full w-full bg-blue-50",
				children: /* @__PURE__ */ jsx("div", {
					className: "text-xs text-blue-600 font-semibold",
					children: "🎵"
				})
			}), /* @__PURE__ */ jsx("div", { className: "absolute inset-0 bg-black/0 transition-colors" })]
		}), /* @__PURE__ */ jsx("div", {
			className: `px-1.5 py-0.5 text-xs font-medium rounded ${colors.bg} ${colors.text}`,
			children: label
		})]
	});
});
MediaThumbnail.displayName = "MediaThumbnail";
function Placeholder({ text }) {
	return /* @__PURE__ */ jsx("div", {
		className: "flex items-center justify-center h-full w-full",
		children: /* @__PURE__ */ jsx("div", {
			className: "text-xs text-gray-400",
			children: text
		})
	});
}
//#endregion
//#region src/canvas/nodes/compose/audio-video-lip-sync.tsx
const AudioVideoLipSyncNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tActions = useTranslations("Workspace.nodes.actions");
	const form = useAbiForm("audio-video-lip-sync");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("audio-video-lip-sync");
	const { hasVideo, hasAudio, videoFileKey, audioFileKey } = useMemo(() => {
		if (!nodeId) return {
			hasVideo: false,
			hasAudio: false,
			videoFileKey: void 0,
			audioFileKey: void 0
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		const video = typeof values.video === "string" ? values.video : void 0;
		const audio = typeof values.audio === "string" ? values.audio : void 0;
		return {
			hasVideo: Boolean(video),
			hasAudio: Boolean(audio),
			videoFileKey: video,
			audioFileKey: audio
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "audio-video-lip-sync",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.audioVideoLipSync"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: tActions("lipSync"),
		executeDisabled: !hasVideo || !hasAudio,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [
							/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("compose.inputData")
							}),
							/* @__PURE__ */ jsx("p", {
								className: "text-xs text-muted-foreground",
								children: t("compose.connectVideoAudio")
							}),
							/* @__PURE__ */ jsxs("div", {
								className: "flex gap-4",
								children: [videoFileKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
									fileKey: videoFileKey,
									label: t("compose.video"),
									type: "video"
								}) : /* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-purple-50",
											children: /* @__PURE__ */ jsx(Video, { className: "w-6 h-6 text-purple-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded",
										children: t("compose.video")
									})]
								}), audioFileKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
									fileKey: audioFileKey,
									label: t("compose.audio"),
									type: "audio"
								}) : /* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-orange-50",
											children: /* @__PURE__ */ jsx(Music, { className: "w-6 h-6 text-orange-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded",
										children: t("compose.audio")
									})]
								})]
							}),
							(!hasVideo || !hasAudio) && /* @__PURE__ */ jsx("p", {
								className: "text-xs text-red-500",
								children: t("compose.connectVideoAudio")
							})
						]
					})
				}),
				/* @__PURE__ */ jsx(NodeTextarea, {
					label: t("lipSync.scenePrompt"),
					icon: Sparkles,
					placeholder: t("lipSync.scenePromptPlaceholder"),
					...form.bind("text"),
					rows: 3
				}),
				/* @__PURE__ */ jsx("p", {
					className: "text-xs text-muted-foreground",
					children: t("lipSync.audioDrivenHint")
				})
			]
		})
	});
};
AudioVideoLipSyncNode.displayName = "AudioVideoLipSyncNode";
var audio_video_lip_sync_default = memo(AudioVideoLipSyncNode);
//#endregion
//#region src/canvas/nodes/compose/concat-video.tsx
const ConcatVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("concat-videos");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("concat-videos");
	const videoFileKeys = useMemo(() => {
		const collected = nodeId ? collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges).videos : void 0;
		if (Array.isArray(collected) && collected.length > 0) return collected.filter((key) => typeof key === "string");
		return data.fileKeys ?? [];
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges,
		data.fileKeys
	]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "concat-videos",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.concatVideo"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: t("actions.concatVideo"),
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx(Card, {
				className: "p-3",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: [
							t("compose.videoFiles"),
							" (",
							videoFileKeys.length,
							")"
						]
					}), /* @__PURE__ */ jsx("div", {
						className: "flex flex-wrap gap-4",
						children: videoFileKeys.length > 0 ? videoFileKeys.map((fileKey, index) => /* @__PURE__ */ jsx(MediaThumbnail, {
							fileKey,
							label: `${t("compose.video")} ${index + 1}`,
							type: "video",
							loadingText: t("compose.loading")
						}, `${fileKey}-${index}`)) : /* @__PURE__ */ jsx("p", {
							className: "text-xs text-red-500",
							children: t("compose.connectVideo")
						})
					})]
				})
			})
		})
	});
};
var concat_video_default = memo(ConcatVideoNode);
//#endregion
//#region src/canvas/hooks/use-upstream-ids.ts
function useUpstreamNodeIds(snapshotIds) {
	const nodeId = useNodeId();
	const edges = useStore((state) => state.edges);
	return useMemo(() => {
		const ids = [];
		if (nodeId) {
			for (const e of edges) if (e.target === nodeId && !ids.includes(e.source)) ids.push(e.source);
		}
		return ids.length > 0 ? ids : snapshotIds ?? [];
	}, [
		nodeId,
		edges,
		snapshotIds
	]);
}
//#endregion
//#region src/canvas/nodes/base/aspect-ratio-picker.tsx
function AspectRatioPicker({ ratios, value, onChange, showSize = true }) {
	const t = useTranslations("Workspace.nodes");
	return /* @__PURE__ */ jsx(Card, {
		className: "p-3",
		children: /* @__PURE__ */ jsxs("div", {
			className: "space-y-2",
			children: [
				/* @__PURE__ */ jsxs(Label, {
					className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
					children: [/* @__PURE__ */ jsx(RectangleHorizontal, { className: "h-4 w-4" }), t("common.aspectRatio")]
				}),
				/* @__PURE__ */ jsx("div", {
					className: "grid grid-cols-5 gap-2",
					children: ratios.map((ratio) => {
						const isSelected = value.value === ratio.value;
						const iconSize = getAspectRatioIconSize(ratio.value);
						return /* @__PURE__ */ jsxs(Button, {
							variant: isSelected ? "default" : "outline",
							size: "sm",
							onClick: () => onChange(ratio),
							className: cn("h-auto min-w-0 py-2 px-1 flex flex-row items-center gap-1 text-xs whitespace-normal transition-all", isSelected ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent hover:text-accent-foreground"),
							children: [/* @__PURE__ */ jsx("div", {
								className: cn("border rounded transition-colors flex-shrink-0", isSelected ? "border-primary-foreground bg-primary-foreground/20" : "border-muted-foreground/30 bg-muted/30"),
								style: iconSize
							}), /* @__PURE__ */ jsxs("div", {
								className: "flex flex-col items-start min-w-0",
								children: [/* @__PURE__ */ jsx("span", {
									className: "text-xs font-medium leading-tight wrap-break-word text-left",
									children: t(`options.${ratio.label}`)
								}), /* @__PURE__ */ jsx("span", {
									className: "text-xs opacity-70 leading-tight",
									children: ratio.value
								})]
							})]
						}, ratio.value);
					})
				}),
				showSize && /* @__PURE__ */ jsxs("div", {
					className: "text-xs text-muted-foreground text-center",
					children: [
						t("common.currentSize"),
						" ",
						value.width,
						" × ",
						value.height
					]
				})
			]
		})
	});
}
//#endregion
//#region src/canvas/nodes/compose/image-fusion.tsx
const resolutions = [
	{
		value: "512",
		key: "res512",
		label: "512"
	},
	{
		value: "1K",
		key: "res1K",
		label: "1K"
	},
	{
		value: "2K",
		key: "res2K",
		label: "2K"
	},
	{
		value: "4K",
		key: "res4K",
		label: "4K"
	}
];
const ImageFusionNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-fusion");
	const ids = useUpstreamNodeIds(data.ids);
	const fromNodes = useNodesData(ids);
	const allImages = fromNodes.filter((node) => node.type === "imageNode").map((node) => coerceBaseNodeData(node.data).fileKeys).filter((keys) => !!keys && keys.length > 0);
	const textNode = fromNodes.find((node) => node.type === "textNode");
	const upstreamTexts = useMemo(() => {
		if (textNode) return coerceBaseNodeData(textNode?.data).texts || [];
		return [];
	}, [textNode]);
	const hasUpstreamTexts = upstreamTexts && upstreamTexts.length > 0;
	const width = form.state.width ?? 1024;
	const height = form.state.height ?? 1024;
	const currentRatio = IMAGE_ASPECT_RATIOS.find((r) => r.width === width && r.height === height) ?? IMAGE_ASPECT_RATIOS[2];
	const [currentResolution, setCurrentResolution] = useState(() => data.selectedResolution ?? resolutions[1]);
	const userPrompt = form.state.text ?? "";
	const textareaRef = useRef(null);
	const insertImageRef = useCallback((imageRef) => {
		if (!textareaRef.current) return;
		const textarea = textareaRef.current;
		const start = textarea.selectionStart;
		const end = textarea.selectionEnd;
		const newText = userPrompt.substring(0, start) + imageRef + userPrompt.substring(end);
		form.set("text", newText);
		setTimeout(() => {
			textarea.focus();
			const newCursorPos = start + imageRef.length;
			textarea.setSelectionRange(newCursorPos, newCursorPos);
		}, 0);
	}, [userPrompt, form]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-fusion",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.imageFusion"),
		icon: /* @__PURE__ */ jsx(Combine, { className: "h-5 w-5" }),
		executeLabel: t("actions.startFusion"),
		executeDisabled: !allImages || allImages.length < 2,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(AspectRatioPicker, {
					ratios: IMAGE_ASPECT_RATIOS,
					value: currentRatio,
					onChange: (ratio) => form.patch({
						width: ratio.width,
						height: ratio.height
					}),
					showSize: true
				}),
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs(Label, {
							className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
							children: [/* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" }), t("common.resolution")]
						}), /* @__PURE__ */ jsx("div", {
							className: "grid grid-cols-4 gap-2",
							children: resolutions.map((res) => /* @__PURE__ */ jsx(Button, {
								variant: currentResolution.value === res.value ? "default" : "outline",
								size: "sm",
								onClick: () => setCurrentResolution(res),
								className: cn("h-auto py-2 px-2 text-xs transition-all", currentResolution.value === res.value ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent hover:text-accent-foreground"),
								children: res.label
							}, res.value))
						})]
					})
				}),
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [
							/* @__PURE__ */ jsxs(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: [t("imageFusion.imageReference"), /* @__PURE__ */ jsxs("span", {
									className: "ml-2 text-xs font-normal",
									children: [
										"(",
										allImages.length,
										"/14)"
									]
								})]
							}),
							/* @__PURE__ */ jsx("div", {
								className: "flex gap-3 flex-wrap",
								children: allImages.slice(0, 14).map((images, index) => /* @__PURE__ */ jsx(MediaThumbnail, {
									fileKey: images[0],
									label: `${t("imageFusion.imageLabel")}${index + 1}`,
									type: "image",
									onClick: () => insertImageRef(`${t("imageFusion.imageLabel")}${index + 1}`)
								}, index))
							}),
							/* @__PURE__ */ jsx("p", {
								className: "text-xs text-muted-foreground",
								children: allImages.length > 14 ? t("imageFusion.maxImagesWarning") : t("imageFusion.imageReferenceHint")
							})
						]
					})
				}),
				hasUpstreamTexts ? /* @__PURE__ */ jsx(Card, {
					className: "p-3 bg-muted/50",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs(Label, {
							className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
							children: [
								/* @__PURE__ */ jsx(Sparkles, { className: "h-4 w-4" }),
								t("imageFusion.fusionPrompt"),
								t("imageEdit.fromUpstream")
							]
						}), /* @__PURE__ */ jsx("div", {
							className: "space-y-1 max-h-32 overflow-y-auto",
							children: upstreamTexts.map((text, index) => /* @__PURE__ */ jsx("div", {
								className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-3",
								children: text
							}, index))
						})]
					})
				}) : /* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs(Label, {
							className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
							children: [/* @__PURE__ */ jsx(Sparkles, { className: "h-4 w-4" }), t("imageFusion.fusionPrompt")]
						}), /* @__PURE__ */ jsx(NodeTextarea, {
							ref: textareaRef,
							showCard: false,
							placeholder: t("imageFusion.fusionPromptPlaceholder"),
							...form.bind("text"),
							rows: 4
						})]
					})
				})
			]
		})
	});
};
var image_fusion_default = memo(ImageFusionNode);
//#endregion
//#region src/canvas/ui/slider.tsx
const Slider = React$1.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsxs(SliderPrimitive.Root, {
	ref,
	className: cn("relative flex w-full touch-none select-none items-center", className),
	...props,
	children: [/* @__PURE__ */ jsx(SliderPrimitive.Track, {
		className: "relative h-2 w-full grow overflow-hidden rounded-full bg-secondary",
		children: /* @__PURE__ */ jsx(SliderPrimitive.Range, { className: "absolute h-full bg-primary" })
	}), /* @__PURE__ */ jsx(SliderPrimitive.Thumb, { className: "block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" })]
}));
Slider.displayName = SliderPrimitive.Root.displayName;
//#endregion
//#region src/canvas/nodes/base/video-duration-slider.tsx
function VideoDurationSlider({ value, onChange }) {
	const t = useTranslations("Workspace.nodes");
	const clamped = clampVideoDuration(value);
	useEffect(() => {
		if (clamped !== value) onChange(clamped);
	}, [
		clamped,
		value,
		onChange
	]);
	return /* @__PURE__ */ jsx(Card, {
		className: "p-3",
		children: /* @__PURE__ */ jsxs("div", {
			className: "space-y-2",
			children: [
				/* @__PURE__ */ jsxs("div", {
					className: "flex items-center justify-between",
					children: [/* @__PURE__ */ jsxs(Label, {
						className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
						children: [/* @__PURE__ */ jsx(Clock, { className: "h-4 w-4" }), t("common.duration")]
					}), /* @__PURE__ */ jsxs("span", {
						className: "text-xs font-medium",
						children: [clamped, "s"]
					})]
				}),
				/* @__PURE__ */ jsx(Slider, {
					value: [clamped],
					onValueChange: ([v]) => onChange(clampVideoDuration(v)),
					min: 1,
					max: 30,
					step: 1,
					className: "w-full"
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "flex justify-between text-[10px] text-muted-foreground",
					children: [/* @__PURE__ */ jsxs("span", { children: [1, "s"] }), /* @__PURE__ */ jsxs("span", { children: [30, "s"] })]
				})
			]
		})
	});
}
//#endregion
//#region src/canvas/nodes/compose/image-gen-video-compose.tsx
/** Same ABI slot as transfer image-gen-video; both inputs from upstream handles. */
const ImageGenVideoComposeNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tActions = useTranslations("Workspace.nodes.actions");
	const form = useAbiForm("image-gen-video");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("image-gen-video");
	const { hasText, hasImage, promptText } = useMemo(() => {
		if (!nodeId) return {
			hasText: false,
			hasImage: false,
			promptText: ""
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		const text = typeof values.text === "string" ? values.text.trim() : "";
		const images = Array.isArray(values.image) ? values.image : [];
		return {
			hasText: text.length > 0,
			hasImage: images.length > 0,
			promptText: text
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	const width = form.state.width ?? 1024;
	const height = form.state.height ?? 576;
	const durationSeconds = form.state.duration ?? 5;
	useEffect(() => {
		if (form.state.duration === void 0) form.set("duration", 5, { history: false });
	}, [form.state.duration, form.set]);
	const currentRatio = VIDEO_ASPECT_RATIOS.find((r) => r.width === width && r.height === height) ?? VIDEO_ASPECT_RATIOS[0];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.imageGenVideoCompose"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: tActions("generateVideo"),
		executeDisabled: !hasText || !hasImage,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [
							/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("compose.inputData")
							}),
							/* @__PURE__ */ jsx("p", {
								className: "text-xs text-muted-foreground",
								children: t("compose.connectTextImage")
							}),
							/* @__PURE__ */ jsxs("div", {
								className: "flex gap-4",
								children: [/* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-green-50",
											children: /* @__PURE__ */ jsx(Type, { className: "w-6 h-6 text-green-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded",
										children: t("compose.text")
									})]
								}), /* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-blue-50",
											children: /* @__PURE__ */ jsx(Image$1, { className: "w-6 h-6 text-blue-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded",
										children: t("compose.image")
									})]
								})]
							})
						]
					})
				}),
				promptText ? /* @__PURE__ */ jsx(Card, {
					className: "p-3 bg-muted/50",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs(Label, {
							className: "text-sm font-medium text-muted-foreground",
							children: [t("common.videoDesc"), t("imageEdit.fromUpstream")]
						}), /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-3",
							children: promptText
						})]
					})
				}) : null,
				/* @__PURE__ */ jsx(AspectRatioPicker, {
					ratios: VIDEO_ASPECT_RATIOS,
					value: currentRatio,
					onChange: (ratio) => form.patch({
						width: ratio.width,
						height: ratio.height
					}),
					showSize: true
				}),
				/* @__PURE__ */ jsx(VideoDurationSlider, {
					value: durationSeconds,
					onChange: (dur) => form.set("duration", dur)
				})
			]
		})
	});
};
ImageGenVideoComposeNode.displayName = "ImageGenVideoComposeNode";
var image_gen_video_compose_default = memo(ImageGenVideoComposeNode);
//#endregion
//#region src/canvas/nodes/base/duration-picker.tsx
function DurationPicker({ durations, value, onChange }) {
	const t = useTranslations("Workspace.nodes");
	return /* @__PURE__ */ jsx(Card, {
		className: "p-3",
		children: /* @__PURE__ */ jsxs("div", {
			className: "space-y-2",
			children: [/* @__PURE__ */ jsxs(Label, {
				className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
				children: [/* @__PURE__ */ jsx(Clock, { className: "h-4 w-4" }), t("common.duration")]
			}), /* @__PURE__ */ jsx("div", {
				className: "grid grid-cols-5 gap-2",
				children: durations.map((dur) => {
					const isSelected = value === dur.value;
					return /* @__PURE__ */ jsx(Button, {
						variant: isSelected ? "default" : "outline",
						size: "sm",
						onClick: () => onChange(dur.value),
						className: cn("h-auto py-2 px-1 text-xs transition-all", isSelected ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent hover:text-accent-foreground"),
						children: dur.label
					}, dur.value);
				})
			})]
		})
	});
}
//#endregion
//#region src/canvas/nodes/compose/image-image-gen-video.tsx
function FramePlaceholder({ label }) {
	return /* @__PURE__ */ jsxs("div", {
		className: "flex flex-col items-center gap-1.5",
		children: [/* @__PURE__ */ jsx("div", {
			className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
			children: /* @__PURE__ */ jsx("div", {
				className: "flex items-center justify-center h-full w-full bg-blue-50",
				children: /* @__PURE__ */ jsx(ImageIcon, { className: "w-6 h-6 text-blue-600" })
			})
		}), /* @__PURE__ */ jsx("div", {
			className: "px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded",
			children: label
		})]
	});
}
const ImageImageGenVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-image-gen-video");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("image-image-gen-video");
	const { firstFrameKey, lastFrameKey } = useMemo(() => {
		if (!nodeId) return {
			firstFrameKey: void 0,
			lastFrameKey: void 0
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		return {
			firstFrameKey: typeof values.image === "string" ? values.image : void 0,
			lastFrameKey: typeof values.end_image === "string" ? values.end_image : void 0
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	const width = form.state.width ?? 1280;
	const height = form.state.height ?? 704;
	const duration = form.state.duration ?? 10;
	const currentRatio = VIDEO_ASPECT_RATIOS.find((r) => r.width === width && r.height === height) ?? VIDEO_ASPECT_RATIOS[1];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-image-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.imageImageGenVideo"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateVideo"),
		executeDisabled: !firstFrameKey || !lastFrameKey,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [
							/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("compose.mediaFiles")
							}),
							/* @__PURE__ */ jsx("p", {
								className: "text-xs text-muted-foreground",
								children: t("compose.connectTwoImages")
							}),
							/* @__PURE__ */ jsxs("div", {
								className: "flex flex-wrap gap-4",
								children: [firstFrameKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
									fileKey: firstFrameKey,
									label: t("compose.firstFrame"),
									type: "image"
								}) : /* @__PURE__ */ jsx(FramePlaceholder, { label: t("compose.firstFrame") }), lastFrameKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
									fileKey: lastFrameKey,
									label: t("compose.lastFrame"),
									type: "image"
								}) : /* @__PURE__ */ jsx(FramePlaceholder, { label: t("compose.lastFrame") })]
							})
						]
					})
				}),
				/* @__PURE__ */ jsx(AspectRatioPicker, {
					ratios: VIDEO_ASPECT_RATIOS,
					value: currentRatio,
					onChange: (ratio) => form.patch({
						width: ratio.width,
						height: ratio.height
					}),
					showSize: true
				}),
				/* @__PURE__ */ jsx(DurationPicker, {
					durations: VIDEO_DURATIONS,
					value: String(duration),
					onChange: (dur) => form.set("duration", Number(dur))
				}),
				/* @__PURE__ */ jsx(NodeTextarea, {
					label: t("compose.generatePromptLabel"),
					icon: Sparkles,
					placeholder: t("compose.generatePromptPlaceholder"),
					...form.bind("text"),
					rows: 4
				})
			]
		})
	});
};
var image_image_gen_video_default = memo(ImageImageGenVideoNode);
//#endregion
//#region src/canvas/nodes/compose/images-gen-video.tsx
const MAX_IMAGES$1 = 9;
const ImagesGenVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("images-gen-video");
	const ids = useUpstreamNodeIds(data.ids);
	const fromNodes = useNodesData(ids);
	const allImages = fromNodes.filter((node) => node.type === "imageNode").map((node) => coerceBaseNodeData(node.data).fileKeys).filter((keys) => !!keys && keys.length > 0);
	const textNode = fromNodes.find((node) => node.type === "textNode");
	const upstreamTexts = useMemo(() => {
		if (textNode) return coerceBaseNodeData(textNode?.data).texts || [];
		return [];
	}, [textNode]);
	const hasUpstreamTexts = upstreamTexts && upstreamTexts.length > 0;
	const width = form.state.width ?? 1280;
	const height = form.state.height ?? 720;
	const duration = form.state.duration ?? 5;
	const currentRatio = VIDEO_ASPECT_RATIOS.find((r) => r.width === width && r.height === height) ?? VIDEO_ASPECT_RATIOS[1];
	const userPrompt = form.state.text ?? "";
	const textareaRef = useRef(null);
	const insertImageRef = useCallback((imageRef) => {
		if (!textareaRef.current) return;
		const textarea = textareaRef.current;
		const start = textarea.selectionStart;
		const end = textarea.selectionEnd;
		const newText = userPrompt.substring(0, start) + imageRef + userPrompt.substring(end);
		form.set("text", newText);
		setTimeout(() => {
			textarea.focus();
			const newCursorPos = start + imageRef.length;
			textarea.setSelectionRange(newCursorPos, newCursorPos);
		}, 0);
	}, [userPrompt, form]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "images-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.imagesGenVideo"),
		icon: /* @__PURE__ */ jsx(Film, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateVideo"),
		executeDisabled: !allImages || allImages.length < 1,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(AspectRatioPicker, {
					ratios: VIDEO_ASPECT_RATIOS,
					value: currentRatio,
					onChange: (ratio) => form.patch({
						width: ratio.width,
						height: ratio.height
					}),
					showSize: true
				}),
				/* @__PURE__ */ jsx(DurationPicker, {
					durations: VIDEO_DURATIONS,
					value: String(duration),
					onChange: (dur) => form.set("duration", Number(dur))
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [
						/* @__PURE__ */ jsxs("span", {
							className: "text-sm font-medium text-muted-foreground",
							children: [t("imageFusion.imageReference"), /* @__PURE__ */ jsxs("span", {
								className: "ml-2 text-xs font-normal",
								children: [
									"(",
									allImages.length,
									"/",
									MAX_IMAGES$1,
									")"
								]
							})]
						}),
						/* @__PURE__ */ jsx("div", {
							className: "flex gap-3 flex-wrap",
							children: allImages.slice(0, MAX_IMAGES$1).map((images, index) => /* @__PURE__ */ jsx(MediaThumbnail, {
								fileKey: images[0],
								label: `${t("imageFusion.imageLabel")}${index + 1}`,
								type: "image",
								onClick: () => insertImageRef(`${t("imageFusion.imageLabel")}${index + 1}`)
							}, index))
						}),
						/* @__PURE__ */ jsx("p", {
							className: "text-xs text-muted-foreground",
							children: t("imageFusion.imageReferenceHint")
						})
					]
				}),
				hasUpstreamTexts ? /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs("span", {
						className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
						children: [
							/* @__PURE__ */ jsx(Sparkles, { className: "h-4 w-4" }),
							t("imageFusion.fusionPrompt"),
							t("imageEdit.fromUpstream")
						]
					}), /* @__PURE__ */ jsx("div", {
						className: "space-y-1 max-h-32 overflow-y-auto",
						children: upstreamTexts.map((text, index) => /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-3",
							children: text
						}, index))
					})]
				}) : /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs("span", {
						className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
						children: [/* @__PURE__ */ jsx(Sparkles, { className: "h-4 w-4" }), t("imageFusion.fusionPrompt")]
					}), /* @__PURE__ */ jsx(NodeTextarea, {
						ref: textareaRef,
						showCard: false,
						placeholder: t("imageFusion.fusionPromptPlaceholder"),
						...form.bind("text"),
						rows: 4
					})]
				})
			]
		})
	});
};
var images_gen_video_default = memo(ImagesGenVideoNode);
//#endregion
//#region src/canvas/nodes/compose/merge-video-audio.tsx
const MergeVideoAudioNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("merge-video-audio");
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "merge-video-audio",
		form,
		selected,
		data,
		title: t("titles.mergeVideoAudio"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.startMerge")
	});
};
var merge_video_audio_default = memo(MergeVideoAudioNode);
//#endregion
//#region src/canvas/nodes/compose/music-cover.tsx
const DEFAULT_STRENGTH$1 = .8;
const MusicCoverNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("music-cover");
	const nodeId = useNodeId();
	const edges = useStore((state) => state.edges);
	const { audioSourceId, refAudioSourceId } = useMemo(() => {
		if (!nodeId) return {
			audioSourceId: null,
			refAudioSourceId: null
		};
		let audioSrc = null;
		let refAudioSrc = null;
		for (const e of edges) {
			if (e.target !== nodeId) continue;
			if (e.targetHandle === "in:audio") audioSrc = e.source;
			else if (e.targetHandle === "in:ref_audio") refAudioSrc = e.source;
		}
		return {
			audioSourceId: audioSrc,
			refAudioSourceId: refAudioSrc
		};
	}, [edges, nodeId]);
	const upstreamIds = useMemo(() => [audioSourceId, refAudioSourceId].filter((v) => !!v), [audioSourceId, refAudioSourceId]);
	const upstreamNodes = useNodesData(upstreamIds);
	const audioKey = useMemo(() => {
		if (!audioSourceId) return void 0;
		const n = upstreamNodes.find((u) => u.id === audioSourceId);
		if (!n || n.type !== "audioNode") return void 0;
		return coerceBaseNodeData(n.data).fileKeys?.[0];
	}, [audioSourceId, upstreamNodes]);
	const refAudioKey = useMemo(() => {
		if (!refAudioSourceId) return void 0;
		const n = upstreamNodes.find((u) => u.id === refAudioSourceId);
		if (!n || n.type !== "audioNode") return void 0;
		return coerceBaseNodeData(n.data).fileKeys?.[0];
	}, [refAudioSourceId, upstreamNodes]);
	const strength = form.state.strength ?? DEFAULT_STRENGTH$1;
	useEffect(() => {
		if (form.state.strength == null) form.set("strength", DEFAULT_STRENGTH$1, { history: false });
	}, [form.state.strength, form.set]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "music-cover",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.musicCover"),
		icon: /* @__PURE__ */ jsx(Palette, { className: "h-5 w-5" }),
		executeLabel: t("actions.coverMusic"),
		executeDisabled: !audioSourceId,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsx(Label, {
							className: "text-sm font-medium text-muted-foreground",
							children: t("compose.inputData")
						}), /* @__PURE__ */ jsxs("div", {
							className: "flex gap-4",
							children: [audioKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
								fileKey: audioKey,
								label: t("musicTasks.sourceAudio"),
								type: "audio"
							}) : /* @__PURE__ */ jsxs("div", {
								className: "flex flex-col items-center gap-1.5",
								children: [/* @__PURE__ */ jsx("div", {
									className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
									children: /* @__PURE__ */ jsx("div", {
										className: "flex items-center justify-center h-full w-full bg-blue-50",
										children: /* @__PURE__ */ jsx(Music, { className: "w-6 h-6 text-blue-600" })
									})
								}), /* @__PURE__ */ jsx("div", {
									className: "px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded",
									children: t("musicTasks.sourceAudio")
								})]
							}), refAudioKey && /* @__PURE__ */ jsx(MediaThumbnail, {
								fileKey: refAudioKey,
								label: t("musicTasks.refAudio"),
								type: "audio"
							})]
						})]
					})
				}),
				/* @__PURE__ */ jsx(NodeTextarea, {
					label: t("musicTasks.promptLabel"),
					icon: MessageSquare,
					placeholder: t("musicTasks.promptPlaceholder"),
					...form.bind("text"),
					rows: 3
				}),
				/* @__PURE__ */ jsx(NodeTextarea, {
					label: t("musicTasks.lyricsLabel"),
					placeholder: t("musicTasks.lyricsPlaceholder"),
					...form.bind("lyrics"),
					rows: 2
				}),
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "flex items-center justify-between",
							children: [/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("musicTasks.strength")
							}), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: strength.toFixed(2)
							})]
						}), /* @__PURE__ */ jsx(Slider, {
							value: [strength],
							onValueChange: ([v]) => form.set("strength", v),
							min: 0,
							max: 1,
							step: .05,
							className: "w-full"
						})]
					})
				})
			]
		})
	});
};
MusicCoverNode.displayName = "MusicCoverNode";
var music_cover_default = memo(MusicCoverNode);
//#endregion
//#region src/canvas/nodes/compose/refs-gen-video.tsx
const MAX_IMAGES = 9;
const MAX_VIDEOS = 3;
const MAX_AUDIOS = 3;
const RefsGenVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("refs-gen-video");
	const ids = useUpstreamNodeIds(data.ids);
	const fromNodes = useNodesData(ids);
	const collectKeys = (nodeType) => fromNodes.filter((node) => node.type === nodeType).map((node) => coerceBaseNodeData(node.data).fileKeys).filter((keys) => !!keys && keys.length > 0);
	const allImages = collectKeys("imageNode");
	const allVideos = collectKeys("videoNode");
	const allAudios = collectKeys("audioNode");
	const totalRefs = allImages.length + allVideos.length + allAudios.length;
	const textNode = fromNodes.find((node) => node.type === "textNode");
	const upstreamTexts = useMemo(() => {
		if (textNode) return coerceBaseNodeData(textNode?.data).texts || [];
		return [];
	}, [textNode]);
	const hasUpstreamTexts = upstreamTexts && upstreamTexts.length > 0;
	const width = form.state.width ?? 1280;
	const height = form.state.height ?? 720;
	const duration = form.state.duration ?? 5;
	const currentRatio = VIDEO_ASPECT_RATIOS.find((r) => r.width === width && r.height === height) ?? VIDEO_ASPECT_RATIOS[1];
	const userPrompt = form.state.text ?? "";
	const textareaRef = useRef(null);
	const insertRefLabel = useCallback((refLabel) => {
		if (!textareaRef.current) return;
		const textarea = textareaRef.current;
		const start = textarea.selectionStart;
		const end = textarea.selectionEnd;
		const newText = userPrompt.substring(0, start) + refLabel + userPrompt.substring(end);
		form.set("text", newText);
		setTimeout(() => {
			textarea.focus();
			const newCursorPos = start + refLabel.length;
			textarea.setSelectionRange(newCursorPos, newCursorPos);
		}, 0);
	}, [userPrompt, form]);
	const referenceRow = (refs, max, type, sectionLabel, itemLabel) => /* @__PURE__ */ jsxs("div", {
		className: "space-y-2",
		children: [/* @__PURE__ */ jsxs("span", {
			className: "text-sm font-medium text-muted-foreground",
			children: [sectionLabel, /* @__PURE__ */ jsxs("span", {
				className: "ml-2 text-xs font-normal",
				children: [
					"(",
					refs.length,
					"/",
					max,
					")"
				]
			})]
		}), /* @__PURE__ */ jsx("div", {
			className: "flex gap-3 flex-wrap",
			children: refs.slice(0, max).map((keys, index) => /* @__PURE__ */ jsx(MediaThumbnail, {
				fileKey: keys[0],
				label: `${itemLabel}${index + 1}`,
				type,
				onClick: () => insertRefLabel(`${itemLabel}${index + 1}`)
			}, index))
		})]
	});
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "refs-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.refsGenVideo"),
		icon: /* @__PURE__ */ jsx(Clapperboard, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateVideo"),
		executeDisabled: totalRefs < 1,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(AspectRatioPicker, {
					ratios: VIDEO_ASPECT_RATIOS,
					value: currentRatio,
					onChange: (ratio) => form.patch({
						width: ratio.width,
						height: ratio.height
					}),
					showSize: true
				}),
				/* @__PURE__ */ jsx(DurationPicker, {
					durations: VIDEO_DURATIONS,
					value: String(duration),
					onChange: (dur) => form.set("duration", Number(dur))
				}),
				referenceRow(allImages, MAX_IMAGES, "image", t("imageFusion.imageReference"), t("imageFusion.imageLabel")),
				allVideos.length > 0 && referenceRow(allVideos, MAX_VIDEOS, "video", t("refsGenVideo.videoReference"), t("refsGenVideo.videoLabel")),
				allAudios.length > 0 && referenceRow(allAudios, MAX_AUDIOS, "audio", t("refsGenVideo.audioReference"), t("refsGenVideo.audioLabel")),
				/* @__PURE__ */ jsx("p", {
					className: "text-xs text-muted-foreground",
					children: t("refsGenVideo.referenceHint")
				}),
				hasUpstreamTexts ? /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs("span", {
						className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
						children: [
							/* @__PURE__ */ jsx(Sparkles, { className: "h-4 w-4" }),
							t("imageFusion.fusionPrompt"),
							t("imageEdit.fromUpstream")
						]
					}), /* @__PURE__ */ jsx("div", {
						className: "space-y-1 max-h-32 overflow-y-auto",
						children: upstreamTexts.map((text, index) => /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-3",
							children: text
						}, index))
					})]
				}) : /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs("span", {
						className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
						children: [/* @__PURE__ */ jsx(Sparkles, { className: "h-4 w-4" }), t("imageFusion.fusionPrompt")]
					}), /* @__PURE__ */ jsx(NodeTextarea, {
						ref: textareaRef,
						showCard: false,
						placeholder: t("refsGenVideo.promptPlaceholder"),
						...form.bind("text"),
						rows: 4
					})]
				})
			]
		})
	});
};
var refs_gen_video_default = memo(RefsGenVideoNode);
//#endregion
//#region src/canvas/nodes/compose/speech-image-gen-video.tsx
const SpeechImageGenVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tActions = useTranslations("Workspace.nodes.actions");
	const form = useAbiForm("audio-image-gen-video");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("audio-image-gen-video");
	const { hasImage, hasAudio, imageFileKey, audioFileKey } = useMemo(() => {
		if (!nodeId) return {
			hasImage: false,
			hasAudio: false,
			imageFileKey: void 0,
			audioFileKey: void 0
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		const image = typeof values.image === "string" ? values.image : void 0;
		const audio = typeof values.audio === "string" ? values.audio : void 0;
		return {
			hasImage: Boolean(image),
			hasAudio: Boolean(audio),
			imageFileKey: image,
			audioFileKey: audio
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	const width = form.state.width ?? 1024;
	const height = form.state.height ?? 576;
	useEffect(() => {
		if (form.state.width === void 0 && form.state.height === void 0) form.patch({
			width: 1024,
			height: 576
		}, { history: false });
	}, [
		form.state.width,
		form.state.height,
		form.patch
	]);
	const currentRatio = VIDEO_ASPECT_RATIOS.find((r) => r.width === width && r.height === height) ?? VIDEO_ASPECT_RATIOS[0];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "audio-image-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.speechImageGenVideo"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: tActions("generateVideo"),
		executeDisabled: !hasImage || !hasAudio,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [
							/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("compose.inputData")
							}),
							/* @__PURE__ */ jsxs("div", {
								className: "flex gap-4",
								children: [imageFileKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
									fileKey: imageFileKey,
									label: t("compose.image"),
									type: "image"
								}) : /* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-blue-50",
											children: /* @__PURE__ */ jsx(Image$1, { className: "w-6 h-6 text-blue-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded",
										children: t("compose.image")
									})]
								}), audioFileKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
									fileKey: audioFileKey,
									label: t("compose.audio"),
									type: "audio"
								}) : /* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-orange-50",
											children: /* @__PURE__ */ jsx(Music, { className: "w-6 h-6 text-orange-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded",
										children: t("compose.audio")
									})]
								})]
							}),
							(!hasImage || !hasAudio) && /* @__PURE__ */ jsx("p", {
								className: "text-xs text-red-500",
								children: t("compose.connectImageAudio")
							})
						]
					})
				}),
				/* @__PURE__ */ jsx(NodeTextarea, {
					label: t("compose.generatePromptLabel"),
					icon: Sparkles,
					placeholder: t("compose.generatePromptPlaceholder"),
					...form.bind("text"),
					rows: 4
				}),
				/* @__PURE__ */ jsx(AspectRatioPicker, {
					ratios: VIDEO_ASPECT_RATIOS,
					value: currentRatio,
					onChange: (ratio) => form.patch({
						width: ratio.width,
						height: ratio.height
					}),
					showSize: true
				})
			]
		})
	});
};
SpeechImageGenVideoNode.displayName = "SpeechImageGenVideoNode";
var speech_image_gen_video_default = memo(SpeechImageGenVideoNode);
//#endregion
//#region src/canvas/nodes/compose/speech-text-gen-video.tsx
const SpeechTextGenVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tActions = useTranslations("Workspace.nodes.actions");
	const form = useAbiForm("speech-text-gen-video");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("speech-text-gen-video");
	const { hasText, hasAudio, promptText, audioFileKey } = useMemo(() => {
		if (!nodeId) return {
			hasText: false,
			hasAudio: false,
			promptText: "",
			audioFileKey: void 0
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		const text = typeof values.text === "string" ? values.text.trim() : "";
		const audio = typeof values.audio === "string" ? values.audio : void 0;
		return {
			hasText: text.length > 0,
			hasAudio: Boolean(audio),
			promptText: text,
			audioFileKey: audio
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "speech-text-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.speechTextGenVideo"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: tActions("generateVideo"),
		executeDisabled: !hasText || !hasAudio,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [/* @__PURE__ */ jsx(Card, {
				className: "p-3",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [
						/* @__PURE__ */ jsx(Label, {
							className: "text-sm font-medium text-muted-foreground",
							children: t("compose.inputData")
						}),
						/* @__PURE__ */ jsxs("div", {
							className: "flex gap-4",
							children: [/* @__PURE__ */ jsxs("div", {
								className: "flex flex-col items-center gap-1.5",
								children: [/* @__PURE__ */ jsx("div", {
									className: `relative w-16 h-16 rounded-md border-2 overflow-hidden ${hasText ? "border-green-400" : "border-gray-300"}`,
									children: /* @__PURE__ */ jsx("div", {
										className: "flex items-center justify-center h-full w-full bg-green-50",
										children: /* @__PURE__ */ jsx(Type, { className: "w-6 h-6 text-green-600" })
									})
								}), /* @__PURE__ */ jsx("div", {
									className: "px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded",
									children: t("compose.text")
								})]
							}), audioFileKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
								fileKey: audioFileKey,
								label: t("compose.referenceAudio"),
								type: "audio"
							}) : /* @__PURE__ */ jsxs("div", {
								className: "flex flex-col items-center gap-1.5",
								children: [/* @__PURE__ */ jsx("div", {
									className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
									children: /* @__PURE__ */ jsx("div", {
										className: "flex items-center justify-center h-full w-full bg-blue-50",
										children: /* @__PURE__ */ jsx(Music, { className: "w-6 h-6 text-blue-600" })
									})
								}), /* @__PURE__ */ jsx("div", {
									className: "px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded",
									children: t("compose.referenceAudio")
								})]
							})]
						}),
						(!hasText || !hasAudio) && /* @__PURE__ */ jsx("p", {
							className: "text-xs text-red-500",
							children: t("compose.connectTextAudioNode")
						})
					]
				})
			}), hasText ? /* @__PURE__ */ jsx(Card, {
				className: "p-3 bg-muted/50",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: [t("compose.inputText"), t("imageEdit.fromUpstream")]
					}), /* @__PURE__ */ jsx("div", {
						className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-3",
						children: promptText
					})]
				})
			}) : null]
		})
	});
};
SpeechTextGenVideoNode.displayName = "SpeechTextGenVideoNode";
var speech_text_gen_video_default = memo(SpeechTextGenVideoNode);
//#endregion
//#region src/canvas/nodes/compose/speech-video-gen-video.tsx
const SpeechVideoGenVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tActions = useTranslations("Workspace.nodes.actions");
	const form = useAbiForm("speech-video-gen-video");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("speech-video-gen-video");
	const { hasVideo, hasText, videoFileKey, promptText } = useMemo(() => {
		if (!nodeId) return {
			hasVideo: false,
			hasText: false,
			videoFileKey: void 0,
			promptText: ""
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		const text = typeof values.text === "string" ? values.text.trim() : "";
		const video = typeof values.video === "string" ? values.video : void 0;
		return {
			hasVideo: Boolean(video),
			hasText: text.length > 0,
			videoFileKey: video,
			promptText: text
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	const manualText = form.state.text?.trim() ?? "";
	const effectiveText = promptText || manualText;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "speech-video-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.speechVideoGenVideo"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: tActions("lipDub"),
		executeDisabled: !hasVideo || !effectiveText,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [
							/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("compose.inputData")
							}),
							/* @__PURE__ */ jsx("p", {
								className: "text-xs text-muted-foreground",
								children: t("compose.connectTextVideoNode")
							}),
							/* @__PURE__ */ jsxs("div", {
								className: "flex gap-4",
								children: [videoFileKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
									fileKey: videoFileKey,
									label: t("compose.video"),
									type: "video"
								}) : /* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-purple-50",
											children: /* @__PURE__ */ jsx(Video, { className: "w-6 h-6 text-purple-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded",
										children: t("compose.video")
									})]
								}), /* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: `relative w-16 h-16 rounded-md border-2 overflow-hidden ${hasText ? "border-green-400" : "border-gray-300"}`,
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-green-50",
											children: /* @__PURE__ */ jsx(Type, { className: "w-6 h-6 text-green-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded",
										children: t("compose.text")
									})]
								})]
							}),
							(!hasVideo || !hasText) && /* @__PURE__ */ jsx("p", {
								className: "text-xs text-red-500",
								children: t("compose.connectTextVideoNode")
							})
						]
					})
				}),
				promptText ? /* @__PURE__ */ jsx(Card, {
					className: "p-3 bg-muted/50",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs(Label, {
							className: "text-sm font-medium text-muted-foreground",
							children: [t("lipDub.targetDialogue"), t("imageEdit.fromUpstream")]
						}), /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-4",
							children: promptText
						})]
					})
				}) : /* @__PURE__ */ jsx(NodeTextarea, {
					label: t("lipDub.targetDialogue"),
					icon: Sparkles,
					placeholder: t("lipDub.targetDialoguePlaceholder"),
					...form.bind("text"),
					rows: 4
				}),
				/* @__PURE__ */ jsx("p", {
					className: "text-xs text-muted-foreground",
					children: t("lipDub.promptHint")
				})
			]
		})
	});
};
SpeechVideoGenVideoNode.displayName = "SpeechVideoGenVideoNode";
var speech_video_gen_video_default = memo(SpeechVideoGenVideoNode);
//#endregion
//#region src/canvas/nodes/transfer/text-gen-speech-shared.ts
function buildEmotionOptions(t) {
	return [
		{
			label: t("emotions.none"),
			value: "none"
		},
		{
			label: t("emotions.happy"),
			value: "happy"
		},
		{
			label: t("emotions.angry"),
			value: "angry"
		},
		{
			label: t("emotions.sad"),
			value: "sad"
		},
		{
			label: t("emotions.fear"),
			value: "fear"
		},
		{
			label: t("emotions.surprised"),
			value: "surprised"
		},
		{
			label: t("emotions.confusion"),
			value: "confusion"
		},
		{
			label: t("emotions.empathy"),
			value: "empathy"
		},
		{
			label: t("emotions.embarrass"),
			value: "embarrass"
		},
		{
			label: t("emotions.excited"),
			value: "excited"
		},
		{
			label: t("emotions.depressed"),
			value: "depressed"
		},
		{
			label: t("emotions.admiration"),
			value: "admiration"
		},
		{
			label: t("emotions.coldness"),
			value: "coldness"
		}
	];
}
function buildStyleOptions(t) {
	return [
		{
			label: t("styles.none"),
			value: "none"
		},
		{
			label: t("styles.serious"),
			value: "serious"
		},
		{
			label: t("styles.arrogant"),
			value: "arrogant"
		},
		{
			label: t("styles.child"),
			value: "child"
		},
		{
			label: t("styles.older"),
			value: "older"
		},
		{
			label: t("styles.girl"),
			value: "girl"
		},
		{
			label: t("styles.pure"),
			value: "pure"
		},
		{
			label: t("styles.sister"),
			value: "sister"
		},
		{
			label: t("styles.sweet"),
			value: "sweet"
		},
		{
			label: t("styles.exaggerated"),
			value: "exaggerated"
		},
		{
			label: t("styles.ethereal"),
			value: "ethereal"
		},
		{
			label: t("styles.whisper"),
			value: "whisper"
		},
		{
			label: t("styles.generous"),
			value: "generous"
		},
		{
			label: t("styles.recite"),
			value: "recite"
		},
		{
			label: t("styles.act_coy"),
			value: "act_coy"
		},
		{
			label: t("styles.warm"),
			value: "warm"
		},
		{
			label: t("styles.shy"),
			value: "shy"
		},
		{
			label: t("styles.comfort"),
			value: "comfort"
		},
		{
			label: t("styles.authority"),
			value: "authority"
		},
		{
			label: t("styles.chat"),
			value: "chat"
		},
		{
			label: t("styles.radio"),
			value: "radio"
		},
		{
			label: t("styles.soulful"),
			value: "soulful"
		},
		{
			label: t("styles.gentle"),
			value: "gentle"
		},
		{
			label: t("styles.story"),
			value: "story"
		},
		{
			label: t("styles.vivid"),
			value: "vivid"
		},
		{
			label: t("styles.program"),
			value: "program"
		},
		{
			label: t("styles.news"),
			value: "news"
		},
		{
			label: t("styles.advertising"),
			value: "advertising"
		},
		{
			label: t("styles.roar"),
			value: "roar"
		},
		{
			label: t("styles.murmur"),
			value: "murmur"
		},
		{
			label: t("styles.shout"),
			value: "shout"
		},
		{
			label: t("styles.deeply"),
			value: "deeply"
		},
		{
			label: t("styles.loudly"),
			value: "loudly"
		}
	];
}
//#endregion
//#region src/canvas/nodes/base/emotion-select.tsx
/**
* Shared single-select emotion picker.
*
* Options come from `buildEmotionOptions(t)` (i18n keys); the "none" option
* maps to `undefined` on the wire. Multi-select usage (chip group) lives in
* the preset node and is intentionally separate.
*/
function EmotionSelect({ value, onChange, id = "emotion-select", className, disabled }) {
	const t = useTranslations("Workspace.nodes");
	const options = useMemo(() => buildEmotionOptions(t), [t]);
	return /* @__PURE__ */ jsxs(Select, {
		value: value ?? "none",
		onValueChange: (v) => onChange(v === "none" ? void 0 : v),
		disabled,
		children: [/* @__PURE__ */ jsx(SelectTrigger, {
			id,
			className: `h-9 ${className ?? "w-full"}`,
			children: /* @__PURE__ */ jsx(SelectValue, { placeholder: t("compose.selectEmotion") })
		}), /* @__PURE__ */ jsx(SelectContent, { children: options.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
			value: opt.value,
			children: opt.label
		}, opt.value)) })]
	});
}
//#endregion
//#region src/canvas/nodes/base/style-select.tsx
/**
* Shared single-select style picker.
*
* Options come from `buildStyleOptions(t)` (i18n keys); the "none" option
* maps to `undefined` on the wire.
*/
function StyleSelect({ value, onChange, id = "style-select", className, disabled }) {
	const t = useTranslations("Workspace.nodes");
	const options = useMemo(() => buildStyleOptions(t), [t]);
	return /* @__PURE__ */ jsxs(Select, {
		value: value ?? "none",
		onValueChange: (v) => onChange(v === "none" ? void 0 : v),
		disabled,
		children: [/* @__PURE__ */ jsx(SelectTrigger, {
			id,
			className: `h-9 ${className ?? "w-full"}`,
			children: /* @__PURE__ */ jsx(SelectValue, { placeholder: t("compose.selectStyle") })
		}), /* @__PURE__ */ jsx(SelectContent, { children: options.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
			value: opt.value,
			children: opt.label
		}, opt.value)) })]
	});
}
//#endregion
//#region src/canvas/nodes/compose/text-audio-gen-speech.tsx
const TextAudioGenSpeechNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("text-audio-gen-speech");
	const emotion = form.state.emotion;
	const style = form.state.style;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "text-audio-gen-speech",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.textAudioGenSpeech"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateSpeech"),
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [/* @__PURE__ */ jsx(Card, {
				className: "p-3",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsx(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: t("compose.inputData")
					}), /* @__PURE__ */ jsxs("div", {
						className: "flex gap-4",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "flex flex-col items-center gap-1.5",
							children: [/* @__PURE__ */ jsx("div", {
								className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
								children: /* @__PURE__ */ jsx("div", {
									className: "flex items-center justify-center h-full w-full bg-green-50",
									children: /* @__PURE__ */ jsx(Type, { className: "w-6 h-6 text-green-600" })
								})
							}), /* @__PURE__ */ jsx("div", {
								className: "px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded",
								children: t("compose.text")
							})]
						}), /* @__PURE__ */ jsxs("div", {
							className: "flex flex-col items-center gap-1.5",
							children: [/* @__PURE__ */ jsx("div", {
								className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
								children: /* @__PURE__ */ jsx("div", {
									className: "flex items-center justify-center h-full w-full bg-blue-50",
									children: /* @__PURE__ */ jsx(Music, { className: "w-6 h-6 text-blue-600" })
								})
							}), /* @__PURE__ */ jsx("div", {
								className: "px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded",
								children: t("compose.audio")
							})]
						})]
					})]
				})
			}), /* @__PURE__ */ jsxs(Card, {
				className: "p-3 nodrag",
				onPointerDown: (e) => e.stopPropagation(),
				children: [/* @__PURE__ */ jsxs("div", {
					className: "mb-4 flex items-center gap-3",
					children: [/* @__PURE__ */ jsx("label", {
						htmlFor: "emotion-select",
						className: "text-sm text-muted-foreground whitespace-nowrap",
						children: t("compose.emotion")
					}), /* @__PURE__ */ jsx(EmotionSelect, {
						value: emotion,
						onChange: (v) => form.patch({
							emotion: v,
							style: void 0
						})
					})]
				}), /* @__PURE__ */ jsxs("div", {
					className: "flex items-center gap-3",
					children: [/* @__PURE__ */ jsx("label", {
						htmlFor: "style-select",
						className: "text-sm text-muted-foreground whitespace-nowrap",
						children: t("compose.style")
					}), /* @__PURE__ */ jsx(StyleSelect, {
						value: style,
						onChange: (v) => form.patch({
							style: v,
							emotion: void 0
						})
					})]
				})]
			})]
		})
	});
};
var text_audio_gen_speech_default = memo(TextAudioGenSpeechNode);
//#endregion
//#region src/canvas/nodes/base/language-select.tsx
const LANGUAGES = [
	{
		value: "Auto",
		tKey: "auto"
	},
	{
		value: "Chinese",
		tKey: "zh"
	},
	{
		value: "English",
		tKey: "en"
	},
	{
		value: "Japanese",
		tKey: "ja"
	},
	{
		value: "Korean",
		tKey: "ko"
	},
	{
		value: "German",
		tKey: "de"
	},
	{
		value: "French",
		tKey: "fr"
	},
	{
		value: "Russian",
		tKey: "ru"
	},
	{
		value: "Portuguese",
		tKey: "pt"
	},
	{
		value: "Spanish",
		tKey: "es"
	},
	{
		value: "Italian",
		tKey: "it"
	}
];
function LanguageSelect({ value, onChange, id = "language-select", className }) {
	const tLang = useTranslations("Languages");
	return /* @__PURE__ */ jsxs(Select, {
		value: value ?? "",
		onValueChange: onChange,
		children: [/* @__PURE__ */ jsx(SelectTrigger, {
			id,
			className: `w-32 h-9 ${className ?? ""}`.trim(),
			children: /* @__PURE__ */ jsx(SelectValue, {})
		}), /* @__PURE__ */ jsx(SelectContent, { children: LANGUAGES.map((lang) => /* @__PURE__ */ jsx(SelectItem, {
			value: lang.value,
			children: tLang(lang.tKey)
		}, lang.value)) })]
	});
}
//#endregion
//#region src/canvas/nodes/compose/text-gen-speech-clone-compose.tsx
const TextGenSpeechCloneComposeNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("text-gen-speech-clone");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("text-gen-speech-clone");
	const { hasText, hasRefAudio, texts, refAudioKey } = useMemo(() => {
		if (!nodeId) return {
			hasText: false,
			hasRefAudio: false,
			texts: [],
			refAudioKey: void 0
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		const rawText = values.text;
		const textList = Array.isArray(rawText) ? rawText.filter((x) => typeof x === "string" && x.trim().length > 0) : typeof rawText === "string" && rawText.trim() ? [rawText.trim()] : [];
		const ref = typeof values.ref_audio === "string" ? values.ref_audio : void 0;
		return {
			hasText: textList.length > 0,
			hasRefAudio: Boolean(ref),
			texts: textList,
			refAudioKey: ref
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "text-gen-speech-clone",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.textGenSpeechCloneCompose"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateSpeech"),
		executeDisabled: !hasText || !hasRefAudio,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [
							/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("compose.inputData")
							}),
							/* @__PURE__ */ jsxs("div", {
								className: "flex gap-4",
								children: [/* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: `relative w-16 h-16 rounded-md border-2 overflow-hidden ${hasText ? "border-green-400" : "border-gray-300"}`,
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-green-50",
											children: /* @__PURE__ */ jsx(Type, { className: "w-6 h-6 text-green-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded",
										children: t("compose.text")
									})]
								}), refAudioKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
									fileKey: refAudioKey,
									label: t("compose.referenceAudio"),
									type: "audio"
								}) : /* @__PURE__ */ jsxs("div", {
									className: "flex flex-col items-center gap-1.5",
									children: [/* @__PURE__ */ jsx("div", {
										className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
										children: /* @__PURE__ */ jsx("div", {
											className: "flex items-center justify-center h-full w-full bg-blue-50",
											children: /* @__PURE__ */ jsx(Music, { className: "w-6 h-6 text-blue-600" })
										})
									}), /* @__PURE__ */ jsx("div", {
										className: "px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded",
										children: t("compose.referenceAudio")
									})]
								})]
							}),
							(!hasText || !hasRefAudio) && /* @__PURE__ */ jsx("p", {
								className: "text-xs text-red-500",
								children: t("compose.connectTextReferenceAudio")
							})
						]
					})
				}),
				hasText ? /* @__PURE__ */ jsxs(Card, {
					className: "p-3 bg-muted/50",
					children: [/* @__PURE__ */ jsxs(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: [t("common.inputText"), t("imageEdit.fromUpstream")]
					}), /* @__PURE__ */ jsx("div", {
						className: "mt-2 space-y-1 max-h-32 overflow-y-auto",
						children: texts.map((text, index) => /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-2",
							children: text
						}, `${index}-${text.slice(0, 48)}`))
					})]
				}) : null,
				/* @__PURE__ */ jsxs(Card, {
					className: "p-3 nodrag",
					onPointerDown: (e) => e.stopPropagation(),
					children: [/* @__PURE__ */ jsxs("div", {
						className: "mb-4 flex flex-wrap items-center gap-3",
						children: [/* @__PURE__ */ jsxs(Label, {
							htmlFor: "clone-compose-language-select",
							className: "text-sm text-muted-foreground whitespace-nowrap",
							children: [t("common.language"), "："]
						}), /* @__PURE__ */ jsx(LanguageSelect, {
							id: "clone-compose-language-select",
							value: form.state.language ?? "Auto",
							onChange: (v) => form.set("language", v)
						})]
					}), /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsxs("label", {
						htmlFor: "clone-compose-ref-text",
						className: "text-sm text-muted-foreground block mb-2",
						children: [t("compose.referenceTextOptional"), "："]
					}), /* @__PURE__ */ jsx("textarea", {
						id: "clone-compose-ref-text",
						className: "w-full h-16 p-2 text-sm rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring",
						placeholder: t("common.referenceTextCloneHint"),
						...form.register("ref_text")
					})] })]
				})
			]
		})
	});
};
TextGenSpeechCloneComposeNode.displayName = "TextGenSpeechCloneComposeNode";
var text_gen_speech_clone_compose_default = memo(TextGenSpeechCloneComposeNode);
//#endregion
//#region src/canvas/nodes/compose/texts-gen-text.tsx
const TextsGenTextNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tBase = useTranslations("Workspace.nodes.base");
	const form = useAbiForm("combine-text");
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "combine-text",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.combineText"),
		icon: /* @__PURE__ */ jsx(Wand2, { className: "h-5 w-5" }),
		executeLabel: tBase("execute"),
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx("div", {
				className: "space-y-2",
				children: /* @__PURE__ */ jsx(NodeTextarea, {
					rows: 6,
					placeholder: t("common.enterInstructions"),
					...form.bind("userPrompt"),
					className: "min-h-[120px]"
				})
			})
		})
	});
};
TextsGenTextNode.displayName = "TextsGenTextNode";
var texts_gen_text_default = memo(TextsGenTextNode);
//#endregion
//#region src/canvas/nodes/compose/video-image-gen-video-mix.tsx
const VideoImageGenVideoMixNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("video-image-gen-video-mix");
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "video-image-gen-video-mix",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.videoImageMix"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateVideo"),
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [/* @__PURE__ */ jsx(Card, {
				className: "p-3",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsx(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: t("compose.mediaFiles")
					}), /* @__PURE__ */ jsx("p", {
						className: "text-xs text-muted-foreground",
						children: t("compose.connectImageVideo")
					})]
				})
			}), /* @__PURE__ */ jsx(NodeTextarea, {
				label: t("compose.generatePromptLabel"),
				icon: Sparkles,
				placeholder: t("compose.generatePromptPlaceholder"),
				...form.bind("text"),
				rows: 4
			})]
		})
	});
};
var video_image_gen_video_mix_default = memo(VideoImageGenVideoMixNode);
//#endregion
//#region src/canvas/nodes/compose/video-image-gen-video-move.tsx
const VideoImageGenVideoMoveNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tActions = useTranslations("Workspace.nodes.actions");
	const form = useAbiForm("video-image-gen-video-move");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("video-image-gen-video-move");
	const { hasImage, hasVideo, imageFileKey, videoFileKey, promptText } = useMemo(() => {
		if (!nodeId) return {
			hasImage: false,
			hasVideo: false,
			imageFileKey: void 0,
			videoFileKey: void 0,
			promptText: ""
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		const text = typeof values.text === "string" ? values.text.trim() : "";
		const image = typeof values.image === "string" ? values.image : void 0;
		const video = typeof values.video === "string" ? values.video : void 0;
		return {
			hasImage: Boolean(image),
			hasVideo: Boolean(video),
			imageFileKey: image,
			videoFileKey: video,
			promptText: text
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	const manualText = form.state.text?.trim() ?? "";
	const effectiveText = promptText || manualText;
	const durationSeconds = form.state.duration ?? 5;
	useEffect(() => {
		if (form.state.duration === void 0) form.set("duration", 5, { history: false });
	}, [form.state.duration, form.set]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "video-image-gen-video-move",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.videoImageMove"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: tActions("motionTrackGenerate"),
		executeDisabled: !hasImage || !hasVideo || !effectiveText,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [
							/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("compose.inputData")
							}),
							/* @__PURE__ */ jsx("p", {
								className: "text-xs text-muted-foreground",
								children: t("motionTrack.connectHint")
							}),
							/* @__PURE__ */ jsxs("div", {
								className: "flex flex-wrap gap-4",
								children: [
									imageFileKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
										fileKey: imageFileKey,
										label: t("compose.image"),
										type: "image"
									}) : /* @__PURE__ */ jsxs("div", {
										className: "flex flex-col items-center gap-1.5",
										children: [/* @__PURE__ */ jsx("div", {
											className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
											children: /* @__PURE__ */ jsx("div", {
												className: "flex items-center justify-center h-full w-full bg-blue-50",
												children: /* @__PURE__ */ jsx(ImageIcon, { className: "w-6 h-6 text-blue-600" })
											})
										}), /* @__PURE__ */ jsx("div", {
											className: "px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded",
											children: t("compose.image")
										})]
									}),
									videoFileKey ? /* @__PURE__ */ jsx(MediaThumbnail, {
										fileKey: videoFileKey,
										label: t("motionTrack.controlVideo"),
										type: "video"
									}) : /* @__PURE__ */ jsxs("div", {
										className: "flex flex-col items-center gap-1.5",
										children: [/* @__PURE__ */ jsx("div", {
											className: "relative w-16 h-16 rounded-md border-2 border-gray-300 overflow-hidden bg-gray-100",
											children: /* @__PURE__ */ jsx("div", {
												className: "flex items-center justify-center h-full w-full bg-purple-50",
												children: /* @__PURE__ */ jsx(Video, { className: "w-6 h-6 text-purple-600" })
											})
										}), /* @__PURE__ */ jsx("div", {
											className: "px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded",
											children: t("motionTrack.controlVideo")
										})]
									}),
									/* @__PURE__ */ jsxs("div", {
										className: "flex flex-col items-center gap-1.5",
										children: [/* @__PURE__ */ jsx("div", {
											className: `relative w-16 h-16 rounded-md border-2 overflow-hidden ${effectiveText ? "border-green-400" : "border-gray-300"}`,
											children: /* @__PURE__ */ jsx("div", {
												className: "flex items-center justify-center h-full w-full bg-green-50",
												children: /* @__PURE__ */ jsx(Type, { className: "w-6 h-6 text-green-600" })
											})
										}), /* @__PURE__ */ jsx("div", {
											className: "px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded",
											children: t("compose.text")
										})]
									})
								]
							}),
							(!hasImage || !hasVideo || !effectiveText) && /* @__PURE__ */ jsx("p", {
								className: "text-xs text-red-500",
								children: t("motionTrack.missing", { items: [
									!hasImage && t("compose.image"),
									!hasVideo && t("motionTrack.controlVideo"),
									!effectiveText && t("compose.text")
								].filter(Boolean).join("、") })
							})
						]
					})
				}),
				promptText ? /* @__PURE__ */ jsx(Card, {
					className: "p-3 bg-muted/50",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs(Label, {
							className: "text-sm font-medium text-muted-foreground",
							children: [t("compose.generatePromptLabel"), t("imageEdit.fromUpstream")]
						}), /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-4",
							children: promptText
						})]
					})
				}) : /* @__PURE__ */ jsx(NodeTextarea, {
					label: t("compose.generatePromptLabel"),
					icon: Sparkles,
					placeholder: t("motionTrack.promptPlaceholder"),
					...form.bind("text"),
					rows: 4
				}),
				/* @__PURE__ */ jsx("p", {
					className: "text-xs text-muted-foreground",
					children: t("motionTrack.promptHint")
				}),
				/* @__PURE__ */ jsx(VideoDurationSlider, {
					value: durationSeconds,
					onChange: (dur) => form.set("duration", dur)
				})
			]
		})
	});
};
VideoImageGenVideoMoveNode.displayName = "VideoImageGenVideoMoveNode";
var video_image_gen_video_move_default = memo(VideoImageGenVideoMoveNode);
//#endregion
//#region src/canvas/nodes/decompose/music-brief.tsx
function buildLanguageOptions$1(tLang) {
	return [
		{
			value: "zh",
			label: tLang("zh")
		},
		{
			value: "en",
			label: tLang("en")
		},
		{
			value: "cantonese",
			label: tLang("yue")
		},
		{
			value: "ja",
			label: tLang("ja")
		},
		{
			value: "ko",
			label: tLang("ko")
		},
		{
			value: "fr",
			label: tLang("fr")
		},
		{
			value: "es",
			label: tLang("es")
		}
	];
}
const MusicBriefNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const LANGUAGE_OPTIONS = buildLanguageOptions$1(useTranslations("Languages"));
	const form = useAbiForm("music-brief");
	const nodeId = useNodeId();
	const edges = useStore((state) => state.edges);
	const textSourceId = useMemo(() => {
		if (!nodeId) return null;
		for (const e of edges) if (e.target === nodeId && e.targetHandle === "in:text") return e.source;
		return null;
	}, [edges, nodeId]);
	const upstreamIds = useMemo(() => textSourceId ? [textSourceId] : [], [textSourceId]);
	const upstreamNodes = useNodesData(upstreamIds);
	const textUpstream = useMemo(() => {
		if (!textSourceId) return null;
		const n = upstreamNodes.find((u) => u.id === textSourceId);
		if (!n || n.type !== "textNode") return null;
		return coerceBaseNodeData(n.data).texts?.[0] ?? "";
	}, [textSourceId, upstreamNodes]);
	const textLocal = form.state.text ?? "";
	const textDisplay = textUpstream !== null ? textUpstream : textLocal;
	const instrumental = form.state.instrumental ?? false;
	const language = form.state.language ?? "zh";
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "music-brief",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.musicBrief"),
		icon: /* @__PURE__ */ jsx(Lightbulb, { className: "h-5 w-5" }),
		executeLabel: t("actions.musicBrief"),
		executeDisabled: !textDisplay.trim(),
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [/* @__PURE__ */ jsxs(Card, {
				className: "p-3 nodrag",
				onPointerDown: (e) => e.stopPropagation(),
				children: [/* @__PURE__ */ jsx(Label, {
					className: "text-sm font-medium text-muted-foreground mb-2 block",
					children: t("musicTasks.promptLabel")
				}), /* @__PURE__ */ jsx(Textarea, {
					placeholder: textUpstream !== null ? t("music.fromUpstreamReadonly") : t("musicTasks.ideaPlaceholder"),
					value: textDisplay,
					onChange: (e) => form.set("text", e.target.value),
					readOnly: textUpstream !== null,
					rows: 3,
					className: "max-h-[120px] resize-none text-xs whitespace-pre-wrap break-words overflow-auto"
				})]
			}), /* @__PURE__ */ jsx(Card, {
				className: "p-3 nodrag",
				onPointerDown: (e) => e.stopPropagation(),
				children: /* @__PURE__ */ jsxs("div", {
					className: "grid grid-cols-2 gap-3",
					children: [/* @__PURE__ */ jsxs("div", {
						className: "flex items-center gap-2",
						children: [/* @__PURE__ */ jsx(Switch, {
							id: "music-brief-instrumental",
							checked: instrumental,
							onCheckedChange: (v) => form.set("instrumental", v)
						}), /* @__PURE__ */ jsx(Label, {
							htmlFor: "music-brief-instrumental",
							className: "text-xs text-muted-foreground",
							children: t("musicTasks.instrumental")
						})]
					}), /* @__PURE__ */ jsxs("div", {
						className: "space-y-1.5",
						children: [/* @__PURE__ */ jsx(Label, {
							className: "text-xs text-muted-foreground",
							children: t("music.language")
						}), /* @__PURE__ */ jsxs(Select, {
							value: language,
							onValueChange: (v) => form.set("language", v),
							children: [/* @__PURE__ */ jsx(SelectTrigger, {
								className: "h-8 text-xs",
								children: /* @__PURE__ */ jsx(SelectValue, {})
							}), /* @__PURE__ */ jsx(SelectContent, { children: LANGUAGE_OPTIONS.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
								value: opt.value,
								className: "text-xs",
								children: opt.label
							}, opt.value)) })]
						})]
					})]
				})
			})]
		})
	});
};
MusicBriefNode.displayName = "MusicBriefNode";
var music_brief_default = memo(MusicBriefNode);
//#endregion
//#region src/canvas/nodes/decompose/separate-sound.tsx
const SeparateSoundNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("separate-sound");
	const nodeId = useNodeId();
	const edges = useStore((state) => state.edges);
	const { audioConnected, textConnected } = useMemo(() => ({
		audioConnected: !!nodeId && edges.some((e) => e.target === nodeId && e.targetHandle === "in:audio"),
		textConnected: !!nodeId && edges.some((e) => e.target === nodeId && e.targetHandle === "in:text")
	}), [edges, nodeId]);
	const text = form.state.text ?? "";
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "separate-sound",
		form,
		selected,
		className: "min-w-[420px]",
		data,
		title: t("titles.separateSound"),
		icon: /* @__PURE__ */ jsx(AudioLines, { className: "h-5 w-5" }),
		executeLabel: t("actions.separateSound"),
		executeDisabled: !audioConnected || !textConnected && !text.trim(),
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx(Card, {
				className: "p-3 nodrag",
				onPointerDown: (e) => e.stopPropagation(),
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-1.5",
					children: [/* @__PURE__ */ jsx(Label, {
						className: "text-xs text-muted-foreground",
						children: t("separateSound.textLabel")
					}), /* @__PURE__ */ jsx(NodeTextarea, {
						showCard: false,
						placeholder: t("separateSound.textPlaceholder"),
						...form.bind("text"),
						rows: 3
					})]
				})
			})
		})
	});
};
SeparateSoundNode.displayName = "SeparateSoundNode";
var separate_sound_default = memo(SeparateSoundNode);
//#endregion
//#region src/canvas/nodes/decompose/split-text.tsx
const SplitTextNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tBase = useTranslations("Workspace.nodes.base");
	const form = useAbiForm("split-text");
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "split-text",
		form,
		selected,
		data,
		title: t("titles.splitText"),
		icon: /* @__PURE__ */ jsx(Scissors, { className: "h-5 w-5" }),
		executeLabel: tBase("execute"),
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx(NodeTextarea, {
				rows: 3,
				placeholder: t("common.enterInstructions"),
				...form.bind("userPrompt"),
				className: "min-h-[80px]"
			})
		})
	});
};
var split_text_default = memo(SplitTextNode);
//#endregion
//#region src/canvas/nodes/decompose/split-video.tsx
const SplitVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("split-video");
	const fileKeys = data.fileKeys;
	useEffect(() => {
		if (form.state.threshold == null) form.set("threshold", 20, { history: false });
	}, []);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "split-video",
		form,
		selected,
		data,
		title: t("titles.splitVideo"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("compose.startSlice"),
		executeDisabled: !fileKeys?.length
	});
};
var split_video_default = memo(SplitVideoNode);
//#endregion
//#region src/canvas/ui/waterfall.tsx
/**
* Waterfall component
* A high-performance virtualized waterfall layout component based on the masonic library
*
* @example
* ```tsx
* <Waterfall
*   items={items}
*   render={MasonryCard}
*   columnWidth={240}
*   columnGutter={16}
*   rowGutter={16}
* />
* ```
*/
const Waterfall = React$1.forwardRef(({ items, render: RenderComponent, columnWidth = 240, columnGutter = 0, rowGutter = columnGutter, maxColumnCount, maxColumnWidth, className, style, itemHeightEstimate = 300, onRender, itemKey }, ref) => {
	return /* @__PURE__ */ jsx("div", {
		ref,
		className,
		style,
		children: /* @__PURE__ */ jsx(Masonry, {
			items,
			render: RenderComponent,
			columnWidth,
			columnGutter,
			rowGutter,
			maxColumnCount,
			maxColumnWidth,
			itemHeightEstimate,
			onRender,
			itemKey
		})
	});
});
Waterfall.displayName = "Waterfall";
//#endregion
//#region src/canvas/nodes/modality/modality-placeholder.tsx
const MODALITY_META = {
	image: {
		icon: Image$1,
		labelKey: "image"
	},
	video: {
		icon: Video,
		labelKey: "video"
	},
	audio: {
		icon: Music,
		labelKey: "audio"
	},
	text: {
		icon: Type,
		labelKey: "text"
	},
	file: {
		icon: FileText,
		labelKey: "file"
	},
	model: {
		icon: Box,
		labelKey: "model3D"
	}
};
/**
* Neutral, on-brand placeholder shown when a modality node's content cannot be
* displayed (missing key, load failure, or invalid asset). Communicates which
* modality belongs here via a muted icon and label.
*/
function ModalityPlaceholder({ modality, variant = "card", className }) {
	const t = useTranslations("Workspace.nodes.modal");
	const { icon: Icon, labelKey } = MODALITY_META[modality];
	if (variant === "thumb") return /* @__PURE__ */ jsx("div", {
		className: `flex h-full w-full flex-col items-center justify-center gap-1 bg-gray-50 text-gray-300 dark:bg-slate-800/60 dark:text-gray-600 ${className ?? ""}`,
		children: /* @__PURE__ */ jsx(Icon, {
			className: "h-6 w-6",
			strokeWidth: 1.5
		})
	});
	return /* @__PURE__ */ jsxs("div", {
		className: `flex w-full select-none flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center dark:border-gray-700 dark:bg-slate-800/60 ${className ?? ""}`,
		children: [
			/* @__PURE__ */ jsx(Icon, {
				className: "h-10 w-10 text-gray-300 dark:text-gray-600",
				strokeWidth: 1.5
			}),
			/* @__PURE__ */ jsx("div", {
				className: "text-sm font-medium text-gray-500 dark:text-gray-400",
				children: t(labelKey)
			}),
			/* @__PURE__ */ jsx("div", {
				className: "text-xs text-gray-400 dark:text-gray-500",
				children: t("unavailable")
			})
		]
	});
}
//#endregion
//#region src/canvas/nodes/modality/audio-node.tsx
const FullScreenAudioModal = ({ fileKey, onClose }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const [mounted, setMounted] = useState(false);
	const { url } = useFileAsyncLoader(fileKey, { priority: "high" });
	useEffect(() => {
		setMounted(true);
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);
	if (!mounted) return null;
	const content = /* @__PURE__ */ jsx("div", {
		className: "fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-11/12 h-5/6 max-h-screen flex flex-col",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0",
				children: [/* @__PURE__ */ jsx("h2", {
					className: "text-lg font-semibold text-gray-900 dark:text-white",
					children: t("audioPreview")
				}), /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "ghost",
					onClick: onClose,
					className: "hover:bg-gray-100 dark:hover:bg-slate-800",
					children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "flex-1 flex flex-col items-center justify-center bg-white dark:bg-slate-800 overflow-auto p-8",
				children: url ? /* @__PURE__ */ jsx("audio", {
					src: url,
					controls: true,
					className: "w-full max-w-2xl",
					autoPlay: true,
					children: "Your browser does not support the audio tag."
				}) : /* @__PURE__ */ jsx("div", {
					className: "text-gray-500 dark:text-gray-400",
					children: "Loading..."
				})
			})]
		})
	});
	return createPortal(content, document.body);
};
const FullScreenWaterfallAudioModal = ({ audioKeys, onClose }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const [mounted, setMounted] = useState(false);
	const { urls } = useFileAsyncLoaderBatch(audioKeys, { priority: "normal" });
	useEffect(() => {
		setMounted(true);
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);
	if (!mounted) return null;
	const AudioThumbnail = memo(({ data: fileKey }) => {
		const url = urls.get(fileKey);
		return /* @__PURE__ */ jsxs("div", {
			className: "relative aspect-square overflow-hidden rounded-md border border-gray-300 dark:border-gray-600 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 shadow-md hover:shadow-lg transition-shadow cursor-pointer",
			children: [url && /* @__PURE__ */ jsx("audio", {
				src: url,
				className: "hidden",
				preload: "metadata"
			}), /* @__PURE__ */ jsxs("div", {
				className: "flex h-full w-full flex-col items-center justify-center p-2 text-center",
				children: [/* @__PURE__ */ jsx(Music, { className: "h-8 w-8 text-blue-600 dark:text-blue-400" }), /* @__PURE__ */ jsx("span", {
					className: "mt-2 text-xs text-blue-700 dark:text-blue-300",
					children: url ? "Audio" : "Loading..."
				})]
			})]
		});
	});
	const content = /* @__PURE__ */ jsx("div", {
		className: "fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-11/12 h-5/6 max-h-screen flex flex-col",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0",
				children: [/* @__PURE__ */ jsx("h2", {
					className: "text-lg font-semibold text-gray-900 dark:text-white",
					children: t("audios", { count: audioKeys.length })
				}), /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "ghost",
					onClick: onClose,
					className: "hover:bg-gray-100 dark:hover:bg-slate-800",
					children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "flex-1 bg-white dark:bg-slate-800 overflow-auto p-6",
				children: /* @__PURE__ */ jsx(Waterfall, {
					items: audioKeys.map((key) => ({
						id: key,
						key
					})),
					render: ({ data: { key } }) => /* @__PURE__ */ jsx(AudioThumbnail, { data: key }),
					columnWidth: 200,
					columnGutter: 12,
					rowGutter: 12,
					itemKey: (data) => data.id,
					maxColumnCount: 6
				})
			})]
		})
	});
	return createPortal(content, document.body);
};
const AudioNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const keys = data.fileKeys ?? [];
	useNodeId();
	const [isFullScreen, setIsFullScreen] = useState(false);
	const [isWaterfallFullScreen, setIsWaterfallFullScreen] = useState(false);
	const [audioError, setAudioError] = useState(false);
	const singleAudioRef = useRef(null);
	const thumbnailAudioRefs = useRef([]);
	const { url: singleAudioUrl } = useFileAsyncLoader(keys[0], { priority: "high" });
	const { urls: batchUrls } = useFileAsyncLoaderBatch(keys.slice(0, 6), { priority: "normal" });
	const isSingle = keys.length === 1;
	const count = keys.length;
	useEffect(() => {
		setAudioError(false);
	}, [singleAudioUrl]);
	const handleDownload = (url, fileKey) => {
		const filename = `audio.${fileKey.includes(".") ? fileKey.split(".").pop() : "mp3"}`;
		const downloadUrl = `${url}${url.includes("?") ? "&" : "?"}download=${filename}`;
		window.open(downloadUrl, "_blank");
	};
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsxs(BaseNodeShell, {
			selected,
			count,
			children: [
				/* @__PURE__ */ jsx(Handle, {
					type: "target",
					position: Position.Left,
					id: "in:audioNode"
				}),
				/* @__PURE__ */ jsx(Handle, {
					type: "source",
					position: Position.Right,
					id: "out:audioNode"
				}),
				/* @__PURE__ */ jsxs(NodeHeader, { children: [
					/* @__PURE__ */ jsx(NodeHeaderIcon, { children: /* @__PURE__ */ jsx(Music, {}) }),
					/* @__PURE__ */ jsx(NodeHeaderTitle, { children: isSingle ? t("audio") : t("audios", { count }) }),
					/* @__PURE__ */ jsxs(NodeHeaderActions, { children: [
						isSingle && /* @__PURE__ */ jsx(Button, {
							size: "sm",
							variant: "ghost",
							onClick: () => setIsFullScreen(true),
							title: t("fullScreenPreview"),
							children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
						}),
						!isSingle && /* @__PURE__ */ jsx(Button, {
							size: "sm",
							variant: "ghost",
							onClick: () => setIsWaterfallFullScreen(true),
							title: t("fullScreenWaterfall"),
							children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
						}),
						/* @__PURE__ */ jsx(NodeHeaderComboAction, { onClick: () => logger.debug("compose mode toggle") }),
						/* @__PURE__ */ jsxs(NodeHeaderMenuAction, {
							label: t("moreOptions"),
							children: [/* @__PURE__ */ jsx(DropdownMenuLabel, { children: t("actions") }), isSingle && singleAudioUrl && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(DropdownMenuSeparator, {}), /* @__PURE__ */ jsxs(DropdownMenuItem, {
								onClick: () => handleDownload(singleAudioUrl, keys[0]),
								children: [/* @__PURE__ */ jsx(Download, { className: "h-4 w-4 mr-2" }), t("download")]
							})] })]
						})
					] })
				] }),
				isSingle ? /* @__PURE__ */ jsx("div", {
					className: "px-3 pb-3 nodrag",
					onPointerDown: (e) => e.stopPropagation(),
					children: audioError ? /* @__PURE__ */ jsx(ModalityPlaceholder, { modality: "audio" }) : singleAudioUrl ? /* @__PURE__ */ jsx("audio", {
						ref: singleAudioRef,
						src: singleAudioUrl,
						controls: true,
						controlsList: "nodownload",
						className: "w-full",
						preload: "metadata",
						onError: () => setAudioError(true),
						onMouseEnter: () => singleAudioRef.current?.play(),
						onMouseLeave: () => singleAudioRef.current?.pause(),
						children: "Your browser does not support the audio tag."
					}) : /* @__PURE__ */ jsx("div", {
						className: "w-full h-10 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-sm",
						children: t("loading")
					})
				}) : /* @__PURE__ */ jsx("div", {
					className: "w-full p-2 nodrag",
					onPointerDown: (e) => e.stopPropagation(),
					children: /* @__PURE__ */ jsx("div", {
						className: "grid grid-cols-3 gap-2",
						children: keys.slice(0, 6).map((key, index) => {
							const isLastAndMore = index === 5 && count > 6;
							const remainingCount = count - 6;
							const url = batchUrls.get(key);
							return isLastAndMore ? /* @__PURE__ */ jsx("div", {
								className: "relative aspect-square overflow-hidden rounded-md border border-gray-300 dark:border-gray-600 bg-gray-200 dark:bg-slate-700 shadow-sm flex items-center justify-center",
								children: /* @__PURE__ */ jsxs("div", {
									className: "text-center",
									children: [/* @__PURE__ */ jsxs("div", {
										className: "text-3xl font-bold text-gray-700 dark:text-gray-300",
										children: ["+", remainingCount]
									}), /* @__PURE__ */ jsx("div", {
										className: "text-xs text-gray-600 dark:text-gray-400 mt-1",
										children: t("more")
									})]
								})
							}, `more-${key}`) : /* @__PURE__ */ jsxs("div", {
								className: "relative aspect-square overflow-hidden rounded-md border border-gray-300 dark:border-gray-600 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 shadow-sm",
								onMouseEnter: () => thumbnailAudioRefs.current[index]?.play(),
								onMouseLeave: () => thumbnailAudioRefs.current[index]?.pause(),
								children: [url && /* @__PURE__ */ jsx("audio", {
									ref: (el) => {
										thumbnailAudioRefs.current[index] = el;
									},
									src: url,
									className: "hidden",
									preload: "metadata"
								}), /* @__PURE__ */ jsxs("div", {
									className: "flex h-full w-full flex-col items-center justify-center p-2 text-center",
									children: [/* @__PURE__ */ jsx(Music, { className: "h-8 w-8 text-blue-600 dark:text-blue-400" }), /* @__PURE__ */ jsx("span", {
										className: "mt-1 text-xs text-blue-700 dark:text-blue-300",
										children: url ? `${t("audio")} ${index + 1}` : t("loading")
									})]
								})]
							}, key);
						})
					})
				})
			]
		}),
		isFullScreen && isSingle && keys[0] && /* @__PURE__ */ jsx(FullScreenAudioModal, {
			fileKey: keys[0],
			onClose: () => setIsFullScreen(false)
		}),
		isWaterfallFullScreen && !isSingle && /* @__PURE__ */ jsx(FullScreenWaterfallAudioModal, {
			audioKeys: keys,
			onClose: () => setIsWaterfallFullScreen(false)
		})
	] });
};
const areEqual$5 = (prevProps, nextProps) => {
	const prevFileKeys = prevProps.data.fileKeys || [];
	const nextFileKeys = nextProps.data.fileKeys || [];
	return prevProps.selected === nextProps.selected && JSON.stringify(prevFileKeys) === JSON.stringify(nextFileKeys);
};
AudioNode.displayName = "AudioNode";
var audio_node_default = memo(AudioNode, areEqual$5);
//#endregion
//#region src/canvas/nodes/modality/file-node.tsx
const getFileIcon = (fileKey) => {
	const ext = fileKey.split(".").pop()?.toLowerCase() || "";
	if (["pdf"].includes(ext)) return /* @__PURE__ */ jsx(FileText, { className: "h-8 w-8 text-red-500" });
	if (["doc", "docx"].includes(ext)) return /* @__PURE__ */ jsx(FileText, { className: "h-8 w-8 text-blue-500" });
	if (["txt"].includes(ext)) return /* @__PURE__ */ jsx(FileText, { className: "h-8 w-8 text-gray-500" });
	if ([
		"xls",
		"xlsx",
		"csv"
	].includes(ext)) return /* @__PURE__ */ jsx(FileSpreadsheet, { className: "h-8 w-8 text-green-500" });
	if (["ppt", "pptx"].includes(ext)) return /* @__PURE__ */ jsx(Presentation, { className: "h-8 w-8 text-orange-500" });
	if ([
		"zip",
		"rar",
		"7z",
		"tar",
		"gz"
	].includes(ext)) return /* @__PURE__ */ jsx(FileArchive, { className: "h-8 w-8 text-purple-500" });
	if ([
		"json",
		"xml",
		"html",
		"css",
		"js",
		"ts",
		"py"
	].includes(ext)) return /* @__PURE__ */ jsx(FileCode, { className: "h-8 w-8 text-cyan-500" });
	return /* @__PURE__ */ jsx(File$1, { className: "h-8 w-8 text-gray-400" });
};
const isOfficeFile = (fileKey) => {
	const ext = fileKey.split(".").pop()?.toLowerCase() || "";
	return [
		"doc",
		"docx",
		"xls",
		"xlsx",
		"ppt",
		"pptx"
	].includes(ext);
};
const getFileOpenUrl = (fileUrl, fileKey) => {
	fileKey.split(".").pop()?.toLowerCase();
	if (isOfficeFile(fileKey)) return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
	return fileUrl;
};
const FileItem = ({ fileKey, onFileClick }) => {
	const { url } = useFileAsyncLoader(fileKey, { priority: "high" });
	const handleClick = () => {
		if (url) onFileClick(fileKey, url);
	};
	return /* @__PURE__ */ jsxs("div", {
		className: "flex flex-col items-center gap-2 cursor-pointer group",
		onClick: handleClick,
		title: fileKey,
		children: [/* @__PURE__ */ jsx("div", {
			className: "p-3 rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors",
			children: getFileIcon(fileKey)
		}), /* @__PURE__ */ jsx("p", {
			className: "text-xs text-center text-gray-600 truncate max-w-[80px] group-hover:text-gray-900",
			children: fileKey.split("/").pop()
		})]
	});
};
const SingleFileDisplay = ({ fileKey, onFileClick }) => {
	const { url } = useFileAsyncLoader(fileKey, { priority: "high" });
	const handleClick = () => {
		if (url) onFileClick(fileKey, url);
	};
	return /* @__PURE__ */ jsxs("div", {
		className: "flex flex-col items-center gap-2",
		children: [/* @__PURE__ */ jsx("div", {
			className: "p-4 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer",
			onClick: handleClick,
			title: fileKey,
			children: getFileIcon(fileKey)
		}), /* @__PURE__ */ jsx("p", {
			className: "text-xs text-center text-gray-600 hover:text-gray-900 max-w-[120px] truncate",
			children: fileKey.split("/").pop()
		})]
	});
};
const FileNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const keys = data.fileKeys ?? [];
	const isSingle = keys.length === 1;
	const count = keys.length;
	const showMore = count > 9;
	const visibleFileCount = showMore ? 8 : Math.min(count, 9);
	const emptyTiles = Math.max(0, 9 - (showMore ? 9 : visibleFileCount));
	const handleFileClick = (fileKey, fileUrl) => {
		const openUrl = getFileOpenUrl(fileUrl, fileKey);
		window.open(openUrl, "_blank");
	};
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		count,
		children: [
			/* @__PURE__ */ jsx(Handle, {
				type: "target",
				position: Position.Left,
				id: "in:fileNode"
			}),
			/* @__PURE__ */ jsx(Handle, {
				type: "source",
				position: Position.Right,
				id: "out:fileNode"
			}),
			/* @__PURE__ */ jsxs(NodeHeader, { children: [
				/* @__PURE__ */ jsx(NodeHeaderIcon, { children: /* @__PURE__ */ jsx(File$1, {}) }),
				/* @__PURE__ */ jsx(NodeHeaderTitle, { children: isSingle ? t("file") : t("files", { count }) }),
				/* @__PURE__ */ jsxs(NodeHeaderActions, { children: [/* @__PURE__ */ jsx(NodeHeaderComboAction, { onClick: () => logger.debug("compose mode toggle") }), /* @__PURE__ */ jsx(NodeHeaderMenuAction, {
					label: t("moreOptions"),
					children: /* @__PURE__ */ jsx(DropdownMenuLabel, { children: t("actions") })
				})] })
			] }),
			/* @__PURE__ */ jsx("div", {
				className: "w-full p-4",
				children: count === 0 ? /* @__PURE__ */ jsx(ModalityPlaceholder, { modality: "file" }) : isSingle ? /* @__PURE__ */ jsx(SingleFileDisplay, {
					fileKey: keys[0],
					onFileClick: handleFileClick
				}) : /* @__PURE__ */ jsxs("div", {
					className: "grid grid-cols-3 gap-4",
					children: [
						keys.slice(0, visibleFileCount).map((fileKey, index) => /* @__PURE__ */ jsx(FileItem, {
							fileKey,
							onFileClick: handleFileClick
						}, index)),
						showMore && /* @__PURE__ */ jsx("div", {
							className: "flex flex-col items-center gap-2",
							children: /* @__PURE__ */ jsx("div", {
								className: "flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 w-16 h-16",
								children: /* @__PURE__ */ jsx("div", {
									className: "text-center",
									children: /* @__PURE__ */ jsxs("div", {
										className: "text-sm font-bold text-gray-600",
										children: ["+", count - 8]
									})
								})
							})
						}),
						Array.from({ length: emptyTiles }).map((_, index) => /* @__PURE__ */ jsx("div", { className: "w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 bg-gray-100 opacity-30" }, `empty-${index}`))
					]
				})
			})
		]
	});
};
const areEqual$4 = (prevProps, nextProps) => {
	const prevFileKeys = prevProps.data.fileKeys || [];
	const nextFileKeys = nextProps.data.fileKeys || [];
	return prevProps.selected === nextProps.selected && JSON.stringify(prevFileKeys) === JSON.stringify(nextFileKeys);
};
FileNode.displayName = "FileNode";
var file_node_default = memo(FileNode, areEqual$4);
//#endregion
//#region src/canvas/nodes/modality/image-node.tsx
const FullScreenImageModal = ({ fileKey, onClose }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const [mounted, setMounted] = useState(false);
	const { url } = useFileAsyncLoader(fileKey, { priority: "high" });
	useEffect(() => {
		setMounted(true);
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);
	if (!mounted) return null;
	const content = /* @__PURE__ */ jsx("div", {
		className: "fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-white rounded-lg shadow-2xl w-11/12 h-5/6 max-h-screen flex flex-col",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0",
				children: [/* @__PURE__ */ jsx("h2", {
					className: "text-lg font-semibold text-gray-900",
					children: t("imagePreview")
				}), /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "ghost",
					onClick: onClose,
					children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "flex-1 overflow-auto bg-white flex items-center justify-center",
				children: url ? /* @__PURE__ */ jsx("img", {
					src: url,
					alt: t("fullScreenPreview"),
					className: "max-w-full max-h-full object-contain"
				}) : /* @__PURE__ */ jsx("div", {
					className: "text-gray-500",
					children: t("loading")
				})
			})]
		})
	});
	return createPortal(content, document.body);
};
const FullScreenWaterfallImageModal = ({ imageKeys, onClose }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const [mounted, setMounted] = useState(false);
	const { urls } = useFileAsyncLoaderBatch(imageKeys, { priority: "normal" });
	useEffect(() => {
		setMounted(true);
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);
	if (!mounted) return null;
	const ImageThumbnail = memo(({ data: fileKey }) => {
		const url = urls.get(fileKey);
		return /* @__PURE__ */ jsx("div", {
			className: "relative overflow-hidden rounded-md border border-gray-300 bg-gray-200 shadow-md hover:shadow-lg transition-shadow cursor-pointer w-full h-full",
			children: url ? /* @__PURE__ */ jsx("img", {
				src: url,
				alt: t("image"),
				className: "h-full w-full object-cover"
			}) : /* @__PURE__ */ jsx("div", {
				className: "flex items-center justify-center h-full w-full bg-gray-300",
				children: /* @__PURE__ */ jsx("div", {
					className: "text-xs text-gray-500",
					children: t("loading")
				})
			})
		});
	});
	const content = /* @__PURE__ */ jsx("div", {
		className: "fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-white rounded-lg shadow-2xl w-11/12 h-5/6 max-h-screen flex flex-col",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0",
				children: [/* @__PURE__ */ jsx("h2", {
					className: "text-lg font-semibold text-gray-900",
					children: t("images", { count: imageKeys.length })
				}), /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "ghost",
					onClick: onClose,
					children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "flex-1 bg-white overflow-auto p-6",
				children: /* @__PURE__ */ jsx(Waterfall, {
					items: imageKeys.map((key) => ({
						id: key,
						key
					})),
					render: ({ data: { key } }) => /* @__PURE__ */ jsx(ImageThumbnail, { data: key }),
					columnWidth: 200,
					columnGutter: 12,
					rowGutter: 12,
					itemKey: (data) => data.id,
					maxColumnCount: 6
				})
			})]
		})
	});
	return createPortal(content, document.body);
};
const ImageGridThumb = ({ url, index, loadingLabel }) => {
	const [errored, setErrored] = useState(false);
	if (url && !errored) return /* @__PURE__ */ jsx("img", {
		src: url,
		alt: `Image ${index + 1}`,
		className: "h-full w-full object-cover",
		onError: () => setErrored(true)
	});
	if (errored) return /* @__PURE__ */ jsx(ModalityPlaceholder, {
		modality: "image",
		variant: "thumb"
	});
	return /* @__PURE__ */ jsx("div", {
		className: "h-full w-full flex items-center justify-center bg-gray-300",
		children: /* @__PURE__ */ jsx("div", {
			className: "text-xs text-gray-500",
			children: loadingLabel
		})
	});
};
const ImageNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const keys = data.fileKeys ?? [];
	const [isFullScreen, setIsFullScreen] = useState(false);
	const [isWaterfallFullScreen, setIsWaterfallFullScreen] = useState(false);
	const [imageDimensions, setImageDimensions] = useState(null);
	const [imageError, setImageError] = useState(false);
	const { url: singleImageUrl } = useFileAsyncLoader(keys[0], { priority: "high" });
	const { urls: batchUrls } = useFileAsyncLoaderBatch(keys.slice(0, 6), { priority: "normal" });
	useEffect(() => {
		if (!singleImageUrl) {
			setImageDimensions(null);
			setImageError(false);
			return;
		}
		setImageError(false);
		const img = new Image();
		img.onload = () => {
			setImageDimensions({
				width: img.naturalWidth,
				height: img.naturalHeight
			});
		};
		img.onerror = () => {
			setImageDimensions(null);
			setImageError(true);
		};
		img.src = singleImageUrl;
	}, [singleImageUrl]);
	const isSingle = keys.length === 1;
	const count = keys.length;
	const mediaNodeWidthPx = isSingle && imageDimensions ? proportionalMediaNodeWidthPx(imageDimensions.width, imageDimensions.height) : void 0;
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsxs(BaseNodeShell, {
			selected,
			count,
			className: mediaNodeWidthPx != null ? "min-w-0 max-w-none" : void 0,
			style: mediaNodeWidthPx != null ? { width: mediaNodeWidthPx } : void 0,
			children: [
				/* @__PURE__ */ jsx(Handle, {
					type: "target",
					position: Position.Left,
					id: "in:imageNode"
				}),
				/* @__PURE__ */ jsx(Handle, {
					type: "source",
					position: Position.Right,
					id: "out:imageNode"
				}),
				/* @__PURE__ */ jsxs(NodeHeader, { children: [
					/* @__PURE__ */ jsx(NodeHeaderIcon, { children: /* @__PURE__ */ jsx(Image$1, {}) }),
					/* @__PURE__ */ jsx(NodeHeaderTitle, { children: isSingle ? t("image") : t("images", { count }) }),
					/* @__PURE__ */ jsxs(NodeHeaderActions, { children: [
						isSingle && /* @__PURE__ */ jsx(Button, {
							size: "sm",
							variant: "ghost",
							onClick: () => setIsFullScreen(true),
							title: t("fullScreenPreview"),
							children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
						}),
						!isSingle && /* @__PURE__ */ jsx(Button, {
							size: "sm",
							variant: "ghost",
							onClick: () => setIsWaterfallFullScreen(true),
							title: t("fullScreenWaterfall"),
							children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
						}),
						/* @__PURE__ */ jsx(NodeHeaderComboAction, { onClick: () => logger.debug("compose mode toggle") }),
						/* @__PURE__ */ jsx(NodeHeaderMenuAction, {
							label: t("moreOptions"),
							children: /* @__PURE__ */ jsx(DropdownMenuLabel, { children: t("actions") })
						})
					] })
				] }),
				isSingle ? /* @__PURE__ */ jsxs("div", {
					className: "relative w-full",
					children: [imageError ? /* @__PURE__ */ jsx(ModalityPlaceholder, { modality: "image" }) : singleImageUrl ? /* @__PURE__ */ jsx("img", {
						src: singleImageUrl,
						alt: "Image content",
						className: "w-full h-auto object-contain",
						onError: () => setImageError(true)
					}) : /* @__PURE__ */ jsx("div", {
						className: "w-full bg-gray-200 flex items-center justify-center text-gray-500 py-16",
						children: t("loading")
					}), imageDimensions && /* @__PURE__ */ jsxs("div", {
						className: "absolute bottom-2 right-2 text-xs text-white bg-black/50 px-2 py-1 rounded",
						children: [
							imageDimensions.width,
							" ×",
							" ",
							imageDimensions.height
						]
					})]
				}) : /* @__PURE__ */ jsx("div", {
					className: "w-full p-2",
					children: /* @__PURE__ */ jsx("div", {
						className: "grid grid-cols-3 gap-2",
						children: keys.slice(0, 6).map((key, index) => {
							const isLastAndMore = index === 5 && count > 6;
							const remainingCount = count - 6;
							const url = batchUrls.get(key);
							return isLastAndMore ? /* @__PURE__ */ jsx("div", {
								className: "relative aspect-square overflow-hidden rounded-md border border-gray-300 bg-gray-200 shadow-sm flex items-center justify-center",
								children: /* @__PURE__ */ jsxs("div", {
									className: "text-center",
									children: [/* @__PURE__ */ jsxs("div", {
										className: "text-3xl font-bold text-gray-700",
										children: ["+", remainingCount]
									}), /* @__PURE__ */ jsx("div", {
										className: "text-xs text-gray-600 mt-1",
										children: t("more")
									})]
								})
							}, `more-${key}`) : /* @__PURE__ */ jsx("div", {
								className: "relative aspect-square overflow-hidden rounded-md border border-gray-300 bg-gray-200 shadow-sm",
								children: /* @__PURE__ */ jsx(ImageGridThumb, {
									url,
									index,
									loadingLabel: t("loading")
								})
							}, key);
						})
					})
				})
			]
		}),
		isFullScreen && isSingle && keys[0] && /* @__PURE__ */ jsx(FullScreenImageModal, {
			fileKey: keys[0],
			onClose: () => setIsFullScreen(false)
		}),
		isWaterfallFullScreen && !isSingle && /* @__PURE__ */ jsx(FullScreenWaterfallImageModal, {
			imageKeys: keys,
			onClose: () => setIsWaterfallFullScreen(false)
		})
	] });
};
const areEqual$3 = (prevProps, nextProps) => {
	const prevFileKeys = prevProps.data.fileKeys || [];
	const nextFileKeys = nextProps.data.fileKeys || [];
	return prevProps.selected === nextProps.selected && JSON.stringify(prevFileKeys) === JSON.stringify(nextFileKeys);
};
ImageNode.displayName = "ImageNode";
var image_node_default = memo(ImageNode, areEqual$3);
//#endregion
//#region src/canvas/nodes/modality/link-node.tsx
const LinkCard = ({ url }) => /* @__PURE__ */ jsx("div", {
	className: "rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:shadow-sm transition-all bg-white dark:bg-slate-900",
	title: url,
	children: /* @__PURE__ */ jsxs("div", {
		className: "flex items-center gap-2",
		children: [/* @__PURE__ */ jsx(Link, { className: "h-4 w-4 shrink-0 text-primary" }), /* @__PURE__ */ jsx("a", {
			href: url,
			target: "_blank",
			rel: "noreferrer",
			className: "text-primary text-xs hover:underline break-all line-clamp-2",
			onClick: (e) => e.stopPropagation(),
			children: url
		})]
	})
});
const LinkNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const urls = data.texts ?? [];
	const count = urls.length;
	const isSingle = count === 1;
	return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		count,
		children: [
			/* @__PURE__ */ jsx(Handle, {
				type: "target",
				position: Position.Left,
				id: "in:linkNode"
			}),
			/* @__PURE__ */ jsx(Handle, {
				type: "source",
				position: Position.Right,
				id: "out:linkNode"
			}),
			/* @__PURE__ */ jsxs(NodeHeader, { children: [
				/* @__PURE__ */ jsx(NodeHeaderIcon, { children: /* @__PURE__ */ jsx(Link, {}) }),
				/* @__PURE__ */ jsx(NodeHeaderTitle, { children: isSingle ? t("link") : t("links", { count }) }),
				/* @__PURE__ */ jsxs(NodeHeaderActions, { children: [/* @__PURE__ */ jsx(NodeHeaderComboAction, { onClick: () => {} }), /* @__PURE__ */ jsx(NodeHeaderMenuAction, {
					label: t("moreOptions"),
					children: /* @__PURE__ */ jsx(DropdownMenuLabel, { children: t("actions") })
				})] })
			] }),
			/* @__PURE__ */ jsx("div", {
				className: "w-full p-4",
				children: count === 0 ? /* @__PURE__ */ jsxs("div", {
					className: "flex w-full select-none flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center dark:border-gray-700 dark:bg-slate-800/60",
					children: [/* @__PURE__ */ jsx(Link, {
						className: "h-10 w-10 text-gray-300 dark:text-gray-600",
						strokeWidth: 1.5
					}), /* @__PURE__ */ jsx("div", {
						className: "text-sm font-medium text-gray-500 dark:text-gray-400",
						children: t("link")
					})]
				}) : /* @__PURE__ */ jsx("div", {
					className: "grid grid-cols-1 gap-2 min-w-[280px]",
					children: urls.map((url, idx) => /* @__PURE__ */ jsx(LinkCard, { url }, idx))
				})
			})
		]
	});
};
const areEqual$2 = (prev, next) => prev.selected === next.selected && JSON.stringify(prev.data.texts ?? []) === JSON.stringify(next.data.texts ?? []);
LinkNode.displayName = "LinkNode";
var link_node_default = memo(LinkNode, areEqual$2);
//#endregion
//#region src/canvas/nodes/modality/model-node.tsx
const ANIMATION_FPS = 30;
const getMixer = (model) => (model?.userData)?.mixer;
const computeObjectBounds = (model) => {
	const box = new THREE.Box3().setFromObject(model);
	if (!box.isEmpty()) return box;
	const splat = model;
	if (typeof splat.forEachSplat === "function") splat.forEachSplat((_i, center) => {
		box.expandByPoint(center);
	});
	return box;
};
const autoScaleAndCenter = (model, camera, containerWidth, containerHeight) => {
	const box = computeObjectBounds(model);
	if (box.isEmpty()) return;
	const size = box.getSize(new THREE.Vector3());
	const center = box.getCenter(new THREE.Vector3());
	const maxDim = Math.max(size.x, size.y, size.z);
	const targetSize = 8;
	let scale = 1;
	if (maxDim > 0) {
		scale = targetSize / maxDim;
		model.scale.multiplyScalar(scale);
	}
	const newCenter = model.position.clone().add(center.clone().sub(model.position).multiplyScalar(scale));
	model.position.sub(newCenter);
	camera.aspect = containerWidth / containerHeight;
	const halfFov = camera.fov * (Math.PI / 180) / 2;
	const halfH = size.y * scale / 2;
	const halfW = size.x * scale / 2;
	const halfD = size.z * scale / 2;
	const cameraZ = (Math.max(halfH / Math.tan(halfFov), halfW / (Math.tan(halfFov) * camera.aspect)) + halfD) * 1.12;
	camera.position.set(0, 0, cameraZ);
	camera.lookAt(0, 0, 0);
	camera.updateProjectionMatrix();
};
const FullScreen3DModal = ({ fileKey, fileExtension, onClose }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const [mounted, setMounted] = useState(false);
	const mountRef = useRef(null);
	const sceneRef = useRef(null);
	const rendererRef = useRef(null);
	const cameraRef = useRef(null);
	const modelRef = useRef(null);
	const animationIdRef = useRef(null);
	const requestRenderRef = useRef(null);
	const controlsRef = useRef(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [hasAnimation, setHasAnimation] = useState(false);
	const [playing, setPlaying] = useState(true);
	const playingRef = useRef(true);
	const clockRef = useRef(null);
	const { url } = useFileAsyncLoader(fileKey, { priority: "high" });
	const setupScene = useCallback(() => {
		if (!mountRef.current || !url) return;
		const init = async () => {
			try {
				setIsLoading(true);
				setError(null);
				logger.debug("[FullScreen3DModal] Starting scene setup with URL:", url, "Extension:", fileExtension);
				const scene = new THREE.Scene();
				scene.background = new THREE.Color(15790320);
				sceneRef.current = scene;
				const width = mountRef.current.clientWidth;
				const height = mountRef.current.clientHeight;
				const camera = new THREE.PerspectiveCamera(45, width / height, .1, 1e3);
				camera.position.set(5, 5, 5);
				cameraRef.current = camera;
				const renderer = new THREE.WebGLRenderer({
					antialias: true,
					alpha: true
				});
				renderer.setSize(width, height);
				renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
				renderer.outputColorSpace = THREE.SRGBColorSpace;
				rendererRef.current = renderer;
				if (mountRef.current) {
					mountRef.current.innerHTML = "";
					mountRef.current.appendChild(renderer.domElement);
				}
				const hemisphereLight = new THREE.HemisphereLight(16777215, 4473924, 1.5);
				hemisphereLight.position.set(0, 20, 0);
				scene.add(hemisphereLight);
				scene.add(camera);
				const cameraLight = new THREE.DirectionalLight(16777215, 2.5);
				cameraLight.position.set(0, 0, 1);
				camera.add(cameraLight);
				const backLight = new THREE.DirectionalLight(16777215, 1);
				backLight.position.set(0, 5, -5);
				scene.add(backLight);
				let renderBudget = 120;
				const requestRender = (frames = 90) => {
					renderBudget = Math.max(renderBudget, frames);
				};
				requestRenderRef.current = requestRender;
				const canvas = renderer.domElement;
				const stopProp = (e) => e.stopPropagation();
				canvas.addEventListener("wheel", stopProp, { passive: false });
				const extension = fileExtension.toLowerCase();
				try {
					if (extension === ".glb" || extension === ".gltf") await loadGLTF(url, scene, modelRef);
					else if (extension === ".obj") await loadOBJ(url, scene, modelRef);
					else if (extension === ".ply" || extension === ".spz" || extension === ".splat" || extension === ".ksplat" || extension === ".sog") await loadSplat(url, scene, modelRef, renderer, extension);
					else if (extension === ".fbx") await loadFBX(url, scene, modelRef);
					else if (extension === ".stl") await loadSTL(url, scene, modelRef);
					else if (extension === ".dae") await loadDAE(url, scene, modelRef);
					else if (extension === ".usdz" || extension === ".usd") await loadUSDZ(url, scene, modelRef);
					else if (extension === ".ptx" || extension === ".pts" || extension === ".xyz") await loadPointCloud(url, scene, extension, modelRef);
					else if (extension === ".3ds") await load3DS(url, scene, modelRef);
					else if (extension === ".igs" || extension === ".iges") await loadIGES(url, scene, modelRef);
					else if (extension === ".step" || extension === ".stp") await loadSTEP(url, scene, modelRef);
					else if (extension === ".vtp") await loadVTP(url, scene, modelRef);
					else throw new Error(`Unsupported file format: ${extension}`);
				} catch (loadErr) {
					logger.error("Failed to load model:", loadErr);
					throw loadErr;
				}
				if (modelRef.current) autoScaleAndCenter(modelRef.current, camera, width, height);
				const { ArcballControls } = await import("three/examples/jsm/controls/ArcballControls.js");
				const controls = new ArcballControls(camera, renderer.domElement, scene);
				controls.enableAnimations = true;
				controls.cursorZoom = true;
				controls.minDistance = .5;
				controls.maxDistance = 100;
				controls.setGizmosVisible(false);
				controls.addEventListener("change", () => requestRender(2));
				controls.saveState();
				controlsRef.current = controls;
				requestRender();
				setHasAnimation(Boolean(getMixer(modelRef.current)));
				const clock = new THREE.Clock();
				clockRef.current = clock;
				let lastAnimFrame = 0;
				const animate = (t = 0) => {
					animationIdRef.current = requestAnimationFrame(animate);
					const mixer = getMixer(modelRef.current);
					if (mixer && playingRef.current) {
						if (t - lastAnimFrame >= 1e3 / ANIMATION_FPS) {
							lastAnimFrame = t;
							mixer.update(clock.getDelta());
							renderer.render(scene, camera);
						}
						return;
					}
					if (renderBudget > 0) {
						renderBudget--;
						renderer.render(scene, camera);
					}
				};
				animate();
				const resizeObserver = new ResizeObserver(() => {
					if (!mountRef.current || !rendererRef.current || !cameraRef.current) return;
					const newWidth = mountRef.current.clientWidth;
					const newHeight = mountRef.current.clientHeight;
					cameraRef.current.aspect = newWidth / newHeight;
					cameraRef.current.updateProjectionMatrix();
					rendererRef.current.setSize(newWidth, newHeight);
					requestRender();
				});
				if (mountRef.current) resizeObserver.observe(mountRef.current);
				setIsLoading(false);
				logger.debug("[FullScreen3DModal] Scene setup complete");
				return () => {
					resizeObserver.disconnect();
					canvas.removeEventListener("wheel", stopProp);
					controls.dispose();
					controlsRef.current = null;
					requestRenderRef.current = null;
					clockRef.current = null;
					if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
					getMixer(modelRef.current)?.stopAllAction();
					disposeSplat(sceneRef.current, modelRef.current);
					renderer.dispose();
				};
			} catch (err) {
				logger.error("3D loading error:", err);
				setError(err instanceof Error ? err.message : "Failed to load 3D model");
				setIsLoading(false);
			}
		};
		let cleanup;
		init().then((c) => {
			cleanup = c;
		});
		return () => {
			if (cleanup) cleanup();
		};
	}, [url, fileExtension]);
	useEffect(() => {
		setMounted(true);
		document.body.style.overflow = "hidden";
		let cleanup;
		if (url) {
			const start = async () => {
				cleanup = await setupScene();
			};
			start();
		}
		return () => {
			document.body.style.overflow = "unset";
			if (cleanup) cleanup();
		};
	}, [url, setupScene]);
	const handleResetView = () => {
		controlsRef.current?.reset();
		requestRenderRef.current?.();
	};
	const handleTogglePlay = () => {
		const next = !playingRef.current;
		if (next) clockRef.current?.getDelta();
		playingRef.current = next;
		setPlaying(next);
		if (!next) requestRenderRef.current?.(2);
	};
	const handleDownload = () => {
		if (url) {
			const link = document.createElement("a");
			link.href = url;
			link.download = `model${fileExtension}`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};
	if (!mounted) return null;
	const content = /* @__PURE__ */ jsx("div", {
		className: "fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-white rounded-lg shadow-2xl w-11/12 h-5/6 max-h-screen flex flex-col",
			children: [
				/* @__PURE__ */ jsxs("div", {
					className: "flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0",
					children: [/* @__PURE__ */ jsx("h2", {
						className: "text-lg font-semibold text-gray-900",
						children: t("model3DPreview")
					}), /* @__PURE__ */ jsxs("div", {
						className: "flex items-center gap-2",
						children: [
							hasAnimation && /* @__PURE__ */ jsx(Button, {
								size: "sm",
								variant: "ghost",
								onClick: handleTogglePlay,
								title: playing ? t("pauseAnimation") : t("playAnimation"),
								children: playing ? /* @__PURE__ */ jsx(Pause, { className: "h-4 w-4" }) : /* @__PURE__ */ jsx(Play, { className: "h-4 w-4" })
							}),
							/* @__PURE__ */ jsx(Button, {
								size: "sm",
								variant: "ghost",
								onClick: handleResetView,
								title: t("resetView"),
								children: /* @__PURE__ */ jsx(RotateCcw, { className: "h-4 w-4" })
							}),
							/* @__PURE__ */ jsx(Button, {
								size: "sm",
								variant: "ghost",
								onClick: handleDownload,
								title: t("downloadModel"),
								children: /* @__PURE__ */ jsx(Download, { className: "h-4 w-4" })
							}),
							/* @__PURE__ */ jsx(Button, {
								size: "sm",
								variant: "ghost",
								onClick: onClose,
								children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
							})
						]
					})]
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "flex-1 relative overflow-hidden bg-gray-100",
					onMouseDown: (e) => e.stopPropagation(),
					onMouseMove: (e) => e.stopPropagation(),
					onMouseUp: (e) => e.stopPropagation(),
					onWheel: (e) => e.stopPropagation(),
					children: [
						isLoading && /* @__PURE__ */ jsxs("div", {
							className: "absolute inset-0 flex flex-col items-center justify-center z-10 bg-black/10",
							children: [/* @__PURE__ */ jsx("div", {
								className: "animate-spin",
								children: /* @__PURE__ */ jsx(Box, { className: "h-8 w-8 text-blue-500" })
							}), /* @__PURE__ */ jsx("p", {
								className: "text-gray-700 mt-4 font-medium",
								children: t("loading3DModel")
							})]
						}),
						error && /* @__PURE__ */ jsx("div", {
							className: "absolute inset-0 flex items-center justify-center z-10",
							children: /* @__PURE__ */ jsxs("div", {
								className: "text-center",
								children: [/* @__PURE__ */ jsx("p", {
									className: "text-red-500 font-semibold",
									children: t("errorLoadingModel")
								}), /* @__PURE__ */ jsx("p", {
									className: "text-gray-500 text-sm mt-2",
									children: error
								})]
							})
						}),
						/* @__PURE__ */ jsx("div", {
							ref: mountRef,
							style: {
								width: "100%",
								height: "100%"
							}
						})
					]
				}),
				/* @__PURE__ */ jsx("div", {
					className: "px-4 py-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-600 flex-shrink-0",
					children: /* @__PURE__ */ jsx("p", { children: t("dragToRotate") })
				})
			]
		})
	});
	return createPortal(content, document.body);
};
const MiniModelPreview = ({ url, fileExtension }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const mountRef = useRef(null);
	const rendererRef = useRef(null);
	const sceneRef = useRef(null);
	const cameraRef = useRef(null);
	const modelRef = useRef(null);
	const animationIdRef = useRef(null);
	const [isLoading, setIsLoading] = useState(true);
	const [hasAnimation, setHasAnimation] = useState(false);
	const [playing, setPlaying] = useState(true);
	const playingRef = useRef(true);
	const visibleRef = useRef(true);
	const clockRef = useRef(null);
	useEffect(() => {
		if (!mountRef.current || !url) return;
		let cleanup;
		const init = async () => {
			try {
				setIsLoading(true);
				const width = mountRef.current.clientWidth;
				const height = mountRef.current.clientHeight;
				const scene = new THREE.Scene();
				scene.background = new THREE.Color(15790320);
				sceneRef.current = scene;
				const camera = new THREE.PerspectiveCamera(45, width / height, .1, 1e3);
				camera.position.set(0, 0, 5);
				cameraRef.current = camera;
				const renderer = new THREE.WebGLRenderer({
					antialias: true,
					alpha: true
				});
				renderer.setSize(width, height);
				renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
				renderer.outputColorSpace = THREE.SRGBColorSpace;
				rendererRef.current = renderer;
				if (mountRef.current) {
					mountRef.current.innerHTML = "";
					mountRef.current.appendChild(renderer.domElement);
				}
				const hemisphereLight = new THREE.HemisphereLight(16777215, 4473924, 1.5);
				hemisphereLight.position.set(0, 20, 0);
				scene.add(hemisphereLight);
				scene.add(camera);
				const cameraLight = new THREE.DirectionalLight(16777215, 2);
				cameraLight.position.set(0, 0, 1);
				camera.add(cameraLight);
				let renderBudget = 120;
				const requestRender = (frames = 90) => {
					renderBudget = Math.max(renderBudget, frames);
				};
				const canvas = renderer.domElement;
				canvas.style.cursor = "grab";
				const stopProp = (e) => e.stopPropagation();
				canvas.addEventListener("pointerdown", stopProp);
				canvas.addEventListener("wheel", stopProp, { passive: false });
				const ext = fileExtension.toLowerCase();
				const modelHolder = { current: null };
				try {
					if (ext === ".glb" || ext === ".gltf") await loadGLTF(url, scene, modelHolder);
					else if (ext === ".obj") await loadOBJ(url, scene, modelHolder);
					else if (ext === ".fbx") await loadFBX(url, scene, modelHolder);
					else if (ext === ".stl") await loadSTL(url, scene, modelHolder);
					else if (ext === ".dae") await loadDAE(url, scene, modelHolder);
					else if (ext === ".ply") await loadPLY(url, scene, modelHolder);
					else if (ext === ".usdz" || ext === ".usd") await loadUSDZ(url, scene, modelHolder);
					else if (ext === ".ptx" || ext === ".pts" || ext === ".xyz") await loadPointCloud(url, scene, ext, modelHolder);
					else if (ext === ".3ds") await load3DS(url, scene, modelHolder);
					else if (ext === ".vtp") await loadVTP(url, scene, modelHolder);
					else if (ext === ".spz" || ext === ".splat" || ext === ".ksplat" || ext === ".sog") await loadSplat(url, scene, modelHolder, renderer, ext);
					else if (ext === ".igs" || ext === ".iges") await loadIGES(url, scene, modelHolder);
					else if (ext === ".step" || ext === ".stp") await loadSTEP(url, scene, modelHolder);
					else throw new Error("Unsupported");
				} catch (e) {
					logger.error("Mini preview load error", e);
				}
				if (modelHolder.current) {
					modelRef.current = modelHolder.current;
					autoScaleAndCenter(modelHolder.current, camera, width, height);
				}
				const { ArcballControls } = await import("three/examples/jsm/controls/ArcballControls.js");
				const controls = new ArcballControls(camera, renderer.domElement, scene);
				controls.enableAnimations = true;
				controls.cursorZoom = true;
				controls.minDistance = .5;
				controls.maxDistance = 100;
				controls.setGizmosVisible(false);
				controls.addEventListener("change", () => requestRender(2));
				requestRender();
				setHasAnimation(Boolean(getMixer(modelRef.current)));
				const clock = new THREE.Clock();
				clockRef.current = clock;
				const io = new IntersectionObserver((entries) => {
					const visible = entries[0]?.isIntersecting ?? true;
					if (visible && !visibleRef.current) clock.getDelta();
					visibleRef.current = visible;
				}, { threshold: .05 });
				if (mountRef.current) io.observe(mountRef.current);
				let lastAnimFrame = 0;
				const animate = (frameTime = 0) => {
					animationIdRef.current = requestAnimationFrame(animate);
					const mixer = getMixer(modelRef.current);
					if (mixer && playingRef.current && visibleRef.current) {
						if (frameTime - lastAnimFrame >= 1e3 / ANIMATION_FPS) {
							lastAnimFrame = frameTime;
							mixer.update(clock.getDelta());
							renderer.render(scene, camera);
						}
						return;
					}
					if (renderBudget > 0) {
						renderBudget--;
						renderer.render(scene, camera);
					}
				};
				animate();
				setIsLoading(false);
				cleanup = () => {
					if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
					io.disconnect();
					clockRef.current = null;
					canvas.removeEventListener("pointerdown", stopProp);
					canvas.removeEventListener("wheel", stopProp);
					controls.dispose();
					getMixer(modelRef.current)?.stopAllAction();
					disposeSplat(sceneRef.current, modelRef.current);
					renderer.dispose();
				};
			} catch (err) {
				logger.error("Mini preview init error", err);
				setIsLoading(false);
			}
		};
		init();
		return () => {
			if (cleanup) cleanup();
		};
	}, [url, fileExtension]);
	const handleTogglePlay = () => {
		const next = !playingRef.current;
		if (next) clockRef.current?.getDelta();
		playingRef.current = next;
		setPlaying(next);
	};
	return /* @__PURE__ */ jsxs("div", {
		className: "w-full h-48 bg-gray-100 relative nodrag",
		children: [
			isLoading && /* @__PURE__ */ jsx("div", {
				className: "absolute inset-0 flex items-center justify-center pointer-events-none",
				children: /* @__PURE__ */ jsx(Box, { className: "h-6 w-6 text-gray-600 animate-pulse" })
			}),
			/* @__PURE__ */ jsx("div", {
				ref: mountRef,
				className: "w-full h-full cursor-grab active:cursor-grabbing"
			}),
			hasAnimation && /* @__PURE__ */ jsx("button", {
				type: "button",
				onClick: handleTogglePlay,
				title: playing ? t("pauseAnimation") : t("playAnimation"),
				className: "absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70",
				children: playing ? /* @__PURE__ */ jsx(Pause, { className: "h-3.5 w-3.5" }) : /* @__PURE__ */ jsx(Play, { className: "h-3.5 w-3.5 translate-x-px" })
			})
		]
	});
};
async function loadGLTF(url, scene, modelRef) {
	const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
	const loader = new GLTFLoader();
	return new Promise((resolve, reject) => {
		loader.load(url, (gltf) => {
			const model = gltf.scene;
			scene.add(model);
			modelRef.current = model;
			if (gltf.animations.length > 0) {
				const mixer = new THREE.AnimationMixer(model);
				gltf.animations.forEach((clip) => {
					mixer.clipAction(clip).play();
				});
				model.userData.mixer = mixer;
			}
			resolve();
		}, void 0, reject);
	});
}
async function loadOBJ(url, scene, modelRef) {
	const { OBJLoader } = await import("three/examples/jsm/loaders/OBJLoader.js");
	const loader = new OBJLoader();
	return new Promise((resolve, reject) => {
		loader.load(url, (object) => {
			scene.add(object);
			modelRef.current = object;
			object.traverse((child) => {
				if (child instanceof THREE.Mesh) {
					if (!child.material) child.material = new THREE.MeshPhongMaterial({ color: 8947848 });
				}
			});
			resolve();
		}, void 0, reject);
	});
}
const SPARK_RENDERER_NAME = "__spark_renderer__";
async function loadSplat(url, scene, modelRef, renderer, ext) {
	const { SparkRenderer, SplatMesh, SplatFileType } = await import("@sparkjsdev/spark");
	if (!scene.getObjectByName(SPARK_RENDERER_NAME)) {
		const spark = new SparkRenderer({ renderer });
		spark.name = SPARK_RENDERER_NAME;
		scene.add(spark);
	}
	const lower = ext.toLowerCase();
	const fileType = lower === ".ply" ? SplatFileType.PLY : lower === ".spz" ? SplatFileType.SPZ : lower === ".splat" ? SplatFileType.SPLAT : lower === ".ksplat" ? SplatFileType.KSPLAT : void 0;
	const mesh = new SplatMesh(fileType ? {
		url,
		fileType
	} : { url });
	await mesh.initialized;
	scene.add(mesh);
	modelRef.current = mesh;
}
function disposeSplat(scene, model) {
	model?.dispose?.();
	(scene?.getObjectByName(SPARK_RENDERER_NAME))?.dispose?.();
}
async function loadFBX(url, scene, modelRef) {
	const { FBXLoader } = await import("three/examples/jsm/loaders/FBXLoader.js");
	const loader = new FBXLoader();
	return new Promise((resolve, reject) => {
		loader.load(url, (object) => {
			scene.add(object);
			modelRef.current = object;
			if (object.animations && object.animations.length > 0) {
				const mixer = new THREE.AnimationMixer(object);
				mixer.clipAction(object.animations[0]).play();
				object.userData.mixer = mixer;
			}
			resolve();
		}, void 0, reject);
	});
}
async function loadSTL(url, scene, modelRef) {
	const { STLLoader } = await import("three/examples/jsm/loaders/STLLoader.js");
	const loader = new STLLoader();
	return new Promise((resolve, reject) => {
		loader.load(url, (geometry) => {
			logger.debug("STL loaded successfully, creating mesh");
			geometry.computeVertexNormals?.();
			const material = new THREE.MeshPhongMaterial({ color: 35071 });
			const mesh = new THREE.Mesh(geometry, material);
			scene.add(mesh);
			modelRef.current = mesh;
			resolve();
		}, void 0, (error) => {
			logger.error("STL loading error:", error);
			reject(error);
		});
	});
}
async function loadDAE(url, scene, modelRef) {
	const { ColladaLoader } = await import("three/examples/jsm/loaders/ColladaLoader.js");
	const loader = new ColladaLoader();
	return new Promise((resolve, reject) => {
		loader.load(url, (collada) => {
			const model = collada.scene;
			scene.add(model);
			modelRef.current = model;
			if (model.animations && model.animations.length > 0) {
				const mixer = new THREE.AnimationMixer(model);
				mixer.clipAction(model.animations[0]).play();
				model.userData.mixer = mixer;
			}
			resolve();
		}, void 0, reject);
	});
}
async function loadPLY(url, scene, modelRef) {
	const { PLYLoader } = await import("three/examples/jsm/loaders/PLYLoader.js");
	const loader = new PLYLoader();
	return new Promise((resolve, reject) => {
		loader.load(url, (geometry) => {
			geometry.computeVertexNormals();
			const material = new THREE.MeshPhongMaterial({ color: 65416 });
			const mesh = new THREE.Mesh(geometry, material);
			scene.add(mesh);
			modelRef.current = mesh;
			resolve();
		}, void 0, reject);
	});
}
async function loadUSDZ(url, scene, modelRef) {
	const { USDZLoader } = await import("three/examples/jsm/loaders/USDZLoader.js");
	const loader = new USDZLoader();
	return new Promise((resolve, reject) => {
		loader.load(url, (model) => {
			scene.add(model);
			modelRef.current = model;
			resolve();
		}, void 0, reject);
	});
}
async function load3DS(url, scene, modelRef) {
	const { TDSLoader } = await import("three/examples/jsm/loaders/TDSLoader.js");
	const loader = new TDSLoader();
	return new Promise((resolve, reject) => {
		loader.load(url, (model) => {
			scene.add(model);
			modelRef.current = model;
			resolve();
		}, void 0, reject);
	});
}
async function loadSTEP(url, scene, modelRef) {
	try {
		(await (await fetch(url)).text()).split("\n");
		const geometry = new THREE.BoxGeometry(1, 1, 1);
		const material = new THREE.MeshPhongMaterial({ color: 4491519 });
		const mesh = new THREE.Mesh(geometry, material);
		scene.add(mesh);
		modelRef.current = mesh;
	} catch (_err) {
		throw new Error("STEP format requires a specialized viewer. Please convert to GLTF or OBJ format.");
	}
}
async function loadIGES(url, scene, modelRef) {
	try {
		await (await fetch(url)).text();
		const geometry = new THREE.BoxGeometry(1, 1, 1);
		const material = new THREE.MeshPhongMaterial({ color: 16746564 });
		const mesh = new THREE.Mesh(geometry, material);
		scene.add(mesh);
		modelRef.current = mesh;
	} catch (_err) {
		throw new Error("IGES format requires a specialized viewer. Please convert to GLTF or OBJ format.");
	}
}
async function loadVTP(url, scene, modelRef) {
	const { VTKLoader } = await import("three/examples/jsm/loaders/VTKLoader.js");
	const loader = new VTKLoader();
	return new Promise((resolve, reject) => {
		loader.load(url, (geometry) => {
			geometry.computeVertexNormals();
			const material = new THREE.MeshPhongMaterial({ color: 16746496 });
			const mesh = new THREE.Mesh(geometry, material);
			scene.add(mesh);
			modelRef.current = mesh;
			resolve();
		}, void 0, reject);
	});
}
async function loadPointCloud(url, scene, _extension, modelRef) {
	const lines = (await (await fetch(url)).text()).split("\n").filter((line) => line.trim().length > 0);
	const positions = [];
	const colors = [];
	lines.forEach((line) => {
		const parts = line.trim().split(/\s+/);
		if (parts.length >= 3) {
			positions.push(parseFloat(parts[0]), parseFloat(parts[1]), parseFloat(parts[2]));
			if (parts.length >= 6) colors.push(parseFloat(parts[3]) / 255, parseFloat(parts[4]) / 255, parseFloat(parts[5]) / 255);
			else colors.push(.5, .5, .5);
		}
	});
	const geometry = new THREE.BufferGeometry();
	geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(positions), 3));
	geometry.setAttribute("color", new THREE.BufferAttribute(new Float32Array(colors), 3));
	const material = new THREE.PointsMaterial({
		size: .1,
		vertexColors: true
	});
	const points = new THREE.Points(geometry, material);
	scene.add(points);
	modelRef.current = points;
}
const ModelNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const fileKeys = data.fileKeys;
	const fileName = data.fileName;
	const fileKey = fileKeys && fileKeys.length > 0 ? fileKeys[0] : void 0;
	const [isFullScreen, setIsFullScreen] = useState(false);
	const { url } = useFileAsyncLoader(fileKey, { priority: "high" });
	const fileExtension = fileKey ? `.${fileKey.split(".").pop()?.toLowerCase()}` || ".glb" : ".glb";
	const isSupported = [
		".glb",
		".gltf",
		".obj",
		".ply",
		".spz",
		".splat",
		".ksplat",
		".sog",
		".fbx",
		".stl",
		".dae",
		".usdz",
		".usd",
		".ptx",
		".pts",
		".xyz",
		".3ds",
		".igs",
		".iges",
		".step",
		".stp",
		".vtp"
	].includes(fileExtension.toLowerCase());
	if (!fileKey) return /* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		children: [
			/* @__PURE__ */ jsx(Handle, {
				type: "target",
				position: Position.Left,
				id: "in:modelNode"
			}),
			/* @__PURE__ */ jsx(Handle, {
				type: "source",
				position: Position.Right,
				id: "out:modelNode"
			}),
			/* @__PURE__ */ jsxs(NodeHeader, { children: [/* @__PURE__ */ jsx(NodeHeaderIcon, { children: /* @__PURE__ */ jsx(Box, {}) }), /* @__PURE__ */ jsx(NodeHeaderTitle, { children: t("model3D") })] }),
			/* @__PURE__ */ jsx(ModalityPlaceholder, { modality: "model" })
		]
	});
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		children: [
			/* @__PURE__ */ jsx(Handle, {
				type: "target",
				position: Position.Left,
				id: "in:modelNode"
			}),
			/* @__PURE__ */ jsx(Handle, {
				type: "source",
				position: Position.Right,
				id: "out:modelNode"
			}),
			/* @__PURE__ */ jsxs(NodeHeader, { children: [
				/* @__PURE__ */ jsx(NodeHeaderIcon, { children: /* @__PURE__ */ jsx(Box, {}) }),
				/* @__PURE__ */ jsxs(NodeHeaderTitle, { children: [fileName ? fileName.substring(0, 20) : t("model3D"), !isSupported && ` (${t("unsupportedFormat").toLowerCase()})`] }),
				/* @__PURE__ */ jsxs(NodeHeaderActions, { children: [
					isSupported && /* @__PURE__ */ jsx(Button, {
						size: "sm",
						variant: "ghost",
						onClick: () => setIsFullScreen(true),
						title: t("fullScreenPreview"),
						children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
					}),
					/* @__PURE__ */ jsx(NodeHeaderComboAction, { onClick: () => logger.debug("compose mode toggle") }),
					/* @__PURE__ */ jsx(NodeHeaderMenuAction, {
						label: t("moreOptions"),
						children: /* @__PURE__ */ jsx(DropdownMenuLabel, { children: t("actions") })
					})
				] })
			] }),
			/* @__PURE__ */ jsx("div", {
				className: "w-full",
				children: isSupported ? url ? /* @__PURE__ */ jsx(MiniModelPreview, {
					url,
					fileExtension
				}) : /* @__PURE__ */ jsx("div", {
					className: "w-full bg-gray-900 flex items-center justify-center py-12",
					children: /* @__PURE__ */ jsxs("div", {
						className: "text-center",
						children: [/* @__PURE__ */ jsx(Box, { className: "h-12 w-12 text-blue-400 mx-auto mb-2" }), /* @__PURE__ */ jsx("p", {
							className: "text-gray-400 text-sm",
							children: t("loadingPreview")
						})]
					})
				}) : /* @__PURE__ */ jsx("div", {
					className: "w-full bg-gray-100 flex items-center justify-center py-8",
					children: /* @__PURE__ */ jsxs("div", {
						className: "text-center",
						children: [/* @__PURE__ */ jsx("p", {
							className: "text-gray-600 text-sm font-medium",
							children: t("unsupportedFormat")
						}), /* @__PURE__ */ jsxs("p", {
							className: "text-gray-500 text-xs mt-1",
							children: [t("supportedFormats"), ": GLB, GLTF, OBJ, FBX, STL, DAE, PLY, SPZ, SPLAT, KSPLAT, SOG, USDZ, USD, 3DS, STEP, IGES, VTP, PTX, PTS, XYZ"]
						})]
					})
				})
			})
		]
	}), isFullScreen && isSupported && fileKey && /* @__PURE__ */ jsx(FullScreen3DModal, {
		fileKey,
		fileExtension,
		onClose: () => setIsFullScreen(false)
	})] });
};
const areEqual$1 = (prevProps, nextProps) => {
	const prevFileKey = prevProps.data.fileKey;
	const nextFileKey = nextProps.data.fileKey;
	const prevFileName = prevProps.data.fileName;
	const nextFileName = nextProps.data.fileName;
	return prevProps.selected === nextProps.selected && prevFileKey === nextFileKey && prevFileName === nextFileName;
};
ModelNode.displayName = "ModelNode";
var model_node_default = memo(ModelNode, areEqual$1);
//#endregion
//#region src/canvas/nodes/modality/text-node.tsx
const FullScreenTextModal = ({ text, onClose, onSave }) => {
	const [mounted, setMounted] = useState(false);
	const [editedText, setEditedText] = useState(text);
	const t = useTranslations("Workspace.nodes.modal");
	useEffect(() => {
		setMounted(true);
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);
	const handleClose = () => {
		if (editedText !== text) onSave(editedText);
		onClose();
	};
	if (!mounted) return null;
	const content = /* @__PURE__ */ jsx("div", {
		className: "fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-11/12 h-5/6 max-h-screen flex flex-col",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0",
				children: [/* @__PURE__ */ jsx("h2", {
					className: "text-lg font-semibold text-gray-900 dark:text-white",
					children: t("textEditor")
				}), /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "ghost",
					onClick: handleClose,
					children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "flex-1 overflow-hidden p-6 bg-gray-50 dark:bg-slate-800",
				children: /* @__PURE__ */ jsx("textarea", {
					className: "w-full h-full resize-none rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 p-4 text-sm text-gray-700 dark:text-gray-300 font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400",
					value: editedText,
					onChange: (e) => setEditedText(e.target.value),
					placeholder: t("enterText")
				})
			})]
		})
	});
	return createPortal(content, document.body);
};
const TextNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const textList = data.texts ?? [];
	const [isFullScreen, setIsFullScreen] = useState(false);
	const { updateNodeData } = useReactFlow();
	const nodeId = useNodeId();
	const handleSaveText = (newText) => {
		if (nodeId) updateNodeData(nodeId, { texts: [newText] });
	};
	const isSingle = textList.length === 1;
	const count = textList.length;
	const showMore = count > 9;
	const visibleTextCount = showMore ? 8 : Math.min(count, 9);
	const emptyTiles = Math.max(0, 9 - (showMore ? 9 : visibleTextCount));
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsxs(BaseNodeShell, {
		selected,
		count,
		children: [
			/* @__PURE__ */ jsx(Handle, {
				type: "target",
				position: Position.Left,
				id: "in:textNode"
			}),
			/* @__PURE__ */ jsx(Handle, {
				type: "source",
				position: Position.Right,
				id: "out:textNode"
			}),
			/* @__PURE__ */ jsxs(NodeHeader, { children: [
				/* @__PURE__ */ jsx(NodeHeaderIcon, { children: /* @__PURE__ */ jsx(FileText, {}) }),
				/* @__PURE__ */ jsx(NodeHeaderTitle, { children: isSingle ? t("text") : t("texts", { count }) }),
				/* @__PURE__ */ jsxs(NodeHeaderActions, { children: [
					isSingle && /* @__PURE__ */ jsx(Button, {
						size: "sm",
						variant: "ghost",
						onClick: () => setIsFullScreen(true),
						title: t("fullScreenPreview"),
						children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
					}),
					/* @__PURE__ */ jsx(NodeHeaderComboAction, { onClick: () => logger.debug("compose mode toggle") }),
					/* @__PURE__ */ jsx(NodeHeaderMenuAction, {
						label: t("moreOptions"),
						children: /* @__PURE__ */ jsx(DropdownMenuLabel, { children: t("actions") })
					})
				] })
			] }),
			count === 0 || isSingle && !textList[0]?.trim() ? /* @__PURE__ */ jsx(ModalityPlaceholder, { modality: "text" }) : isSingle ? /* @__PURE__ */ jsx("div", {
				className: "max-h-[600px] min-w-[120px] max-w-[420px] overflow-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 p-3 shadow-lg nodrag",
				onPointerDown: (e) => e.stopPropagation(),
				onWheel: (e) => e.stopPropagation(),
				children: /* @__PURE__ */ jsx("div", {
					className: "whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300",
					children: textList[0]
				})
			}) : /* @__PURE__ */ jsx("div", {
				className: "w-60 min-h-[135px] rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800 p-3",
				children: /* @__PURE__ */ jsxs("div", {
					className: "grid h-full grid-cols-3 gap-2",
					children: [
						textList.slice(0, visibleTextCount).map((text, index) => /* @__PURE__ */ jsx("div", {
							className: "relative aspect-video overflow-hidden rounded-md border border-gray-300 dark:border-gray-600 bg-gray-200 dark:bg-slate-700 p-2 text-xs text-gray-600 dark:text-gray-400 shadow-sm",
							children: /* @__PURE__ */ jsxs("div", {
								className: "h-full w-full overflow-hidden",
								children: [text.substring(0, 50), text.length > 50 && "..."]
							})
						}, index)),
						showMore && /* @__PURE__ */ jsx("div", {
							className: "flex aspect-video items-center justify-center rounded-md border border-gray-400 dark:border-gray-600 bg-gradient-to-br from-gray-300 to-gray-400 dark:from-slate-600 dark:to-slate-700 shadow-sm",
							children: /* @__PURE__ */ jsxs("div", {
								className: "text-center",
								children: [/* @__PURE__ */ jsxs("div", {
									className: "text-sm font-bold text-gray-700 dark:text-gray-200",
									children: ["+", count - 8]
								}), /* @__PURE__ */ jsx("div", {
									className: "text-xs text-gray-600 dark:text-gray-400",
									children: t("more")
								})]
							})
						}),
						Array.from({ length: emptyTiles }).map((_, index) => /* @__PURE__ */ jsx("div", { className: "aspect-video rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-slate-700 opacity-50" }, `empty-${index}`))
					]
				})
			})
		]
	}), isFullScreen && isSingle && /* @__PURE__ */ jsx(FullScreenTextModal, {
		text: textList[0],
		onClose: () => setIsFullScreen(false),
		onSave: handleSaveText
	})] });
};
const areEqual = (prevProps, nextProps) => {
	const prevTexts = prevProps.data.texts || [];
	const nextTexts = nextProps.data.texts || [];
	return prevProps.selected === nextProps.selected && JSON.stringify(prevTexts) === JSON.stringify(nextTexts);
};
TextNode.displayName = "TextNode";
var text_node_default = memo(TextNode, areEqual);
//#endregion
//#region src/canvas/nodes/modality/video-node.tsx
const FullScreenVideoModal = ({ fileKey, onClose }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const [mounted, setMounted] = useState(false);
	const { url } = useFileAsyncLoader(fileKey, { priority: "high" });
	useEffect(() => {
		setMounted(true);
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);
	if (!mounted) return null;
	const content = /* @__PURE__ */ jsx("div", {
		className: "fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-white rounded-lg shadow-2xl w-11/12 h-5/6 max-h-screen flex flex-col",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0",
				children: [/* @__PURE__ */ jsx("h2", {
					className: "text-lg font-semibold text-gray-900",
					children: t("videoPreview")
				}), /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "ghost",
					onClick: onClose,
					children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "flex-1 flex items-center justify-center bg-white overflow-auto",
				children: url ? /* @__PURE__ */ jsx("video", {
					src: url,
					controls: true,
					className: "max-w-full max-h-full object-contain",
					autoPlay: true,
					children: "Your browser does not support the video tag."
				}) : /* @__PURE__ */ jsx("div", {
					className: "text-gray-500",
					children: t("loading")
				})
			})]
		})
	});
	return createPortal(content, document.body);
};
const FullScreenWaterfallModal = ({ videoKeys, onClose }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const [mounted, setMounted] = useState(false);
	const { urls } = useFileAsyncLoaderBatch(videoKeys, { priority: "normal" });
	useEffect(() => {
		setMounted(true);
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);
	if (!mounted) return null;
	const VideoThumbnail = memo(({ data: fileKey }) => {
		const videoRef = useRef(null);
		const [videoHeight, setVideoHeight] = useState(null);
		const url = urls.get(fileKey);
		useEffect(() => {
			const video = videoRef.current;
			if (!video || !url) return;
			const handleLoadedMetadata = () => {
				if (video.videoWidth && video.videoHeight) {
					const aspectRatio = video.videoHeight / video.videoWidth;
					setVideoHeight(200 * aspectRatio);
				}
			};
			video.addEventListener("loadedmetadata", handleLoadedMetadata);
			if (video.readyState >= 1) handleLoadedMetadata();
			return () => {
				video.removeEventListener("loadedmetadata", handleLoadedMetadata);
			};
		}, [url]);
		return /* @__PURE__ */ jsx("div", {
			className: "relative overflow-hidden rounded-md border border-gray-300 bg-gray-200 shadow-md hover:shadow-lg transition-shadow cursor-pointer",
			style: {
				width: 200,
				height: videoHeight || 112.5
			},
			onMouseEnter: (e) => {
				e.currentTarget.querySelector("video")?.play();
			},
			onMouseLeave: (e) => {
				e.currentTarget.querySelector("video")?.pause();
			},
			children: url ? /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("video", {
				ref: videoRef,
				src: url,
				className: "h-full w-full object-cover",
				preload: "metadata"
			}), /* @__PURE__ */ jsx("div", {
				className: "absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/20 transition-colors",
				children: /* @__PURE__ */ jsx("div", {
					className: "flex h-8 w-8 items-center justify-center rounded-full bg-white/90",
					children: /* @__PURE__ */ jsx(Video, { className: "h-4 w-4 text-gray-800" })
				})
			})] }) : /* @__PURE__ */ jsx("div", {
				className: "flex items-center justify-center h-full w-full",
				children: /* @__PURE__ */ jsx("div", {
					className: "text-xs text-gray-500",
					children: t("loading")
				})
			})
		});
	});
	const content = /* @__PURE__ */ jsx("div", {
		className: "fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-white rounded-lg shadow-2xl w-11/12 h-5/6 max-h-screen flex flex-col",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0",
				children: [/* @__PURE__ */ jsx("h2", {
					className: "text-lg font-semibold text-gray-900",
					children: t("videos", { count: videoKeys.length })
				}), /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "ghost",
					onClick: onClose,
					children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "flex-1 bg-white overflow-auto p-6",
				children: /* @__PURE__ */ jsx(Waterfall, {
					items: videoKeys.map((key) => ({
						id: key,
						key
					})),
					render: ({ data: { key } }) => /* @__PURE__ */ jsx(VideoThumbnail, { data: key }),
					columnWidth: 200,
					columnGutter: 12,
					rowGutter: 12,
					itemHeightEstimate: 200,
					itemKey: (data) => data.id,
					maxColumnCount: 6
				})
			})]
		})
	});
	return createPortal(content, document.body);
};
const VideoGridThumb = ({ url, loadingLabel }) => {
	const [errored, setErrored] = useState(false);
	if (url && !errored) return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("video", {
		src: url,
		className: "h-full w-full object-cover",
		preload: "metadata",
		onError: () => setErrored(true)
	}), /* @__PURE__ */ jsx("div", {
		className: "absolute inset-0 flex items-center justify-center bg-black/30",
		children: /* @__PURE__ */ jsx("div", {
			className: "flex h-6 w-6 items-center justify-center rounded-full bg-white/90",
			children: /* @__PURE__ */ jsx(Video, { className: "h-3 w-3 text-gray-800" })
		})
	})] });
	if (errored) return /* @__PURE__ */ jsx(ModalityPlaceholder, {
		modality: "video",
		variant: "thumb"
	});
	return /* @__PURE__ */ jsx("div", {
		className: "h-full w-full flex items-center justify-center bg-gray-300",
		children: /* @__PURE__ */ jsx("div", {
			className: "text-xs text-gray-500",
			children: loadingLabel
		})
	});
};
const VideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes.modal");
	const keys = data.fileKeys ?? [];
	const singleVideoRef = useRef(null);
	const [isFullScreen, setIsFullScreen] = useState(false);
	const [isWaterfallFullScreen, setIsWaterfallFullScreen] = useState(false);
	const [videoDimensions, setVideoDimensions] = useState(null);
	const [videoError, setVideoError] = useState(false);
	const { url: singleVideoUrl } = useFileAsyncLoader(keys[0], { priority: "high" });
	const { urls: batchUrls } = useFileAsyncLoaderBatch(keys.slice(0, 6), { priority: "normal" });
	const isSingle = keys.length === 1;
	const count = keys.length;
	useEffect(() => {
		if (!isSingle || !singleVideoUrl) {
			setVideoDimensions(null);
			setVideoError(false);
			return;
		}
		setVideoError(false);
		const video = document.createElement("video");
		video.preload = "metadata";
		const onLoadedMetadata = () => {
			const w = video.videoWidth;
			const h = video.videoHeight;
			if (w > 0 && h > 0) setVideoDimensions({
				width: w,
				height: h
			});
			else setVideoDimensions(null);
		};
		const onError = () => {
			setVideoDimensions(null);
			setVideoError(true);
		};
		video.addEventListener("loadedmetadata", onLoadedMetadata);
		video.addEventListener("error", onError);
		video.src = singleVideoUrl;
		return () => {
			video.removeEventListener("loadedmetadata", onLoadedMetadata);
			video.removeEventListener("error", onError);
			video.removeAttribute("src");
			video.load();
		};
	}, [isSingle, singleVideoUrl]);
	const handleDownload = (url, fileKey) => {
		const filename = `video.${fileKey.includes(".") ? fileKey.split(".").pop() : "mp4"}`;
		const downloadUrl = `${url}${url.includes("?") ? "&" : "?"}download=${filename}`;
		window.open(downloadUrl, "_blank");
	};
	const mediaNodeWidthPx = isSingle && videoDimensions ? proportionalMediaNodeWidthPx(videoDimensions.width, videoDimensions.height) : void 0;
	return /* @__PURE__ */ jsxs(Fragment, { children: [
		/* @__PURE__ */ jsxs(BaseNodeShell, {
			selected,
			count,
			className: mediaNodeWidthPx != null ? "min-w-0 max-w-none" : void 0,
			style: mediaNodeWidthPx != null ? { width: mediaNodeWidthPx } : void 0,
			children: [
				/* @__PURE__ */ jsx(Handle, {
					type: "target",
					position: Position.Left,
					id: "in:videoNode"
				}),
				/* @__PURE__ */ jsx(Handle, {
					type: "source",
					position: Position.Right,
					id: "out:videoNode"
				}),
				/* @__PURE__ */ jsxs(NodeHeader, { children: [
					/* @__PURE__ */ jsx(NodeHeaderIcon, { children: /* @__PURE__ */ jsx(Video, {}) }),
					/* @__PURE__ */ jsx(NodeHeaderTitle, { children: isSingle ? t("video") : t("videos", { count }) }),
					/* @__PURE__ */ jsxs(NodeHeaderActions, { children: [
						isSingle && /* @__PURE__ */ jsx(Button, {
							size: "sm",
							variant: "ghost",
							onClick: () => setIsFullScreen(true),
							title: t("fullScreenPreview"),
							children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
						}),
						!isSingle && /* @__PURE__ */ jsx(Button, {
							size: "sm",
							variant: "ghost",
							onClick: () => setIsWaterfallFullScreen(true),
							title: t("fullScreenWaterfall"),
							children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
						}),
						/* @__PURE__ */ jsx(NodeHeaderComboAction, { onClick: () => logger.debug("compose mode toggle") }),
						/* @__PURE__ */ jsxs(NodeHeaderMenuAction, {
							label: t("moreOptions"),
							children: [/* @__PURE__ */ jsx(DropdownMenuLabel, { children: t("actions") }), isSingle && singleVideoUrl && /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(DropdownMenuSeparator, {}), /* @__PURE__ */ jsxs(DropdownMenuItem, {
								onClick: () => handleDownload(singleVideoUrl, keys[0]),
								children: [/* @__PURE__ */ jsx(Download, { className: "h-4 w-4 mr-2" }), t("download")]
							})] })]
						})
					] })
				] }),
				isSingle ? /* @__PURE__ */ jsxs("div", {
					className: "relative w-full nodrag",
					onPointerDown: (e) => e.stopPropagation(),
					children: [videoError ? /* @__PURE__ */ jsx(ModalityPlaceholder, { modality: "video" }) : singleVideoUrl ? /* @__PURE__ */ jsx("video", {
						ref: singleVideoRef,
						src: singleVideoUrl,
						controls: true,
						controlsList: "nodownload",
						className: "w-full h-auto object-contain",
						preload: "metadata",
						onError: () => setVideoError(true),
						onMouseEnter: () => singleVideoRef.current?.play(),
						onMouseLeave: () => singleVideoRef.current?.pause(),
						children: "Your browser does not support the video tag."
					}) : /* @__PURE__ */ jsx("div", {
						className: "w-full bg-gray-200 flex items-center justify-center text-gray-500 py-16",
						children: t("loading")
					}), videoDimensions && /* @__PURE__ */ jsxs("div", {
						className: "absolute bottom-2 right-2 text-xs text-white bg-black/50 px-2 py-1 rounded pointer-events-none",
						children: [
							videoDimensions.width,
							" ×",
							" ",
							videoDimensions.height
						]
					})]
				}) : /* @__PURE__ */ jsx("div", {
					className: "w-full p-2 nodrag",
					onPointerDown: (e) => e.stopPropagation(),
					children: /* @__PURE__ */ jsx("div", {
						className: "grid grid-cols-3 gap-2",
						children: keys.slice(0, 6).map((key, index) => {
							const isLastAndMore = index === 5 && keys.length > 6;
							const remainingCount = keys.length - 6;
							const url = batchUrls.get(key);
							return isLastAndMore ? /* @__PURE__ */ jsx("div", {
								className: "relative aspect-square overflow-hidden rounded-md border border-gray-300 bg-gray-200 shadow-sm flex items-center justify-center",
								children: /* @__PURE__ */ jsxs("div", {
									className: "text-center",
									children: [/* @__PURE__ */ jsxs("div", {
										className: "text-3xl font-bold text-gray-700",
										children: ["+", remainingCount]
									}), /* @__PURE__ */ jsx("div", {
										className: "text-xs text-gray-600 mt-1",
										children: t("more")
									})]
								})
							}, `more-${key}`) : /* @__PURE__ */ jsx("div", {
								className: "relative aspect-square overflow-hidden rounded-md border border-gray-300 bg-gray-200 shadow-sm",
								onMouseEnter: (e) => {
									e.currentTarget.querySelector("video")?.play();
								},
								onMouseLeave: (e) => {
									e.currentTarget.querySelector("video")?.pause();
								},
								children: /* @__PURE__ */ jsx(VideoGridThumb, {
									url,
									loadingLabel: t("loading")
								})
							}, key);
						})
					})
				})
			]
		}),
		isFullScreen && isSingle && keys[0] && /* @__PURE__ */ jsx(FullScreenVideoModal, {
			fileKey: keys[0],
			onClose: () => setIsFullScreen(false)
		}),
		isWaterfallFullScreen && !isSingle && /* @__PURE__ */ jsx(FullScreenWaterfallModal, {
			videoKeys: keys,
			onClose: () => setIsWaterfallFullScreen(false)
		})
	] });
};
VideoNode.displayName = "VideoNode";
var video_node_default = memo(VideoNode);
//#endregion
//#region src/canvas/nodes/transfer/audio-describe.tsx
const AudioDescribeNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("audio-describe");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "audio-describe",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.audioDescribe"),
		icon: /* @__PURE__ */ jsx(Music, { className: "h-5 w-5" }),
		executeLabel: t("actions.describeAudio"),
		executeDisabled: !fileKeys?.length,
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx(NodeTextarea, {
				label: t("audioDescribe.promptLabel"),
				icon: MessageSquare,
				placeholder: t("audioDescribe.promptPlaceholder"),
				...form.bind("text"),
				rows: 3
			})
		})
	});
};
AudioDescribeNode.displayName = "AudioDescribeNode";
var audio_describe_default = memo(AudioDescribeNode);
//#endregion
//#region src/canvas/nodes/transfer/audio-gen-text-speech-recognize.tsx
const AudioGenTextSpeechRecognizeNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("transcribe");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "transcribe",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.speechRecognize"),
		icon: /* @__PURE__ */ jsx(Music, { className: "h-5 w-5" }),
		executeLabel: t("actions.recognizeSpeech"),
		executeDisabled: !fileKeys.length
	});
};
AudioGenTextSpeechRecognizeNode.displayName = "AudioGenTextSpeechRecognizeNode";
var audio_gen_text_speech_recognize_default = memo(AudioGenTextSpeechRecognizeNode);
//#endregion
//#region src/canvas/nodes/transfer/convert-voice.tsx
const VOICE_OPTIONS = [{
	key: "female",
	value: "zh_famale_1.wav"
}, {
	key: "male",
	value: "zh_male_1.wav"
}];
const ConvertVoiceNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("convert_voice");
	const fileKeys = data.fileKeys;
	const [extraSpeakers, setExtraSpeakers] = useState([]);
	const speakers = [...VOICE_OPTIONS, ...extraSpeakers];
	const targetKey = form.state.targetKey ?? "zh_famale_1.wav";
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "convert_voice",
		form,
		selected,
		data,
		title: t("titles.convertVoice"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.startReplace"),
		executeDisabled: !fileKeys?.length,
		children: /* @__PURE__ */ jsx(Card, {
			className: "mx-4 mb-4 p-4 nodrag",
			onPointerDown: (e) => e.stopPropagation(),
			children: /* @__PURE__ */ jsxs("div", {
				className: "mb-4 flex flex-wrap items-center gap-3",
				children: [
					/* @__PURE__ */ jsx("label", {
						htmlFor: "voice-select",
						className: "text-sm text-muted-foreground whitespace-nowrap",
						children: t("convertVoice.voiceLabel")
					}),
					/* @__PURE__ */ jsxs(Select, {
						value: targetKey,
						onValueChange: (value) => form.set("targetKey", value),
						children: [/* @__PURE__ */ jsx(SelectTrigger, {
							id: "voice-select",
							className: "w-36 h-9",
							children: /* @__PURE__ */ jsx(SelectValue, { placeholder: t("convertVoice.selectVoice") })
						}), /* @__PURE__ */ jsx(SelectContent, { children: speakers.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
							value: opt.value,
							children: opt.key ? t(`common.voiceOptions.${opt.key}`) : opt.value
						}, opt.value)) })]
					}),
					/* @__PURE__ */ jsx(SpeakerVoiceUploader, {
						trigger: /* @__PURE__ */ jsx(Button, {
							variant: "outline",
							size: "icon",
							className: "ml-1",
							title: t("convertVoice.uploadVoice"),
							children: /* @__PURE__ */ jsx(Upload, { className: "w-4 h-4" })
						}),
						onChange: (key) => {
							setExtraSpeakers((prev) => [...prev, {
								key: "",
								value: key
							}]);
						}
					}),
					/* @__PURE__ */ jsx(SpeakerVoiceRecorder$1, {
						trigger: /* @__PURE__ */ jsx(Button, {
							variant: "outline",
							size: "icon",
							className: "ml-1",
							title: t("convertVoice.recordVoice"),
							children: /* @__PURE__ */ jsx(Mic, { className: "w-4 h-4" })
						}),
						onChange: (key) => {
							setExtraSpeakers((prev) => [...prev, {
								key: "",
								value: key
							}]);
						}
					})
				]
			})
		})
	});
};
var convert_voice_default = memo(ConvertVoiceNode);
const SpeakerVoiceUploader = ({ trigger, onChange: _onChange }) => {
	const [_uploaded, setUploaded] = useState(false);
	const doUpload = async (files) => {
		logger.debug("Uploading files:", files);
		setUploaded(true);
	};
	return /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsxs("label", {
		className: "cursor-pointer",
		children: [/* @__PURE__ */ jsx("input", {
			type: "file",
			multiple: true,
			hidden: true,
			onChange: (e) => {
				const files = Array.from(e.target.files || []);
				if (files.length > 0) doUpload(files);
			}
		}), trigger]
	}) });
};
const SpeakerVoiceRecorder$1 = ({ trigger, onChange: _onChange }) => {
	const t = useTranslations("Workspace.nodes.convertVoice");
	const [file, _setFile] = useState();
	const onFinish = async () => {
		if (!file) return;
		logger.debug("Recording file:", file);
	};
	return /* @__PURE__ */ jsxs(Dialog, { children: [/* @__PURE__ */ jsx(DialogTrigger, {
		asChild: true,
		children: trigger
	}), /* @__PURE__ */ jsxs(DialogContent, {
		className: "sm:max-w-md",
		children: [
			/* @__PURE__ */ jsxs(DialogHeader, { children: [/* @__PURE__ */ jsx(DialogTitle, { children: t("recordAudio") }), /* @__PURE__ */ jsx(DialogDescription, { children: t("recordHint") })] }),
			/* @__PURE__ */ jsx("div", {
				className: "overflow-auto scroll-smooth w-80 max-h-80",
				children: /* @__PURE__ */ jsx("p", {
					className: "text-sm text-gray-500",
					children: t("recordNeedImpl")
				})
			}),
			/* @__PURE__ */ jsx(DialogFooter, {
				className: "sm:justify-start",
				children: /* @__PURE__ */ jsx(DialogClose, {
					asChild: true,
					children: /* @__PURE__ */ jsx(Button, {
						type: "button",
						variant: "secondary",
						onClick: () => onFinish(),
						children: t("done")
					})
				})
			})
		]
	})] });
};
//#endregion
//#region src/canvas/nodes/transfer/denoise-audio.tsx
const DenoiseAudioNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("denoise_audio");
	const fileKeys = data.fileKeys;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "denoise_audio",
		form,
		selected,
		data,
		title: t("titles.denoiseAudio"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.startDenoise"),
		executeDisabled: !fileKeys?.length
	});
};
var denoise_audio_default = memo(DenoiseAudioNode);
//#endregion
//#region src/canvas/nodes/transfer/extract-audio.tsx
const ExtractAudioNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("extract-audio");
	const fileKeys = data.fileKeys;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "extract-audio",
		form,
		selected,
		data,
		title: t("titles.extractAudioTrack"),
		icon: /* @__PURE__ */ jsx(Music, { className: "h-5 w-5" }),
		executeLabel: t("actions.extractAudioTrack"),
		executeDisabled: !fileKeys?.length
	});
};
var extract_audio_default = memo(ExtractAudioNode);
//#endregion
//#region src/canvas/nodes/transfer/file-gen-text.tsx
const FileGenTextNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("parse-document");
	const { fileKeys = [] } = data;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "parse-document",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.fileGenText"),
		icon: /* @__PURE__ */ jsx(FileText, { className: "h-5 w-5" }),
		executeLabel: t("actions.parseDocument"),
		executeDisabled: !fileKeys?.length
	});
};
FileGenTextNode.displayName = "FileGenTextNode";
var file_gen_text_default = memo(FileGenTextNode);
//#endregion
//#region src/canvas/nodes/transfer/get-first-frame.tsx
const GetFirstFrameNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("get-first-frame");
	const fileKeys = data.fileKeys;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "get-first-frame",
		form,
		selected,
		data,
		title: t("titles.getFirstFrame"),
		icon: /* @__PURE__ */ jsx(Camera, { className: "h-5 w-5" }),
		executeLabel: t("actions.getFirstFrame"),
		executeDisabled: !fileKeys?.length
	});
};
var get_first_frame_default = memo(GetFirstFrameNode);
//#endregion
//#region src/canvas/nodes/transfer/get-last-frame.tsx
const GetLastFrameNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("get-last-frame");
	const fileKeys = data.fileKeys;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "get-last-frame",
		form,
		selected,
		data,
		title: t("titles.getLastFrame"),
		icon: /* @__PURE__ */ jsx(Film, { className: "h-5 w-5" }),
		executeLabel: t("actions.getLastFrame"),
		executeDisabled: !fileKeys?.length
	});
};
var get_last_frame_default = memo(GetLastFrameNode);
//#endregion
//#region src/canvas/nodes/transfer/image-body-seg.tsx
const ImageBodySegNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-body-seg");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-body-seg",
		form,
		selected,
		data,
		title: t("titles.imageBodySeg"),
		icon: /* @__PURE__ */ jsx(Puzzle, { className: "h-5 w-5" }),
		executeLabel: t("actions.segmentBody"),
		executeDisabled: !fileKeys?.length
	});
};
ImageBodySegNode.displayName = "ImageBodySegNode";
var image_body_seg_default = memo(ImageBodySegNode);
//#endregion
//#region src/canvas/nodes/transfer/image-gen-image.tsx
const ImageGenImageNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-edit");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("image-edit");
	const { hasImage, promptText } = useMemo(() => {
		if (!nodeId) return {
			hasImage: false,
			promptText: ""
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		const text = typeof values.text === "string" ? values.text.trim() : "";
		const imageRaw = values.image;
		const imageKey = Array.isArray(imageRaw) ? typeof imageRaw[0] === "string" ? imageRaw[0] : void 0 : typeof imageRaw === "string" ? imageRaw : void 0;
		return {
			hasImage: Boolean(imageKey),
			promptText: text
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	const manualText = form.state.text?.trim() ?? "";
	const effectiveText = promptText || manualText;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-edit",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.imageGenImage"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.editImage"),
		executeDisabled: !hasImage || !effectiveText,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [!hasImage && /* @__PURE__ */ jsx("p", {
				className: "text-xs text-red-500",
				children: t("compose.connectTextImage")
			}), promptText ? /* @__PURE__ */ jsx(Card, {
				className: "p-3 bg-muted/50",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: [t("imageEdit.editInstruction"), t("imageEdit.fromUpstream")]
					}), /* @__PURE__ */ jsx("div", {
						className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-3",
						children: promptText
					})]
				})
			}) : /* @__PURE__ */ jsx(NodeTextarea, {
				label: t("imageEdit.editInstruction"),
				icon: Sparkles,
				placeholder: t("imageEdit.editPlaceholder"),
				...form.bind("text"),
				rows: 4
			})]
		})
	});
};
ImageGenImageNode.displayName = "ImageGenImageNode";
var image_gen_image_default = memo(ImageGenImageNode);
//#endregion
//#region src/canvas/nodes/transfer/image-gen-image-upscale.tsx
const UPSCALE_TIERS$1 = [
	{
		value: "1k",
		labelKey: "upscaleTier1k"
	},
	{
		value: "2k",
		labelKey: "upscaleTier2k"
	},
	{
		value: "4k",
		labelKey: "upscaleTier4k"
	}
];
const ImageGenImageUpscaleNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-upscale");
	const fileKeys = data.fileKeys ?? [];
	const resolution = form.state.resolution ?? "2k";
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-upscale",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.imageUpscale"),
		icon: /* @__PURE__ */ jsx(Sparkles, { className: "h-5 w-5" }),
		executeLabel: t("actions.imageUpscale"),
		executeDisabled: !fileKeys?.length,
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 pt-0 space-y-3",
			children: /* @__PURE__ */ jsx(Card, {
				className: "p-3",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [
						/* @__PURE__ */ jsxs(Label, {
							className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
							children: [/* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" }), t("common.upscaleTierLabel")]
						}),
						/* @__PURE__ */ jsx("div", {
							className: "grid grid-cols-3 gap-2",
							children: UPSCALE_TIERS$1.map((tier) => /* @__PURE__ */ jsx(Button, {
								variant: resolution === tier.value ? "default" : "outline",
								size: "sm",
								onClick: () => form.set("resolution", tier.value),
								className: cn("h-auto py-2 px-1 flex flex-col gap-0.5 text-xs", resolution === tier.value ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent"),
								children: /* @__PURE__ */ jsx("span", {
									className: "font-medium",
									children: t(`common.${tier.labelKey}`)
								})
							}, tier.value))
						}),
						/* @__PURE__ */ jsx("p", {
							className: "text-xs text-muted-foreground leading-snug",
							children: t("common.upscaleTierHint")
						})
					]
				})
			})
		})
	});
};
ImageGenImageUpscaleNode.displayName = "ImageGenImageUpscaleNode";
var image_gen_image_upscale_default = memo(ImageGenImageUpscaleNode);
//#endregion
//#region src/canvas/nodes/transfer/image-gen-model.tsx
const ImageGenModelNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-gen-model");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-gen-model",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.imageGenModel"),
		icon: /* @__PURE__ */ jsx(Box, { className: "h-5 w-5" }),
		executeLabel: t("actions.generate3DModel"),
		executeDisabled: !fileKeys?.length
	});
};
ImageGenModelNode.displayName = "ImageGenModelNode";
var image_gen_model_default = memo(ImageGenModelNode);
//#endregion
//#region src/canvas/nodes/transfer/image-gen-text.tsx
const ImageGenTextNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-gen-text");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-gen-text",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.imageGenText"),
		icon: /* @__PURE__ */ jsx(Image$1, { className: "h-5 w-5" }),
		executeLabel: t("actions.describeImage"),
		executeDisabled: !fileKeys?.length,
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx(NodeTextarea, {
				label: t("imageGenText.promptLabel"),
				icon: MessageSquare,
				placeholder: t("imageGenText.promptPlaceholder"),
				...form.bind("text"),
				rows: 3
			})
		})
	});
};
ImageGenTextNode.displayName = "ImageGenTextNode";
var image_gen_text_default = memo(ImageGenTextNode);
//#endregion
//#region src/canvas/nodes/transfer/image-gen-video.tsx
const ImageGenVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tActions = useTranslations("Workspace.nodes.actions");
	const form = useAbiForm("image-gen-video");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const hasImageInput = useMemo(() => {
		if (!nodeId) return false;
		return edges.some((edge) => {
			if (edge.target !== nodeId) return false;
			if (parseTargetHandleId(edge.targetHandle) !== "image") return false;
			const source = nodeLookup.get(edge.source);
			if (source?.type !== "imageNode") return false;
			return (coerceBaseNodeData(source.data).fileKeys?.length ?? 0) > 0;
		});
	}, [
		nodeId,
		edges,
		nodeLookup
	]);
	const promptText = form.state.text?.trim() ?? "";
	const width = form.state.width ?? 1024;
	const height = form.state.height ?? 576;
	const durationSeconds = form.state.duration ?? 5;
	useEffect(() => {
		if (form.state.duration === void 0) form.set("duration", 5, { history: false });
	}, [form.state.duration, form.set]);
	const currentRatio = VIDEO_ASPECT_RATIOS.find((r) => r.width === width && r.height === height) ?? VIDEO_ASPECT_RATIOS[0];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.imageGenVideo"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: tActions("generateVideo"),
		executeDisabled: !hasImageInput || !promptText,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(NodeTextarea, {
					rows: 4,
					placeholder: t("common.videoDesc"),
					...form.bind("text")
				}),
				/* @__PURE__ */ jsx(AspectRatioPicker, {
					ratios: VIDEO_ASPECT_RATIOS,
					value: currentRatio,
					onChange: (ratio) => form.patch({
						width: ratio.width,
						height: ratio.height
					}),
					showSize: true
				}),
				/* @__PURE__ */ jsx(VideoDurationSlider, {
					value: durationSeconds,
					onChange: (dur) => form.set("duration", dur)
				})
			]
		})
	});
};
ImageGenVideoNode.displayName = "ImageGenVideoNode";
var image_gen_video_default = memo(ImageGenVideoNode);
//#endregion
//#region src/canvas/nodes/transfer/image-matting.tsx
const ImageMattingNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-matting");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-matting",
		form,
		selected,
		data,
		title: t("titles.imageMatting"),
		icon: /* @__PURE__ */ jsx(Wand2, { className: "h-5 w-5" }),
		executeLabel: t("actions.extractForeground"),
		executeDisabled: !fileKeys?.length
	});
};
ImageMattingNode.displayName = "ImageMattingNode";
var image_matting_default = memo(ImageMattingNode);
//#endregion
//#region src/canvas/nodes/transfer/image-normal.tsx
const ImageNormalNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-normal");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-normal",
		form,
		selected,
		data,
		title: t("titles.imageNormal"),
		icon: /* @__PURE__ */ jsx(Layers, { className: "h-5 w-5" }),
		executeLabel: t("actions.estimateNormal"),
		executeDisabled: !fileKeys?.length
	});
};
ImageNormalNode.displayName = "ImageNormalNode";
var image_normal_default = memo(ImageNormalNode);
//#endregion
//#region src/canvas/nodes/transfer/image-pose.tsx
const ImagePoseNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("image-pose");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-pose",
		form,
		selected,
		data,
		title: t("titles.imagePose"),
		icon: /* @__PURE__ */ jsx(PersonStanding, { className: "h-5 w-5" }),
		executeLabel: t("actions.detectPose"),
		executeDisabled: !fileKeys?.length
	});
};
ImagePoseNode.displayName = "ImagePoseNode";
var image_pose_default = memo(ImagePoseNode);
//#endregion
//#region src/canvas/nodes/transfer/link-gen-text.tsx
const LinkGenTextNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("link");
	const { texts = [] } = data;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "link",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.linkGenText"),
		icon: /* @__PURE__ */ jsx(Link, { className: "h-5 w-5" }),
		executeLabel: t("actions.extractContent"),
		executeDisabled: !texts?.length
	});
};
LinkGenTextNode.displayName = "LinkGenTextNode";
var link_gen_text_default = memo(LinkGenTextNode);
//#endregion
//#region src/canvas/nodes/transfer/music-extract.tsx
/** Stem/track options shared by the music-extract / music-lego / music-complete nodes. */
const MUSIC_TRACK_OPTIONS = [
	"vocals",
	"backing_vocals",
	"drums",
	"percussion",
	"bass",
	"guitar",
	"keyboard",
	"synth",
	"strings",
	"brass",
	"woodwinds",
	"fx"
];
const DEFAULT_TRACK = "vocals";
const MusicExtractNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("music-extract");
	const nodeId = useNodeId();
	const edges = useStore((state) => state.edges);
	const audioConnected = useMemo(() => !!nodeId && edges.some((e) => e.target === nodeId && e.targetHandle === "in:audio"), [edges, nodeId]);
	const track = form.state.track ?? DEFAULT_TRACK;
	useEffect(() => {
		if (form.state.track == null) form.set("track", DEFAULT_TRACK, { history: false });
	}, [form.state.track, form.set]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "music-extract",
		form,
		selected,
		className: "min-w-[420px]",
		data,
		title: t("titles.musicExtract"),
		icon: /* @__PURE__ */ jsx(Split, { className: "h-5 w-5" }),
		executeLabel: t("actions.extractStem"),
		executeDisabled: !audioConnected || !track,
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx(Card, {
				className: "p-3 nodrag",
				onPointerDown: (e) => e.stopPropagation(),
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-1.5",
					children: [/* @__PURE__ */ jsx(Label, {
						className: "text-xs text-muted-foreground",
						children: t("musicTasks.track")
					}), /* @__PURE__ */ jsxs(Select, {
						value: track,
						onValueChange: (v) => form.set("track", v),
						children: [/* @__PURE__ */ jsx(SelectTrigger, {
							className: "h-8 text-xs",
							children: /* @__PURE__ */ jsx(SelectValue, {})
						}), /* @__PURE__ */ jsx(SelectContent, { children: MUSIC_TRACK_OPTIONS.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
							value: opt,
							className: "text-xs",
							children: t(`musicTasks.trackOptions.${opt}`)
						}, opt)) })]
					})]
				})
			})
		})
	});
};
MusicExtractNode.displayName = "MusicExtractNode";
var music_extract_default = memo(MusicExtractNode);
//#endregion
//#region src/canvas/nodes/transfer/music-complete.tsx
const MusicCompleteNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("music-complete");
	const nodeId = useNodeId();
	const edges = useStore((state) => state.edges);
	const audioConnected = useMemo(() => !!nodeId && edges.some((e) => e.target === nodeId && e.targetHandle === "in:audio"), [edges, nodeId]);
	const tracks = form.state.tracks ?? [];
	const toggleTrack = (track) => {
		const next = tracks.includes(track) ? tracks.filter((v) => v !== track) : [...tracks, track];
		form.set("tracks", next);
	};
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "music-complete",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.musicComplete"),
		icon: /* @__PURE__ */ jsx(PackagePlus, { className: "h-5 w-5" }),
		executeLabel: t("actions.completeMusic"),
		executeDisabled: !audioConnected,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [/* @__PURE__ */ jsx(Card, {
				className: "p-3 nodrag",
				onPointerDown: (e) => e.stopPropagation(),
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsx(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: t("musicTasks.tracks")
					}), /* @__PURE__ */ jsx("div", {
						className: "flex flex-wrap gap-1.5",
						children: MUSIC_TRACK_OPTIONS.map((opt) => /* @__PURE__ */ jsx(Button, {
							size: "sm",
							variant: tracks.includes(opt) ? "secondary" : "outline",
							className: "h-7 px-2.5 text-xs rounded-full",
							onClick: () => toggleTrack(opt),
							children: t(`musicTasks.trackOptions.${opt}`)
						}, opt))
					})]
				})
			}), /* @__PURE__ */ jsx(NodeTextarea, {
				label: t("musicTasks.promptLabel"),
				icon: MessageSquare,
				placeholder: t("musicTasks.promptPlaceholder"),
				...form.bind("text"),
				rows: 3
			})]
		})
	});
};
MusicCompleteNode.displayName = "MusicCompleteNode";
var music_complete_default = memo(MusicCompleteNode);
//#endregion
//#region src/canvas/nodes/transfer/music-lego.tsx
const MusicLegoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("music-lego");
	const nodeId = useNodeId();
	const edges = useStore((state) => state.edges);
	const audioConnected = useMemo(() => !!nodeId && edges.some((e) => e.target === nodeId && e.targetHandle === "in:audio"), [edges, nodeId]);
	const track = form.state.track ?? "";
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "music-lego",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.musicLego"),
		icon: /* @__PURE__ */ jsx(Layers, { className: "h-5 w-5" }),
		executeLabel: t("actions.addTrack"),
		executeDisabled: !audioConnected || !track,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(Card, {
					className: "p-3 nodrag",
					onPointerDown: (e) => e.stopPropagation(),
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-1.5",
						children: [/* @__PURE__ */ jsx(Label, {
							className: "text-xs text-muted-foreground",
							children: t("musicTasks.track")
						}), /* @__PURE__ */ jsxs(Select, {
							value: track,
							onValueChange: (v) => form.set("track", v),
							children: [/* @__PURE__ */ jsx(SelectTrigger, {
								className: "h-8 text-xs",
								children: /* @__PURE__ */ jsx(SelectValue, { placeholder: t("musicTasks.track") })
							}), /* @__PURE__ */ jsx(SelectContent, { children: MUSIC_TRACK_OPTIONS.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
								value: opt,
								className: "text-xs",
								children: t(`musicTasks.trackOptions.${opt}`)
							}, opt)) })]
						})]
					})
				}),
				/* @__PURE__ */ jsx(NodeTextarea, {
					label: t("musicTasks.promptLabel"),
					icon: MessageSquare,
					placeholder: t("musicTasks.promptPlaceholder"),
					...form.bind("text"),
					rows: 3
				}),
				/* @__PURE__ */ jsx(NodeTextarea, {
					label: t("musicTasks.lyricsLabel"),
					placeholder: t("musicTasks.lyricsPlaceholder"),
					...form.bind("lyrics"),
					rows: 2
				})
			]
		})
	});
};
MusicLegoNode.displayName = "MusicLegoNode";
var music_lego_default = memo(MusicLegoNode);
//#endregion
//#region src/canvas/nodes/transfer/music-repaint.tsx
const DEFAULT_STRENGTH = .3;
const MusicRepaintNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("music-repaint");
	const nodeId = useNodeId();
	const edges = useStore((state) => state.edges);
	const audioConnected = useMemo(() => !!nodeId && edges.some((e) => e.target === nodeId && e.targetHandle === "in:audio"), [edges, nodeId]);
	const startTime = form.state.start_time;
	const endTime = form.state.end_time;
	const strength = form.state.strength ?? DEFAULT_STRENGTH;
	useEffect(() => {
		if (form.state.strength == null) form.set("strength", DEFAULT_STRENGTH, { history: false });
	}, [form.state.strength, form.set]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "music-repaint",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.musicRepaint"),
		icon: /* @__PURE__ */ jsx(Brush, { className: "h-5 w-5" }),
		executeLabel: t("actions.repaintMusic"),
		executeDisabled: !audioConnected || startTime == null || endTime == null,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				/* @__PURE__ */ jsx(NodeTextarea, {
					label: t("musicTasks.promptLabel"),
					icon: MessageSquare,
					placeholder: t("musicTasks.promptPlaceholder"),
					...form.bind("text"),
					rows: 3
				}),
				/* @__PURE__ */ jsx(NodeTextarea, {
					label: t("musicTasks.lyricsLabel"),
					placeholder: t("musicTasks.lyricsPlaceholder"),
					...form.bind("lyrics"),
					rows: 2
				}),
				/* @__PURE__ */ jsx(Card, {
					className: "p-3 nodrag",
					onPointerDown: (e) => e.stopPropagation(),
					children: /* @__PURE__ */ jsxs("div", {
						className: "grid grid-cols-2 gap-3",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "space-y-1.5",
							children: [/* @__PURE__ */ jsx(Label, {
								className: "text-xs text-muted-foreground",
								children: t("musicTasks.startTime")
							}), /* @__PURE__ */ jsx(Input, {
								type: "number",
								min: 0,
								step: .1,
								value: startTime ?? "",
								onChange: (e) => form.set("start_time", e.target.value === "" ? void 0 : Number(e.target.value)),
								className: "h-8 text-xs"
							})]
						}), /* @__PURE__ */ jsxs("div", {
							className: "space-y-1.5",
							children: [/* @__PURE__ */ jsx(Label, {
								className: "text-xs text-muted-foreground",
								children: t("musicTasks.endTime")
							}), /* @__PURE__ */ jsx(Input, {
								type: "number",
								min: 0,
								step: .1,
								value: endTime ?? "",
								onChange: (e) => form.set("end_time", e.target.value === "" ? void 0 : Number(e.target.value)),
								className: "h-8 text-xs"
							})]
						})]
					})
				}),
				/* @__PURE__ */ jsx(Card, {
					className: "p-3",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "flex items-center justify-between",
							children: [/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("musicTasks.strength")
							}), /* @__PURE__ */ jsx("span", {
								className: "text-xs font-medium",
								children: strength.toFixed(2)
							})]
						}), /* @__PURE__ */ jsx(Slider, {
							value: [strength],
							onValueChange: ([v]) => form.set("strength", v),
							min: 0,
							max: 1,
							step: .05,
							className: "w-full"
						})]
					})
				})
			]
		})
	});
};
MusicRepaintNode.displayName = "MusicRepaintNode";
var music_repaint_default = memo(MusicRepaintNode);
//#endregion
//#region src/canvas/nodes/transfer/remove-subtitle.tsx
const RemoveVideoSubtitleNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("subtitle_remove");
	const fileKeys = data.fileKeys;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "subtitle_remove",
		form,
		selected,
		data,
		title: t("titles.removeSubtitle"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.removeSubtitle"),
		executeDisabled: !fileKeys?.length
	});
};
var remove_subtitle_default = memo(RemoveVideoSubtitleNode);
//#endregion
//#region src/canvas/nodes/transfer/remove-video-audio.tsx
const RemoveVideoAudioNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("remove-video-audio");
	const fileKeys = data.fileKeys;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "remove-video-audio",
		form,
		selected,
		data,
		title: t("titles.removeVideoAudio"),
		icon: /* @__PURE__ */ jsx(VolumeX, { className: "h-5 w-5" }),
		executeLabel: t("actions.removeVideoAudio"),
		executeDisabled: !fileKeys?.length
	});
};
var remove_video_audio_default = memo(RemoveVideoAudioNode);
//#endregion
//#region src/canvas/nodes/transfer/remove-watermark.tsx
const RemoveWatermarkNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("remove_watermark");
	const fileKeys = data.fileKeys;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "remove_watermark",
		form,
		selected,
		data,
		title: t("titles.removeWatermark"),
		icon: /* @__PURE__ */ jsx(Droplets, { className: "h-5 w-5" }),
		executeLabel: t("actions.removeWatermark"),
		executeDisabled: !fileKeys?.length,
		children: /* @__PURE__ */ jsx(Card, {
			className: "mx-4 mb-4 p-4 space-y-4",
			children: /* @__PURE__ */ jsx("div", {
				className: "text-sm text-muted-foreground",
				children: t("removeWatermark.hint")
			})
		})
	});
};
var remove_watermark_default = memo(RemoveWatermarkNode);
//#endregion
//#region src/canvas/nodes/transfer/separate-audio-track.tsx
const SeparateAudioTrackNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("separate_audio_track");
	const fileKeys = data.fileKeys;
	const expands = useFlow((s) => s.expands);
	const handleTaskUpdate = useCallback((task) => {
		if (task?.status === "COMPLETED") {
			const audioKeys = task?.data?.uploadedFiles;
			if (audioKeys && audioKeys.length > 0) {
				const vocals = audioKeys.filter((fileKey) => fileKey.includes("_vocals"));
				const keys = vocals.length > 0 ? vocals : audioKeys;
				expands("", [{
					type: "audioNode",
					data: { fileKeys: keys }
				}]);
			}
			return true;
		}
		return false;
	}, [expands]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "separate_audio_track",
		form,
		selected,
		data,
		title: t("titles.separateTrack"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.extractVocals"),
		executeDisabled: !fileKeys?.length,
		onTaskUpdate: handleTaskUpdate
	});
};
var separate_audio_track_default = memo(SeparateAudioTrackNode);
//#endregion
//#region src/canvas/nodes/transfer/separate-speaker.tsx
const SeparateSpeakerNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("separate_speaker");
	const fileKeys = data.fileKeys;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "separate_speaker",
		form,
		selected,
		data,
		title: t("titles.separateSpeaker"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.startSeparation"),
		executeDisabled: !fileKeys?.length
	});
};
var separate_speaker_default = memo(SeparateSpeakerNode);
//#endregion
//#region src/canvas/nodes/transfer/speech-gen-video.tsx
const SpeechGenVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("speech-text-gen-video");
	const ids = useUpstreamNodeIds(data.ids);
	const localFileKeys = data.fileKeys ?? [];
	const fromNodes = useNodesData(ids);
	const audioNode = fromNodes.find((node) => node.type === "audioNode");
	const textNode = fromNodes.find((node) => node.type === "textNode");
	const fileKeys = useMemo(() => {
		if (audioNode) return coerceBaseNodeData(audioNode.data).fileKeys || [];
		return localFileKeys;
	}, [audioNode, localFileKeys]);
	const upstreamTexts = useMemo(() => {
		if (textNode) return coerceBaseNodeData(textNode.data).texts || [];
		return data.texts || [];
	}, [textNode, data]);
	const hasUpstreamTexts = upstreamTexts && upstreamTexts.length > 0;
	const videoPrompt = form.state.text ?? "";
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "speech-text-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.speechGenVideo"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateVideo"),
		executeDisabled: !(videoPrompt || hasUpstreamTexts) || !fileKeys?.length,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [/* @__PURE__ */ jsx(Card, {
				className: "p-3",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [
						/* @__PURE__ */ jsx(Label, {
							className: "text-sm font-medium text-muted-foreground",
							children: t("speechGenVideo.inputContent")
						}),
						/* @__PURE__ */ jsx("div", {
							className: "flex gap-4",
							children: fileKeys && fileKeys.length > 0 && /* @__PURE__ */ jsx(MediaThumbnail, {
								fileKey: fileKeys[0],
								label: t("speechGenVideo.audio"),
								type: "audio"
							})
						}),
						(!fileKeys || fileKeys.length === 0) && /* @__PURE__ */ jsx("p", {
							className: "text-xs text-red-500",
							children: t("speechGenVideo.connectAudioHint")
						})
					]
				})
			}), hasUpstreamTexts ? /* @__PURE__ */ jsx(Card, {
				className: "p-3 bg-muted/50",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: [t("speechGenVideo.audioPromptLabel"), t("imageEdit.fromUpstream")]
					}), /* @__PURE__ */ jsx("div", {
						className: "space-y-1 max-h-32 overflow-y-auto",
						children: upstreamTexts.map((text, index) => /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-3",
							children: text
						}, index))
					})]
				})
			}) : /* @__PURE__ */ jsx(NodeTextarea, {
				label: t("speechGenVideo.audioPromptLabel"),
				icon: Wand2,
				rows: 4,
				placeholder: t("speechGenVideo.audioPromptPlaceholder"),
				...form.bind("text")
			})]
		})
	});
};
SpeechGenVideoNode.displayName = "SpeechGenVideoNode";
var speech_gen_video_default = memo(SpeechGenVideoNode);
//#endregion
//#region src/canvas/nodes/base/resolution-picker.tsx
function ResolutionPicker({ tiers, value, onChange }) {
	const t = useTranslations("Workspace.nodes");
	return /* @__PURE__ */ jsx(Card, {
		className: "p-3",
		children: /* @__PURE__ */ jsxs("div", {
			className: "space-y-2",
			children: [/* @__PURE__ */ jsxs(Label, {
				className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
				children: [/* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" }), t("common.resolution")]
			}), /* @__PURE__ */ jsx("div", {
				className: "grid grid-cols-3 gap-2",
				children: tiers.map((tier) => {
					const isSelected = value === tier.value;
					return /* @__PURE__ */ jsx(Button, {
						variant: isSelected ? "default" : "outline",
						size: "sm",
						onClick: () => onChange(tier),
						className: cn("h-auto py-2 px-1 text-xs transition-all", isSelected ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent hover:text-accent-foreground"),
						children: tier.label
					}, tier.value);
				})
			})]
		})
	});
}
//#endregion
//#region src/canvas/nodes/transfer/text-gen-image.tsx
const DEFAULT_RATIO = IMAGE_ASPECT_RATIOS.find((r) => r.value === "1:1") ?? IMAGE_ASPECT_RATIOS[0];
const DEFAULT_TIER = IMAGE_RESOLUTION_TIERS[0];
const TextGenImageNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const { texts = [] } = data;
	const form = useAbiForm("image-gen");
	const width = form.state.width ?? DEFAULT_RATIO.width;
	const height = form.state.height ?? DEFAULT_RATIO.height;
	const { ratio: currentRatio, tier: currentTier } = useMemo(() => {
		for (const tier of IMAGE_RESOLUTION_TIERS) {
			const ratio = IMAGE_ASPECT_RATIOS.find((r) => r.width * tier.scale === width && r.height * tier.scale === height);
			if (ratio) return {
				ratio,
				tier
			};
		}
		return {
			ratio: DEFAULT_RATIO,
			tier: DEFAULT_TIER
		};
	}, [width, height]);
	useEffect(() => {
		if (form.state.width === void 0 || form.state.height === void 0) form.patch({
			width: DEFAULT_RATIO.width * DEFAULT_TIER.scale,
			height: DEFAULT_RATIO.height * DEFAULT_TIER.scale
		}, { history: false });
	}, [
		form.state.width,
		form.state.height,
		form.patch
	]);
	const applySize = useCallback((ratio, tier) => {
		form.patch({
			width: ratio.width * tier.scale,
			height: ratio.height * tier.scale
		});
	}, [form]);
	const handleSelectRatio = useCallback((ratio) => applySize(ratio, currentTier), [applySize, currentTier]);
	const handleSelectTier = useCallback((tier) => applySize(currentRatio, tier), [applySize, currentRatio]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "image-gen",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.textGenImage"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateImage"),
		executeDisabled: !texts?.length,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [/* @__PURE__ */ jsx(AspectRatioPicker, {
				ratios: IMAGE_ASPECT_RATIOS,
				value: {
					...currentRatio,
					width,
					height
				},
				onChange: handleSelectRatio,
				showSize: true
			}), /* @__PURE__ */ jsx(ResolutionPicker, {
				tiers: IMAGE_RESOLUTION_TIERS,
				value: currentTier.value,
				onChange: handleSelectTier
			})]
		})
	});
};
TextGenImageNode.displayName = "TextGenImageNode";
var text_gen_image_default = memo(TextGenImageNode);
//#endregion
//#region src/canvas/nodes/transfer/text-gen-music.tsx
function buildLanguageOptions(tLang) {
	return [
		{
			value: "zh",
			label: tLang("zh")
		},
		{
			value: "en",
			label: tLang("en")
		},
		{
			value: "cantonese",
			label: tLang("yue")
		},
		{
			value: "ja",
			label: tLang("ja")
		},
		{
			value: "ko",
			label: tLang("ko")
		},
		{
			value: "fr",
			label: tLang("fr")
		},
		{
			value: "es",
			label: tLang("es")
		}
	];
}
function buildBpmOptions(autoLabel) {
	return [
		{
			value: "auto",
			label: autoLabel
		},
		{
			value: "60",
			label: "60"
		},
		{
			value: "80",
			label: "80"
		},
		{
			value: "90",
			label: "90"
		},
		{
			value: "100",
			label: "100"
		},
		{
			value: "110",
			label: "110"
		},
		{
			value: "120",
			label: "120"
		},
		{
			value: "130",
			label: "130"
		},
		{
			value: "140",
			label: "140"
		},
		{
			value: "160",
			label: "160"
		},
		{
			value: "180",
			label: "180"
		}
	];
}
const KEYSCALE_OPTIONS = [
	"C major",
	"C minor",
	"D major",
	"D minor",
	"E major",
	"E minor",
	"F major",
	"F minor",
	"G major",
	"G minor",
	"A major",
	"A minor",
	"B major",
	"B minor"
];
const DEFAULT_BPM = 140;
const DEFAULT_KEYSCALE = "C minor";
const FullScreenEditor = ({ title, value, readOnly, placeholder, onClose, onSave }) => {
	const [mounted, setMounted] = useState(false);
	const [draft, setDraft] = useState(value);
	useEffect(() => {
		setMounted(true);
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "unset";
		};
	}, []);
	useEffect(() => {
		if (readOnly) setDraft(value);
	}, [readOnly, value]);
	if (!mounted) return null;
	const handleClose = () => {
		if (!readOnly && draft !== value) onSave(draft);
		onClose();
	};
	return createPortal(/* @__PURE__ */ jsx("div", {
		className: "fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4",
		children: /* @__PURE__ */ jsxs("div", {
			className: "bg-white dark:bg-slate-900 rounded-lg shadow-2xl w-11/12 h-5/6 max-h-screen flex flex-col",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0",
				children: [/* @__PURE__ */ jsx("h2", {
					className: "text-lg font-semibold text-gray-900 dark:text-white",
					children: title
				}), /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "ghost",
					onClick: handleClose,
					children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "flex-1 overflow-hidden p-6 bg-gray-50 dark:bg-slate-800",
				children: /* @__PURE__ */ jsx("textarea", {
					className: "w-full h-full resize-none rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 whitespace-pre-wrap break-words",
					value: draft,
					readOnly,
					placeholder,
					onChange: (e) => setDraft(e.target.value)
				})
			})]
		})
	}), document.body);
};
const TextGenMusicNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tModal = useTranslations("Workspace.nodes.modal");
	const LANGUAGE_OPTIONS = buildLanguageOptions(useTranslations("Languages"));
	const BPM_OPTIONS = buildBpmOptions(t("music.auto"));
	const form = useAbiForm("gen-music");
	const nodeId = useNodeId();
	const edges = useStore((state) => state.edges);
	const { tagsSourceId, lyricsSourceId, refAudioSourceId } = useMemo(() => {
		if (!nodeId) return {
			tagsSourceId: null,
			lyricsSourceId: null,
			refAudioSourceId: null
		};
		let tagsSrc = null;
		let lyricsSrc = null;
		let refAudioSrc = null;
		for (const e of edges) {
			if (e.target !== nodeId) continue;
			if (e.targetHandle === "in:tags") tagsSrc = e.source;
			else if (e.targetHandle === "in:lyrics") lyricsSrc = e.source;
			else if (e.targetHandle === "in:ref_audio") refAudioSrc = e.source;
		}
		return {
			tagsSourceId: tagsSrc,
			lyricsSourceId: lyricsSrc,
			refAudioSourceId: refAudioSrc
		};
	}, [edges, nodeId]);
	const upstreamIds = useMemo(() => [
		tagsSourceId,
		lyricsSourceId,
		refAudioSourceId
	].filter((v) => !!v), [
		tagsSourceId,
		lyricsSourceId,
		refAudioSourceId
	]);
	const upstreamNodes = useNodesData(upstreamIds);
	const tagsUpstream = useMemo(() => {
		if (!tagsSourceId) return null;
		const n = upstreamNodes.find((u) => u.id === tagsSourceId);
		if (!n || n.type !== "textNode") return null;
		return coerceBaseNodeData(n.data).texts?.[0] ?? "";
	}, [tagsSourceId, upstreamNodes]);
	const lyricsUpstream = useMemo(() => {
		if (!lyricsSourceId) return null;
		const n = upstreamNodes.find((u) => u.id === lyricsSourceId);
		if (!n || n.type !== "textNode") return null;
		return coerceBaseNodeData(n.data).texts?.[0] ?? "";
	}, [lyricsSourceId, upstreamNodes]);
	const refAudioKey = useMemo(() => {
		if (!refAudioSourceId) return void 0;
		const n = upstreamNodes.find((u) => u.id === refAudioSourceId);
		if (!n || n.type !== "audioNode") return void 0;
		return coerceBaseNodeData(n.data).fileKeys?.[0];
	}, [refAudioSourceId, upstreamNodes]);
	const tagsLocal = form.state.tags ?? "";
	const lyricsLocal = form.state.lyrics ?? "";
	const tagsDisplay = tagsUpstream !== null ? tagsUpstream : tagsLocal;
	const lyricsDisplay = lyricsUpstream !== null ? lyricsUpstream : lyricsLocal;
	const language = form.state.language ?? "zh";
	const keyscale = form.state.keyscale ?? DEFAULT_KEYSCALE;
	const bpm = form.state.bpm == null ? String(DEFAULT_BPM) : String(form.state.bpm);
	const duration = form.state.duration ?? 30;
	useEffect(() => {
		if (form.state.bpm == null) form.set("bpm", DEFAULT_BPM, { history: false });
	}, [form.state.bpm, form.set]);
	useEffect(() => {
		if (form.state.keyscale == null) form.set("keyscale", DEFAULT_KEYSCALE, { history: false });
	}, [form.state.keyscale, form.set]);
	const [tagsExpanded, setTagsExpanded] = useState(false);
	const [lyricsExpanded, setLyricsExpanded] = useState(false);
	const canExecute = !!tagsDisplay.trim() || !!lyricsDisplay.trim() || !!(data.texts?.[0] && String(data.texts[0]).trim());
	return /* @__PURE__ */ jsxs(AbiNodeShell, {
		feature: "gen-music",
		form,
		selected,
		className: "min-w-[520px]",
		data,
		title: t("titles.textGenMusic"),
		icon: /* @__PURE__ */ jsx(Music, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateMusic"),
		executeDisabled: !canExecute,
		children: [
			/* @__PURE__ */ jsxs("div", {
				className: "p-4 space-y-4",
				children: [
					/* @__PURE__ */ jsxs(Card, {
						className: "p-3",
						children: [
							/* @__PURE__ */ jsxs("div", {
								className: "flex items-center justify-between gap-2 mb-2",
								children: [/* @__PURE__ */ jsxs(Label, {
									className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
									children: [/* @__PURE__ */ jsx(Tag, { className: "h-4 w-4" }), t("music.styleSettings")]
								}), /* @__PURE__ */ jsx(Button, {
									size: "sm",
									variant: "ghost",
									className: "h-6 w-6 p-0",
									onClick: () => setTagsExpanded(true),
									title: tModal("fullScreenPreview"),
									children: /* @__PURE__ */ jsx(Maximize2, { className: "h-3.5 w-3.5" })
								})]
							}),
							/* @__PURE__ */ jsx(Textarea, {
								placeholder: tagsUpstream !== null ? t("music.fromUpstreamReadonly") : t("music.tagsPlaceholder"),
								value: tagsDisplay,
								onChange: (e) => form.set("tags", e.target.value),
								readOnly: tagsUpstream !== null,
								rows: 2,
								className: "max-h-[80px] resize-none text-xs whitespace-pre-wrap break-words overflow-auto"
							}),
							/* @__PURE__ */ jsx("p", {
								className: "text-xs text-muted-foreground mt-1.5",
								children: t("music.tagsHint")
							})
						]
					}),
					/* @__PURE__ */ jsxs(Card, {
						className: "p-3",
						children: [/* @__PURE__ */ jsxs("div", {
							className: "flex items-center justify-between gap-2 mb-2",
							children: [/* @__PURE__ */ jsx(Label, {
								className: "text-sm font-medium text-muted-foreground",
								children: t("music.inputLyrics")
							}), /* @__PURE__ */ jsx(Button, {
								size: "sm",
								variant: "ghost",
								className: "h-6 w-6 p-0",
								onClick: () => setLyricsExpanded(true),
								title: tModal("fullScreenPreview"),
								children: /* @__PURE__ */ jsx(Maximize2, { className: "h-3.5 w-3.5" })
							})]
						}), /* @__PURE__ */ jsx(Textarea, {
							placeholder: lyricsUpstream !== null ? t("music.fromUpstreamReadonly") : t("music.lyricsPlaceholder"),
							value: lyricsDisplay,
							onChange: (e) => form.set("lyrics", e.target.value),
							readOnly: lyricsUpstream !== null,
							className: "min-h-[120px] max-h-[180px] resize-none text-xs overflow-auto whitespace-pre-wrap break-words"
						})]
					}),
					/* @__PURE__ */ jsx(Card, {
						className: "p-3",
						children: /* @__PURE__ */ jsxs("div", {
							className: "grid grid-cols-3 gap-3",
							children: [
								/* @__PURE__ */ jsxs("div", {
									className: "space-y-1.5",
									children: [/* @__PURE__ */ jsx(Label, {
										className: "text-xs text-muted-foreground",
										children: t("music.language")
									}), /* @__PURE__ */ jsxs(Select, {
										value: language,
										onValueChange: (v) => form.set("language", v),
										children: [/* @__PURE__ */ jsx(SelectTrigger, {
											className: "h-8 text-xs",
											children: /* @__PURE__ */ jsx(SelectValue, {})
										}), /* @__PURE__ */ jsx(SelectContent, { children: LANGUAGE_OPTIONS.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
											value: opt.value,
											className: "text-xs",
											children: opt.label
										}, opt.value)) })]
									})]
								}),
								/* @__PURE__ */ jsxs("div", {
									className: "space-y-1.5",
									children: [/* @__PURE__ */ jsx(Label, {
										className: "text-xs text-muted-foreground",
										children: t("music.keyscale")
									}), /* @__PURE__ */ jsxs(Select, {
										value: keyscale,
										onValueChange: (v) => form.set("keyscale", v),
										children: [/* @__PURE__ */ jsx(SelectTrigger, {
											className: "h-8 text-xs",
											children: /* @__PURE__ */ jsx(SelectValue, {})
										}), /* @__PURE__ */ jsx(SelectContent, { children: KEYSCALE_OPTIONS.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
											value: opt,
											className: "text-xs",
											children: opt
										}, opt)) })]
									})]
								}),
								/* @__PURE__ */ jsxs("div", {
									className: "space-y-1.5",
									children: [/* @__PURE__ */ jsx(Label, {
										className: "text-xs text-muted-foreground",
										children: t("music.bpm")
									}), /* @__PURE__ */ jsxs(Select, {
										value: bpm,
										onValueChange: (v) => form.set("bpm", v === "auto" ? void 0 : Number(v)),
										children: [/* @__PURE__ */ jsx(SelectTrigger, {
											className: "h-8 text-xs",
											children: /* @__PURE__ */ jsx(SelectValue, { placeholder: t("music.auto") })
										}), /* @__PURE__ */ jsx(SelectContent, { children: BPM_OPTIONS.map((opt) => /* @__PURE__ */ jsx(SelectItem, {
											value: opt.value,
											className: "text-xs",
											children: opt.label
										}, opt.value)) })]
									})]
								})
							]
						})
					}),
					/* @__PURE__ */ jsx(Card, {
						className: "p-3",
						children: /* @__PURE__ */ jsxs("div", {
							className: "space-y-2",
							children: [
								/* @__PURE__ */ jsxs("div", {
									className: "flex items-center justify-between",
									children: [/* @__PURE__ */ jsxs(Label, {
										className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
										children: [/* @__PURE__ */ jsx(Clock, { className: "h-4 w-4" }), t("music.audioDuration")]
									}), /* @__PURE__ */ jsx("span", {
										className: "text-xs font-medium",
										children: duration >= 60 ? `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, "0")}` : `${duration}s`
									})]
								}),
								/* @__PURE__ */ jsx(Slider, {
									value: [duration],
									onValueChange: ([v]) => {
										const snapPoint = Math.round(v / 30) * 30;
										const snapped = Math.abs(v - snapPoint) <= 5 ? snapPoint : v;
										form.set("duration", Math.max(30, Math.min(240, snapped)));
									},
									min: 30,
									max: 240,
									step: 1,
									className: "w-full"
								}),
								/* @__PURE__ */ jsxs("div", {
									className: "flex justify-between text-[10px] text-muted-foreground",
									children: [
										/* @__PURE__ */ jsx("span", { children: "0:30" }),
										/* @__PURE__ */ jsx("span", { children: "1:00" }),
										/* @__PURE__ */ jsx("span", { children: "1:30" }),
										/* @__PURE__ */ jsx("span", { children: "2:00" }),
										/* @__PURE__ */ jsx("span", { children: "2:30" }),
										/* @__PURE__ */ jsx("span", { children: "3:00" }),
										/* @__PURE__ */ jsx("span", { children: "3:30" }),
										/* @__PURE__ */ jsx("span", { children: "4:00" })
									]
								})
							]
						})
					}),
					refAudioKey && /* @__PURE__ */ jsxs(Card, {
						className: "p-3",
						children: [/* @__PURE__ */ jsx(Label, {
							className: "text-sm font-medium text-muted-foreground mb-2 block",
							children: t("music.referenceAudio")
						}), /* @__PURE__ */ jsx(MediaThumbnail, {
							fileKey: refAudioKey,
							label: t("music.referenceAudio"),
							type: "audio"
						})]
					})
				]
			}),
			tagsExpanded && /* @__PURE__ */ jsx(FullScreenEditor, {
				title: t("music.styleSettings"),
				value: tagsDisplay,
				readOnly: tagsUpstream !== null,
				placeholder: t("music.tagsPlaceholder"),
				onClose: () => setTagsExpanded(false),
				onSave: (next) => form.set("tags", next)
			}),
			lyricsExpanded && /* @__PURE__ */ jsx(FullScreenEditor, {
				title: t("music.inputLyrics"),
				value: lyricsDisplay,
				readOnly: lyricsUpstream !== null,
				placeholder: t("music.lyricsPlaceholder"),
				onClose: () => setLyricsExpanded(false),
				onSave: (next) => form.set("lyrics", next)
			})
		]
	});
};
TextGenMusicNode.displayName = "TextGenMusicNode";
var text_gen_music_default = memo(TextGenMusicNode);
//#endregion
//#region src/canvas/nodes/transfer/text-gen-speech-clone.tsx
const TextGenSpeechCloneNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("text-gen-speech-clone");
	const texts = data.texts ?? [];
	const refAudio = data.ref_audio;
	const setVoice = (v) => form.patch({ ref_audio: v });
	const { upload, isUploading } = useUpload({
		onSuccess: (resp) => setVoice(resp.key),
		onError: (err) => logger.error("Voice upload failed:", err)
	});
	const onFileSelect = async (e) => {
		const file = e.target.files?.[0];
		if (file) await upload(file);
		e.target.value = "";
	};
	const audioRef = useRef(null);
	const playPreview = useCallback(() => {
		if (!refAudio) return;
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current = null;
		}
		const audio = new Audio(getFileUrl(refAudio));
		audioRef.current = audio;
		audio.play().catch((err) => logger.error("Failed to play reference audio:", err));
	}, [refAudio]);
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "text-gen-speech-clone",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.textGenSpeechClone"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateSpeech"),
		executeDisabled: !texts?.length || !refAudio,
		children: /* @__PURE__ */ jsxs(Card, {
			className: "mx-4 mb-4 p-4 nodrag",
			onPointerDown: (e) => e.stopPropagation(),
			children: [
				/* @__PURE__ */ jsxs("div", {
					className: "mb-3 flex flex-wrap items-center gap-3",
					children: [
						/* @__PURE__ */ jsxs(Label, {
							className: "text-sm text-muted-foreground",
							children: [t("common.referenceAudio"), "："]
						}),
						/* @__PURE__ */ jsxs("label", {
							className: "cursor-pointer",
							children: [/* @__PURE__ */ jsx("input", {
								type: "file",
								accept: "audio/*",
								hidden: true,
								onChange: onFileSelect
							}), /* @__PURE__ */ jsx(Button, {
								variant: "outline",
								size: "icon",
								title: t("common.uploadVoice"),
								disabled: isUploading,
								asChild: true,
								children: /* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsx(Upload, { className: "w-4 h-4" }) })
							})]
						}),
						/* @__PURE__ */ jsx(SpeakerVoiceRecorder, {
							trigger: /* @__PURE__ */ jsx(Button, {
								variant: "outline",
								size: "icon",
								title: t("common.recordVoice"),
								children: /* @__PURE__ */ jsx(Mic, { className: "w-4 h-4" })
							}),
							onChange: (key) => setVoice(key)
						}),
						/* @__PURE__ */ jsx(Button, {
							type: "button",
							variant: "ghost",
							size: "icon",
							title: t("common.previewVoice"),
							onClick: playPreview,
							disabled: !refAudio,
							children: /* @__PURE__ */ jsx(Ear, { className: "w-5 h-5 text-primary" })
						}),
						/* @__PURE__ */ jsxs(Label, {
							htmlFor: "clone-language-select",
							className: "text-sm text-muted-foreground",
							children: [t("common.language"), "："]
						}),
						/* @__PURE__ */ jsx(LanguageSelect, {
							id: "clone-language-select",
							value: form.state.language ?? "Auto",
							onChange: (v) => form.set("language", v)
						})
					]
				}),
				/* @__PURE__ */ jsx("div", {
					className: "mb-4 text-xs text-muted-foreground truncate",
					children: refAudio ? refAudio : t("common.noReferenceAudio")
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "mb-4",
					children: [/* @__PURE__ */ jsxs("label", {
						htmlFor: "ref-text-input",
						className: "text-sm text-muted-foreground block mb-2",
						children: [t("common.referenceText"), "："]
					}), /* @__PURE__ */ jsx("textarea", {
						id: "ref-text-input",
						className: "w-full h-16 p-2 text-sm rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring",
						placeholder: t("common.referenceTextCloneHint"),
						...form.register("ref_text")
					})]
				}),
				texts && texts.length > 0 && /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: [
							t("common.inputText"),
							" (",
							texts.length,
							")"
						]
					}), /* @__PURE__ */ jsx("div", {
						className: "space-y-1 max-h-32 overflow-y-auto",
						children: texts.map((text, index) => /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-2",
							children: text
						}, `${index}-${text.slice(0, 48)}`))
					})]
				})
			]
		})
	});
};
TextGenSpeechCloneNode.displayName = "TextGenSpeechCloneNode";
var text_gen_speech_clone_default = memo(TextGenSpeechCloneNode);
//#endregion
//#region src/canvas/nodes/transfer/text-gen-speech-instruct.tsx
const TextGenSpeechInstructNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("text-gen-speech-instruct");
	const texts = data.texts ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "text-gen-speech-instruct",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.textGenSpeechInstruct"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateSpeech"),
		executeDisabled: !texts?.length,
		children: /* @__PURE__ */ jsxs(Card, {
			className: "mx-4 mb-4 p-4 nodrag",
			onPointerDown: (e) => e.stopPropagation(),
			children: [
				/* @__PURE__ */ jsxs("div", {
					className: "mb-4 flex flex-wrap items-center gap-3",
					children: [/* @__PURE__ */ jsxs(Label, {
						htmlFor: "instruct-language-select",
						className: "text-sm text-muted-foreground",
						children: [t("common.language"), "："]
					}), /* @__PURE__ */ jsx(LanguageSelect, {
						id: "instruct-language-select",
						value: form.state.language ?? "Chinese",
						onChange: (v) => form.set("language", v)
					})]
				}),
				/* @__PURE__ */ jsxs("div", {
					className: "mb-4",
					children: [/* @__PURE__ */ jsxs("label", {
						htmlFor: "instruct-input",
						className: "text-sm text-muted-foreground block mb-2",
						children: [t("common.voiceDescription"), "："]
					}), /* @__PURE__ */ jsx("textarea", {
						id: "instruct-input",
						className: "w-full h-24 p-2 text-sm rounded-md border border-input bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring",
						placeholder: t("common.voiceDescriptionPlaceholder"),
						...form.register("instruct")
					})]
				}),
				texts && texts.length > 0 && /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: [
							t("common.inputText"),
							" (",
							texts.length,
							")"
						]
					}), /* @__PURE__ */ jsx("div", {
						className: "space-y-1 max-h-32 overflow-y-auto",
						children: texts.map((text, index) => /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-2",
							children: text
						}, `${index}-${text.slice(0, 48)}`))
					})]
				})
			]
		})
	});
};
TextGenSpeechInstructNode.displayName = "TextGenSpeechInstructNode";
var text_gen_speech_instruct_default = memo(TextGenSpeechInstructNode);
//#endregion
//#region src/canvas/nodes/transfer/text-gen-speech-preset.tsx
const TextGenSpeechPresetNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tLang = useTranslations("Languages");
	const form = useAbiForm("text-gen-speech-preset");
	const texts = data.texts ?? [];
	const speaker = form.state.speaker ?? DEFAULT_QWEN_SPEAKER.value;
	const setForm = form.set;
	const patch = form.patch;
	useEffect(() => {
		const next = {};
		if (form.state.speaker === void 0) next.speaker = DEFAULT_QWEN_SPEAKER.value;
		if (form.state.language === void 0) next.language = DEFAULT_QWEN_SPEAKER.language;
		if (Object.keys(next).length > 0) patch(next);
	}, [
		form.state.speaker,
		form.state.language,
		patch
	]);
	const handleSpeakerChange = (value) => {
		const sp = QWEN_SPEAKERS.find((s) => s.value === value);
		patch(sp ? {
			speaker: value,
			language: sp.language
		} : { speaker: value });
	};
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "text-gen-speech-preset",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.textGenSpeechPreset"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: t("actions.generateSpeech"),
		executeDisabled: !texts?.length,
		children: /* @__PURE__ */ jsxs(Card, {
			className: "mx-4 mb-4 p-4 nodrag",
			onPointerDown: (e) => e.stopPropagation(),
			children: [/* @__PURE__ */ jsxs("div", {
				className: "mb-4 flex flex-wrap items-center gap-3",
				children: [
					/* @__PURE__ */ jsxs(Label, {
						htmlFor: "speaker-select",
						className: "text-sm text-muted-foreground",
						children: [t("common.voice"), "："]
					}),
					/* @__PURE__ */ jsxs(Select, {
						value: speaker,
						onValueChange: handleSpeakerChange,
						children: [/* @__PURE__ */ jsx(SelectTrigger, {
							id: "speaker-select",
							className: "w-44 h-9",
							children: /* @__PURE__ */ jsx(SelectValue, {})
						}), /* @__PURE__ */ jsx(SelectContent, { children: QWEN_SPEAKERS.map((sp) => /* @__PURE__ */ jsxs(SelectItem, {
							value: sp.value,
							children: [
								sp.value,
								" · ",
								t(`genders.${sp.gender}`),
								" ·",
								" ",
								tLang(sp.langKey)
							]
						}, sp.value)) })]
					}),
					/* @__PURE__ */ jsxs(Label, {
						htmlFor: "preset-language-select",
						className: "text-sm text-muted-foreground",
						children: [t("common.language"), "："]
					}),
					/* @__PURE__ */ jsx(LanguageSelect, {
						id: "preset-language-select",
						value: form.state.language ?? "Chinese",
						onChange: (v) => setForm("language", v)
					})
				]
			}), texts && texts.length > 0 && /* @__PURE__ */ jsxs("div", {
				className: "space-y-2",
				children: [/* @__PURE__ */ jsxs(Label, {
					className: "text-sm font-medium text-muted-foreground",
					children: [
						t("common.inputText"),
						" (",
						texts.length,
						")"
					]
				}), /* @__PURE__ */ jsx("div", {
					className: "space-y-1 max-h-32 overflow-y-auto",
					children: texts.map((text, index) => /* @__PURE__ */ jsx("div", {
						className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-2",
						children: text
					}, `${index}-${text.slice(0, 48)}`))
				})]
			})]
		})
	});
};
TextGenSpeechPresetNode.displayName = "TextGenSpeechPresetNode";
var text_gen_speech_preset_default = memo(TextGenSpeechPresetNode);
//#endregion
//#region src/canvas/nodes/transfer/text-gen-text.tsx
const GenTextNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tBase = useTranslations("Workspace.nodes.base");
	const form = useAbiForm("gen-text");
	const { texts: localTexts = [] } = data;
	const ids = useUpstreamNodeIds(data.ids);
	const textNodes = useNodesData(ids).filter((node) => node.type === "textNode");
	const texts = useMemo(() => {
		if (textNodes.length > 0) return textNodes[0].data?.texts || [];
		return localTexts;
	}, [textNodes, localTexts]);
	const upstreamPrompt = useMemo(() => {
		if (textNodes.length > 1) return (textNodes[1].data?.texts || [])[0] || "";
		return "";
	}, [textNodes]);
	const hasUpstreamPrompt = !!upstreamPrompt;
	const userPrompt = form.state.userPrompt ?? "";
	const effectivePrompt = hasUpstreamPrompt ? upstreamPrompt : userPrompt;
	const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
	const [fullscreenValue, setFullscreenValue] = useState("");
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "gen-text",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.textGenText"),
		icon: /* @__PURE__ */ jsx(Wand2, { className: "h-5 w-5" }),
		executeLabel: tBase("execute"),
		executeDisabled: !effectivePrompt.trim() || !texts?.length,
		headerActions: !hasUpstreamPrompt ? /* @__PURE__ */ jsx(Button, {
			size: "sm",
			variant: "ghost",
			className: "nodrag size-6 p-1",
			onClick: () => {
				setFullscreenValue(userPrompt);
				setIsFullscreenOpen(true);
			},
			title: tBase("fullscreenEdit"),
			children: /* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" })
		}) : void 0,
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx("div", {
				className: "space-y-2",
				children: hasUpstreamPrompt ? /* @__PURE__ */ jsx(Card, {
					className: "p-3 bg-muted/50",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs(Label, {
							className: "text-sm font-medium text-muted-foreground",
							children: [t("textGenText.instructions"), t("imageEdit.fromUpstream")]
						}), /* @__PURE__ */ jsx("div", {
							className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 max-h-32 overflow-y-auto",
							children: upstreamPrompt
						})]
					})
				}) : /* @__PURE__ */ jsx(NodeTextarea, {
					rows: 6,
					placeholder: t("common.enterInstructions"),
					...form.bind("userPrompt"),
					className: "min-h-[120px] max-h-[200px] overflow-y-auto",
					enableFullscreen: false
				})
			})
		})
	}), /* @__PURE__ */ jsx(Dialog, {
		open: isFullscreenOpen,
		onOpenChange: (open) => {
			if (!open) setIsFullscreenOpen(false);
		},
		children: /* @__PURE__ */ jsxs(DialogContent, {
			className: "w-[90vw] h-[90vh] max-w-none flex flex-col",
			"aria-describedby": void 0,
			children: [
				/* @__PURE__ */ jsx(DialogHeader, {
					className: "flex-shrink-0",
					children: /* @__PURE__ */ jsx(DialogTitle, { children: t("textGenText.instructions") })
				}),
				/* @__PURE__ */ jsx("div", {
					className: "flex-1 min-h-0 flex flex-col",
					children: /* @__PURE__ */ jsx(Textarea, {
						value: fullscreenValue,
						onChange: (e) => setFullscreenValue(e.target.value),
						placeholder: t("common.enterInstructions"),
						className: "resize-none h-full w-full overflow-y-auto flex-1"
					})
				}),
				/* @__PURE__ */ jsxs(DialogFooter, {
					className: "flex-shrink-0",
					children: [/* @__PURE__ */ jsx(Button, {
						variant: "outline",
						onClick: () => setIsFullscreenOpen(false),
						children: tBase("cancel")
					}), /* @__PURE__ */ jsx(Button, {
						onClick: () => {
							form.set("userPrompt", fullscreenValue);
							setIsFullscreenOpen(false);
						},
						children: tBase("confirm")
					})]
				})
			]
		})
	})] });
};
GenTextNode.displayName = "GenTextNode";
var text_gen_text_default = memo(GenTextNode);
//#endregion
//#region src/canvas/nodes/transfer/text-gen-video.tsx
const TextGenVideoNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const tActions = useTranslations("Workspace.nodes.actions");
	const form = useAbiForm("text-gen-video");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const ids = useUpstreamNodeIds(data.ids);
	const resolvedSpec = useNodeAbiSpec("text-gen-video");
	const composeTextNode = useNodesData(ids).find((node) => node.type === "textNode");
	const upstreamTexts = useMemo(() => {
		if (composeTextNode) return coerceBaseNodeData(composeTextNode.data).texts ?? [];
		if (nodeId) {
			const nodes = Array.from(nodeLookup.values());
			const text = collectHandleValues(nodeId, resolvedSpec, nodes, edges).text;
			if (Array.isArray(text)) return text.filter((item) => typeof item === "string" && item.trim().length > 0);
			if (typeof text === "string" && text.trim()) return [text];
		}
		return data.texts ?? [];
	}, [
		composeTextNode,
		nodeId,
		nodeLookup,
		edges,
		resolvedSpec,
		data.texts
	]);
	const hasUpstreamTexts = upstreamTexts.length > 0;
	const localText = form.state.text ?? "";
	const executeDisabled = !hasUpstreamTexts && !localText.trim();
	const width = form.state.width ?? 1024;
	const height = form.state.height ?? 576;
	const durationSeconds = form.state.duration ?? 5;
	useEffect(() => {
		if (form.state.duration === void 0) form.set("duration", 5, { history: false });
	}, [form.state.duration, form.set]);
	const currentRatio = VIDEO_ASPECT_RATIOS.find((r) => r.width === width && r.height === height) ?? VIDEO_ASPECT_RATIOS[1];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "text-gen-video",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.textGenVideo"),
		icon: /* @__PURE__ */ jsx(Atom, { className: "h-5 w-5" }),
		executeLabel: tActions("generateVideo"),
		executeDisabled,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [
				hasUpstreamTexts ? /* @__PURE__ */ jsx(Card, {
					className: "p-3 bg-muted/50",
					children: /* @__PURE__ */ jsxs("div", {
						className: "space-y-2",
						children: [/* @__PURE__ */ jsxs(Label, {
							className: "text-sm font-medium text-muted-foreground",
							children: [t("common.videoDesc"), t("imageEdit.fromUpstream")]
						}), /* @__PURE__ */ jsx("div", {
							className: "space-y-1 max-h-32 overflow-y-auto",
							children: upstreamTexts.map((text, index) => /* @__PURE__ */ jsx("div", {
								className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-3",
								children: text
							}, index))
						})]
					})
				}) : /* @__PURE__ */ jsx(NodeTextarea, {
					rows: 4,
					placeholder: t("common.videoDesc"),
					...form.bind("text")
				}),
				/* @__PURE__ */ jsx(AspectRatioPicker, {
					ratios: VIDEO_ASPECT_RATIOS,
					value: currentRatio,
					onChange: (ratio) => form.patch({
						width: ratio.width,
						height: ratio.height
					}),
					showSize: true
				}),
				/* @__PURE__ */ jsx(VideoDurationSlider, {
					value: durationSeconds,
					onChange: (dur) => form.set("duration", dur)
				})
			]
		})
	});
};
TextGenVideoNode.displayName = "TextGenVideoNode";
var text_gen_video_default = memo(TextGenVideoNode);
//#endregion
//#region src/canvas/nodes/transfer/video-edit.tsx
const VideoEditNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("video-edit");
	const nodeId = useNodeId();
	const nodeLookup = useStore((state) => state.nodeLookup);
	const edges = useStore((state) => state.edges);
	const resolvedSpec = useNodeAbiSpec("video-edit");
	const { hasVideo, promptText } = useMemo(() => {
		if (!nodeId) return {
			hasVideo: false,
			promptText: ""
		};
		const values = collectHandleValues(nodeId, resolvedSpec, Array.from(nodeLookup.values()), edges);
		const text = typeof values.text === "string" ? values.text.trim() : "";
		const videoRaw = values.video;
		const videoKey = Array.isArray(videoRaw) ? typeof videoRaw[0] === "string" ? videoRaw[0] : void 0 : typeof videoRaw === "string" ? videoRaw : void 0;
		return {
			hasVideo: Boolean(videoKey),
			promptText: text
		};
	}, [
		nodeId,
		resolvedSpec,
		nodeLookup,
		edges
	]);
	const manualText = form.state.text?.trim() ?? "";
	const effectiveText = promptText || manualText;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "video-edit",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.videoEdit"),
		icon: /* @__PURE__ */ jsx(Clapperboard, { className: "h-5 w-5" }),
		executeLabel: t("actions.editVideo"),
		executeDisabled: !hasVideo || !effectiveText,
		children: /* @__PURE__ */ jsxs("div", {
			className: "p-4 space-y-4",
			children: [!hasVideo && /* @__PURE__ */ jsx("p", {
				className: "text-xs text-red-500",
				children: t("videoEdit.connectTextVideo")
			}), promptText ? /* @__PURE__ */ jsx(Card, {
				className: "p-3 bg-muted/50",
				children: /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [/* @__PURE__ */ jsxs(Label, {
						className: "text-sm font-medium text-muted-foreground",
						children: [t("videoEdit.editInstruction"), t("videoEdit.fromUpstream")]
					}), /* @__PURE__ */ jsx("div", {
						className: "text-sm text-foreground p-2 bg-background rounded border border-border/50 line-clamp-3",
						children: promptText
					})]
				})
			}) : /* @__PURE__ */ jsx(NodeTextarea, {
				label: t("videoEdit.editInstruction"),
				icon: Sparkles,
				placeholder: t("videoEdit.editPlaceholder"),
				...form.bind("text"),
				rows: 4
			})]
		})
	});
};
VideoEditNode.displayName = "VideoEditNode";
var video_edit_default = memo(VideoEditNode);
//#endregion
//#region src/canvas/nodes/transfer/video-gen-model.tsx
const VideoGenModelNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("video-gen-model");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "video-gen-model",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.videoGenModel"),
		icon: /* @__PURE__ */ jsx(Move3d, { className: "h-5 w-5" }),
		executeLabel: t("actions.captureMotion"),
		executeDisabled: !fileKeys?.length
	});
};
VideoGenModelNode.displayName = "VideoGenModelNode";
var video_gen_model_default = memo(VideoGenModelNode);
//#endregion
//#region src/canvas/nodes/transfer/video-gen-text.tsx
const VideoGenTextNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("video-gen-text");
	const fileKeys = data.fileKeys ?? [];
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "video-gen-text",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.videoGenText"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: t("actions.describeVideo"),
		executeDisabled: !fileKeys?.length,
		children: /* @__PURE__ */ jsx("div", {
			className: "p-4 space-y-4",
			children: /* @__PURE__ */ jsx(NodeTextarea, {
				label: t("videoGenText.promptLabel"),
				icon: MessageSquare,
				placeholder: t("videoGenText.promptPlaceholder"),
				...form.bind("text"),
				rows: 3
			})
		})
	});
};
VideoGenTextNode.displayName = "VideoGenTextNode";
var video_gen_text_default = memo(VideoGenTextNode);
//#endregion
//#region src/canvas/nodes/transfer/video-gen-text-speech-recognize.tsx
const VideoGenTextSpeechRecognizeNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("transcribe");
	const { fileKeys = [] } = data;
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "transcribe",
		form,
		selected,
		className: "min-w-[480px]",
		data,
		title: t("titles.speechRecognize"),
		icon: /* @__PURE__ */ jsx(Video, { className: "h-5 w-5" }),
		executeLabel: t("actions.describeVideo"),
		executeDisabled: !fileKeys?.length
	});
};
VideoGenTextSpeechRecognizeNode.displayName = "VideoGenTextSpeechRecognizeNode";
var video_gen_text_speech_recognize_default = memo(VideoGenTextSpeechRecognizeNode);
//#endregion
//#region src/canvas/nodes/transfer/video-upscale.tsx
const UPSCALE_TIERS = [{
	value: "1k",
	labelKey: "upscaleTier1k"
}, {
	value: "2k",
	labelKey: "upscaleTier2k"
}];
const VideoUpscaleNode = ({ selected, data }) => {
	const t = useTranslations("Workspace.nodes");
	const form = useAbiForm("video-upscale");
	const fileKeys = data.fileKeys ?? [];
	const stateResolution = form.state.resolution;
	const resolution = stateResolution === "1k" || stateResolution === "2k" ? stateResolution : "2k";
	return /* @__PURE__ */ jsx(AbiNodeShell, {
		feature: "video-upscale",
		form,
		selected,
		data,
		title: t("titles.videoUpscale"),
		icon: /* @__PURE__ */ jsx(Sparkles, { className: "h-5 w-5" }),
		executeLabel: t("actions.startUpscale"),
		executeDisabled: !fileKeys?.length,
		children: /* @__PURE__ */ jsxs(Card, {
			className: "mx-4 mb-4 p-3 space-y-2",
			children: [
				/* @__PURE__ */ jsxs(Label, {
					className: "flex items-center gap-2 text-sm font-medium text-muted-foreground",
					children: [/* @__PURE__ */ jsx(Maximize2, { className: "h-4 w-4" }), t("common.upscaleTierLabel")]
				}),
				/* @__PURE__ */ jsx("div", {
					className: "grid grid-cols-2 gap-2",
					children: UPSCALE_TIERS.map((tier) => /* @__PURE__ */ jsx(Button, {
						variant: resolution === tier.value ? "default" : "outline",
						size: "sm",
						onClick: () => form.set("resolution", tier.value),
						className: cn("h-auto py-2 px-1 flex flex-col gap-0.5 text-xs", resolution === tier.value ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent"),
						children: /* @__PURE__ */ jsx("span", {
							className: "font-medium",
							children: t(`common.${tier.labelKey}`)
						})
					}, tier.value))
				}),
				/* @__PURE__ */ jsx("p", {
					className: "text-xs text-muted-foreground leading-snug",
					children: t("common.upscaleTierHint")
				}),
				fileKeys?.length > 0 && /* @__PURE__ */ jsxs("div", {
					className: "text-xs text-gray-500 pt-1 border-t",
					children: [
						t("video.fileCount"),
						" ",
						fileKeys.length
					]
				})
			]
		})
	});
};
//#endregion
//#region src/canvas/node-types.tsx
/**
* Node type mapping
* Based on the full list from the saas project
*/
const NODE_TYPES = {
	imageNode: image_node_default,
	textNode: text_node_default,
	videoNode: video_node_default,
	audioNode: audio_node_default,
	fileNode: file_node_default,
	modelNode: model_node_default,
	linkNode: link_node_default,
	addImageNode: add_image_node_default,
	addTextNode: add_text_node_default,
	addAudioNode: add_audio_node_default,
	addVideoNode: add_video_node_default,
	addLinkNode: add_link_node_default,
	addFileNode: add_file_node_default,
	addModelNode: AddModelNode,
	imageGenVideoNode: image_gen_video_default,
	imageGenVideoComposeNode: image_gen_video_compose_default,
	textGenVideoNode: text_gen_video_default,
	imageGenModelNode: image_gen_model_default,
	imagePoseNode: image_pose_default,
	imageBodySegNode: image_body_seg_default,
	imageNormalNode: image_normal_default,
	imageMattingNode: image_matting_default,
	videoGenModelNode: video_gen_model_default,
	speechGenVideoNode: speech_gen_video_default,
	imageGenImageNode: image_gen_image_default,
	imageGenImageUpscaleNode: image_gen_image_upscale_default,
	genTextNode: text_gen_text_default,
	textGenImageNode: text_gen_image_default,
	textGenMusicNode: text_gen_music_default,
	textGenSpeechCloneNode: text_gen_speech_clone_default,
	textGenSpeechCloneComposeNode: text_gen_speech_clone_compose_default,
	textGenSpeechPresetNode: text_gen_speech_preset_default,
	textGenSpeechInstructNode: text_gen_speech_instruct_default,
	removeVideoSubtitleNode: remove_subtitle_default,
	videoUpscaleNode: memo(VideoUpscaleNode),
	videoEditNode: video_edit_default,
	removeWatermarkNode: remove_watermark_default,
	extractAudioNode: extract_audio_default,
	removeVideoAudioNode: remove_video_audio_default,
	denoiseAudioSubtitleNode: denoise_audio_default,
	separateAudioTrackNode: separate_audio_track_default,
	separateSpeakerNode: separate_speaker_default,
	convertVoiceNode: convert_voice_default,
	audioDescribeNode: audio_describe_default,
	imageGenTextNode: image_gen_text_default,
	videoGenTextNode: video_gen_text_default,
	videoGenTextSpeechRecognizeNode: video_gen_text_speech_recognize_default,
	audioGenTextSpeechRecognizeNode: audio_gen_text_speech_recognize_default,
	fileGenTextNode: file_gen_text_default,
	linkGenTextNode: link_gen_text_default,
	getFirstFrameNode: get_first_frame_default,
	getLastFrameNode: get_last_frame_default,
	musicRepaintNode: music_repaint_default,
	musicExtractNode: music_extract_default,
	musicLegoNode: music_lego_default,
	musicCompleteNode: music_complete_default,
	dropVideoNode: drop_video_default,
	arrangeNode: arrange_text_default,
	concatVideoNode: concat_video_default,
	mergeVideoAudioNode: merge_video_audio_default,
	audioVideoLipSyncNode: audio_video_lip_sync_default,
	imageFusionNode: image_fusion_default,
	imagesGenVideoNode: images_gen_video_default,
	refsGenVideoNode: refs_gen_video_default,
	speechImageGenVideoNode: speech_image_gen_video_default,
	speechTextGenVideoNode: speech_text_gen_video_default,
	speechVideoGenVideoNode: speech_video_gen_video_default,
	videoImageGenVideoMixNode: video_image_gen_video_mix_default,
	videoImageGenVideoMoveNode: video_image_gen_video_move_default,
	imageImageGenVideoNode: image_image_gen_video_default,
	textAudioGenSpeechNode: text_audio_gen_speech_default,
	textsGenTextNode: texts_gen_text_default,
	concatVideoComposeNode: concat_video_default,
	musicCoverNode: music_cover_default,
	splitVideoNode: split_video_default,
	splitTextNode: split_text_default,
	musicBriefNode: music_brief_default,
	separateSoundNode: separate_sound_default
};
/**
* Edge type mapping
*/
const EDGE_TYPES = { "custom-edge": CustomEdge };
/**
* Node categories
*/
const NODE_CATEGORIES = {
	DATA: [
		"imageNode",
		"textNode",
		"videoNode",
		"audioNode",
		"fileNode",
		"modelNode",
		"linkNode"
	],
	ADD: [
		"addImageNode",
		"addTextNode",
		"addAudioNode",
		"addVideoNode",
		"addLinkNode",
		"addFileNode",
		"addModelNode"
	],
	TRANSFORM: [
		"imageGenVideoNode",
		"textGenVideoNode",
		"imageGenModelNode",
		"imagePoseNode",
		"imageBodySegNode",
		"imageNormalNode",
		"imageMattingNode",
		"videoGenModelNode",
		"speechGenVideoNode",
		"imageGenImageNode",
		"imageGenImageUpscaleNode",
		"genTextNode",
		"textGenImageNode",
		"textGenMusicNode",
		"textGenSpeechCloneNode",
		"textGenSpeechPresetNode",
		"textGenSpeechInstructNode",
		"removeVideoSubtitleNode",
		"removeWatermarkNode",
		"videoUpscaleNode",
		"videoEditNode",
		"extractAudioNode",
		"removeVideoAudioNode",
		"denoiseAudioSubtitleNode",
		"separateAudioTrackNode",
		"separateSpeakerNode",
		"convertVoiceNode",
		"audioDescribeNode",
		"imageGenTextNode",
		"videoGenTextNode",
		"videoGenTextSpeechRecognizeNode",
		"audioGenTextSpeechRecognizeNode",
		"fileGenTextNode",
		"linkGenTextNode",
		"getFirstFrameNode",
		"getLastFrameNode",
		"musicRepaintNode",
		"musicExtractNode",
		"musicLegoNode",
		"musicCompleteNode"
	],
	BATCH: [
		"dropVideoNode",
		"arrangeNode",
		"concatVideoNode"
	],
	COMPOSE: [
		"mergeVideoAudioNode",
		"audioVideoLipSyncNode",
		"imageFusionNode",
		"imagesGenVideoNode",
		"refsGenVideoNode",
		"speechImageGenVideoNode",
		"speechTextGenVideoNode",
		"speechVideoGenVideoNode",
		"videoImageGenVideoMoveNode",
		"videoImageGenVideoMixNode",
		"imageImageGenVideoNode",
		"textAudioGenSpeechNode",
		"textGenSpeechCloneComposeNode",
		"imageGenVideoComposeNode",
		"textsGenTextNode",
		"concatVideoComposeNode",
		"musicCoverNode"
	],
	DECOMPOSE: [
		"splitVideoNode",
		"splitTextNode",
		"musicBriefNode",
		"separateSoundNode"
	]
};
//#endregion
//#region src/canvas/flow-canvas.tsx
const FIT_VIEW_OPTS = {
	duration: 800,
	padding: .3,
	maxZoom: 1.2,
	minZoom: .1
};
const selector = (state) => ({
	nodes: state.nodes,
	edges: state.edges
});
/** Tracks the `dark` class on <html> to drive React Flow's colorMode. */
function useDocumentColorMode() {
	const [colorMode, setColorMode] = useState("light");
	useEffect(() => {
		const read = () => setColorMode(document.documentElement.classList.contains("dark") ? "dark" : "light");
		const observer = new MutationObserver((mutations) => {
			if (mutations.some((m) => m.attributeName === "class")) read();
		});
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["class"]
		});
		read();
		return () => observer.disconnect();
	}, []);
	return colorMode;
}
const FlowCanvas = forwardRef(function FlowCanvas({ children, panelPosition = "bottom-center", panelClassName = "!mb-5 z-10", hidden = false, showControls = true, className, followNewNodes = true }, ref) {
	const tEdges = useTranslations("Workspace.edges");
	const colorMode = useDocumentColorMode();
	const reactFlowInstance = useReactFlow();
	const { nodes, edges } = useFlow(useShallow(selector));
	const onNodesChange = useFlow.getState().onNodesChange;
	const onEdgesChange = useFlow.getState().onEdgesChange;
	const onSelectionChange = useFlow.getState().onSelectionChange;
	const onConnect = useFlow.getState().onConnect;
	useImperativeHandle(ref, () => ({
		fitView: (nodeIds) => {
			reactFlowInstance.fitView({
				...FIT_VIEW_OPTS,
				...nodeIds ? { nodes: nodeIds.map((id) => ({ id })) } : {}
			});
		},
		focusNode: (nodeId) => {
			reactFlowInstance.fitView({
				...FIT_VIEW_OPTS,
				nodes: [{ id: nodeId }]
			});
		},
		tidyLayout: (seedIds) => useFlow.getState().autoLayout(seedIds)
	}), [reactFlowInstance]);
	const isValidConnection = useCallback((connection) => {
		const { nodes, edges, reconnectingEdgeId } = useFlow.getState();
		return isValidFlowConnection(connection, nodes, edges, reconnectingEdgeId ?? void 0);
	}, []);
	const [pendingDeleteEdgeId, setPendingDeleteEdgeId] = useState(null);
	const onReconnectStart = useCallback((_event, edge) => {
		useFlow.getState().setReconnectingEdgeId(edge.id);
	}, []);
	const onReconnect = useCallback((oldEdge, newConnection) => {
		const { edges, setEdges, commitHistory } = useFlow.getState();
		commitHistory();
		setEdges(reconnectEdge(oldEdge, newConnection, edges));
	}, []);
	const onReconnectEnd = useCallback((_event, edge, _handleType, connectionState) => {
		useFlow.getState().setReconnectingEdgeId(null);
		if (!connectionState.toHandle) setPendingDeleteEdgeId(edge.id);
	}, []);
	const confirmDeleteEdge = useCallback(() => {
		if (!pendingDeleteEdgeId) return;
		const { edges, setEdges, commitHistory } = useFlow.getState();
		commitHistory();
		setEdges(edges.filter((e) => e.id !== pendingDeleteEdgeId));
		setPendingDeleteEdgeId(null);
	}, [pendingDeleteEdgeId]);
	useEffect(() => {
		if (!followNewNodes) return;
		return useFlow.getState().onNodeCreated((nodeIds) => {
			if (nodeIds.length === 0) return;
			setTimeout(() => {
				reactFlowInstance.fitView({
					...FIT_VIEW_OPTS,
					nodes: nodeIds.map((id) => ({ id }))
				});
			}, 50);
		});
	}, [reactFlowInstance, followNewNodes]);
	const handleNodeDoubleClick = useCallback((_event, node) => {
		if (!node?.position) return;
		reactFlowInstance.fitView({
			...FIT_VIEW_OPTS,
			nodes: [{ id: node.id }]
		});
	}, [reactFlowInstance]);
	const handleDragStart = useCallback(() => {
		useFlow.getState().commitHistory();
	}, []);
	const handlePaneClick = useCallback(() => {
		const store = useFlow.getState();
		if (store.comboMode) store.setComboMode(false);
	}, []);
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key !== "Escape") return;
			const store = useFlow.getState();
			if (store.comboMode) store.setComboMode(false);
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, []);
	return /* @__PURE__ */ jsxs(Fragment, { children: [/* @__PURE__ */ jsx("div", {
		className: cn("w-full h-full", hidden && "invisible pointer-events-none", className),
		"aria-hidden": hidden,
		children: /* @__PURE__ */ jsxs(ReactFlow, {
			nodes,
			onNodesChange,
			edges,
			onEdgesChange,
			onConnect,
			isValidConnection,
			onReconnect,
			onReconnectStart,
			onReconnectEnd,
			nodeTypes: NODE_TYPES,
			edgeTypes: EDGE_TYPES,
			defaultEdgeOptions: {
				type: "custom-edge",
				selectable: false,
				focusable: false
			},
			connectionLineStyle: {
				strokeWidth: 3,
				stroke: "#94a3b8",
				strokeLinecap: "round"
			},
			onSelectionChange,
			onNodeDoubleClick: handleNodeDoubleClick,
			onPaneClick: handlePaneClick,
			onNodeDragStart: handleDragStart,
			onSelectionDragStart: handleDragStart,
			nodeDragThreshold: 1,
			nodeOrigin: [.5, .5],
			selectNodesOnDrag: false,
			fitView: true,
			minZoom: .001,
			maxZoom: 1e3,
			proOptions: { hideAttribution: true },
			colorMode,
			children: [
				/* @__PURE__ */ jsx(Background, {}),
				showControls && /* @__PURE__ */ jsx(Controls, {}),
				children ? /* @__PURE__ */ jsx(Panel, {
					position: panelPosition,
					className: panelClassName,
					children
				}) : null
			]
		})
	}), /* @__PURE__ */ jsx(AlertDialog, {
		open: pendingDeleteEdgeId !== null,
		onOpenChange: (open) => {
			if (!open) setPendingDeleteEdgeId(null);
		},
		children: /* @__PURE__ */ jsxs(AlertDialogContent, { children: [/* @__PURE__ */ jsxs(AlertDialogHeader, { children: [/* @__PURE__ */ jsx(AlertDialogTitle, { children: tEdges("deleteConfirmTitle") }), /* @__PURE__ */ jsx(AlertDialogDescription, { children: tEdges("deleteConfirmDescription") })] }), /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [/* @__PURE__ */ jsx(AlertDialogCancel, { children: tEdges("cancel") }), /* @__PURE__ */ jsx(AlertDialogAction, {
			onClick: confirmDeleteEdge,
			children: tEdges("delete")
		})] })] })
	})] });
});
//#endregion
//#region src/canvas/nodes/modality/model-node.formats.ts
/**
* 3D Model Node - Supported Formats
*
* This document lists all 3D file formats supported by the model-node.tsx component and their features.
*/
const SUPPORTED_FORMATS = {
	glb: {
		name: "glTF Binary",
		extension: ".glb",
		description: "Recommended format. Supports animation, materials, and compression.",
		features: [
			"Animation",
			"PBR materials",
			"Lightweight",
			"Cross-platform"
		],
		loader: "GLTFLoader"
	},
	gltf: {
		name: "glTF (Embedded JSON)",
		extension: ".gltf",
		description: "Text form of glTF; good for editing and debugging.",
		features: [
			"Animation",
			"PBR materials",
			"Easy to edit"
		],
		loader: "GLTFLoader"
	},
	obj: {
		name: "Wavefront OBJ",
		extension: ".obj",
		description: "Classic mesh format; widely supported.",
		features: [
			"Simple meshes",
			"Broad compatibility",
			"Lightweight"
		],
		loader: "OBJLoader"
	},
	fbx: {
		name: "Autodesk FBX",
		extension: ".fbx",
		description: "Professional 3D format; supports skeletons and animation.",
		features: [
			"Skeletal animation",
			"Morph targets",
			"Materials",
			"Complex topology"
		],
		loader: "FBXLoader"
	},
	ply: {
		name: "Polygon File Format",
		extension: ".ply",
		description: "Point cloud and mesh format; supports color and normals.",
		features: [
			"Point cloud",
			"Color info",
			"Normal data"
		],
		loader: "PLYLoader"
	},
	ptx: {
		name: "Leica Point Cloud",
		extension: ".ptx",
		description: "Laser-scan point cloud format.",
		features: [
			"High-density point cloud",
			"Scan data",
			"XYZ + RGB"
		],
		loader: "PointCloudLoader (Custom)"
	},
	pts: {
		name: "Simple Point Cloud",
		extension: ".pts",
		description: "Simple point cloud format (X Y Z R G B).",
		features: [
			"Lightweight point cloud",
			"XYZ coordinates",
			"RGB color"
		],
		loader: "PointCloudLoader (Custom)"
	},
	xyz: {
		name: "XYZ Point Cloud",
		extension: ".xyz",
		description: "Simple point cloud format (X Y Z).",
		features: ["Basic point cloud", "Simple format"],
		loader: "PointCloudLoader (Custom)"
	},
	spz: {
		name: "Scaniverse Splat",
		extension: ".spz",
		description: "Compressed Gaussian splat format.",
		features: [
			"High quality",
			"Fast rendering",
			"Compressed"
		],
		loader: "SplatMesh (Spark)"
	},
	splat: {
		name: "Splat Format",
		extension: ".splat",
		description: "Gaussian splat format (Antimatter15).",
		features: ["Gaussian splat", "Photorealistic rendering"],
		loader: "SplatMesh (Spark)"
	},
	ksplat: {
		name: "Gaussian Splat",
		extension: ".ksplat",
		description: "Gaussian splat format (mkkellogg).",
		features: ["Gaussian splat", "Optimized variant"],
		loader: "SplatMesh (Spark)"
	},
	sog: {
		name: "PlayCanvas Splat",
		extension: ".sog",
		description: "PlayCanvas Gaussian splat format.",
		features: ["Gaussian splat", "Web-optimized"],
		loader: "SplatMesh (Spark)"
	},
	stl: {
		name: "Stereolithography",
		extension: ".stl",
		description: "Common 3D printing format.",
		features: [
			"3D printing",
			"Simple meshes",
			"Binary / ASCII"
		],
		loader: "STLLoader"
	},
	step: {
		name: "STEP (ISO 10303)",
		extension: ".step / .stp",
		description: "Professional CAD interchange format.",
		features: [
			"Parametric geometry",
			"Assemblies",
			"Precise data"
		],
		note: "Requires dedicated library support; convert to GLTF or OBJ recommended."
	},
	iges: {
		name: "IGES",
		extension: ".igs / .iges",
		description: "CAD file interchange format.",
		features: ["CAD data", "Precise geometry"],
		note: "Requires dedicated library support; convert to GLTF or OBJ recommended."
	},
	dae: {
		name: "COLLADA",
		extension: ".dae",
		description: "COLLADA 3D format.",
		features: [
			"Animation",
			"Materials",
			"Cross-platform"
		],
		loader: "ColladaLoader"
	},
	usdz: {
		name: "USD Zip",
		extension: ".usdz",
		description: "USD compressed format (Apple's recommendation for AR).",
		features: [
			"AR / VR",
			"Compressed",
			"Cross-platform"
		],
		loader: "USDZLoader"
	},
	usd: {
		name: "Pixar USD",
		extension: ".usd",
		description: "Pixar Universal Scene Description format.",
		features: [
			"Complex scenes",
			"Parametric",
			"Version control"
		],
		loader: "USDZLoader"
	},
	tds: {
		name: "3D Studio Max",
		extension: ".3ds",
		description: "3DS Max model format.",
		features: [
			"Animation",
			"Materials",
			"Legacy format"
		],
		loader: "TDSLoader"
	},
	vtp: {
		name: "VTK Polydata",
		extension: ".vtp",
		description: "VTK polygon format.",
		features: ["Scientific computing", "Mesh data"],
		loader: "VTKLoader"
	}
};
const FORMAT_CATEGORIES = {
	recommended: {
		description: "Best compatibility and feature support",
		formats: [".glb", ".gltf"]
	},
	general3D: {
		description: "Widely supported general-purpose formats",
		formats: [
			".fbx",
			".obj",
			".dae"
		]
	},
	pointCloud: {
		description: "For point cloud data",
		formats: [
			".ply",
			".ptx",
			".pts",
			".xyz"
		]
	},
	gaussianSplat: {
		description: "Next-generation high-quality rendering format (requires the Spark library)",
		formats: [
			".spz",
			".splat",
			".ksplat",
			".sog"
		]
	},
	cad: {
		description: "Engineering design and manufacturing files",
		formats: [
			".step",
			".stp",
			".igs",
			".iges"
		],
		note: "Convert to GLTF for the best support."
	},
	"3DPrinting": {
		description: "3D-printing-related formats",
		formats: [".stl"]
	},
	AppleAR: {
		description: "Apple's recommended AR format",
		formats: [".usdz"]
	},
	academic: {
		description: "Academic and scientific-computing formats",
		formats: [".vtp", ".usd"]
	},
	legacy: {
		description: "Still supported but no longer recommended",
		formats: [".3ds", ".dae"]
	}
};
/**
* 1. Using GLB files
*
* GLB is the recommended general-purpose format, with these advantages:
* - Supports animation and skeletons
* - Supports PBR materials
* - Smaller files and fast loading
* - Best cross-platform compatibility
*
* Exporting GLB for Three.js:
* - Blender: File > Export > glTF 2.0 (.glb/.gltf)
* - Maya: Use the babylon.js exporter or a third-party plugin
* - 3ds Max: Use the babylon.js exporter
*/
/**
* 2. Point cloud data
*
* Supports multiple point cloud formats:
* - PLY: Most flexible, supports colors and normals
* - PTX: Standard format for Leica scanners
* - PTS/XYZ: Simple text formats
*
* Point cloud file format examples:
* XYZ format:
*   0.0 0.0 0.0
*   1.0 1.0 1.0
*
* XYZ RGB format:
*   0.0 0.0 0.0 255 0 0
*   1.0 1.0 1.0 0 255 0
*/
/**
* 3. Gaussian splat formats
*
* These formats provide the highest quality real-time rendering:
*
* SPZ (recommended):
* - Scaniverse format, with the best compression ratio
* - Website: https://scaniverse.com
*
* SPLAT:
* - Antimatter15 implementation
* - Larger files, but widely supported
*
* Generating Gaussian splats:
* - Use professional tools such as Reality Capture or Metashape
* - Or use the open-source gaussian-splatting project
*/
/**
* 4. Handling CAD formats
*
* STEP/IGES formats require special handling:
* - High component complexity
* - Requires dedicated CAD libraries
* - Recommended to convert to GLTF or OBJ in CAD software
*
* Conversion steps:
* 1. Open the file in CAD software
* 2. Export as GLTF or OBJ
* 3. Upload the converted file
*/
/**
* 5. 3D printing formats
*
* STL format:
* - Used for 3D printing
* - Mesh format without textures or colors
* - Can be binary or ASCII
*/
const RECOMMENDATIONS = {
	bestGeneralPurpose: ".glb",
	bestPointCloud: ".ply",
	bestPhotogrammetry: ".spz (Gaussian Splat)",
	"3DPrinting": ".stl",
	web: ".glb or .gltf",
	mobileAR: ".glb or .usdz",
	gameEngine: ".fbx or .glb",
	professionalCAD: ".step (convert to .glb)",
	pointCloudVisualization: ".ply or .ptx",
	smallestFileSize: ".glb (compressed)"
};
const TROUBLESHOOTING = {
	formatUnsupported: { solution: "Convert the file to a supported format (.glb is recommended)." },
	fileFailedToLoad: {
		causes: [
			"Network failure during download",
			"Corrupted file",
			"Wrong format"
		],
		solution: "Validate the file and try re-converting or re-exporting from the source software."
	},
	colorsOrMaterialsMissing: {
		causes: [
			"The format does not support textures",
			"Incorrect export settings",
			"Missing referenced texture files"
		],
		solution: "Use a format that supports materials (such as .glb), or ensure all assets are exported correctly."
	},
	modelNotFullyVisible: {
		causes: ["Model is very large or very small", "Camera framing issue"],
		solution: "Auto-fit handles most cases; use the mouse controls to adjust the view manually."
	},
	animationNotPlaying: {
		causes: ["The format does not support animation", "Animation was not included on export"],
		solution: "Use a format that supports animation (.glb, .fbx) and ensure the animation is exported."
	}
};
//#endregion
//#region src/canvas/ui/badge.tsx
const badgeVariants = cva("inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden", {
	variants: { variant: {
		default: "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
		secondary: "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
		destructive: "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
		outline: "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground"
	} },
	defaultVariants: { variant: "default" }
});
function Badge({ className, variant, asChild = false, ...props }) {
	return /* @__PURE__ */ jsx(asChild ? Slot : "span", {
		"data-slot": "badge",
		className: cn(badgeVariants({ variant }), className),
		...props
	});
}
//#endregion
//#region src/canvas/ui/checkbox.tsx
function Checkbox({ className, ...props }) {
	return /* @__PURE__ */ jsx(CheckboxPrimitive.Root, {
		"data-slot": "checkbox",
		className: cn("peer border-input dark:bg-input/30 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground dark:data-[state=checked]:bg-primary data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50", className),
		...props,
		children: /* @__PURE__ */ jsx(CheckboxPrimitive.Indicator, {
			"data-slot": "checkbox-indicator",
			className: "flex items-center justify-center text-current transition-none",
			children: /* @__PURE__ */ jsx(CheckIcon, { className: "size-3.5" })
		})
	});
}
//#endregion
//#region src/canvas/ui/error-boundary.tsx
var ErrorBoundary = class extends Component {
	constructor(props) {
		super(props);
		this.state = {
			hasError: false,
			error: null
		};
	}
	static getDerivedStateFromError(error) {
		return {
			hasError: true,
			error
		};
	}
	componentDidCatch(error, info) {
		logger.error("ErrorBoundary caught:", error, info.componentStack);
	}
	handleReset = () => {
		this.setState({
			hasError: false,
			error: null
		});
	};
	render() {
		if (this.state.hasError) {
			if (this.props.fallback) return this.props.fallback;
			const t = getClientTranslator("Errors");
			return /* @__PURE__ */ jsxs("div", {
				className: "flex flex-col items-center justify-center min-h-[200px] gap-4 p-8 text-center",
				children: [/* @__PURE__ */ jsx("p", {
					className: "text-sm text-muted-foreground",
					children: this.state.error?.message || t("somethingWentWrong")
				}), /* @__PURE__ */ jsx("button", {
					type: "button",
					onClick: this.handleReset,
					className: "px-4 py-2 text-sm rounded-md border hover:bg-accent transition-colors",
					children: t("tryAgain")
				})]
			});
		}
		return this.props.children;
	}
};
//#endregion
//#region src/canvas/ui/separator.tsx
function Separator({ className, orientation = "horizontal", decorative = true, ...props }) {
	return /* @__PURE__ */ jsx(SeparatorPrimitive.Root, {
		"data-slot": "separator",
		decorative,
		orientation,
		className: cn("bg-border shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px", className),
		...props
	});
}
//#endregion
//#region src/canvas/ui/sheet.tsx
function Sheet({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Root, {
		"data-slot": "sheet",
		...props
	});
}
function SheetTrigger({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Trigger, {
		"data-slot": "sheet-trigger",
		...props
	});
}
function SheetClose({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Close, {
		"data-slot": "sheet-close",
		...props
	});
}
function SheetPortal({ ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Portal, {
		"data-slot": "sheet-portal",
		...props
	});
}
function SheetOverlay({ className, ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Overlay, {
		"data-slot": "sheet-overlay",
		className: cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50", className),
		...props
	});
}
function SheetContent({ className, children, side = "right", ...props }) {
	return /* @__PURE__ */ jsxs(SheetPortal, { children: [/* @__PURE__ */ jsx(SheetOverlay, {}), /* @__PURE__ */ jsxs(DialogPrimitive.Content, {
		"data-slot": "sheet-content",
		className: cn("bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500", side === "right" && "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm", side === "left" && "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm", side === "top" && "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b", side === "bottom" && "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t", className),
		...props,
		children: [children, /* @__PURE__ */ jsxs(DialogPrimitive.Close, {
			className: "ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none",
			children: [/* @__PURE__ */ jsx(XIcon, { className: "size-4" }), /* @__PURE__ */ jsx("span", {
				className: "sr-only",
				children: "Close"
			})]
		})]
	})] });
}
function SheetHeader({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "sheet-header",
		className: cn("flex flex-col gap-1.5 p-4", className),
		...props
	});
}
function SheetFooter({ className, ...props }) {
	return /* @__PURE__ */ jsx("div", {
		"data-slot": "sheet-footer",
		className: cn("mt-auto flex flex-col gap-2 p-4", className),
		...props
	});
}
function SheetTitle({ className, ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Title, {
		"data-slot": "sheet-title",
		className: cn("text-foreground font-semibold", className),
		...props
	});
}
function SheetDescription({ className, ...props }) {
	return /* @__PURE__ */ jsx(DialogPrimitive.Description, {
		"data-slot": "sheet-description",
		className: cn("text-muted-foreground text-sm", className),
		...props
	});
}
//#endregion
export { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger, AudioRecorderWithVisualizer, Badge, Button, CANVAS_LOCALES, CanvasProvider, Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Checkbox, DEFAULT_QWEN_SPEAKER, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger, EDGE_TYPES, ErrorBoundary, FORMAT_CATEGORIES, FlowCanvas, Input, Label, LibInput, NODE_CATEGORIES, NODE_TYPES, NodePluginIdSelect, Progress, QWEN_SPEAKERS, RECOMMENDATIONS, SSE_TASK_MESSAGE_EVENT, SUPPORTED_FORMATS, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue, Separator, Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger, Skeleton, Slider, SpeakerVoiceRecorder, Switch, TASK_CANCEL_REQUEST_EVENT, TROUBLESHOOTING, Tabs, TabsContent, TabsList, TabsTrigger, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, UploadValidationError, VideoRecorder, WORKSPACE_MODE_KEY, Waterfall, WhiteBoard, apiClient, apiDelete, apiGet, apiPatch, apiPost, apiPut, apiUrl, applyResolvedOutputRoutes, assetUrl, badgeVariants, buttonVariants, canvasMessages, cn, computeOutputView, configureCanvasHost, createMaterial, createTask, deleteMaterial, emitSSEConnected, emitSSETaskMessage, emitTaskCancelRequest, getCanvasHost, getCanvasLocale, getClientTranslator, getFileUrl, getGlobalFileCache, getGlobalFileQueue, getImageDimensions, getMaterialFileUrls, getNodePluginModels, getNodePluginParams, getPresignedUploadUrl, getPrivateFileUrl, getSharedFileUrl, getTaskStopUrl, getTaskWaitUrl, getVideoMetadata, hostFetch, isCanvasLocale, isImageFile, isVideoFile, listMaterials, listTasks, loadPluginModelCatalog, normalizeTaskPayloadData, pluginDisplayName, refreshPluginsRegistry, saveFromTask, setCanvasLocale, showErrorToast, toggleFavorite, traceMaterialWorkflow, updateTaskStatus, useAbiExecution, useAbiForm, useBatchTaskManager, useCreateTask, useDropzone, useFileAsyncLoader, useFileAsyncLoaderBatch, useFileLoaderStats, useFileUrl, useFlow, useLiveModelsStore, useMultipleUpload, useNodeAbiSpec, useNodePluginIds, useNodePluginIdsUnion, useNodePluginModels, useNodePluginParams, useNodePluginResolver, useNodeState, useNodeTaskUpdate, useNodeType, usePluginMeta, usePluginsRegistry, usePluginsRegistryStore, useResolvedPluginId, useTask, useTaskStore, useTaskSubscription, useUpload, useUpstreamNodeIds, validateFile };
